/**
 * Excel Service
 * Handles reading workbook content and applying modifications
 */

/* global Excel, console, fetch, btoa */

import {
  WorkbookContent,
  WorksheetContent,
  CellRange,
  CellStyleInfo,
  ExcelChart,
  ExcelTable,
  WorkbookModification,
  WorkbookOperation,
  WorksheetModification,
  CellModification,
  RangeModification,
  TableModification,
  LogoModification,
  CellFormat,
  ShapeModification,
  ActiveGraphModification,
  ChartModification,
  OpenWorkbookInfo,
  WorkbookConnection,
  WorkbookNamedObject,
} from "./types";
import { getCompanyLogo } from "../shared/api/finster";
import { FACTSET_SCOPES } from "../shared/factset/factsetConstants";
import { getFactSetOAuthSession } from "../shared/factset/factsetOAuthClient";
import { registerFactSetClientSessionClearHandler } from "../shared/factset/factsetSessionLifecycle";
import {
  tryDispatchCellViaTtf,
  type TtfRunContext,
} from "./services/factset/ttfCellDispatch";
import { diag, isDiagEnabled } from "./aiDiag";
import {
  shouldUseExcelIntentBridge,
  setCellContents,
  fillRangeContents,
  getActiveCellAddress as getActiveCellAddressViaBridge,
  getActiveSheetName as getActiveSheetNameViaBridge,
  getActiveExcelContext as getActiveExcelContextViaBridge,
  autofitRange,
  resizeAbsolute,
  setCalculationMode,
  getRangeInfo,
  getRangeAsCsv,
  getWorkbookSheets,
  getSelectedAddressRange,
  applicationCalculate,
  getCalculationMode,
  getCharts,
  getTables,
  parseFormulas,
  addWorksheet,
  activateWorksheet,
  deleteWorksheet,
  renameWorksheet,
  setWorksheetVisibility,
  addWorkbook,
  copyWorksheet,
  insertWorksheetsFromBase64,
  deleteRange,
  insertRange,
  clearRange,
  sortRange,
  applyFormatting,
  moveRange,
  moveSheet,
  addActiveGraph,
  setActiveGraphXml,
  type AddActiveGraphCoords,
  addExcelChart,
  updateExcelChart,
  addSeriesToChart,
  updateSeriesInChart,
  removeSeriesFromChart,
  addImageFromBase64,
  addNamedObject,
  hideColumns,
  hideRows,
  showColumns,
  showRows,
  groupColumns,
  groupRows,
  ungroupColumns,
  ungroupRows,
  freezeUnfreezePanes,
  deleteShape,
  modifyShapes,
  selectShapes,
  selectRange,
  removeNamedObject,
  searchWorkbook,
  getOpenWorkbooks,
  getWorkbookConnections,
  getWorkbookNamedObjects,
  refreshWorksheets,
  refreshCalculate,
  getRefreshStatus,
  getRefresh,
  awaitRefreshCalculate,
  beginUndo,
  endUndo,
  setComment as setCellComment,
  deleteComment as deleteCellComment,
  setNote as setCellNote,
  deleteNote as deleteCellNote,
  insertTtfApiResponse,
} from "./excelHostOps";
import type { ShapeModificationEntry } from "./excelHostOps";
import type {
  RangeAddress,
  AddWorksheetOptions,
  ChartingChartInfo,
  ClearRangeClearType,
} from "./services/factset/spreadsheetIntents";
import {
  hostReadyRetryCopy,
  isEditModeError,
  waitForHostReady,
  waitForHostReadyPatient,
  drainEditModeAfterWrite,
  type HostReadyReason,
} from "./services/hostReady";
import {
  isFdsBoundFormula,
  anyFormulaWrite,
  findFqlDatePolicyViolation,
} from "./formulaWriteDetection";
import { supportsRangeMutationWorkbookName } from "./excelTargetingConfig";
import { foregroundCoordinator } from "./foregroundCoordinator";
import {
  inferFactSetFormatHint,
  isDirectFactSetFormula,
  normalizeFactSetNumberFormat,
} from "./factsetValueSemantics";
import { FactSetFormulaFormatRegistry } from "./factsetFormulaFormatRegistry";
import {
  getFactSetFormatGuard,
  normalizeFactSetFormatsForModification,
} from "./factsetFormatEnforcement";
import { parseCellRangeAddress } from "./excelCellAddress";

/**
 * Extract an Excel number-format string from a `CellFormat.numberFormat`
 * (which can be either a raw string or a `{ format }` object). Returns
 * `undefined` when the value is missing or not a non-empty string.
 */
function extractNumberFormatString(format?: CellFormat): string | undefined {
  const nf = format?.numberFormat;
  if (!nf) return undefined;
  const raw = typeof nf === "string" ? nf : nf.format;
  return typeof raw === "string" && raw.length > 0 ? raw : undefined;
}

/**
 * Enforce the percentage-point display invariant for a direct FactSet cell
 * write at the common host boundary. Formulas and values remain untouched.
 */
export function normalizeDirectFactSetCellFormat(
  modification: CellModification,
): CellModification {
  if (modification.action !== "set") return modification;
  const formula =
    typeof modification.formula === "string"
      ? modification.formula
      : typeof modification.value === "string"
        ? modification.value
        : undefined;
  if (!isDirectFactSetFormula(formula)) return modification;

  const hint = inferFactSetFormatHint({ formula });
  if (hint.semantics !== "percentage_points") return modification;
  const currentNumberFormat = extractNumberFormatString(modification.format);
  const correctedNumberFormat = currentNumberFormat
    ? normalizeFactSetNumberFormat(
        currentNumberFormat,
        hint.semantics,
      ).format
    : hint.recommendedNumberFormat;
  if (
    !correctedNumberFormat ||
    correctedNumberFormat === currentNumberFormat
  ) {
    return modification;
  }

  diag("format", "factset_direct_cell_format_normalized", {
    address: modification.address,
    dataItem: hint.dataItem,
    source: hint.source,
    originalFormat: currentNumberFormat,
    correctedFormat: correctedNumberFormat,
  });
  return {
    ...modification,
    format: {
      ...(modification.format ?? {}),
      numberFormat: { format: correctedNumberFormat },
    },
  };
}

/**
 * Identify direct percentage-point writes whose content is fully replaced
 * later in the same host payload. Those transient writes must not apply a
 * number format that the surviving ordinary value/formula would inherit.
 */
function collectNonFinalFactSetCellWrites(
  modifications: WorkbookModification,
): WeakSet<CellModification> {
  interface OrderedContentWrite {
    sheetKey: string;
    address: string;
    cell?: CellModification;
  }

  const ordered: OrderedContentWrite[] = [];
  for (const worksheet of modifications.worksheets ?? []) {
    const sheetKey = (worksheet.sheetName ?? "").trim().toLowerCase();
    for (const cell of worksheet.cells ?? []) {
      if (
        cell.action === "clear" ||
        (cell.action === "set" &&
          (cell.formula !== undefined || cell.value !== undefined))
      ) {
        ordered.push({ sheetKey, address: cell.address, cell });
      }
    }
    for (const range of worksheet.ranges ?? []) {
      if (
        (range.action === "clear" &&
          ((range.clear?.clearType ?? "All") === "All" ||
            (range.clear?.clearType ?? "All") === "Contents")) ||
        (range.action === "fill" &&
          (matrixHasContent(range.formulas) || matrixHasContent(range.values)))
      ) {
        ordered.push({ sheetKey, address: range.address });
      }
    }
  }

  const nonFinal = new WeakSet<CellModification>();
  for (let index = 0; index < ordered.length; index += 1) {
    const write = ordered[index];
    const cell = write.cell;
    if (!cell || cell.action !== "set") continue;
    const formula =
      typeof cell.formula === "string"
        ? cell.formula
        : typeof cell.value === "string"
          ? cell.value
          : undefined;
    if (
      inferFactSetFormatHint({ formula }).semantics !== "percentage_points"
    ) {
      continue;
    }
    const fullyOverwritten = ordered.slice(index + 1).some(
      (later) =>
        later.sheetKey === write.sheetKey &&
        excelRangeContainsAddress(later.address, write.address),
    );
    if (fullyOverwritten) nonFinal.add(cell);
  }
  return nonFinal;
}

function matrixHasContent(value: unknown): boolean {
  return (
    Array.isArray(value) &&
    value.some(
      (row) =>
        Array.isArray(row) &&
        row.some((cell) => cell !== undefined),
    )
  );
}

function withoutCellNumberFormat(
  modification: CellModification,
): CellModification {
  if (!modification.format?.numberFormat) return modification;
  const { numberFormat: _numberFormat, ...remainingFormat } =
    modification.format;
  return {
    ...modification,
    ...(Object.keys(remainingFormat).length > 0
      ? { format: remainingFormat }
      : { format: undefined }),
  };
}

/**
 * Apply a planner-shaped `CellFormat` to a range via the FactSet
 * `ApplyFormatting` intent. Best-effort by contract:
 *
 *  - Transport / host-side failures are caught and surfaced through
 *    `trackOperation` rather than thrown. Format failure should never
 *    abort the surrounding apply (the cell writes already landed; we
 *    don't want a font-color error to undo a successful formula write).
 *  - **Exception**: `InvalidOperationInCellEditMode` is re-thrown so
 *    the surrounding apply's outer catch can surface the unified L5
 *    abort message instead of letting the cascade silently continue.
 *  - Non-empty `errors[]` from the host is surfaced as a single warn
 *    line so the operator can tell which range(s) didn't fully format.
 *  - `format.merge` is not carried by the wire intent — emit a warn
 *    so the operator knows merge has to come through a different path.
 *
 * `numberFormat` may also have been applied per-cell via
 * `InsertContentsToRange.contents[].numberFormat` upstream; sending it
 * again on `ApplyFormatting` is idempotent.
 */
async function applyFormatToRangeViaBridge(
  address: string,
  format: CellFormat,
  trackOperation: (op: string, address?: string) => void,
  sheetName?: string,
  workbookName?: string,
  strict = false,
): Promise<void> {
  diag("format", "apply_format", { address, numberFormat: extractNumberFormatString(format), bold: format.font?.bold, fontColor: format.font?.color, fillColor: format.fill?.color, sheetName });
  try {
    const errors = await applyFormatting([{ address, format, sheetName, workbookName }]);
    if (errors.length > 0) {
      if (strict) {
        throw new Error(
          `ApplyFormatting reported ${errors.length} error(s): ${JSON.stringify(errors)}`,
        );
      }
      trackOperation(
        `⚠️ ${address}: ApplyFormatting reported ${errors.length} error(s): ${JSON.stringify(errors)}`,
        address
      );
    }
  } catch (e) {
    // Edit-mode errors must propagate so the surrounding apply path's
    // outer catch can stop with the unified message — same cascade-
    // protection rationale as the cell/range loops in the bridge apply.
    if (isEditModeError(e)) throw e;
    if (strict) throw e;
    trackOperation(
      `⚠️ ${address}: ApplyFormatting failed: ${e instanceof Error ? e.message : String(e)}`,
      address
    );
  }
  if (format.merge === true) {
    trackOperation(
      `⚠️ ${address}: skipping merge — not supported by ApplyFormatting intent`,
      address
    );
  }
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Excel `CVErr` sentinel values. Both Office.js (`range.values`) and the
 * FactSet bridge (`GetRangeInfo` cell records) surface error cells as
 * these magic negative integers — they are HRESULT-style codes
 * (`0x800A07XX`) that decode to the user-facing `#DIV/0!`, `#N/A`,
 * etc. Without this mapping the model sees raw `-2146826246`s in cell
 * values and reports them as legitimate large negative numbers (e.g.
 * "B5 contains a large negative value: -2146826246"), which is a
 * confidence-destroying summary bug.
 *
 * Reference: the `Excel.ErrorCellValueType` enum in Office.js and the
 * underlying `xlCVErr*` constants. Numbers below are stable across
 * Excel versions and matched by inspection of `signed int32` values
 * Office.js returns for known `#NAME?` / `#DIV/0!` / etc. cells.
 */
const CVERR_SENTINEL_TO_LABEL: Readonly<Record<number, string>> = {
  [-2146826281]: "#NULL!",
  [-2146826288]: "#NUM!",
  [-2146826265]: "#REF!",
  [-2146826259]: "#VALUE!",
  [-2146826252]: "#N/A",
  [-2146826246]: "#DIV/0!",
  [-2146826245]: "#GETTING_DATA",
  [-2146826273]: "#NAME?",
};

const EXCEL_TTF_MAX_ATTEMPTS_PER_APPLY = 3;
const EXCEL_TTF_RUN_BUDGET_MS = 25_000;

/**
 * Coerce an Excel-shaped raw cell value into the AI-prompt-safe scalar
 * shape we use across `WorkbookContent`. The motivating fix is mapping
 * `CVErr` sentinel ints (e.g. `-2146826246` → `"#DIV/0!"`) to readable
 * error labels so chat summaries don't report them as huge negative
 * numbers, but the helper also normalises a few other shapes that
 * either path can leak: `undefined` collapses to `null`, NaN/Infinity
 * collapse to `null` (numeric junk), and non-primitive objects collapse
 * to a `String()`-ified form so the JSON serialiser doesn't emit
 * `[object Object]`.
 *
 * Pass-through for all other primitives.
 */
function sanitizeCellValue(
  v: unknown
): string | number | boolean | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return null;
    const errLabel = CVERR_SENTINEL_TO_LABEL[v];
    if (errLabel) return errLabel;
    return v;
  }
  if (typeof v === "string" || typeof v === "boolean") return v;
  return String(v);
}

const CLEAR_RANGE_CLEAR_TYPES: readonly ClearRangeClearType[] = [
  "Comments",
  "Contents",
  "Formats",
  "Hyperlinks",
  "Notes",
  "Outline",
  "All",
];

function normalizeClearRangeClearType(
  clearType: ClearRangeClearType | string | undefined,
): ClearRangeClearType | undefined | null {
  if (typeof clearType !== "string") return clearType;
  const normalized = clearType.trim().toLowerCase();
  return (
    CLEAR_RANGE_CLEAR_TYPES.find(
      (candidate) => candidate.toLowerCase() === normalized,
    ) ?? null
  );
}

/**
 * 2D variant of `sanitizeCellValue` for the `values` matrix on a
 * `CellRange`. Returns the same array reference (in place) when
 * nothing was rewritten — the JS engine reuses the literal and we
 * avoid a copy on the hot path. Allocates a new outer array (and
 * rewritten inner rows) only when at least one cell needed
 * sanitisation, so the steady-state cost on clean workbooks is one
 * pass + zero allocations.
 */
function sanitizeValueMatrix(
  values: (string | number | boolean | null)[][] | undefined
): (string | number | boolean | null)[][] | undefined {
  if (!values) return values;
  let dirty = false;
  const out: (string | number | boolean | null)[][] = new Array(values.length);
  for (let r = 0; r < values.length; r++) {
    const row = values[r];
    if (!row) {
      out[r] = row;
      continue;
    }
    let rowDirty = false;
    const newRow: (string | number | boolean | null)[] = new Array(row.length);
    for (let c = 0; c < row.length; c++) {
      const cleaned = sanitizeCellValue(row[c]);
      newRow[c] = cleaned;
      if (cleaned !== row[c]) rowDirty = true;
    }
    if (rowDirty) {
      dirty = true;
      out[r] = newRow;
    } else {
      out[r] = row;
    }
  }
  return dirty ? out : values;
}

/**
 * Additional formatting properties requested from GetRangeInfo alongside
 * the "values"/"formulas" property groups. These give the AI visibility
 * into existing cell styles without requesting the full (heavy) "format"
 * property group.
 */
const RANGE_INFO_FORMAT_PROPERTIES: string[] = [
  "numberFormat",
  "fillColor",
  "fontColor",
  "horizontalAlignment",
  "mergeCells",
  "font",
];

const RANGE_FORMAT_DIAGNOSTIC_LOG_LIMIT = 5;
let rangeFormatDiagnosticLogCount = 0;

function shouldLogRangeFormatDiagnostics(): boolean {
  try {
    return (
      typeof window !== "undefined" &&
      window.localStorage?.getItem("finster.excel.rangeFormatDiagnostics") ===
        "true"
    );
  } catch {
    return false;
  }
}

function logRangeFormatDiagnostics(
  raw: unknown,
  context: { range: string; sheetName?: string; source: string }
): void {
  if (
    !shouldLogRangeFormatDiagnostics() ||
    rangeFormatDiagnosticLogCount >= RANGE_FORMAT_DIAGNOSTIC_LOG_LIMIT
  ) {
    return;
  }
  rangeFormatDiagnosticLogCount += 1;

  const root = asRecord(raw);
  const rangeInfo = root ? root.rangeInfo : undefined;
  const records = Array.isArray(raw)
    ? raw
    : Array.isArray(rangeInfo)
      ? rangeInfo
      : [];
  const sample = records
    .filter((record): record is Record<string, unknown> => !!record && typeof record === "object")
    .slice(0, 8)
    .map((record) => {
      const formatKeys = RANGE_INFO_FORMAT_PROPERTIES.filter((key) => key in record);
      const font = asRecord(record.font);
      const previewValue = (value: unknown): unknown =>
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean" ||
        value === null
          ? value
          : undefined;
      return {
        address: typeof record.address === "string" ? record.address : undefined,
        row: typeof record.row === "number" ? record.row : undefined,
        column: typeof record.column === "number" ? record.column : undefined,
        formatKeys,
        fontKeys: font ? Object.keys(font).slice(0, 8) : undefined,
        formatPreview: {
          numberFormat: previewValue(record.numberFormat),
          fillColor: previewValue(record.fillColor),
          fontColor: previewValue(record.fontColor),
          horizontalAlignment: previewValue(record.horizontalAlignment),
          mergeCells: previewValue(record.mergeCells),
          fontBold: font ? previewValue(font.bold) : undefined,
          fontColorNested: font ? previewValue(font.color) : undefined,
        },
      };
    });

  console.info("[Excel] Range formatting diagnostics", {
    ...context,
    requestedProperties: RANGE_INFO_FORMAT_PROPERTIES,
    recordCount: records.length,
    sample,
  });
}

function logAdaptedRangeFormatDiagnostics(
  range: CellRange,
  context: { requestedRange: string; sheetName?: string; source: string }
): void {
  if (!shouldLogRangeFormatDiagnostics()) {
    return;
  }

  console.info("[Excel] Adapted range formatting diagnostics", {
    ...context,
    adaptedRange: range.address,
    hasNumberFormats: !!range.numberFormats,
    numberFormatRows: range.numberFormats?.length ?? 0,
    cellStyleCount: Object.keys(range.cellStyles ?? {}).length,
    sampleCellStyles: Object.entries(range.cellStyles ?? {})
      .slice(0, 8)
      .map(([key, style]) => ({ key, style })),
  });
}

/** Default fill color in BGR int — white (16777215 = 0xFFFFFF). */
const DEFAULT_FILL_COLOR = 16777215;
/** Default font color in BGR int — black (0). */
const DEFAULT_FONT_COLOR = 0;

/**
 * Convert a BGR integer (as returned by the FactSet bridge) to a
 * "#RRGGBB" hex string. Returns `undefined` for default colors
 * (black font / white fill) to keep the sparse map compact.
 */
function bgrToHex(bgr: number, isFont: boolean): string | undefined {
  if (isFont && bgr === DEFAULT_FONT_COLOR) return undefined;
  if (!isFont && (bgr === DEFAULT_FILL_COLOR || bgr === 0)) return undefined;
  const r = bgr & 0xFF;
  const g = (bgr >> 8) & 0xFF;
  const b = (bgr >> 16) & 0xFF;
  const hex = (n: number) => { const s = n.toString(16); return s.length < 2 ? "0" + s : s; };
  return `#${hex(r)}${hex(g)}${hex(b)}`.toUpperCase();
}

/**
 * Normalize the Excel XlHAlign enum string to a simple alignment keyword.
 * Returns `undefined` for "general" (default) to keep the sparse map compact.
 */
function normalizeHAlign(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const lower = raw.toLowerCase();
  if (lower.includes("left")) return "left";
  if (lower.includes("center")) return "center";
  if (lower.includes("right")) return "right";
  if (lower.includes("justify")) return "justify";
  return undefined; // "general" or unknown → omit
}

/**
 * Convert a raw Office.js / bridge `numberFormat` 2D matrix into a sparse
 * representation: cells with "General" (or empty) format are stored as
 * `null`, and the entire array is dropped (returns `undefined`) when every
 * cell is "General". This keeps the JSON payload compact — only
 * non-default formats are surfaced to the AI.
 */
function sparseNumberFormats(
  raw: string[][] | undefined | null
): (string | null)[][] | undefined {
  if (!raw || raw.length === 0) return undefined;
  let sawAny = false;
  const out: (string | null)[][] = new Array(raw.length);
  for (let r = 0; r < raw.length; r++) {
    const row = raw[r];
    if (!row) {
      out[r] = [];
      continue;
    }
    const newRow: (string | null)[] = new Array(row.length);
    for (let c = 0; c < row.length; c++) {
      const fmt = row[c];
      if (fmt && fmt !== "General") {
        newRow[c] = fmt;
        sawAny = true;
      } else {
        newRow[c] = null;
      }
    }
    out[r] = newRow;
  }
  return sawAny ? out : undefined;
}

/**
 * Extract a sparse `cellStyles` map from an Office.js `getCellProperties`
 * 2D result array. Only cells with non-default formatting get an entry.
 * Returns `undefined` when every cell has default styles.
 */
async function extractCellStylesFromOfficeJs(
  usedRange: Excel.Range,
  context: Excel.RequestContext,
  rowLimit?: number
): Promise<Record<string, CellStyleInfo> | undefined> {
  try {
    const propsResult = usedRange.getCellProperties({
      format: {
        font: { bold: true, italic: true, size: true, strikethrough: true, underline: true, color: true },
        fill: { color: true },
        horizontalAlignment: true,
      },
    });
    await context.sync();

    const cellStyles: Record<string, CellStyleInfo> = {};
    const rows = propsResult.value;
    const effectiveRowLimit = rowLimit ?? rows.length;

    for (let r = 0; r < Math.min(rows.length, effectiveRowLimit); r++) {
      const row = rows[r];
      if (!row) continue;
      for (let c = 0; c < row.length; c++) {
        const cell = row[c];
        if (!cell || !cell.format) continue;

        const style: CellStyleInfo = {};
        let hasStyle = false;

        if (cell.format.font?.bold === true) {
          style.bold = true;
          hasStyle = true;
        }

        if (cell.format.font?.italic === true) {
          style.italic = true;
          hasStyle = true;
        }

        const fontSize = cell.format.font?.size;
        if (typeof fontSize === "number" && fontSize > 0 && fontSize !== 11) {
          style.fontSize = fontSize;
          hasStyle = true;
        }

        if (cell.format.font?.strikethrough === true) {
          style.strikethrough = true;
          hasStyle = true;
        }

        const underline = cell.format.font?.underline;
        if (underline && underline !== "None") {
          style.underline = true;
          hasStyle = true;
        }

        const fontColor = cell.format.font?.color;
        if (fontColor && typeof fontColor === "string" && fontColor !== "#000000" && fontColor !== "black") {
          style.fontColor = fontColor.toUpperCase();
          hasStyle = true;
        }

        const fillColor = cell.format.fill?.color;
        if (fillColor && typeof fillColor === "string" && fillColor !== "#FFFFFF" && fillColor !== "white" && fillColor !== "") {
          style.fillColor = fillColor.toUpperCase();
          hasStyle = true;
        }

        const hAlign = cell.format.horizontalAlignment;
        if (hAlign && hAlign !== "General") {
          const normalized = (hAlign as string).toLowerCase();
          if (normalized === "left" || normalized === "center" || normalized === "right" || normalized === "justify") {
            style.align = normalized;
            hasStyle = true;
          }
        }

        if (hasStyle) {
          cellStyles[`${r},${c}`] = style;
        }
      }
    }

    return Object.keys(cellStyles).length > 0 ? cellStyles : undefined;
  } catch (e) {
    console.warn("[Excel] getCellProperties failed (non-fatal, cellStyles will be omitted):", e);
    return undefined;
  }
}

/**
 * Convert a zero-based column index to an Excel column name.
 * 0 -> "A", 25 -> "Z", 26 -> "AA", 701 -> "ZZ", 702 -> "AAA".
 */
function columnIndexToLetter(index: number): string {
  if (!Number.isInteger(index) || index < 0) {
    throw new RangeError(`Invalid column index: ${index}`);
  }
  let n = index;
  let result = "";
  while (n >= 0) {
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26) - 1;
  }
  return result;
}

/**
 * Inverse of `columnIndexToLetter`: `"A"` → 0, `"Z"` → 25, `"AA"` → 26, etc.
 * Accepts either casing. Throws on non-letter input.
 */
function columnLetterToIndex(letters: string): number {
  if (!letters || !/^[A-Za-z]+$/.test(letters)) {
    throw new RangeError(`Invalid column letters: "${letters}"`);
  }
  let n = 0;
  const upper = letters.toUpperCase();
  for (let i = 0; i < upper.length; i++) {
    n = n * 26 + (upper.charCodeAt(i) - 64);
  }
  return n - 1;
}

/**
 * Planner chart-type enum → wire `xl*` chart-type enum used by
 * `factset.Spreadsheet.Charting.AddExcelChart`. Falls back to `xlColumnClustered`
 * when the planner emits an unrecognised type — Excel's most defensible
 * default for "make me a chart from this data" without further context.
 *
 * Kept a separate module-level constant (rather than inline in the call
 * site) because the same map is reused by the Office.js path's
 * `Excel.ChartType.*` lookup — the wire enum and the Office.js enum are
 * one-to-one once you strip the `xl` prefix and lowercase, so we keep the
 * canonical mapping here and let each call site translate as needed.
 */
const PLANNER_CHART_TYPE_TO_WIRE: Record<string, string> = {
  bar: "xlBarClustered",
  column: "xlColumnClustered",
  line: "xlLine",
  pie: "xlPie",
  area: "xlArea",
  scatter: "xlXYScatter",
  doughnut: "xlDoughnut",
};

/**
 * Planner chart-type enum → Office.js `Excel.ChartType` enum used by
 * `sheet.charts.add()` and `chart.chartType`. Mirrors
 * `PLANNER_CHART_TYPE_TO_WIRE` one-to-one (Office.js's `xlBarClustered`
 * is `barClustered` etc.) but kept as a separate map so a typo on
 * either side trips a TypeScript error rather than silently falling
 * through. Resolved lazily inside `Excel.run` callbacks because the
 * `Excel` global is undefined in bridge-mode runtimes.
 *
 * Returns `undefined` for unknown types — call sites should fall back
 * to `Excel.ChartType.columnClustered` (the same default the wire path
 * uses via `PLANNER_CHART_TYPE_TO_WIRE`'s `xlColumnClustered`).
 */
function plannerChartTypeToOffice(
  plannerType: string | undefined
): Excel.ChartType | undefined {
  if (!plannerType) return undefined;
  switch (plannerType.toLowerCase()) {
    case "bar":
      return Excel.ChartType.barClustered;
    case "column":
      return Excel.ChartType.columnClustered;
    case "line":
      return Excel.ChartType.line;
    case "pie":
      return Excel.ChartType.pie;
    case "area":
      return Excel.ChartType.area;
    case "scatter":
      return Excel.ChartType.xyscatter;
    case "doughnut":
      return Excel.ChartType.doughnut;
    default:
      return undefined;
  }
}

/**
 * Parse an Excel range reference like `"Sheet1!B2:B10"`,
 * `"'My Sheet'!$B$2:$B$10"`, or bare `"B2:B10"` into a sheet name
 * (when present) and the residual address.
 *
 * Used by the Office.js chart-series path: `Excel.Workbook` does NOT
 * expose `getRange()` in this project's Office.js typings, so we
 * resolve cross-sheet references via
 * `worksheets.getItem(sheetName).getRange(address)` and bare addresses
 * via the chart's containing sheet's `getRange(address)`.
 *
 * Quoted sheet names follow Excel's grammar: surround with `'...'`
 * when the name contains spaces or special characters; literal `'`
 * inside the name is escaped as `''`. Returns `{ address: rangeRef }`
 * with `sheetName` undefined if no prefix is present.
 */
function parseSheetPrefixedRange(rangeRef: string): {
  sheetName?: string;
  address: string;
} {
  if (rangeRef.startsWith("'")) {
    const closeIdx = rangeRef.indexOf("'!");
    if (closeIdx > 0) {
      return {
        sheetName: rangeRef.slice(1, closeIdx).replace(/''/g, "'"),
        address: rangeRef.slice(closeIdx + 2),
      };
    }
    return { address: rangeRef };
  }
  const bangIdx = rangeRef.indexOf("!");
  if (bangIdx > 0) {
    return {
      sheetName: rangeRef.slice(0, bangIdx),
      address: rangeRef.slice(bangIdx + 1),
    };
  }
  return { address: rangeRef };
}

/**
 * Resolve a (possibly sheet-prefixed) Excel range string to an
 * `Excel.Range` proxy in the current `Excel.RequestContext`. Sheet
 * prefix → cross-sheet `worksheets.getItem(sheetName).getRange(addr)`;
 * no prefix → `fallbackSheet.getRange(addr)`.
 *
 * `worksheets.getItem(name)` throws `ItemNotFound` synchronously if
 * the sheet doesn't exist; the surrounding `Excel.run` callback's
 * catch surfaces that as a clean per-chart skip rather than aborting
 * the whole apply.
 */
function resolveRangeForOfficeJs(
  context: Excel.RequestContext,
  fallbackSheet: Excel.Worksheet,
  rangeRef: string
): Excel.Range {
  const { sheetName, address } = parseSheetPrefixedRange(rangeRef);
  if (sheetName) {
    return context.workbook.worksheets.getItem(sheetName).getRange(address);
  }
  return fallbackSheet.getRange(address);
}

/**
 * Default Excel column width / row height in **points**, at 100% zoom
 * and the standard 11pt Calibri font. Used to translate the planner's
 * cell-anchored chart `position` (`startCell`, `width` in cells, `height`
 * in rows) to the point rectangle the wire
 * `factset.Spreadsheet.Charting.AddExcelChart` intent expects.
 *
 * Excel's COM `Top`/`Left`/`Width`/`Height` properties on a chart shape
 * are points (1pt = 1/72 inch), and the chart-tools.md catalog confirms
 * the wire intent expects the same. The standard column at "8.43 chars"
 * is ~48pt wide; the standard row is 15pt tall.
 *
 * These are approximations — real column widths can vary per-sheet and
 * per font — but the chart still lands in the right neighbourhood. Call
 * sites that need exact placement should bypass this helper and pass
 * `position` in points directly through `addExcelChart()`.
 */
const DEFAULT_CHART_COL_WIDTH_PT = 48;
const DEFAULT_CHART_ROW_HEIGHT_PT = 15;
const MAX_CELL_ANCHORED_CHART_WIDTH_CELLS = 30;
const MAX_CELL_ANCHORED_CHART_HEIGHT_ROWS = 80;

/**
 * Translate a cell-anchored chart placement (planner-shape) into the
 * point rectangle the wire `factset.Spreadsheet.Charting.AddExcelChart`
 * intent expects. `startCell` becomes the top-left anchor; `widthCells`
 * / `heightRows` are sized in cell units and scaled by the defaults
 * above. Returns `undefined` for invalid `startCell` so the caller
 * falls back to host defaults rather than positioning the chart at
 * a garbage offset.
 */
function plannerChartPositionToPoints(
  startCell: string | undefined,
  widthCells: number | undefined,
  heightRows: number | undefined
): { top: number; left: number; width: number; height: number } | undefined {
  if (!startCell) return undefined;
  const cellMatch = /^\$?([A-Z]+)\$?(\d+)$/i.exec(startCell);
  if (!cellMatch) return undefined;
  const colIdx = columnLetterToIndex(cellMatch[1]);
  const rowIdx = parseInt(cellMatch[2], 10) - 1;
  // Reasonable defaults if the planner omitted size: 6 columns × 15 rows
  // matches the existing schema docstring in `excelAIService.ts`.
  const wCells = widthCells ?? 6;
  const hRows = heightRows ?? 15;
  const dimensionsLookLikeCellUnits =
    wCells <= MAX_CELL_ANCHORED_CHART_WIDTH_CELLS &&
    hRows <= MAX_CELL_ANCHORED_CHART_HEIGHT_ROWS;
  return {
    top: rowIdx * DEFAULT_CHART_ROW_HEIGHT_PT,
    left: colIdx * DEFAULT_CHART_COL_WIDTH_PT,
    width: dimensionsLookLikeCellUnits ? wCells * DEFAULT_CHART_COL_WIDTH_PT : wCells,
    height: dimensionsLookLikeCellUnits ? hRows * DEFAULT_CHART_ROW_HEIGHT_PT : hRows,
  };
}

function chartPositionToPoints(
  position: ChartModification["position"]
): { top: number; left: number; width: number; height: number } | undefined {
  if (!position) return undefined;
  if (
    typeof position.top === "number" &&
    typeof position.left === "number" &&
    typeof position.width === "number" &&
    typeof position.height === "number"
  ) {
    return {
      top: position.top,
      left: position.left,
      width: position.width,
      height: position.height,
    };
  }
  return plannerChartPositionToPoints(
    position.startCell,
    position.width,
    position.height
  );
}

function normalizeExcelLegendPosition(position: unknown): string | undefined {
  if (typeof position !== "string") return undefined;
  const trimmed = position.trim();
  if (!trimmed) return undefined;
  switch (trimmed.toLowerCase()) {
    case "top":
    case "xllegendpositiontop":
      return "xlLegendPositionTop";
    case "bottom":
    case "xllegendpositionbottom":
      return "xlLegendPositionBottom";
    case "left":
    case "xllegendpositionleft":
      return "xlLegendPositionLeft";
    case "right":
    case "xllegendpositionright":
      return "xlLegendPositionRight";
    case "corner":
    case "xllegendpositioncorner":
      return "xlLegendPositionCorner";
    case "custom":
    case "xllegendpositioncustom":
      return "xlLegendPositionCustom";
    default:
      return undefined;
  }
}

function sanitizeAddExcelChartInfo(
  chartInfo: Parameters<typeof addExcelChart>[0]["chartInfo"]
): Parameters<typeof addExcelChart>[0]["chartInfo"] {
  if (!chartInfo) return undefined;
  const sanitized = { ...chartInfo } as Record<string, unknown>;
  if ("axes" in sanitized && !Array.isArray(sanitized.axes)) {
    delete sanitized.axes;
  }
  const legend = asRecord(sanitized.legend);
  if (legend && "position" in legend) {
    const normalizedPosition = normalizeExcelLegendPosition(legend.position);
    sanitized.legend = { ...legend };
    if (normalizedPosition) {
      (sanitized.legend as Record<string, unknown>).position = normalizedPosition;
    } else {
      delete (sanitized.legend as Record<string, unknown>).position;
    }
  }
  return sanitized as Parameters<typeof addExcelChart>[0]["chartInfo"];
}

function recordField<T = unknown>(
  record: Record<string, unknown>,
  field: string
): T | undefined {
  return field in record ? record[field] as T : undefined;
}

function objectField(
  record: Record<string, unknown>,
  field: string
): Record<string, unknown> | undefined {
  const value = record[field];
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function stringField(
  record: Record<string, unknown>,
  field: string
): string | undefined {
  const value = record[field];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function numberField(
  record: Record<string, unknown>,
  field: string
): number | undefined {
  const value = record[field];
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function chartSeriesToWire(
  series: ChartModification["series"]
): Parameters<typeof updateSeriesInChart>[0]["series"] {
  if (!series) return {};
  const raw = series as unknown as Record<string, unknown>;
  const friendlyChartType = stringField(raw, "chartType");
  const wireChartType = friendlyChartType
    ? PLANNER_CHART_TYPE_TO_WIRE[friendlyChartType.toLowerCase()] ?? friendlyChartType
    : undefined;
  const rawAxisGroup = stringField(raw, "axisGroup");
  const wireAxisGroup =
    rawAxisGroup === "secondary"
      ? "xlSecondary"
      : rawAxisGroup === "primary"
        ? "xlPrimary"
        : rawAxisGroup;
  const fill = objectField(raw, "fill");
  const fillColor =
    stringField(raw, "fillColor") ??
    stringField(raw, "colorRgb") ??
    stringField(raw, "color");
  const line = objectField(raw, "line");
  const lineColor = stringField(raw, "lineColor");
  const lineWeight = numberField(raw, "lineWeight");

  return {
    ...(stringField(raw, "values") !== undefined ? { values: stringField(raw, "values") } : {}),
    ...(stringField(raw, "categories") !== undefined ? { categories: stringField(raw, "categories") } : {}),
    ...(stringField(raw, "name") !== undefined ? { name: stringField(raw, "name") } : {}),
    ...(wireChartType ? { chartType: wireChartType } : {}),
    ...(wireAxisGroup ? { axisGroup: wireAxisGroup } : {}),
    ...(numberField(raw, "plotOrder") !== undefined ? { plotOrder: numberField(raw, "plotOrder") } : {}),
    ...(typeof raw.smooth === "boolean" ? { smooth: raw.smooth } : {}),
    ...(typeof raw.invertIfNegative === "boolean" ? { invertIfNegative: raw.invertIfNegative } : {}),
    ...(numberField(raw, "explosion") !== undefined ? { explosion: numberField(raw, "explosion") } : {}),
    ...(fill || fillColor
      ? {
          fill: {
            ...(fill ?? {}),
            ...(fillColor && !("colorRgb" in (fill ?? {})) ? { colorRgb: fillColor } : {}),
          },
        }
      : {}),
    ...(line || lineColor !== undefined || lineWeight !== undefined
      ? {
          line: {
            ...(line ?? {}),
            ...(lineColor !== undefined && !("colorRgb" in (line ?? {})) ? { colorRgb: lineColor } : {}),
            ...(lineWeight !== undefined && !("weight" in (line ?? {})) ? { weight: lineWeight } : {}),
          },
        }
      : {}),
    ...(objectField(raw, "marker") ? { marker: objectField(raw, "marker") } : {}),
    ...(typeof raw.hasDataLabels === "boolean" ? { hasDataLabels: raw.hasDataLabels } : {}),
    ...(objectField(raw, "dataLabels") ? { dataLabels: objectField(raw, "dataLabels") } : {}),
    ...(typeof raw.hasLeaderLines === "boolean" ? { hasLeaderLines: raw.hasLeaderLines } : {}),
    ...(objectField(raw, "leaderLines") ? { leaderLines: objectField(raw, "leaderLines") } : {}),
    ...(Array.isArray(recordField(raw, "points")) ? { points: recordField(raw, "points") } : {}),
  };
}

/**
 * Accepts the address shapes Excel (and `factset.Spreadsheet.ResizeExcelElements`)
 * allow:
 *  - A1 / $A$1 / Sheet1!A1
 *  - A1:B10 / Sheet1!$A$1:$B$10 / R1C1
 *  - Whole-column (e.g. `A:A`, `A:C`, `$A:$C`) — valid for autofit / resize
 *  - Whole-row    (e.g. `1:1`, `1:5`, `$1:$5`) — valid for autofit / resize
 */
function isValidExcelAddress(address: string): boolean {
  if (!address || typeof address !== "string") return false;
  const bare = address.includes("!") ? address.split("!").pop()! : address;
  // Row 0 doesn't exist in Excel — reject before any affirmative match so
  // addresses like "A0" or range endpoints like "A1:B0" are caught.
  if (/(^|:)\$?[A-Z]+\$?0+(?!\d)/i.test(bare)) return false;
  // A1-style (with optional $ and range)
  if (/^\$?[A-Z]+\$?\d+(:\$?[A-Z]+\$?\d+)?$/i.test(bare)) return true;
  // Whole-column (letters-only, with optional `$` anchors) — e.g. `A:A`, `$C:$C`.
  if (/^\$?[A-Z]+(:\$?[A-Z]+)?$/i.test(bare)) return true;
  // Whole-row (digits-only, with optional `$` anchors) — e.g. `1:1`, `$5:$10`.
  // Reject `0` as a row number to match the A1 row-0 guard above.
  if (/^\$?[1-9]\d*(:\$?[1-9]\d*)?$/.test(bare)) return true;
  // Be permissive: if it looks vaguely like a cell ref, let Excel validate it
  if (/^[A-Z$]/i.test(bare) && /\d/.test(bare)) return true;
  return false;
}

/**
 * `true` when `address` is a whole-column reference like `"A:A"`,
 * `"B:D"`, or `"$A:$C"`. Used to gate `hideColumns` (and any future
 * column-only intent) so cell- or row-shaped addresses don't reach the
 * host with a guaranteed rejection.
 */
function isColumnShapedAddress(address: string): boolean {
  if (!address || typeof address !== "string") return false;
  const bare = address.includes("!") ? address.split("!").pop()! : address;
  return /^\$?[A-Z]+(:\$?[A-Z]+)?$/i.test(bare);
}

/**
 * `true` when `address` is a whole-row reference like `"5:5"`,
 * `"$5:$10"`. Used to gate `hideRows`.
 */
function isRowShapedAddress(address: string): boolean {
  if (!address || typeof address !== "string") return false;
  const bare = address.includes("!") ? address.split("!").pop()! : address;
  return /^\$?[1-9]\d*(:\$?[1-9]\d*)?$/.test(bare);
}

// ============================================================================
// CONFIGURATION
// ============================================================================

// When true, adds comments to cells that are modified by Finster.
//
// Disabled in the MVP: cell-comment writes (`AddComment` / `DeleteComment`,
// v0 §6.21 / §6.22 in `f2_docs/excel/finster-docs/missing-intents-v0.md`)
// are deferred from the Excel intent surface and Finster has been asked
// to stop dropping comments onto user cells. The flag, setter/getter,
// settings-pane toggle, and the per-modification write call sites are
// intentionally kept so a future variant (e.g. comments only on a
// specific action class, or a different annotation channel) can revive
// the path without re-implementing it. To re-enable for diagnostics, call
// `setAddCommentsOnEdit(true)` from the console or unhide the settings
// row in `ExcelApp.tsx`.
let addCommentsOnEdit = false;

/**
 * Enable or disable automatic comments when cells are edited.
 *
 * Disabled by default in the MVP. Kept so a future feature can revive the
 * comment-annotation path without re-implementing `addFinsterComment` and
 * its call sites — see the module-level note above.
 *
 * Note: while the MVP-disable is in effect, the two write call sites in
 * `applyCellModification` / `applyRangeModification` short-circuit with a
 * hard-coded `false &&` guard, so toggling this flag back to `true` alone
 * will **not** re-enable comments. The full revival path is to (1) flip
 * the default below to `true`, (2) drop the `false &&` from those two
 * guards, and (3) unhide the settings row in `ExcelApp.tsx`.
 */
export function setAddCommentsOnEdit(enabled: boolean): void {
  addCommentsOnEdit = enabled;
}

/**
 * Get the current setting for automatic comments.
 *
 * Defaults to `false` while the comment-annotation feature is deferred.
 */
export function getAddCommentsOnEdit(): boolean {
  return addCommentsOnEdit;
}

// ============================================================================
// READ WORKBOOK CONTENT
// ============================================================================

/**
 * Empty workbook content shell.
 *
 * Used as a defensive fallback when the bridge-mode read pipeline (`GetWorkbookSheets`
 * / `GetRangeInfo`) errors out. Returning a safely-shaped payload lets the
 * chat flow proceed to the apply step, where `InsertContentsToRange` takes
 * over. The AI model still gets the user's natural-language request, so
 * direct writes like "put 42 in A1" still work even when the read failed.
 */
function emptyWorkbookContentForBridgeMode(): WorkbookContent {
  return {
    activeSheetIndex: 0,
    activeSheetName: "",
    sheets: [],
  };
}

// ============================================================================
// INTENT-BRIDGE READ ADAPTERS
// ============================================================================

/**
 * Loose record helper for narrowing arbitrary intent payloads. Used by
 * the read-side adapters below to walk objects without committing to a
 * full schema up-front (per-element shapes are typed but not always
 * documented; callers narrow at the use site).
 */
function asRecord(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : undefined;
}

/**
 * Tight used-range rectangle. `lastRow` / `lastCol` are 1-indexed
 * ends (the same convention `tightBoundingBoxAddress` and
 * `boundingBoxFromCsvRows` already produce). `startRow` / `startCol`
 * keep the host's real origin: a `D5:F10` used range must not be
 * widened to `A1:F10`, or every derived read and row/column count
 * silently gains four phantom columns. `undefined` on
 * `AdaptedWorkbookSheet.usedRange` is ambiguous on its own — see
 * `usedRangePresent` / `usedRangeUnparsed` before concluding the
 * sheet is blank.
 */
interface UsedRangeBox {
  lastRow: number;
  lastCol: number;
  /** 1-indexed origin row. Defaults to 1 when the host only gave a bottom-right. */
  startRow: number;
  /** 1-indexed origin column. Defaults to 1 when the host only gave a bottom-right. */
  startCol: number;
  /** Bare A1 rectangle matching start/last, e.g. `"D5:F10"`. */
  address: string;
}

/**
 * One entry produced by `adaptWorkbookSheets`. `name` / `index` are
 * always present (sheets without a name are dropped); the rest are
 * populated only when the caller requested them via
 * `GetWorkbookSheetsOptions.properties` AND the host returned the
 * matching field. Older hosts that don't honour `options.properties`
 * leave every optional field `undefined`, and the read pipeline
 * gracefully falls back to its probe-ladder path.
 */
interface AdaptedWorkbookSheet {
  name: string;
  index: number;
  /**
   * Tight bounding box of the sheet's used range, derived either from
   * the host's `address` string (`"Sheet1!D5:F10"` → `{ startRow: 5,
   * startCol: 4, lastRow: 10, lastCol: 6, address: "D5:F10" }`) or
   * from the `{ rowIndex, columnIndex, rowCount, columnCount }`
   * numeric quad when address is missing. `undefined` when the host
   * confirmed blank, omitted the field, or sent a value we could not
   * parse — disambiguate with `usedRangePresent` / `usedRangeUnparsed`.
   */
  usedRange?: UsedRangeBox;
  /**
   * `true` when the host returned a `usedRange` field for this
   * sheet (populated, explicitly empty, or unparseable). `false`
   * means the host omitted the field — typically an older host that
   * doesn't honour `options.properties: ["usedRange"]`. Confirmed
   * blank is `usedRange == null && usedRangePresent && !usedRangeUnparsed`.
   * Unknown is `!usedRangePresent`. Unparseable is `usedRangeUnparsed`.
   */
  usedRangePresent: boolean;
  /**
   * `true` when the host sent a `usedRange` value we could not parse
   * (e.g. whole-column `"A:A"`). Distinct from a confirmed-blank sheet.
   */
  usedRangeUnparsed?: boolean;
  /**
   * Excel-native visibility after `adaptVisibilityField` (COM
   * `xlSheet*` constants included). Omitted when unrecognised.
   */
  visibility?: "Visible" | "Hidden" | "VeryHidden";
  isProtected?: boolean;
  /** Host sheet kind, e.g. `"xlWorksheet"` / `"xlChart"`. Omitted when the host did not send it. */
  sheetType?: string;
}

/**
 * Narrow `GetWorkbookSheets`'s `sheets` payload into the
 * `AdaptedWorkbookSheet` rows Finster cares about. The wire shape is a
 * typed array; per the f2_docs `excel-tools.md` "Get Workbook Sheets"
 * section, each entry always carries `{ index, name }` and, when the
 * caller requested them via `options.properties`, may additionally
 * carry `visibility`, `usedRange`, and `isProtected`. The adapter
 * stays defensive against wrapper-shape drift (`{ sheets: [...] }`,
 * `{ items: [...] }`), the per-property casing disagreement in the
 * doc (text says `Visibility`/`UsedRange`/`IsProtected` but the
 * example uses lowercase), and a `usedRange` field that may arrive
 * as either a string address (`"Sheet1!A1:C10"`), a `{ address }`
 * object, or the full `{ rowIndex, columnIndex, rowCount,
 * columnCount, address }` quad.
 *
 * **Indexing note (silent off-by-one today, will resolve when FactSet's
 * pre-announced migration lands).** The wire `index` is currently 1-based
 * for `getWorkbookSheets` and `getActiveExcelContext`, but
 * `WorkbookContent.activeSheetIndex` and `WorksheetContent.index` are
 * declared 0-based in `json-schema-v0.md` (matching Office.js's
 * `worksheet.position`). Bridge mode passes the wire value through
 * unchanged, so a bridge-mode `activeSheetIndex` of `1` and an
 * Office.js-mode value of `0` both mean "first sheet" — a silent
 * off-by-one between the two modes. FactSet has pre-announced a 0-based
 * migration for `getActiveExcelContext`, `getWorkbookSheets`,
 * `addWorksheet.options.index`, `getTables`, and `getCharts`; that
 * eliminates the disagreement. See
 * `f2_docs/excel/finster-docs/missing-intents-v0.md`
 * → `#open-zero-index-migration`. The `i` fallback below picks 0-based
 * positions (matching the schema) only when the wire payload is missing
 * an `index` / `position` field entirely.
 */
function adaptWorkbookSheets(raw: unknown): AdaptedWorkbookSheet[] {
  let list: unknown[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else {
    const rec = asRecord(raw);
    if (rec) {
      if (Array.isArray(rec.sheets)) list = rec.sheets as unknown[];
      else if (Array.isArray(rec.items)) list = rec.items as unknown[];
    }
  }
  const result: AdaptedWorkbookSheet[] = [];
  for (let i = 0; i < list.length; i++) {
    const item = asRecord(list[i]);
    if (!item) continue;
    const name = adaptSheetNameField(item);
    if (!name) continue;
    const rawUsedRange =
      item.usedRange ?? (item as Record<string, unknown>).UsedRange;
    const usedRangePresent =
      "usedRange" in item || "UsedRange" in (item as Record<string, unknown>);
    const usedRangeResult = usedRangePresent
      ? adaptUsedRangeField(rawUsedRange)
      : undefined;
    result.push({
      name,
      index: adaptSheetIndexField(item, i),
      usedRange:
        usedRangeResult && typeof usedRangeResult === "object"
          ? usedRangeResult
          : undefined,
      usedRangePresent,
      usedRangeUnparsed: usedRangeResult === "unparsed",
      visibility: adaptVisibilityField(
        item.visibility ?? (item as Record<string, unknown>).Visibility,
      ),
      isProtected: adaptBooleanField(
        item.isProtected ?? (item as Record<string, unknown>).IsProtected,
      ),
      sheetType: adaptSheetTypeField(item),
    });
  }
  return result;
}

function adaptSheetNameField(item: Record<string, unknown>): string | undefined {
  for (const key of ["name", "Name"] as const) {
    const value = item[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return undefined;
}

function adaptSheetIndexField(
  item: Record<string, unknown>,
  fallback: number,
): number {
  for (const key of ["index", "Index", "position", "Position"] as const) {
    const value = item[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return fallback;
}

function adaptSheetTypeField(item: Record<string, unknown>): string | undefined {
  for (const key of ["sheetType", "SheetType"] as const) {
    const value = item[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return undefined;
}

/**
 * Coerce a `GetWorkbookSheets.usedRange` field into the
 * `UsedRangeBox` shape the read pipeline expects.
 *
 * Accepted shapes:
 *   - `null` / `{}` / `{ rowCount: 0 }` → confirmed blank (`"blank"`).
 *   - unparseable address / unrecognised shape → `"unparsed"` (not
 *     blank — the caller must not skip the sheet).
 *   - `"Sheet1!D5:F10"` (or `"D5:F10"`) → the host's real rectangle;
 *     the sheet prefix is stripped, origin is kept, counts are
 *     extents not last indices.
 *   - `{ address: "Sheet1!D5:F10" }` → same as the string case.
 *   - `{ rowIndex, columnIndex, rowCount, columnCount }` → treated as
 *     0-indexed start + count (`startRow = rowIndex + 1`,
 *     `lastRow = rowIndex + rowCount`). A 1-indexed host therefore
 *     yields a same-size rectangle shifted one row down (e.g. A1:D10
 *     reported as A2:D11). That is deliberate: one extra empty row
 *     is cheaper than clipping a row of real data. Do not "fix" it
 *     to A1 while FactSet's 0-based migration is outstanding.
 */
function adaptUsedRangeField(
  raw: unknown,
): UsedRangeBox | "blank" | "unparsed" {
  if (raw == null) return "blank";

  if (typeof raw === "string") {
    if (!raw.trim()) return "blank";
    return boxFromAddressString(raw) ?? "unparsed";
  }

  const rec = asRecord(raw);
  if (!rec) return "unparsed";
  if (Object.keys(rec).length === 0) return "blank";

  const address = rec.address ?? rec.Address;
  if (typeof address === "string" && address.length > 0) {
    return boxFromAddressString(address) ?? "unparsed";
  }

  const rowCount = numericField(rec, ["rowCount", "RowCount"]);
  const columnCount = numericField(rec, ["columnCount", "ColumnCount"]);
  if (rowCount === undefined || columnCount === undefined) return "unparsed";
  if (rowCount <= 0 || columnCount <= 0) return "blank";
  const rowIndex =
    numericField(rec, ["rowIndex", "RowIndex", "row", "Row"]) ?? 0;
  const columnIndex =
    numericField(rec, [
      "columnIndex",
      "ColumnIndex",
      "column",
      "Column",
    ]) ?? 0;
  const startRow = rowIndex + 1;
  const startCol = columnIndex + 1;
  const lastRow = rowIndex + rowCount;
  const lastCol = columnIndex + columnCount;
  return {
    startRow,
    startCol,
    lastRow,
    lastCol,
    address: a1RangeFromBounds(startRow, startCol, lastRow, lastCol),
  };
}

function boxFromAddressString(address: string): UsedRangeBox | undefined {
  if (!address) return undefined;
  const { range } = splitSheetPrefix(address);
  const bounds = parseA1RangeBounds(range);
  if (!bounds) return undefined;
  const startRow = bounds.topLeft.row;
  const startCol = bounds.topLeft.col;
  const lastRow = bounds.bottomRight.row;
  const lastCol = bounds.bottomRight.col;
  return {
    startRow,
    startCol,
    lastRow,
    lastCol,
    address: a1RangeFromBounds(startRow, startCol, lastRow, lastCol),
  };
}

function a1RangeFromBounds(
  startRow: number,
  startCol: number,
  lastRow: number,
  lastCol: number,
): string {
  const start = `${columnIndexToLetter(startCol - 1)}${startRow}`;
  const end = `${columnIndexToLetter(lastCol - 1)}${lastRow}`;
  return start === end ? start : `${start}:${end}`;
}

function numericField(
  rec: Record<string, unknown>,
  keys: ReadonlyArray<string>,
): number | undefined {
  for (const key of keys) {
    const v = rec[key];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return undefined;
}

/**
 * Project a host visibility string onto the Excel-native enum the
 * agent sees (`Visible` / `Hidden` / `VeryHidden`).
 *
 * Accepts the catalog PascalCase values, case-folded variants, and the
 * Excel COM constants captured on the FactSet wire (`xlSheetVisible`,
 * `xlSheetHidden`, `xlSheetVeryHidden` in `excel-docs/intent-details.md`).
 * Unrecognised values (including numeric 0) are dropped rather than
 * guessed — a missing `visibility` is unknown, not "Visible".
 */
function adaptVisibilityField(
  raw: unknown,
): "Visible" | "Hidden" | "VeryHidden" | undefined {
  if (typeof raw !== "string") return undefined;
  const normalized = raw.trim().toLowerCase();
  if (normalized === "visible" || normalized === "xlsheetvisible") {
    return "Visible";
  }
  if (normalized === "hidden" || normalized === "xlsheethidden") {
    return "Hidden";
  }
  if (
    normalized === "veryhidden" ||
    normalized === "very_hidden" ||
    normalized === "xlsheetveryhidden"
  ) {
    return "VeryHidden";
  }
  return undefined;
}

function adaptBooleanField(raw: unknown): boolean | undefined {
  if (typeof raw === "boolean") return raw;
  return undefined;
}

/**
 * Soft cap on `console.info("[Excel] GetRangeInfo raw payload", raw)`
 * emissions per session. The single `loggedRangeInfoOnce` boolean we
 * had previously was useful for confirming the wire shape on the
 * first request, but blind for every subsequent sheet — and the bug
 * that motivated this rewrite (empty `rangeInfo: []` for every sheet
 * in the workbook) was hard to spot precisely because we only saw
 * the first sheet's payload. A small budget gives us coverage across
 * the typical 1–3 sheet workbook without flooding the console on
 * large books.
 */
const RANGE_INFO_LOG_BUDGET = 5;
let loggedRangeInfoCount = 0;

/**
 * Narrow `GetRangeInfo`'s `rangeInfo` payload into a `CellRange`.
 *
 * Observed wire shape (from the captured examples in
 * `excel-docs/intent-details.md` — a log of real payloads, **not** a
 * contracted schema): the response looks like
 *
 *   { rangeInfo: CellRecord[] }
 *
 * where each `CellRecord` is approximately
 *
 *   { value?, address, formula?, text?, numberFormat?, row, column }
 *
 * The structural observation we rely on is "`rangeInfo` is an array of
 * cell records, not the placeholder object `excel-tools.md` shows" —
 * the public doc and the captures only disagree at the array-vs-object
 * level. Per-field detail (1-indexed `row` / `column`, empty cells
 * **omitting `value`** entirely, precedent-formula spills carrying
 * `value` / `text` with `formula === ""`) holds across every example
 * we have, but is example-level until FactSet confirms it — see
 * `f2_docs/excel/finster-docs/missing-intents-v0.md` §2.2 for the
 * pending FactSet ask.
 *
 * The adapter is **defensive about per-field detail**:
 *   1. Walks every record to compute the bounding box (`minRow..maxRow`
 *      x `minColumn..maxColumn`) using the observed 1-indexed `row` /
 *      `column`, falling back to parsing the `address` if those are
 *      missing or malformed.
 *   2. Allocates a dense 2D `values` matrix and a dense `formulas`
 *      matrix sized to the bounding box, with `null` for absent cells.
 *      Treats both `"value" in cell === false` (the captured "empty
 *      cell" shape) and an explicit `value: null` (the shape we have
 *      not seen but cannot rule out) as `null` in the output.
 *   3. Returns a `CellRange` with `startRow` / `startColumn` set to
 *      the 0-based bounding-box origin so the matrices align with
 *      `address`.
 *
 * Defensively also accepts:
 *   - a bare `CellRecord[]` (`raw === [...]`)
 *   - the legacy `{ rangeInfo: { values, formulas } }` 2D-array shape
 *     (in case the host ever switches back) — used only as a last-
 *     resort fallback.
 *
 * Returns `undefined` when the payload doesn't look range-shaped — the
 * caller treats that the same as "no used range". When the payload is
 * a *well-formed envelope* (object with `rangeInfo: []` or a bare
 * `[]`) but yields zero usable records, emits a `console.warn` so
 * silent empty reads are visible — the regression from 2026-04-27
 * showed exactly that shape on every sheet and surfaced nothing
 * until this diagnostic landed.
 */
function adaptRangeInfo(raw: unknown, fallbackAddress: string): CellRange | undefined {
  if (isDiagEnabled() && loggedRangeInfoCount < RANGE_INFO_LOG_BUDGET) {
    loggedRangeInfoCount += 1;
    console.info(
      `[Excel] GetRangeInfo raw payload (${loggedRangeInfoCount}/${RANGE_INFO_LOG_BUDGET}) for ${fallbackAddress}:`,
      raw
    );
  }

  // Locate the cell-record array. Accept three shapes:
  //   A) { rangeInfo: CellRecord[] }       ← documented wire shape
  //   B) CellRecord[]                       ← bare array (defensive)
  //   C) { rangeInfo: { values, formulas } } ← legacy 2D fallback
  let cellRecords: Record<string, unknown>[] | undefined;
  let legacyInner: Record<string, unknown> | undefined;
  let documentedAddress: string | undefined;
  // True when we found a well-formed envelope (an object with a
  // `rangeInfo` array, or a bare array) but that envelope was empty.
  // Distinguishes "host returned [] because there's no data" from "we
  // never recognized the shape at all" — the former gets a
  // diagnostic warn, the latter is silent (the caller's CSV fallback
  // will recover).
  let envelopeWasEmpty = false;

  if (Array.isArray(raw)) {
    cellRecords = (raw as unknown[]).filter(
      (r): r is Record<string, unknown> => !!r && typeof r === "object"
    );
    if (raw.length === 0) envelopeWasEmpty = true;
  } else {
    const root = asRecord(raw);
    if (!root) return undefined;
    const inner = root.rangeInfo;
    if (Array.isArray(inner)) {
      cellRecords = (inner as unknown[]).filter(
        (r): r is Record<string, unknown> => !!r && typeof r === "object"
      );
      if (inner.length === 0) envelopeWasEmpty = true;
    } else {
      legacyInner = asRecord(inner) ?? root;
      const a = legacyInner.address;
      if (typeof a === "string") documentedAddress = a;
    }
  }

  if (cellRecords && cellRecords.length > 0) {
    return adaptRangeInfoFromCellRecords(cellRecords, fallbackAddress);
  }

  // Well-formed envelope, zero usable cells. Warn so empty reads are
  // visible in the console even when the call itself "succeeded" — the
  // motivating regression returned this shape for every sheet and
  // nothing surfaced to the operator until the CSV probe diagnostic
  // landed. Off the budget'd log path because this is an anomaly, not
  // a tracing aid.
  if (isDiagEnabled() && envelopeWasEmpty) {
    console.warn(
      `[Excel] GetRangeInfo for ${fallbackAddress} returned 0 usable cell records (envelope present but empty). Caller will fall back to CSV if available.`
    );
  }

  // Legacy 2D-array fallback (kept for forward-compat if the host ever
  // changes its mind). This branch is the original adapter behaviour.
  if (legacyInner) {
    const values = Array.isArray(legacyInner.values)
      ? (legacyInner.values as unknown[][])
      : undefined;
    const formulas = Array.isArray(legacyInner.formulas)
      ? (legacyInner.formulas as unknown[][])
      : undefined;
    if (!values && !formulas) return undefined;
    const rowCount =
      typeof legacyInner.rowCount === "number"
        ? legacyInner.rowCount
        : values?.length ?? formulas?.length ?? 0;
    const columnCount =
      typeof legacyInner.columnCount === "number"
        ? legacyInner.columnCount
        : values?.[0]?.length ?? formulas?.[0]?.length ?? 0;
    if (rowCount === 0 || columnCount === 0) return undefined;
    return {
      address: documentedAddress ?? fallbackAddress,
      startRow: 0,
      startColumn: 0,
      rowCount,
      columnCount,
      values: (values ?? []) as (string | number | boolean | null)[][],
      formulas: formulas as (string | null)[][] | undefined,
    };
  }

  return undefined;
}

/**
 * Discriminate a GetRangeInfo success payload for `readRangeContent`.
 *
 * `adaptRangeInfo` returns `undefined` for both a confirmed-empty
 * envelope and an unusable shape. The executor must not collapse those:
 * empty `[]` / `{ rangeInfo: [] }` plus empty CSV is `emptyRange`;
 * garbage plus empty CSV is a dual-host failure. Other callers of
 * `adaptRangeInfo` keep the original `CellRange | undefined` contract.
 */
function classifyGetRangeInfoPayload(
  raw: unknown,
  fallbackAddress: string,
):
  | { kind: "range"; range: CellRange }
  | { kind: "empty" }
  | { kind: "unusable" } {
  const adapted = adaptRangeInfo(raw, fallbackAddress);
  if (adapted) return { kind: "range", range: adapted };
  if (isEmptyGetRangeInfoEnvelope(raw)) return { kind: "empty" };
  return { kind: "unusable" };
}

/**
 * True when the host returned a well-formed envelope with zero cells.
 * Bare `[]` and `{ rangeInfo: [] }` are confirmed blanks. Anything
 * else that `adaptRangeInfo` could not project (including
 * `{ rangeInfo: [{}] }` and non-array payloads) is not empty.
 */
function isEmptyGetRangeInfoEnvelope(raw: unknown): boolean {
  if (Array.isArray(raw)) return raw.length === 0;
  const root = asRecord(raw);
  if (!root) return false;
  return Array.isArray(root.rangeInfo) && root.rangeInfo.length === 0;
}

/**
 * Resolve a cell record's 1-indexed `(row, column)` coordinates.
 *
 * Prefers explicit `row` / `column` fields when present (numeric and
 * positive). Falls back to parsing the A1-style `address` when they
 * are missing or malformed. Returns `undefined` when neither path
 * yields a usable coordinate.
 */
function resolveCellRecordPosition(
  cell: Record<string, unknown>
): { row: number; column: number } | undefined {
  const rRaw = cell.row;
  const cRaw = cell.column;
  if (
    typeof rRaw === "number" &&
    Number.isFinite(rRaw) &&
    rRaw >= 1 &&
    typeof cRaw === "number" &&
    Number.isFinite(cRaw) &&
    cRaw >= 1
  ) {
    return { row: Math.floor(rRaw), column: Math.floor(cRaw) };
  }
  const addr = cell.address;
  if (typeof addr === "string") {
    const m = /^\$?([A-Z]+)\$?(\d+)$/i.exec(
      addr.includes("!") ? addr.split("!").pop()! : addr
    );
    if (m) {
      try {
        const col1 = columnLetterToIndex(m[1]) + 1;
        const row1 = parseInt(m[2], 10);
        if (row1 >= 1 && col1 >= 1) return { row: row1, column: col1 };
      } catch {
        // fall through
      }
    }
  }
  return undefined;
}

/**
 * Convert a `CellRecord[]` payload (the documented wire shape of
 * `GetRangeInfo`) into a dense `CellRange`. See `adaptRangeInfo` for
 * the discriminator semantics around empty cells and precedent-formula
 * spills.
 */
function adaptRangeInfoFromCellRecords(
  cellRecords: Record<string, unknown>[],
  fallbackAddress: string
): CellRange | undefined {
  let minRow = Infinity;
  let maxRow = -Infinity;
  let minCol = Infinity;
  let maxCol = -Infinity;
  const positioned: Array<{
    row: number;
    column: number;
    cell: Record<string, unknown>;
  }> = [];
  for (const cell of cellRecords) {
    const pos = resolveCellRecordPosition(cell);
    if (!pos) continue;
    positioned.push({ row: pos.row, column: pos.column, cell });
    if (pos.row < minRow) minRow = pos.row;
    if (pos.row > maxRow) maxRow = pos.row;
    if (pos.column < minCol) minCol = pos.column;
    if (pos.column > maxCol) maxCol = pos.column;
  }
  if (
    positioned.length === 0 ||
    !Number.isFinite(minRow) ||
    !Number.isFinite(minCol)
  ) {
    return undefined;
  }

  const rowCount = maxRow - minRow + 1;
  const columnCount = maxCol - minCol + 1;
  const values: (string | number | boolean | null)[][] = Array.from(
    { length: rowCount },
    () => Array<string | number | boolean | null>(columnCount).fill(null)
  );
  const formulas: (string | null)[][] = Array.from(
    { length: rowCount },
    () => Array<string | null>(columnCount).fill(null)
  );
  const numberFormats: (string | null)[][] = Array.from(
    { length: rowCount },
    () => Array<string | null>(columnCount).fill(null)
  );
  let sawAnyFormula = false;
  let sawAnyNumberFormat = false;
  const cellStyles: Record<string, CellStyleInfo> = {};

  for (const { row, column, cell } of positioned) {
    const r = row - minRow;
    const c = column - minCol;
    if ("value" in cell) {
      values[r][c] = sanitizeCellValue(cell.value);
    }
    if (typeof cell.formula === "string" && cell.formula.length > 0) {
      formulas[r][c] = cell.formula;
      sawAnyFormula = true;
    }
    if (
      typeof cell.numberFormat === "string" &&
      cell.numberFormat.length > 0 &&
      cell.numberFormat !== "General"
    ) {
      numberFormats[r][c] = cell.numberFormat;
      sawAnyNumberFormat = true;
    }

    // Extract cell style info for the sparse map
    const style: CellStyleInfo = {};
    let hasStyle = false;

    // Bold (from font object)
    const font = cell.font as Record<string, unknown> | undefined;
    if (font && font.bold === true) {
      style.bold = true;
      hasStyle = true;
    }

    // Italic
    if (font && font.italic === true) {
      style.italic = true;
      hasStyle = true;
    }

    // Font size (omit default 11pt)
    if (font && typeof font.size === "number" && font.size > 0 && font.size !== 11) {
      style.fontSize = font.size;
      hasStyle = true;
    }

    // Strikethrough
    if (font && font.strikethrough === true) {
      style.strikethrough = true;
      hasStyle = true;
    }

    // Underline (any style other than "none")
    if (font && typeof font.underline === "string" && font.underline !== "xlUnderlineStyleNone") {
      style.underline = true;
      hasStyle = true;
    }

    // Fill color
    if (typeof cell.fillColor === "number") {
      const hex = bgrToHex(cell.fillColor, false);
      if (hex) { style.fillColor = hex; hasStyle = true; }
    }

    // Font color
    if (typeof cell.fontColor === "number") {
      const hex = bgrToHex(cell.fontColor, true);
      if (hex) { style.fontColor = hex; hasStyle = true; }
    }

    // Horizontal alignment
    const hAlign = normalizeHAlign(cell.horizontalAlignment);
    if (hAlign) { style.align = hAlign; hasStyle = true; }

    // Merged cells
    if (cell.mergeCells === true) {
      style.merged = true;
      hasStyle = true;
    }

    if (hasStyle) {
      cellStyles[`${r},${c}`] = style;
    }
  }

  // Use the bounding box derived from min/max as the canonical range
  // address (in 1-based Excel coordinates). Prefer the computed
  // bounding box over the caller's `fallbackAddress` because the
  // caller often passes the *requested* range (or a bare sheet name)
  // and the actual populated range is smaller and more precise.
  const topLeft = `${columnIndexToLetter(minCol - 1)}${minRow}`;
  const bottomRight = `${columnIndexToLetter(maxCol - 1)}${maxRow}`;
  const computedAddress =
    rowCount === 1 && columnCount === 1
      ? topLeft
      : `${topLeft}:${bottomRight}`;
  // Preserve the caller's sheet prefix (if any) so downstream code
  // that splits on "!" still gets the sheet name.
  const sheetPrefix =
    typeof fallbackAddress === "string" && fallbackAddress.includes("!")
      ? `${fallbackAddress.split("!")[0]}!`
      : "";
  const address = `${sheetPrefix}${computedAddress}`;

  const hasCellStyles = Object.keys(cellStyles).length > 0;

  return {
    address,
    startRow: minRow - 1,
    startColumn: minCol - 1,
    rowCount,
    columnCount,
    values,
    formulas: sawAnyFormula ? formulas : undefined,
    numberFormats: sawAnyNumberFormat ? numberFormats : undefined,
    cellStyles: hasCellStyles ? cellStyles : undefined,
  };
}

/**
 * Parse a `GetRangeAsCsv` body into a jagged 2D string matrix.
 *
 * RFC 4180: commas and newlines inside `"quoted"` fields stay in the
 * cell; `""` is an escaped quote. Unquoted input still splits on `,`
 * the same way the previous naive parser did. Trailing empty rows are
 * dropped (the host already does this); interior empty rows are kept
 * as empty arrays so row indices line up with sheet rows.
 *
 * Returns `undefined` for empty / non-string input.
 */
function parseCsvRows(csv: string): string[][] | undefined {
  if (typeof csv !== "string" || csv.length === 0) return undefined;

  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  const flushRow = (includeCurrentCell: boolean) => {
    if (includeCurrentCell) {
      if (row.length === 0 && cell.length === 0) {
        rows.push([]);
      } else {
        row.push(cell);
        rows.push(row);
      }
    }
    row = [];
    cell = "";
  };

  for (let i = 0; i < csv.length; i++) {
    const char = csv[i];
    if (inQuotes) {
      if (char === '"' && csv[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        cell += char;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
    } else if (char === ",") {
      row.push(cell);
      cell = "";
    } else if (char === "\n") {
      flushRow(true);
    } else if (char !== "\r") {
      cell += char;
    }
  }

  if (row.length > 0 || cell.length > 0 || inQuotes) {
    flushRow(true);
  }

  while (rows.length > 0 && csvRowIsEmpty(rows[rows.length - 1])) {
    rows.pop();
  }
  return rows.length === 0 ? undefined : rows;
}

function csvRowIsEmpty(row: string[]): boolean {
  return row.length === 0 || row.every((cell) => cell === "");
}

/**
 * Compute the tight 1-indexed bounding box of populated cells in a
 * jagged CSV matrix returned by `parseCsvRows`. Empty cells (`""`) are
 * NOT counted; only non-empty strings extend the box.
 *
 * Returns `undefined` when every cell is empty (i.e. the sheet is
 * blank or the probe missed). The 1-indexed shape mirrors the rest of
 * the bridge-mode read code (`adaptRangeInfo` uses 1-indexed `row` /
 * `column`).
 */
function boundingBoxFromCsvRows(
  rows: string[][]
): { lastRow: number; lastCol: number } | undefined {
  let lastRow = 0;
  let lastCol = 0;
  for (let r = 0; r < rows.length; r++) {
    const row = rows[r];
    if (!row) continue;
    let rowLastCol = 0;
    for (let c = 0; c < row.length; c++) {
      if (row[c] !== "") rowLastCol = c + 1;
    }
    if (rowLastCol > 0) {
      lastRow = r + 1;
      if (rowLastCol > lastCol) lastCol = rowLastCol;
    }
  }
  if (lastRow === 0 || lastCol === 0) return undefined;
  return { lastRow, lastCol };
}

/**
 * Build a dense `CellRange` from a CSV row matrix and a tight bounding
 * box. Rows shorter than `lastCol` are right-padded with `null`;
 * missing rows are emitted as all-`null` arrays so the matrix is
 * rectangular.
 *
 * Used as the fallback payload when `GetRangeInfo` against the tight
 * box still comes back empty (i.e. the host has CSV data but no rich
 * cell records). Less rich than `adaptRangeInfo`'s output (no
 * formulas) but still gives the model concrete cell values.
 */
function buildCellRangeFromCsvRows(
  rows: string[][],
  lastRow: number,
  lastCol: number,
  address: string
): CellRange {
  const values: (string | number | boolean | null)[][] = [];
  for (let r = 0; r < lastRow; r++) {
    const row = rows[r] ?? [];
    const out: (string | number | boolean | null)[] = new Array(lastCol);
    for (let c = 0; c < lastCol; c++) {
      const v = row[c];
      out[c] = v === undefined || v === "" ? null : v;
    }
    values.push(out);
  }
  return {
    address,
    startRow: 0,
    startColumn: 0,
    rowCount: lastRow,
    columnCount: lastCol,
    values,
  };
}

/**
 * Build a tight A1-style bounding-box address (e.g. `"A1:F12"`) from a
 * 1-indexed `{ lastRow, lastCol }` pair. Always rooted at A1; the
 * caller is responsible for promoting the sheet name onto the
 * `RangeAddress.worksheetName` field.
 */
function tightBoundingBoxAddress(lastRow: number, lastCol: number): string {
  const lastColLetter = columnIndexToLetter(lastCol - 1);
  return lastRow === 1 && lastCol === 1
    ? "A1"
    : `A1:${lastColLetter}${lastRow}`;
}

/**
 * Probe ladder for `readSheetRangeViaBridge`. Each entry is the
 * request-side cap for one `getRangeAsCsv` size probe; we walk the
 * ladder until the bounding box of the response is strictly inside
 * the requested rectangle (i.e. the data fits) or we run out of
 * rungs.
 *
 * Why a ladder instead of one wide probe: a wide initial probe
 * (`A1:ZZ10000`, ~7M cells request-side) makes the host either
 * timeout or backpressure into the next intent — observed 2026-04-27
 * as an `InsertContentsToRange` 15s timeout immediately following
 * two consecutive 15s probe timeouts on a tiny sheet. The captured
 * `getRangeAsCsv` example uses `"A1:C10"`, so we start small and
 * grow only when the data demands it.
 *
 * Sizes:
 *   - Rung 0 (`A1:Z200`)   — covers the typical "small assumption
 *     model" or simple input sheet in one round-trip.
 *   - Rung 1 (`A1:CV1000`) — 100 columns × 1000 rows; covers most
 *     banker workbooks.
 *   - Rung 2 (`A1:ZZ5000`) — large model territory; capped before
 *     `A1:ZZ10000` because anything past this should be a sheet
 *     subset the model targets explicitly, not a sweep.
 *
 * If even rung 2's response saturates the request rectangle, we use
 * the resulting bounding box as-is (the data is at least that big)
 * and let the rich `getRangeInfo` call run against it — the model
 * sees a truncated view, but writes still work because they go
 * through `InsertContentsToRange` against an address the model
 * names, not against this probe.
 */
const SHEET_PROBE_LADDER: ReadonlyArray<{
  address: string;
  rowCap: number;
  colCap: number;
}> = [
  { address: "A1:Z200", rowCap: 200, colCap: 26 },
  { address: "A1:CV1000", rowCap: 1000, colCap: 100 },
  { address: "A1:ZZ5000", rowCap: 5000, colCap: 702 },
];

/**
 * Sheet-name predicate for FactSet-internal / cache sheets that should
 * be skipped by the bridge-mode read pipeline. Today only
 * `__FDSCACHE__` is observed in the wild (see logs `5:07:11 AM | 📊
 * Got 3 sheet(s): "__FDSCACHE__" (0r), …` from the
 * `04-27-empty-used-range` repro), but FactSet's leading-and-trailing
 * underscore convention is broad, so we filter the whole class to
 * future-proof. Surfacing the cache contents to the AI is pure noise
 * — the model can't usefully reason about it and the rows are large
 * enough to crowd out the user's actual sheet data in the prompt.
 */
function isInternalSheetName(name: string): boolean {
  return /^__.+__$/.test(name);
}

/**
 * Read a sheet's used range via the intent bridge.
 *
 * Probe-then-read with a small ladder of probe sizes
 * (`SHEET_PROBE_LADDER`) — a single wide probe (`A1:ZZ10000`) was
 * observed on 2026-04-27 to make the host time out for ~15s twice
 * per sheet AND backpressure the next `InsertContentsToRange` write
 * into its own 15s timeout, even on a one-sheet blank workbook. The
 * ladder starts small (`A1:Z200`) and only escalates when the
 * response actually saturates the requested rectangle, so blank /
 * small sheets resolve in one round-trip.
 *
 * Flow:
 *   1. Walk `SHEET_PROBE_LADDER`, calling `getRangeAsCsv` at each
 *      rung. Stop as soon as the bounding box of the response is
 *      strictly inside the requested rectangle (the data fits), or
 *      a probe call throws (host can't service the size — don't
 *      keep escalating, that just compounds the latency hit).
 *   2. If the final box is `undefined`, the sheet is blank — return
 *      `undefined` without calling `getRangeInfo`.
 *   3. Otherwise call `getRangeInfo` against the tight bounding box
 *      (`tightBoundingBoxAddress`) for rich values + formulas. On
 *      failure or empty payload, fall back to the CSV-derived
 *      values-only `CellRange`.
 *
 * `sheetName` is forwarded as `worksheetName` on every call. Returns
 * `undefined` for genuinely blank sheets and when the first probe
 * rung itself fails (caller treats both the same).
 */
async function readSheetRangeViaBridge(
  sheetName: string,
  workbookName?: string,
): Promise<CellRange | undefined> {
  let csvRows: string[][] | undefined;
  let box: { lastRow: number; lastCol: number } | undefined;
  let probeAddress: string | undefined;

  for (const rung of SHEET_PROBE_LADDER) {
    let rows: string[][] | undefined;
    try {
      const csv = await getRangeAsCsv(
        rung.address,
        undefined,
        sheetName,
        workbookName,
      );
      rows = parseCsvRows(csv);
    } catch (e) {
      console.warn(
        `[Excel] GetRangeAsCsv probe at "${rung.address}" failed for sheet "${sheetName}"; using last-good box:`,
        e
      );
      // Stop escalating on host failure — piling up timeouts just
      // makes the next user write wait longer behind the queue.
      break;
    }

    csvRows = rows;
    probeAddress = rung.address;
    box = rows ? boundingBoxFromCsvRows(rows) : undefined;

    if (!box) {
      // Sheet is empty within this rung. Smaller rungs are subsets,
      // so an empty response at rung N means the whole sheet is
      // blank up to rung N's bounds — no need to escalate.
      return undefined;
    }

    // Bounding box strictly inside the rung → data fits, stop here.
    if (box.lastRow < rung.rowCap && box.lastCol < rung.colCap) {
      break;
    }
    // Otherwise: edge-saturated. Loop and escalate to the next rung.
  }

  if (!box || !csvRows) {
    return undefined;
  }

  const richAddress = tightBoundingBoxAddress(box.lastRow, box.lastCol);

  try {
    const rangeInfo = await getRangeInfo(
      richAddress,
      { propertyGroups: ["values", "formulas"], properties: RANGE_INFO_FORMAT_PROPERTIES, includeEmptyCells: false },
      sheetName,
      workbookName,
    );
    logRangeFormatDiagnostics(rangeInfo, {
      range: richAddress,
      sheetName,
      source: "probe",
    });
    const adapted = adaptRangeInfo(rangeInfo, richAddress);
    if (adapted) {
      logAdaptedRangeFormatDiagnostics(adapted, {
        requestedRange: richAddress,
        sheetName,
        source: "probe",
      });
      return adapted;
    }
  } catch (e) {
    console.warn(
      `[Excel] GetRangeInfo failed for sheet "${sheetName}" range "${richAddress}" (probe was "${probeAddress}", will fall back to CSV payload):`,
      e
    );
  }

  // CSV-only fallback: rich call failed or returned nothing, but we
  // have a bounding box and parsed rows from the last successful
  // probe. Build a values-only `CellRange` so the model still gets
  // concrete cell contents (no formulas, no number formats).
  return buildCellRangeFromCsvRows(
    csvRows,
    box.lastRow,
    box.lastCol,
    richAddress
  );
}

/**
 * Read a sheet's used range when the bounding box is already known
 * (e.g. caller pulled it from `GetWorkbookSheets`'s `usedRange`
 * property). Skips the entire `SHEET_PROBE_LADDER` — no
 * `GetRangeAsCsv` size probe, just one `GetRangeInfo` against the
 * tight `A1:Xn` address derived from `box`. On failure or empty
 * payload, falls back to a single `GetRangeAsCsv` against the same
 * address so we still emit a values-only `CellRange` (formulas
 * dropped, matching the legacy probe path's degraded behaviour).
 *
 * Returns `undefined` when `box` is empty/zero-sized (caller treats
 * the sheet as blank and skips it in the extract output) or when
 * both `GetRangeInfo` and the CSV fallback fail.
 *
 * Used by the bridge-mode extract callers
 * (`extractWorkbookContentViaBridge`, `extractAllSheetsContentViaBridge`)
 * when `GetWorkbookSheets` returned a per-sheet `usedRange`. Older
 * hosts that ignore `options.properties` won't surface a box, and
 * callers fall back to `readSheetRangeViaBridge`'s probe ladder
 * automatically.
 */
async function readSheetRangeViaBridgeFromBox(
  sheetName: string,
  box: UsedRangeBox,
  maxRows?: number,
  maxCellsPerSheet?: number,
  workbookName?: string,
): Promise<CellRange | undefined> {
  if (box.lastRow <= 0 || box.lastCol <= 0) return undefined;

  const effectiveLastCol =
    maxCellsPerSheet !== undefined
      ? Math.min(box.lastCol, Math.max(1, maxCellsPerSheet))
      : box.lastCol;
  const cellCapRows =
    maxCellsPerSheet !== undefined && effectiveLastCol > 0
      ? Math.max(1, Math.floor(maxCellsPerSheet / effectiveLastCol))
      : Number.POSITIVE_INFINITY;
  const effectiveLastRow = Math.min(
    box.lastRow,
    maxRows ?? Number.POSITIVE_INFINITY,
    cellCapRows,
  );
  const richAddress = tightBoundingBoxAddress(effectiveLastRow, effectiveLastCol);
  // The used-range metadata is the authoritative sheet height. Keep it even
  // when the bounded payload happens not to truncate so downstream consumers
  // never infer full workbook scope from a preview matrix.
  const withTotalRows = (range: CellRange): CellRange => ({
    ...range,
    totalRowCount: box.lastRow,
  });

  try {
    const rangeInfo = await getRangeInfo(
      richAddress,
      { propertyGroups: ["values", "formulas"], properties: RANGE_INFO_FORMAT_PROPERTIES, includeEmptyCells: false },
      sheetName,
      workbookName,
    );
    logRangeFormatDiagnostics(rangeInfo, {
      range: richAddress,
      sheetName,
      source: "usedRange",
    });
    const adapted = adaptRangeInfo(rangeInfo, richAddress);
    if (adapted) {
      const adaptedWithTotalRows = withTotalRows(adapted);
      logAdaptedRangeFormatDiagnostics(adaptedWithTotalRows, {
        requestedRange: richAddress,
        sheetName,
        source: "usedRange",
      });
      return adaptedWithTotalRows;
    }
  } catch (e) {
    console.warn(
      `[Excel] GetRangeInfo (from usedRange) failed for sheet "${sheetName}" range "${richAddress}" — will fall back to CSV:`,
      e,
    );
  }

  try {
    const csv = await getRangeAsCsv(
      richAddress,
      undefined,
      sheetName,
      workbookName,
    );
    const rows = parseCsvRows(csv);
    if (!rows) return undefined;
    const csvBox = boundingBoxFromCsvRows(rows);
    if (!csvBox) return undefined;
    const fallback = buildCellRangeFromCsvRows(
      rows,
      csvBox.lastRow,
      csvBox.lastCol,
      tightBoundingBoxAddress(csvBox.lastRow, csvBox.lastCol),
    );
    return withTotalRows(fallback);
  } catch (e) {
    console.warn(
      `[Excel] GetRangeAsCsv (from usedRange) fallback failed for sheet "${sheetName}":`,
      e,
    );
    return undefined;
  }
}

/**
 * Dispatcher used by the bridge-mode extract callers. Picks the
 * fast-path (`readSheetRangeViaBridgeFromBox`) when the
 * `GetWorkbookSheets` adapter surfaced a known `usedRange`, short-
 * circuits when the host explicitly confirmed the sheet is blank,
 * and only falls back to the legacy probe ladder
 * (`readSheetRangeViaBridge`) when the host omitted the field
 * entirely (older host that ignored `options.properties`).
 *
 * Four observable cases (all return the same `CellRange | undefined`
 * contract):
 *   1. `usedRange` populated → fast-path one `getRangeInfo` against
 *      the tight A1-anchored address. Zero CSV probes.
 *   2. `usedRange` undefined **and** `usedRangePresent` true → host
 *      explicitly told us this sheet is blank (it honoured
 *      `options.properties: ["usedRange"]` and returned `null` /
 *      `{}` / `{ rowCount: 0 }`). Return `undefined` immediately —
 *      no point probing a sheet the host already confirmed blank.
 *   3. `usedRange` undefined **and** `usedRangePresent` false → host
 *      omitted the field entirely (older host). Fall back to the
 *      probe ladder so we still surface any content.
 *   4. `usedRange` is huge (degenerate workbook with garbage
 *      far-out cells) → fast-path, single call. The host's own
 *      `getRangeInfo` shape limits already apply.
 *
 * Cases 1+2 together mean: when the host honours `properties`, a
 * 20-sheet workbook with 15 blank sheets costs exactly 5 cell reads
 * total (one per non-blank sheet), down from 20 in the pre-`properties`
 * world.
 */
async function readSheetUsedRangeViaBridge(
  sheet: AdaptedWorkbookSheet,
  maxRows?: number,
  maxCellsPerSheet?: number,
  workbookName?: string,
): Promise<CellRange | undefined> {
  if (sheet.usedRange) {
    return readSheetRangeViaBridgeFromBox(
      sheet.name,
      sheet.usedRange,
      maxRows,
      maxCellsPerSheet,
      workbookName,
    );
  }
  if (sheet.usedRangePresent && !sheet.usedRangeUnparsed) {
    // Host honoured `options.properties` and explicitly reported no
    // used range — sheet is blank. Skip the probe ladder.
    return undefined;
  }
  return readSheetRangeViaBridge(sheet.name, workbookName);
}

/**
 * Read a **known** A1-style range (e.g. a selection like `"A3:C5"` or
 * a sheet-prefixed `"Sheet1!A3:C5"`) via the intent bridge. Unlike
 * `readSheetRangeViaBridge`, this path skips the size-probe step
 * because the caller already knows the exact range — no need to
 * derive a bounding box.
 *
 * Tries `GetRangeInfo` first; on empty / error, falls back to
 * `GetRangeAsCsv` against the same address and adapts the CSV body
 * into a values-only `CellRange`. Returns `undefined` when both legs
 * fail or both come back empty.
 *
 * Used by `getSelectedRangeData` (read-back of the user's current
 * selection); keep separate from `readSheetRangeViaBridge` so the
 * sheet-wide probe isn't accidentally fired for narrow ranges.
 */
async function readKnownRangeViaBridge(
  address: string,
  sheetName?: string
): Promise<CellRange | undefined> {
  try {
    const rangeInfo = await getRangeInfo(
      address,
      { propertyGroups: ["values", "formulas"], properties: RANGE_INFO_FORMAT_PROPERTIES, includeEmptyCells: false },
      sheetName
    );
    logRangeFormatDiagnostics(rangeInfo, {
      range: address,
      sheetName,
      source: "knownRange",
    });
    const adapted = adaptRangeInfo(rangeInfo, address);
    if (adapted) {
      logAdaptedRangeFormatDiagnostics(adapted, {
        requestedRange: address,
        sheetName,
        source: "knownRange",
      });
      return adapted;
    }
  } catch (e) {
    console.warn(
      `[Excel] GetRangeInfo failed for ${address} (will try GetRangeAsCsv):`,
      e
    );
  }
  try {
    const csv = await getRangeAsCsv(address, undefined, sheetName);
    const rows = parseCsvRows(csv);
    if (!rows) return undefined;
    const box = boundingBoxFromCsvRows(rows);
    if (!box) return undefined;
    return buildCellRangeFromCsvRows(rows, box.lastRow, box.lastCol, address);
  } catch (e) {
    console.warn(`[Excel] GetRangeAsCsv fallback failed for ${address}:`, e);
    return undefined;
  }
}

/**
 * Defensive narrower for the typed-but-element-shape-undocumented
 * `GetCharts` / `GetTables` arrays. The wire shape is a structured
 * array; per-element fields aren't yet documented in
 * `excel-tools.md`, so callers cast at the use site. Returns
 * `undefined` rather than throwing so the caller can warn-and-
 * continue when the host returns something unexpected (e.g. an
 * envelope shape we haven't seen yet).
 */
function adaptOpaqueArray<T>(raw: unknown): T[] | undefined {
  if (Array.isArray(raw)) return raw as T[];
  const rec = asRecord(raw);
  if (rec && Array.isArray(rec.items)) return rec.items as T[];
  return undefined;
}

/**
 * Compute the smallest bounding A1 range that covers every address in
 * `addresses`. Each address may be a single cell (`"A1"`), a range
 * (`"A1:C5"`), or an already-bracketed mix; sheet prefixes are stripped.
 * Returns `undefined` when no parseable address was found.
 *
 * Used to fold a list of per-cell / per-range edits from one apply pass
 * into a single `ParseFormulas` call, so the verifier sees one structured
 * blob per step instead of one call per edit.
 */
function boundingRangeFor(addresses: string[]): string | undefined {
  let minRow = Infinity;
  let minCol = Infinity;
  let maxRow = -Infinity;
  let maxCol = -Infinity;
  for (const addrRaw of addresses) {
    if (!addrRaw) continue;
    const bang = addrRaw.lastIndexOf("!");
    const bare = (bang >= 0 ? addrRaw.slice(bang + 1) : addrRaw).replace(/\$/g, "");
    const parts = bare.split(":");
    for (const part of parts) {
      const m = /^([A-Z]+)(\d+)$/i.exec(part);
      if (!m) continue;
      let col: number;
      try {
        col = columnLetterToIndex(m[1]);
      } catch {
        continue;
      }
      const row = parseInt(m[2], 10) - 1;
      if (col < minCol) minCol = col;
      if (col > maxCol) maxCol = col;
      if (row < minRow) minRow = row;
      if (row > maxRow) maxRow = row;
    }
  }
  if (!Number.isFinite(minRow) || !Number.isFinite(minCol)) return undefined;
  const top = `${columnIndexToLetter(minCol)}${minRow + 1}`;
  const bottom = `${columnIndexToLetter(maxCol)}${maxRow + 1}`;
  return top === bottom ? top : `${top}:${bottom}`;
}

/**
 * Read parsed formulas for a list of edited addresses via the intent
 * bridge. Returns the raw `parsedFormulas` payload (typed array;
 * per-element shape isn't documented yet — see `intent-details.md` →
 * "Parse Formulas") or `undefined` if the bounding-range computation
 * or the `ParseFormulas` call itself fails.
 *
 * Bridge-mode only — caller must check `shouldUseExcelIntentBridge()`
 * before invoking. `sheetName` is forwarded as the `worksheetName` on
 * the address; pass `undefined` to default to the active sheet.
 */
export async function parseFormulasForEditedCells(
  editedAddresses: string[],
  sheetName?: string,
  workbookName?: string,
): Promise<unknown | undefined> {
  if (editedAddresses.length === 0) return undefined;
  const bounding = boundingRangeFor(editedAddresses);
  if (!bounding) return undefined;
  try {
    return await parseFormulas(bounding, "StringOnly", sheetName, workbookName);
  } catch (e) {
    console.warn(
      `[Excel] ParseFormulas failed for bounding range ${bounding}:`,
      e
    );
    return undefined;
  }
}

/**
 * Extract current workbook content for sending to AI.
 *
 * `pinnedWorkbookName` (bridge mode only) routes every host read at
 * a specific workbook so a user-initiated workbook switch can't
 * redirect the read. `undefined` falls back to the active workbook.
 */
export async function extractWorkbookContent(
  pinnedWorkbookName?: string,
): Promise<WorkbookContent> {
  if (shouldUseExcelIntentBridge()) {
    return extractWorkbookContentViaBridge(pinnedWorkbookName);
  }
  return Excel.run(async (context) => {
    const workbook = context.workbook;
    const sheets = workbook.worksheets;
    const activeSheet = workbook.worksheets.getActiveWorksheet();
    const activeCell = workbook.getActiveCell();
    const selectedRange = workbook.getSelectedRange();

    workbook.load("name");
    sheets.load("items/name");
    activeSheet.load("name,position");
    activeCell.load("address");
    selectedRange.load("address");

    await context.sync();

    const worksheets: WorksheetContent[] = [];

    // Get content for each sheet (limited to used range)
    for (let i = 0; i < sheets.items.length; i++) {
      const sheet = sheets.items[i];
      const usedRange = sheet.getUsedRangeOrNullObject(true);

      usedRange.load("address,values,formulas,numberFormat,rowCount,columnCount");
      sheet.load("name");

      await context.sync();

      let cellRange: CellRange | undefined;

      if (!usedRange.isNullObject) {
        const styles = await extractCellStylesFromOfficeJs(usedRange, context);
        cellRange = {
          address: usedRange.address,
          startRow: 0,
          startColumn: 0,
          rowCount: usedRange.rowCount,
          columnCount: usedRange.columnCount,
          values: sanitizeValueMatrix(
            usedRange.values as (string | number | boolean | null)[][]
          ) as (string | number | boolean | null)[][],
          formulas: usedRange.formulas as (string | null)[][],
          numberFormats: sparseNumberFormats(usedRange.numberFormat as string[][]),
          cellStyles: styles,
        };
      }

      worksheets.push({
        name: sheet.name,
        index: i,
        usedRange: cellRange,
      });
    }

    return {
      activeSheetIndex: activeSheet.position,
      activeSheetName: activeSheet.name,
      workbookName: workbook.name,
      sheets: worksheets,
      activeCellAddress: activeCell.address,
      selectedRangeAddress: selectedRange.address,
    };
  });
}

/**
 * Get content of the active sheet only (more efficient for single-sheet
 * operations). `pinnedWorkbookName` (bridge mode only) routes the
 * read at a specific workbook; `undefined` falls back to the active
 * workbook. Note that even with a pinned workbook the function still
 * reads the *active sheet* of that workbook — there's no such thing
 * as a "non-active sheet of a non-active workbook" because the host
 * exposes only the active worksheet name per workbook.
 */
export async function extractActiveSheetContent(
  pinnedWorkbookName?: string,
): Promise<WorksheetContent> {
  if (shouldUseExcelIntentBridge()) {
    return extractActiveSheetContentViaBridge(pinnedWorkbookName);
  }
  return Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet();
    const usedRange = sheet.getUsedRangeOrNullObject(true);

    sheet.load("name,position");
    usedRange.load("address,values,formulas,numberFormat,rowCount,columnCount");

    await context.sync();

    let cellRange: CellRange | undefined;

    if (!usedRange.isNullObject) {
      const styles = await extractCellStylesFromOfficeJs(usedRange, context);
      cellRange = {
        address: usedRange.address,
        startRow: 0,
        startColumn: 0,
        rowCount: usedRange.rowCount,
        columnCount: usedRange.columnCount,
        values: sanitizeValueMatrix(
          usedRange.values as (string | number | boolean | null)[][]
        ) as (string | number | boolean | null)[][],
        formulas: usedRange.formulas as (string | null)[][],
        numberFormats: sparseNumberFormats(usedRange.numberFormat as string[][]),
        cellStyles: styles,
      };
    }

    return {
      name: sheet.name,
      index: sheet.position,
      usedRange: cellRange,
    };
  });
}

/**
 * Options for scoping a workbook extract — used to right-size reads
 * per operation so that a single-cell write does not pay for a full
 * multi-sheet read.
 *
 * The defaults match the legacy positional-arg behaviour exactly, so
 * adding the options bag is non-breaking for any caller that omits it.
 */
export interface ExtractWorkbookOptions {
  /** Rows read from the target sheet. Default 300. */
  maxTargetRows?: number;
  /** Rows read from non-target sheets. Default 10. */
  maxOtherRows?: number;
  /**
   * When `true`, skip the per-sheet `usedRange` read for all sheets
   * other than the target. Each non-target sheet still appears in
   * `sheets[]` with `name` + `index`, but `usedRange` is left
   * `undefined`. This shaves N-1 host round-trips from a per-step
   * extract on multi-sheet workbooks — the dominant contributor to
   * mid-run Excel freezing. Use it for per-step pre-reads and
   * per-step verifier reads inside a multi-step plan; the final
   * post-plan verifier still wants the full extract.
   *
   * Bridge-mode only (the Office.js fallback already paid the read
   * cost in a single `Excel.run` and gains nothing from skipping).
   */
  otherSheetsMetadataOnly?: boolean;
  /**
   * When `true`, **also** skip the target sheet's `usedRange` read
   * — every sheet (including the target) appears in `sheets[]`
   * with `name` + `index` only.
   *
   * Used by the per-step verifier read when
   * `applyResult.editedCells.length === 0`. Steps that don't edit
   * any cell (e.g. add-sheet, create-table-shell, add-chart) have
   * nothing cell-level to verify; the metadata-only payload is
   * still enough for the verifier to confirm "sheet 'X' now
   * exists" without paying for the target sheet's `GetRangeInfo`
   * round-trip. When this flag is set, `otherSheetsMetadataOnly`
   * is treated as `true` regardless of its own value.
   *
   * Bridge-mode only.
   */
  targetSheetMetadataOnly?: boolean;
  /**
   * Hard cap on the number of sheets that receive a `usedRange`
   * read. Sheets beyond the cap appear in `sheets[]` with metadata
   * only (`usedRange` left `undefined`). Default 25; useful for
   * extreme-shape workbooks (50+ sheets) where even the metadata-only
   * path would exceed the chat token budget.
   */
  maxSheets?: number;
  /**
   * Hard cap on the cell count read per sheet (`rowCount *
   * columnCount`). When exceeded, the read truncates to the first
   * `maxCellsPerSheet / columnCount` rows, with `totalRowCount` on
   * the resulting `CellRange` reporting the true size so the LLM
   * still sees the shape. Default 5000.
   */
  maxCellsPerSheet?: number;
  /**
   * Whether to attach workbook-scoped chart/table metadata via
   * `GetCharts` / `GetTables`. Default true. Per-step executor reads
   * set this false because those calls are workbook-scoped enrichment,
   * not required to write or verify a target-sheet cell edit.
   */
  includeChartsAndTables?: boolean;
  /**
   * Whether to attach workbook-scoped enrichment via `GetOpenWorkbooks`,
   * `GetWorkbookConnections`, and `GetWorkbookNamedObjects`. Default
   * true. Per-step executor reads set this false because those fields
   * are cached/global context, not needed to ground a target-sheet write.
   */
  includeWorkbookEnrichment?: boolean;
  /**
   * Pin every read to a specific workbook. When set, the bridge
   * extract sends `workbookName` on `GetWorkbookSheets`,
   * `GetRangeInfo` / `GetRangeAsCsv` for each sheet read,
   * `GetCharts` / `GetTables`, and the workbook-introspection
   * intents. Used by the per-plan execution path so a user-initiated
   * workbook switch mid-plan can't redirect reads to the wrong
   * workbook. The host transparently flickers focus to the target
   * workbook for these intents and restores the user's active
   * workbook by the end of each call. `undefined` falls back to the
   * active workbook (the legacy behaviour for chat-only and ad-hoc
   * extracts). Bridge-mode only.
   */
  workbookName?: string;
}

/**
 * Extract all sheets with tiered row limits.
 *
 * The target sheet (either `targetSheetName` or the active sheet) gets up to
 * `maxTargetRows` rows so the AI has full context for the current step.
 * All other sheets get only `maxOtherRows` rows — enough for a header row plus
 * a few preview rows so the AI can infer each sheet's schema without blowing
 * up the token budget on workbooks with many large sheets. `totalRowCount` on
 * the returned `CellRange` still reflects the true size of the used range.
 *
 * Pass an `ExtractWorkbookOptions` bag for the scope-sized variants
 * (per-step pre-reads, verifier reads). The legacy positional-arg
 * form is preserved for callers that don't need the new controls.
 */
export async function extractAllSheetsContent(
  targetSheetName?: string,
  maxTargetRowsOrOptions: number | ExtractWorkbookOptions = 300,
  maxOtherRowsLegacy: number = 10,
): Promise<WorkbookContent> {
  const options: ExtractWorkbookOptions =
    typeof maxTargetRowsOrOptions === "number"
      ? {
          maxTargetRows: maxTargetRowsOrOptions,
          maxOtherRows: maxOtherRowsLegacy,
        }
      : maxTargetRowsOrOptions;
  const maxTargetRows = options.maxTargetRows ?? 300;
  const maxOtherRows = options.maxOtherRows ?? 10;
  const maxSheets = options.maxSheets ?? 25;
  const maxCellsPerSheet = options.maxCellsPerSheet ?? 5_000;
  if (shouldUseExcelIntentBridge()) {
    return extractAllSheetsContentViaBridge(
      targetSheetName,
      maxTargetRows,
      maxOtherRows,
      options
    );
  }
  return Excel.run(async (context) => {
    const workbook = context.workbook;
    const sheets = workbook.worksheets;
    const activeSheet = workbook.worksheets.getActiveWorksheet();
    const activeCell = workbook.getActiveCell();
    const selectedRange = workbook.getSelectedRange();

    workbook.load("name");
    sheets.load("items/name");
    activeSheet.load("name,position");
    activeCell.load("address");
    selectedRange.load("address");

    await context.sync();

    const worksheets: WorksheetContent[] = [];

    let sheetsRead = 0;
    for (let i = 0; i < sheets.items.length; i++) {
      const sheet = sheets.items[i];
      sheet.load("name");
      await context.sync();
      const isTarget = targetSheetName
        ? sheet.name === targetSheetName
        : sheet.name === activeSheet.name;
      if (!isTarget && sheetsRead >= maxSheets) {
        worksheets.push({
          name: sheet.name,
          index: i,
        });
        continue;
      }
      const usedRange = sheet.getUsedRangeOrNullObject(true);

      usedRange.load("address,values,formulas,numberFormat,rowCount,columnCount");

      await context.sync();
      sheetsRead += 1;

      let cellRange: CellRange | undefined;

      if (!usedRange.isNullObject) {
        const rowLimit = isTarget ? maxTargetRows : maxOtherRows;
        const cellCapRows =
          usedRange.columnCount > 0
            ? Math.max(1, Math.floor(maxCellsPerSheet / usedRange.columnCount))
            : Number.POSITIVE_INFINITY;
        const effectiveRowLimit = Math.min(rowLimit, cellCapRows);
        const slicedValues = usedRange.values?.slice(0, effectiveRowLimit) as (string | number | boolean | null)[][];
        const limitedValues = sanitizeValueMatrix(slicedValues) as (string | number | boolean | null)[][];
        const limitedFormulas = usedRange.formulas?.slice(0, effectiveRowLimit) as (string | null)[][];
        const limitedNumberFormats = (usedRange.numberFormat as string[][])?.slice(0, effectiveRowLimit);
        const sentRows = limitedValues?.length ?? 0;

        const styles = await extractCellStylesFromOfficeJs(usedRange, context, effectiveRowLimit);
        cellRange = {
          address: usedRange.address,
          startRow: 0,
          startColumn: 0,
          rowCount: sentRows,
          columnCount: usedRange.columnCount,
          values: limitedValues,
          formulas: limitedFormulas,
          numberFormats: sparseNumberFormats(limitedNumberFormats),
          cellStyles: styles,
          totalRowCount: usedRange.rowCount,
        };
      }

      worksheets.push({
        name: sheet.name,
        index: i,
        usedRange: cellRange,
      });
    }

    return {
      activeSheetIndex: activeSheet.position,
      activeSheetName: activeSheet.name,
      workbookName: workbook.name,
      sheets: worksheets,
      activeCellAddress: activeCell.address,
      selectedRangeAddress: selectedRange.address,
    };
  });
}

/**
 * Lightweight workbook probe used to size up *what* a user is asking for
 * before paying the cost of a full per-sheet `usedRange` read. Lets the
 * intent classifier run on cheap metadata only; the heavier
 * `extractAllSheetsContent` is upgraded lazily and only on the branches
 * that actually need cell values.
 *
 * What's in the probe:
 *  - `activeSheetName` / `activeSheetIndex` / `activeCellAddress` /
 *    `selectedRangeAddress` — driven by `GetActiveExcelContext`
 *    (already cached by AI-2616, so the call is effectively free
 *    when the page just hit it for the header).
 *  - `workbookName` — same source.
 *  - `sheetNames` — driven by `GetWorkbookSheets` (also cached by
 *    AI-2616).
 *  - `sheetUsedRangeSummaries` — opportunistically populated when
 *    the host honours `GetWorkbookSheets`'s
 *    `options.properties: ["usedRange"]` (no extra round-trip,
 *    just a tighter response shape). Lets a future caller-side
 *    branch short-circuit a full-extract upgrade when the only
 *    referenced sheet is blank.
 *
 * What's NOT in the probe (deliberately):
 *  - Per-sheet cell values / formulas — these are the expensive
 *    part of `extractAllSheetsContent` and the dominant
 *    contributor to mid-run Excel freezing. Bounding boxes alone
 *    are cheap and live in `sheetUsedRangeSummaries`.
 *  - Charts, tables, columns/rows visibility — only consumed by the
 *    chart-aware chat branch and the verifier, both of which now
 *    pull a targeted extract when (and only when) they need it.
 *
 * Both bridge and Office.js modes remain metadata-only. Office.js can load the
 * workbook name, worksheet names, active sheet, and selection in one sync
 * without touching any worksheet used-range values.
 */
export interface ProbeContext {
  activeSheetIndex: number;
  activeSheetName: string;
  activeCellAddress: string;
  selectedRangeAddress: string;
  workbookName?: string;
  sheetNames: string[];
  /**
   * Per-sheet `{ name, isBlank }` summary derived from the bridge
   * call's `usedRange` property. `isBlank: true` means the host
   * confirmed an empty `usedRange` (or omitted one) — a downstream
   * branch can short-circuit a full-extract upgrade when the only
   * sheet the user referenced is blank. Sheets without a recognised
   * `usedRange` shape (older hosts) are omitted from this list
   * entirely so callers don't mistake "unknown" for "blank".
   * Bridge-mode only; Office.js falls through to the legacy probe.
   */
  sheetUsedRangeSummaries?: Array<{ name: string; isBlank: boolean }>;
}

/**
 * Gather the lightweight `ProbeContext` for a fresh chat turn.
 *
 * In bridge mode this is three cache-aware host RPCs at most
 * (`GetActiveExcelContext`, `GetWorkbookSheets`,
 * `GetSelectedAddressRange` / `GetActiveCellAddress`), all of
 * which AI-2616 already deduplicates within a session.
 *
 * In Office.js mode the same contract is fulfilled with one metadata-only
 * `Excel.run`; no `getUsedRange*` call is issued.
 */
export async function gatherProbeContext(): Promise<ProbeContext> {
  if (!shouldUseExcelIntentBridge()) {
    return Excel.run(async (context) => {
      const workbook = context.workbook;
      const sheets = workbook.worksheets;
      const activeSheet = sheets.getActiveWorksheet();
      const activeCell = workbook.getActiveCell();
      const selectedRange = workbook.getSelectedRange();

      workbook.load("name");
      sheets.load("items/name");
      activeSheet.load("name,position");
      activeCell.load("address");
      selectedRange.load("address");
      await context.sync();

      const sheetNames = sheets.items
        .map((sheet) => sheet.name)
        .filter((name) => name && !isInternalSheetName(name));
      if (
        sheetNames.length === 0 &&
        activeSheet.name &&
        !isInternalSheetName(activeSheet.name)
      ) {
        sheetNames.push(activeSheet.name);
      }
      return {
        activeSheetIndex: activeSheet.position,
        activeSheetName: activeSheet.name,
        activeCellAddress: activeCell.address ?? "",
        selectedRangeAddress: selectedRange.address ?? "",
        workbookName: workbook.name,
        sheetNames,
      };
    });
  }
  const [active, sheetList, cursor] = await Promise.all([
    safeGetActiveExcelContext(),
    // Ask the host for `usedRange` as part of the probe. The wire
    // payload only grows by a small bounding-box per sheet, no extra
    // round-trip, and lets the lightweight probe answer "is this
    // sheet blank?" without a follow-up read. Older hosts that
    // don't honour `options.properties` simply omit the field —
    // adapter returns `usedRange: undefined`, the summary list
    // skips those entries.
    getWorkbookSheets(undefined, { properties: ["usedRange"] })
      .then((raw) => adaptWorkbookSheets(raw))
      .catch((e) => {
        console.warn(
          "[Excel] gatherProbeContext: GetWorkbookSheets failed; defaulting to active sheet only:",
          e,
        );
        return [] as AdaptedWorkbookSheet[];
      }),
    safeGetCursorContext(),
  ]);
  const visibleSheets = sheetList.filter(
    (s) => s.name && !isInternalSheetName(s.name),
  );
  const sheetNames = visibleSheets.map((s) => s.name);
  if (sheetNames.length === 0 && active.name) {
    sheetNames.push(active.name);
  }
  // Summarise only sheets where the host actually returned a
  // parseable `usedRange` (populated **or** confirmed empty). Sheets
  // where the field was omitted or unparseable (`A:A`, garbage) stay
  // off the list so callers don't mislabel "unknown" as "blank".
  const sheetUsedRangeSummaries = visibleSheets
    .filter((s) => s.usedRangePresent && !s.usedRangeUnparsed)
    .map((s) => ({ name: s.name, isBlank: !s.usedRange }));
  let workbookName = active.workbookName;
  if (!workbookName) {
    try {
      const openWorkbooks = await getOpenWorkbooks();
      const activeWorkbook =
        openWorkbooks.find((workbook) => workbook.isActive === true) ??
        openWorkbooks[0];
      workbookName = activeWorkbook?.name ?? activeWorkbook?.fullName;
    } catch (error) {
      console.warn(
        "[Excel] gatherProbeContext: GetOpenWorkbooks failed while resolving workbook name:",
        error,
      );
    }
  }
  return {
    activeSheetIndex: active.index,
    activeSheetName: active.name,
    activeCellAddress: cursor.activeCellAddress ?? "",
    selectedRangeAddress:
      cursor.selectedRangeAddress ?? active.selectionAddress ?? "",
    workbookName,
    sheetNames,
    ...(sheetUsedRangeSummaries.length > 0
      ? { sheetUsedRangeSummaries }
      : {}),
  };
}

/**
 * Build a stub `WorkbookContent` from a `ProbeContext` for code paths
 * that only need the metadata fields (sheet names, active sheet,
 * cursor address) and never inspect per-sheet cell values.
 *
 * Each `sheets[i].usedRange` is intentionally left `undefined` so
 * any downstream consumer that accidentally tries to read cell
 * values gets a clear "missing data" signal rather than silently
 * scanning an empty array.
 */
export function workbookContentFromProbe(probe: ProbeContext): WorkbookContent {
  return {
    activeSheetIndex: probe.activeSheetIndex,
    activeSheetName: probe.activeSheetName,
    activeCellAddress: probe.activeCellAddress,
    selectedRangeAddress: probe.selectedRangeAddress,
    workbookName: probe.workbookName,
    sheets: probe.sheetNames.map((name, idx) => ({ name, index: idx })),
  };
}

// ============================================================================
// INTENT-BRIDGE EXTRACT IMPLEMENTATIONS
// ============================================================================

/**
 * Resolve the active workbook/worksheet context via the intent bridge,
 * with a defensive fallback so callers don't have to special-case
 * intent failures.
 *
 * Surfaces the typed fields (`workbookName`, `selection`, `isBusy`,
 * `isInEditMode`) alongside the active sheet's `{ name, index }`.
 * Every field except the active-sheet pair is optional and will be
 * `undefined` when the host omits it (or when the intent itself
 * fails — in which case the entire call falls back to the empty pair
 * plus all-undefined fields).
 */
async function safeGetActiveExcelContext(): Promise<{
  name: string;
  index: number;
  workbookName?: string;
  selectionAddress?: string;
  isBusy?: boolean;
  isInEditMode?: boolean;
}> {
  try {
    const ctx = await getActiveExcelContextViaBridge();
    return {
      name: ctx.activeSheet.name,
      index: ctx.activeSheet.index,
      workbookName: ctx.workbookName,
      selectionAddress: ctx.selection?.address,
      isBusy: ctx.isBusy,
      isInEditMode: ctx.isInEditMode,
    };
  } catch (e) {
    console.warn(
      "[Excel] GetActiveExcelContext failed; falling back to empty active-sheet info:",
      e
    );
    return { name: "", index: 0 };
  }
}

/**
 * Best-effort cursor / selection read: returns the active cell address
 * and the currently-selected range address, with both fields swallowed
 * to `undefined` on failure.
 *
 * Used by every `extract*` path (Office.js + bridge) so the AI prompt
 * payload always carries enough context for "what cell am I on?" /
 * "what range did I select?" without an extra round-trip on the chat
 * side. Failure of either leg is non-fatal: the chat path handles a
 * missing field by saying "I don't know" instead of fabricating one.
 *
 * Today both bridge intents (`GetActiveCellAddress` /
 * `GetSelectedAddressRange`) return BARE A1 addresses with no sheet
 * prefix — see `missing-intents-v0.md` §8.1. Office.js's `address`
 * property includes the sheet prefix; we keep that as-is so the model
 * can disambiguate when needed.
 */
async function safeGetCursorContext(): Promise<{
  activeCellAddress?: string;
  selectedRangeAddress?: string;
}> {
  let activeCellAddress: string | undefined;
  let selectedRangeAddress: string | undefined;
  try {
    activeCellAddress = await getActiveCellAddress();
  } catch (e) {
    console.warn("[Excel] getActiveCellAddress failed (non-fatal):", e);
  }
  try {
    selectedRangeAddress = await getSelectedRangeAddress();
  } catch (e) {
    console.warn("[Excel] getSelectedRangeAddress failed (non-fatal):", e);
  }
  return { activeCellAddress, selectedRangeAddress };
}

// ============================================================================
// WORKBOOK ENRICHMENT CACHE (AI-2616)
// ============================================================================
//
// The three workbook-introspection reads below (`GetOpenWorkbooks`,
// `GetWorkbookConnections`, `GetWorkbookNamedObjects`) used to fire on
// every prompt as part of `extractWorkbookContent*` — three RPCs per
// chat message, regardless of whether the prompt actually needed any of
// them. FactSet flagged this as wasteful per-prompt traffic in Linear
// AI-2616; this in-process cache keeps the first call per workbook and
// reuses it across subsequent prompts.
//
// **Keying.** Single slot keyed by `WorkbookContent.workbookName`
// (already populated from `safeGetActiveExcelContext()`). Workbook
// switch implicitly drops the slot — the read function compares names
// and overwrites on mismatch. A `Map<string, …>` is overkill; users
// rarely toggle between workbooks inside a single chat session, and the
// upgrade is a 10-line swap if needed.
//
// **Invalidation.** Only `namedObjects` is invalidated targetedly,
// because `addNamedObject` / `removeNamedObject` are the only applier
// branches we control that mutate it. `connections` / `openWorkbooks`
// have zero mutation surface in our agent, so the cached values stay
// valid for the session. UI-driven ribbon changes to named ranges /
// connections by the user fall outside this invalidation — the user
// can reload the addin or switch workbooks to refresh.
//
// **Failure handling.** No negative caching. If a fetch throws, the
// slot stays `undefined` and the next prompt retries — matching the
// existing "best-effort, swallow to console.warn" contract.
//
// **Concurrency.** Two rapid prompts can race: both miss, both fetch,
// both upsert. Last write wins; no corruption (each call hydrates its
// own `wb` from local variables). A single-flight guard isn't needed
// for the current call rate; can be added later if log noise warrants.
//
// **Scope: bridge-mode only.** `enrichWorkbookContentViaBridge` runs
// only from the bridge-mode read paths; Office.js mode leaves the
// fields undefined and never hits the cache. Applier-side
// `invalidateNamedObjectsCache()` calls in Office.js mode are
// harmless no-ops against an empty cache.

interface WorkbookEnrichmentCacheEntry {
  workbookName: string | undefined;
  openWorkbooks?: OpenWorkbookInfo[];
  connections?: WorkbookConnection[];
  namedObjects?: WorkbookNamedObject[];
}

let workbookEnrichmentCache: WorkbookEnrichmentCacheEntry | undefined;

/**
 * Return the cached enrichment entry if-and-only-if it matches
 * `workbookName`. A different name (or no cache) returns `undefined`
 * and forces the caller to refetch.
 */
function readEnrichmentCache(
  workbookName: string | undefined
): WorkbookEnrichmentCacheEntry | undefined {
  if (!workbookEnrichmentCache) return undefined;
  if (workbookEnrichmentCache.workbookName !== workbookName) return undefined;
  return workbookEnrichmentCache;
}

/**
 * Merge a partial enrichment patch into the cache slot. Merge semantics
 * on same-key are load-bearing: in the cold-start sequence
 * (`connections` succeeds → upsert; `namedObjects` succeeds → upsert),
 * the second upsert must not clobber the first. Different-key
 * (workbook switch) replaces the slot entirely — that's the implicit
 * invalidation on workbook change.
 */
function upsertEnrichmentCacheEntry(
  workbookName: string | undefined,
  patch: Partial<Omit<WorkbookEnrichmentCacheEntry, "workbookName">>
): void {
  if (workbookEnrichmentCache?.workbookName === workbookName) {
    workbookEnrichmentCache = { ...workbookEnrichmentCache, ...patch };
  } else {
    workbookEnrichmentCache = { workbookName, ...patch };
  }
}

/**
 * Drop the cached `namedObjects` slice while leaving `connections` /
 * `openWorkbooks` intact. Call after a successful `addNamedObject` /
 * `removeNamedObject` applier branch so the next
 * `extractWorkbookContent*` re-fetches the named-objects list and the
 * planner sees the post-mutation state on the very next prompt. Other
 * cached fields don't depend on named-object mutation so we keep them
 * to preserve the "skip these RPCs after first fetch" guarantee.
 */
function invalidateNamedObjectsCache(): void {
  if (workbookEnrichmentCache) {
    workbookEnrichmentCache = {
      ...workbookEnrichmentCache,
      namedObjects: undefined,
    };
  }
}

/**
 * Best-effort enrichment of a `WorkbookContent` snapshot with the three
 * workbook-introspection intents (`GetOpenWorkbooks`,
 * `GetWorkbookConnections`, `GetWorkbookNamedObjects`). Mutates `wb` in
 * place; each intent failure is swallowed to a `console.warn` so a
 * slow/failing introspection call never blocks the primary read
 * pipeline.
 *
 * Empty arrays are still attached so the planner can reason about the
 * difference between "we asked and found nothing" and "we never asked"
 * (the latter leaves the field `undefined`).
 *
 * **Cache-first (AI-2616).** Each field is served from the in-process
 * `workbookEnrichmentCache` when available; only cache misses (cold
 * start, workbook switch, or post-`invalidateNamedObjectsCache()`) hit
 * the wire. See the cache-module banner above for keying /
 * invalidation / failure semantics.
 *
 * Trade-off: UI-driven ribbon changes (user defines a name via Formulas
 * → Define Name, or adds a data connection) won't appear in cached
 * fields until the user switches workbooks or reloads the addin. Our
 * agent-driven `addNamedObject` / `removeNamedObject` paths invalidate
 * the relevant slot so subsequent prompts see the new state.
 */
async function enrichWorkbookContentViaBridge(
  wb: WorkbookContent
): Promise<void> {
  const cached = readEnrichmentCache(wb.workbookName);

  if (cached?.openWorkbooks !== undefined) {
    wb.openWorkbooks = cached.openWorkbooks;
  } else {
    try {
      wb.openWorkbooks = await getOpenWorkbooks();
    } catch (e) {
      console.warn("[Excel] GetOpenWorkbooks failed (non-fatal):", e);
    }
    upsertEnrichmentCacheEntry(wb.workbookName, {
      openWorkbooks: wb.openWorkbooks,
    });
  }

  if (cached?.connections !== undefined) {
    wb.connections = cached.connections;
  } else if (wb.workbookName) {
    // AI-2617: `workbookName` is required by spec for
    // GetWorkbookConnections; skip the call when no active workbook is
    // known. See the GetWorkbookNamedObjects branch below for the same
    // pattern (and the `WorkbookContent` JSDoc for the caching policy).
    try {
      wb.connections = await getWorkbookConnections(wb.workbookName);
    } catch (e) {
      console.warn("[Excel] GetWorkbookConnections failed (non-fatal):", e);
    }
    upsertEnrichmentCacheEntry(wb.workbookName, {
      connections: wb.connections,
    });
  }

  if (cached?.namedObjects !== undefined) {
    wb.namedObjects = cached.namedObjects;
  } else if (wb.workbookName) {
    // AI-2617: `workbookName` is required by spec + Zod schema. Skip
    // the call entirely when no active workbook name is available
    // (bridge `GetActiveExcelContext` failed / Office.js mode); the
    // wrapper would throw fail-fast anyway. Leaving `namedObjects`
    // undefined in the cache means a future prompt with a valid
    // workbook name will retry — same behavior as a failed RPC.
    try {
      wb.namedObjects = await getWorkbookNamedObjects({
        workbookName: wb.workbookName,
        skipHidden: true,
      });
    } catch (e) {
      console.warn("[Excel] GetWorkbookNamedObjects failed (non-fatal):", e);
    }
    upsertEnrichmentCacheEntry(wb.workbookName, {
      namedObjects: wb.namedObjects,
    });
  }
  // ActiveGraph XML extraction (`factset.Charting.GetActiveGraphXml`)
  // is intentionally not run during workbook-context capture: the
  // planner is no longer allowed to consume the FDSChart XML of the
  // currently-selected ActiveGraph (the surface is too immature for
  // round-trip mutations and risks confusing the model). The dispatcher
  // / wrapper / dev-tools dialog remain wired for non-agent / debugging
  // flows — see `getActiveGraphXml` in `excelHostOps.ts`.
}

/**
 * Best-effort enrichment of the per-sheet `charts` / `tables` arrays
 * via the `GetCharts` / `GetTables` intents. Both intents return typed
 * arrays directly on the wire; the per-element shape is still
 * example-pending so callers narrow at the use site (see
 * `adaptOpaqueArray`). Both reads are workbook-scoped —
 * the host doesn't shard them per sheet — so we attach the full
 * arrays to whichever worksheet matches `activeSheetName` (or the
 * first worksheet when no active sheet is known) and let downstream
 * narrow further. Failures swallow to a `console.warn` per the
 * "warn-on-failure" guidance — these reads must never block the
 * primary read pipeline.
 *
 * Extracted from the original `extractWorkbookContentViaBridge` body
 * so `extractAllSheetsContentViaBridge` (the bridge path the chat /
 * planner / executor actually use) stays in sync. Without this, chat
 * summaries miss tables and native charts on the sheet — observed
 * 2026-04-27 with a workbook that had a "Column1" structured table
 * and a column chart that the model didn't mention.
 */
async function attachChartsAndTablesViaBridge(
  worksheets: WorksheetContent[],
  activeSheetName: string,
  activeWorkbookName?: string
): Promise<void> {
  // The post-2026-05-04 `Charting.GetCharts` namespace runtime-rejects
  // calls without `workbookName` against multi-workbook hosts (and
  // sometimes even against single-workbook hosts) with a generic
  // `"Failed to get charts"` error. When we know the active workbook
  // name from `safeGetActiveExcelContext()`, thread it through so the
  // host has unambiguous targeting; otherwise fall back to the no-args
  // call and let the host pick a default. Same shape applies to
  // `GetTables` for forward-compatibility.
  const chartsOptions = activeWorkbookName
    ? { workbookName: activeWorkbookName }
    : undefined;
  const tablesOptions = activeWorkbookName
    ? { workbookName: activeWorkbookName }
    : undefined;
  let chartsBlob: ExcelChart[] | undefined;
  try {
    const raw = await getCharts(chartsOptions);
    chartsBlob = adaptOpaqueArray<ExcelChart>(raw);
  } catch (e) {
    console.warn("[Excel] GetCharts failed (non-fatal):", e);
  }
  let tablesBlob: ExcelTable[] | undefined;
  try {
    const raw = await getTables(tablesOptions);
    tablesBlob = adaptOpaqueArray<ExcelTable>(raw);
  } catch (e) {
    console.warn("[Excel] GetTables failed (non-fatal):", e);
  }
  if (!chartsBlob && !tablesBlob) return;
  const targetIdx = Math.max(
    0,
    worksheets.findIndex((w) => w.name === activeSheetName)
  );
  const target = worksheets[targetIdx] ?? worksheets[0];
  if (!target) return;
  if (chartsBlob && chartsBlob.length > 0) target.charts = chartsBlob;
  if (tablesBlob && tablesBlob.length > 0) target.tables = tablesBlob;
}

/**
 * Bridge-mode implementation of `extractWorkbookContent`. Pulls the sheet
 * list via `GetWorkbookSheets` then reads each sheet's used range via
 * `GetRangeInfo` (with a `GetRangeAsCsv` fallback). On any read failure
 * for a single sheet, that sheet is included with `usedRange: undefined`
 * so the rest of the read still succeeds.
 *
 * Falls back to `emptyWorkbookContentForBridgeMode()` only when the sheet
 * enumeration itself fails — i.e. we have no idea what's in the workbook.
 */
async function extractWorkbookContentViaBridge(
  pinnedWorkbookName?: string,
): Promise<WorkbookContent> {
  // Ask the host for `usedRange` + `visibility` per sheet. Hosts that
  // honour `options.properties` give us a 1-RPC summary (sheet list +
  // bounding box + visibility) — empty sheets cost zero reads, and the
  // non-empty ones go through `readSheetRangeViaBridgeFromBox`, which
  // skips the legacy 1-3 rung `GetRangeAsCsv` probe ladder entirely.
  // Older hosts that ignore the option simply leave `usedRange` /
  // `visibility` `undefined`, and we fall back to the probe-ladder
  // path automatically.
  //
  // `pinnedWorkbookName`, when set, locks every read to a specific
  // workbook so a user-initiated workbook switch mid-plan can't
  // redirect the read to the wrong workbook. `undefined` falls back
  // to the active workbook (chat-only / ad-hoc extracts).
  let sheetList: AdaptedWorkbookSheet[] = [];
  try {
    const raw = await getWorkbookSheets(pinnedWorkbookName, {
      properties: ["usedRange", "visibility"],
    });
    sheetList = adaptWorkbookSheets(raw);
  } catch (e) {
    console.warn(
      "[Excel] GetWorkbookSheets failed; returning empty workbook content for bridge mode:",
      e
    );
    return emptyWorkbookContentForBridgeMode();
  }

  const active = await safeGetActiveExcelContext();
  const reportedWorkbookName = pinnedWorkbookName ?? active.workbookName;
  const worksheets: WorksheetContent[] = [];
  for (const sheet of sheetList) {
    // Skip FactSet-internal cache sheets (e.g. `__FDSCACHE__`). They
    // never contain user data and crowd the prompt with noise — see
    // `isInternalSheetName`.
    if (isInternalSheetName(sheet.name)) continue;
    const usedRange = await readSheetUsedRangeViaBridge(
      sheet,
      undefined,
      undefined,
      pinnedWorkbookName,
    );
    worksheets.push({
      name: sheet.name,
      index: sheet.index,
      usedRange,
      visibility: sheet.visibility,
    });
  }

  if (worksheets.length === 0 && active.name) {
    worksheets.push({ name: active.name, index: active.index });
  }

  // `attachChartsAndTablesViaBridge` already accepts a workbook name
  // and forwards it on `GetCharts({ workbookName })` / `GetTables({
  // workbookName })`. Pass the pinned target when we have it so a
  // workbook switch can't redirect the chart/table enrichment either.
  await attachChartsAndTablesViaBridge(
    worksheets,
    active.name,
    reportedWorkbookName,
  );

  const cursor = await safeGetCursorContext();

  const wb: WorkbookContent = {
    activeSheetIndex: active.index,
    activeSheetName: active.name,
    workbookName: reportedWorkbookName,
    sheets: worksheets,
    activeCellAddress: cursor.activeCellAddress,
    selectedRangeAddress: cursor.selectedRangeAddress ?? active.selectionAddress,
    isBusy: active.isBusy,
    isInEditMode: active.isInEditMode,
  };
  // `enrichWorkbookContentViaBridge` keys off `wb.workbookName`, so
  // setting it to `pinnedWorkbookName` above is enough to route the
  // workbook-introspection reads (and their cache slot) to the right
  // workbook.
  await enrichWorkbookContentViaBridge(wb);
  return wb;
}

/**
 * Bridge-mode implementation of `extractActiveSheetContent`. Resolves the
 * active sheet's `{ name, index }` first, then reads its used range via
 * the same `GetRangeInfo` → `GetRangeAsCsv` pipeline.
 */
async function extractActiveSheetContentViaBridge(
  pinnedWorkbookName?: string,
): Promise<WorksheetContent> {
  const active = await safeGetActiveExcelContext();
  // Don't suppress an internal sheet here — if it's literally the
  // active sheet the user sees, we still want to surface it (otherwise
  // chat would silently report a "blank" workbook). The internal-sheet
  // filter only skips them in the all-sheets sweep where they're noise.
  const usedRange = active.name
    ? await readSheetRangeViaBridge(active.name, pinnedWorkbookName)
    : undefined;
  return {
    name: active.name,
    index: active.index,
    usedRange,
  };
}

/**
 * Bridge-mode implementation of `extractAllSheetsContent`. Same shape as
 * the Office.js path, with the per-sheet row truncation applied to the
 * already-read intent payload (slice after the read rather than ask the
 * host for fewer rows — `GetRangeInfo` doesn't expose a row-limit knob).
 */
async function extractAllSheetsContentViaBridge(
  targetSheetName?: string,
  maxTargetRows: number = 300,
  maxOtherRows: number = 10,
  options: ExtractWorkbookOptions = {}
): Promise<WorkbookContent> {
  // Same `properties: ["usedRange", "visibility"]` request as
  // `extractWorkbookContentViaBridge` — see the comment there for the
  // RPC-floor rationale and the older-host fallback contract.
  //
  // `options.workbookName` (when set) pins every read to a specific
  // workbook so a user-initiated workbook switch mid-plan can't
  // redirect the read to the wrong workbook. `undefined` falls back
  // to the active workbook (chat-only / ad-hoc extracts).
  const pinnedWorkbookName = options.workbookName;
  let sheetList: AdaptedWorkbookSheet[] = [];
  try {
    const raw = await getWorkbookSheets(pinnedWorkbookName, {
      properties: ["usedRange", "visibility"],
    });
    sheetList = adaptWorkbookSheets(raw);
  } catch (e) {
    console.warn(
      "[Excel] GetWorkbookSheets failed; returning empty workbook content for bridge mode:",
      e
    );
    return emptyWorkbookContentForBridgeMode();
  }

  const active = await safeGetActiveExcelContext();
  const reportedWorkbookName = pinnedWorkbookName ?? active.workbookName;
  const worksheets: WorksheetContent[] = [];
  // Hard caps for scoped reads. The `maxSheets` budget counts sheets
  // that actually paid for a `usedRange` read; metadata-only entries
  // (e.g. when `otherSheetsMetadataOnly` is set) don't draw down from
  // the budget.
  const targetSheetMetadataOnly = options.targetSheetMetadataOnly === true;
  // `targetSheetMetadataOnly` implies `otherSheetsMetadataOnly` —
  // if the caller doesn't even want the target sheet read, the
  // non-target sheets definitely don't need cell data either.
  const otherSheetsMetadataOnly =
    targetSheetMetadataOnly || options.otherSheetsMetadataOnly === true;
  const maxSheets = options.maxSheets ?? 25;
  const maxCellsPerSheet = options.maxCellsPerSheet ?? 5_000;
  let sheetsRead = 0;
  for (const sheet of sheetList) {
    // Skip FactSet-internal cache sheets (e.g. `__FDSCACHE__`); same
    // rationale as `extractWorkbookContentViaBridge`.
    if (isInternalSheetName(sheet.name)) continue;
    const isTarget = targetSheetName
      ? sheet.name === targetSheetName
      : sheet.name === active.name;
    // Decide whether to pay for this sheet's `usedRange` read. The
    // target sheet pays unless the caller explicitly opted into
    // `targetSheetMetadataOnly` (no edited cells to verify, so the
    // round-trip would be wasted); non-target sheets are skipped
    // when the caller opts in to metadata-only mode or the
    // `maxSheets` budget is exhausted.
    const skipUsedRange =
      (isTarget && targetSheetMetadataOnly) ||
      (!isTarget && (otherSheetsMetadataOnly || sheetsRead >= maxSheets));
    if (skipUsedRange) {
      worksheets.push({
        name: sheet.name,
        index: sheet.index,
        visibility: sheet.visibility,
      });
      continue;
    }
    const rowLimit = isTarget ? maxTargetRows : maxOtherRows;
    const fullRange = await readSheetUsedRangeViaBridge(
      sheet,
      rowLimit,
      maxCellsPerSheet,
      pinnedWorkbookName,
    );
    sheetsRead += 1;
    let truncated: CellRange | undefined = fullRange;
    if (fullRange) {
      // Per-sheet cell-count cap (safety net). If the sheet exceeds
      // the cap, derive a smaller row cap that fits.
      const cellCapRows =
        fullRange.columnCount > 0
          ? Math.max(1, Math.floor(maxCellsPerSheet / fullRange.columnCount))
          : Number.POSITIVE_INFINITY;
      const effectiveRowLimit = Math.min(rowLimit, cellCapRows);
      if (fullRange.values && fullRange.values.length > effectiveRowLimit) {
        const limitedValues = fullRange.values.slice(0, effectiveRowLimit);
        const limitedFormulas = fullRange.formulas?.slice(0, effectiveRowLimit);
        truncated = {
          ...fullRange,
          rowCount: limitedValues.length,
          values: limitedValues,
          formulas: limitedFormulas,
          totalRowCount: fullRange.totalRowCount ?? fullRange.rowCount,
        };
      }
    }
    worksheets.push({
      name: sheet.name,
      index: sheet.index,
      usedRange: truncated,
      visibility: sheet.visibility,
    });
  }

  if (worksheets.length === 0 && active.name) {
    worksheets.push({ name: active.name, index: active.index });
  }

  if (options.includeChartsAndTables !== false) {
    await attachChartsAndTablesViaBridge(
      worksheets,
      active.name,
      reportedWorkbookName,
    );
  }

  const cursor = await safeGetCursorContext();

  const wb: WorkbookContent = {
    activeSheetIndex: active.index,
    activeSheetName: active.name,
    workbookName: reportedWorkbookName,
    sheets: worksheets,
    activeCellAddress: cursor.activeCellAddress,
    selectedRangeAddress: cursor.selectedRangeAddress ?? active.selectionAddress,
    isBusy: active.isBusy,
    isInEditMode: active.isInEditMode,
  };
  if (options.includeWorkbookEnrichment !== false) {
    await enrichWorkbookContentViaBridge(wb);
  }
  return wb;
}

// ============================================================================
// APPLY MODIFICATIONS
// ============================================================================
//
// The edit-mode / host-ready gate primitives that used to live here
// (`waitForHostReady`, `EDIT_MODE_USER_MESSAGE`, `BUSY_USER_MESSAGE`,
// `isEditModeError`, `HostReadyReason`, `EDIT_MODE_ERROR_CODE`) now
// live in `services/factset/hostReady.ts` so the open-template chain
// can share the exact same gate. Imports are at the top of this file;
// the call-site at `applyWorkbookModificationsViaIntents` is unchanged.

/**
 * Result of applying workbook modifications
 */
export interface ApplyModificationsResult {
  success: boolean;
  message: string;
  operations: string[]; // List of operations performed (with cell addresses)
  /**
   * Cell/range addresses confirmed by a resolved host call or Office.js sync.
   * On a mixed result this contains only successful siblings; failed targets
   * are reported separately in `failedActions`.
   */
  editedCells: string[];
  /**
   * Actions that the bridge / Office.js path could not honour (typically
   * because the underlying intent isn't wired yet, or the action is a
   * no-op shape like "format" with no payload). Each entry is a short
   * human-readable label, e.g. `range A:A action "format"`. Used by the
   * caller to detect "step did nothing" no-ops — when this list is
   * non-empty AND `editedCells` is empty, the apply call is treated as a
   * silent failure rather than a misleading success.
   */
  skippedActions?: string[];
  /**
   * Actions that were attempted against the host and failed. Unlike
   * `skippedActions` (unsupported/no-payload/no-op), these represent
   * requested mutations that should have happened but did not. Any
   * non-empty value means the apply result is at best partial and must
   * not be reported as a clean success.
   */
  failedActions?: string[];
  /** Internal-only failures from optional generated citation Notes. */
  citationNoteFailures?: string[];
  /**
   * Set when the apply pauses on `stuck_edit` / `stuck_busy`.
   * The plan runner uses this to queue the step for a single
   * end-of-plan drain attempt with a fresh gate, falling back to an
   * inline Retry button if the drain still can't make progress.
   * `hostDispatchAttempted` distinguishes pre-apply pauses from in-flight
   * host errors so replay protection can fail closed after dispatch.
   */
  retryReason?: "stuck_edit" | "stuck_busy";
  /**
   * Distinguishes a host-ready pause caught before any mutation dispatch from
   * one raised after dispatch began. Set only on retryable host-ready results.
   */
  hostDispatchAttempted?: boolean;
  /**
   * True iff the burst's `WorkbookModification` payload contained at
   * least one cell-content formula (FDS or ordinary). Computed from
   * the requested modifications via `anyFormulaWrite` — covers
   * `worksheets[].cells[].formula` / formula-like `cells[].value`,
   * `worksheets[].ranges[].formulas` / `ranges[].values` strings starting with `=`,
   * `worksheets[].tables[].rows` entries starting with `=`, and
   * `workbookOperations[].insertTtfApiResponse.apiResponse.results[].formula`.
   *
   * Computed from the planner's request shape, NOT from host-confirmed
   * writes (a partially-skipped burst can still set this flag). False
   * positives are safe: they cause at most one extra per-burst recalc
   * (idempotent) and one extra per-step verifier read (a single
   * `GetRangeInfo` round-trip).
   *
   * Read by:
   *  - The per-burst `ApplicationCalculate("full")` gate inside the
   *    apply path's manual-calc bracket — fires only when this is true.
   *  - The per-step verifier in `ExcelApp.tsx::handleQuery` — skips
   *    the post-step `GetRangeInfo` read when this is false.
   */
  appliedFormulaWrites?: boolean;
  /**
   * Set when the planner emitted a `refreshFactSetData` workbook op
   * during this burst. The workbook-op schema has no sheet payload, so
   * a deferred refresh is always implicitly "refresh all". Consumed by
   * the plan-runner's trailing `finalizeRefreshAndRecalc` helper —
   * runs `HarmonizedRefreshWorksheets` exactly once at the end of the
   * plan rather than inline mid-plan.
   */
  deferredRefreshAll?: boolean;
  /**
   * Mode the planner requested via a `recalculate` workbook op. Deferred
   * to the trailing recalc; only one wins per run (last-write-wins is
   * fine — the planner rarely emits more than one and `"full"`
   * dominates anyway).
   */
  deferredRecalcMode?: "recalculate" | "full" | "fullRebuild";
}

export interface ApplyWorkbookModificationsOptions {
  signal?: AbortSignal;
  onHostReadyWaitProgress?: (info: {
    attempt: number;
    maxAttempts: number;
    reason: HostReadyReason;
    initialReason: HostReadyReason;
  }) => void;
  onHostReadyWaitCleared?: (info: {
    attempts: number;
    initialReason: HostReadyReason;
  }) => void;
  /**
   * Pin the apply burst to a specific workbook. When set (bridge
   * mode only), the apply path:
   *  - Threads `workbookName` onto every sheet-CRUD / named-object /
   *    range-mutation intent that accepts one, so the host routes
   *    the write to the right workbook transparently.
   *  - Calls `reactivateWorkbookForWrite()` before the write burst
   *    so `InsertContentsToRange` and the other intents that don't
   *    expose `workbookName` (refresh, calc, calc-mode) target the
   *    pinned workbook.
   *  - Throws `WorkbookUnavailableError` if the pinned workbook is
   *    no longer open, letting the orchestrator (`handleQuery`)
   *    surface a clean "workbook closed" message.
   *
   * `undefined` falls back to whichever workbook is currently
   * active — the legacy behaviour for chat-only chart insert and
   * other one-shot apply calls outside the plan loop.
   */
  pinnedWorkbookName?: string;
  /**
   * Skip the boundary `normalizeFactSetFormatsForModification` pass below.
   * Set by the executor path (`executeModifyWorkbookTool`) when
   * `modifications` was already normalized with the run-scoped
   * `factSetFormatRegistry`, which accumulates FactSet formula provenance
   * across every step of the run. Re-normalizing here with a fresh, empty
   * registry can't see that history — it would only catch inline formula
   * writes self-contained within this single call — so for pre-normalized
   * callers it's both redundant work and misleading (it looks like full
   * enforcement but isn't). Callers with no run-scoped registry (e.g.
   * one-shot chat-only chart insert) should leave this unset so the boundary
   * pass remains their only normalization.
   */
  skipFactSetFormatNormalization?: boolean;
}

/**
 * Raised by the bridge apply path when reactivation of the pinned
 * workbook fails — most commonly because the user closed it
 * mid-plan. Carries the workbook name so the orchestrator
 * (`handleQuery` in `ExcelApp.tsx`) can render the friendly
 * "the workbook is no longer open" message into the pinned chat
 * tab without having to re-derive the name.
 */
export class WorkbookUnavailableError extends Error {
  readonly workbookName: string;
  readonly reason: string;
  constructor(workbookName: string, reason: string) {
    super(
      `Workbook "${workbookName}" is no longer available for writes (${reason}).`,
    );
    this.name = "WorkbookUnavailableError";
    this.workbookName = workbookName;
    this.reason = reason;
    // Restore the prototype chain under ES5 target — otherwise
    // `super(message)` collapses it to `Error.prototype` and the
    // `instanceof WorkbookUnavailableError` checks in the chat handler
    // silently fall through to the generic apply-failure path,
    // bypassing the friendly "workbook is no longer open" message.
    Object.setPrototypeOf(this, WorkbookUnavailableError.prototype);
  }
}

/**
 * Pick a worksheet name to pass into the `activateWorksheet` call
 * we use to flip focus to the pinned workbook before a write
 * burst. The intent accepts `worksheetName` + `{ workbookName }`
 * (where the sheet name is required by the wire protocol), so we
 * need a valid candidate even when the upcoming writes don't
 * explicitly target a sheet (e.g. workbook-only operations).
 *
 * Preference order:
 *  1. The first `WorksheetModification` that names a sheet — the
 *     write is about to target that sheet anyway, so flipping
 *     there is "free".
 *  2. A `SheetModification.name` (when the modification is sheet
 *     CRUD on a specific name).
 *  3. The active sheet name (when we have a probe result).
 *  4. `"Sheet1"` as a last-resort sentinel — the host accepts
 *     this and falls back to the workbook's first sheet if
 *     `Sheet1` doesn't exist.
 */
function pickReactivationSheetHint(
  modifications: WorkbookModification,
  activeSheetName: string,
): string {
  for (const ws of modifications.worksheets ?? []) {
    const name = ws.sheetName?.trim();
    if (name) return name;
  }
  for (const annotation of modifications.annotations ?? []) {
    const name = annotation.sheetName?.trim();
    if (name) return name;
  }
  for (const sh of modifications.sheets ?? []) {
    const name = sh.name?.trim();
    if (name) return name;
  }
  if (activeSheetName) return activeSheetName;
  return "Sheet1";
}

/**
 * Focus-free liveness check for the pinned workbook.
 *
 * The background-targeting apply path does NOT activate the pinned workbook
 * (that would yank the user's view), so it loses the free liveness signal the
 * legacy `activateWorksheet(pin)` call gave us — a closed pinned workbook
 * would otherwise only surface as a cryptic mid-burst write failure. This
 * probe restores the clean abort: `GetOpenWorkbooks` is a pure read (no
 * activation), and if it succeeds and the pin is absent we throw
 * `WorkbookUnavailableError` so the orchestrator posts the friendly "the
 * workbook is no longer open" message into the pinned chat and stops the plan.
 *
 * If the probe itself fails we cannot tell whether the pin is gone, so we
 * proceed best-effort and let the first write's own error handling surface a
 * real failure — the same posture as the old activation `catch`, minus the
 * focus change.
 */
async function assertPinnedWorkbookOpenNoFocus(
  pinnedWorkbookName: string,
): Promise<void> {
  let open: OpenWorkbookInfo[];
  try {
    open = await getOpenWorkbooks();
  } catch (e) {
    console.warn(
      `[Excel] GetOpenWorkbooks liveness probe failed for pinned workbook "${pinnedWorkbookName}"; proceeding best-effort:`,
      e,
    );
    return;
  }
  const stillOpen = open.some(
    (wb) =>
      wb?.name === pinnedWorkbookName || wb?.fullName === pinnedWorkbookName,
  );
  if (!stillOpen) {
    throw new WorkbookUnavailableError(
      pinnedWorkbookName,
      "workbook is no longer open",
    );
  }
}

/**
 * Run one **foreground-only** intent against the pinned workbook.
 *
 * Most of the apply burst self-routes to the pin in the background via
 * `workbookName` (see `useRangeMutationTargeting`), so we never activate the
 * pin and the user's Excel view stays put. But a few intents are documented in
 * `f2_docs/excel/excel-tools.md` as active-workbook-only — `AddActiveGraph`
 * ("operates on the active sheet") and `CopyWorksheet` ("Supports only an
 * active workbook"). For those we must briefly bring the pin forward.
 *
 * The foreground coordinator does that safely: it serialises every foreground
 * window behind one global lock (so concurrent multi-workbook runs never
 * ping-pong the user's focus), snapshots the user's real focus before
 * activating the pin, and restores it when the burst drains — unless the user
 * manually switched workbooks in the meantime, in which case it leaves them be.
 *
 * When background targeting is OFF (kill-switch) the legacy pre-burst
 * `activateWorksheet(pin)` already made the pin the active workbook, so there's
 * nothing to bring forward — we run `fn` inline. Likewise for the no-pin
 * one-shot apply path (single-workbook / Office.js), where focus is a non-issue
 * and `pinnedWorkbookName` is undefined.
 *
 * `sheet` is the worksheet to activate on the pin. For `AddActiveGraph` it is
 * the chart's target sheet; for `CopyWorksheet` it is the source sheet (or the
 * burst's tracked active sheet when the planner omitted one), which both brings
 * the pin forward and makes the intent's "active sheet" resolve to the pin.
 */
async function runForegroundOnlyOnPin<T>(
  pinnedWorkbookName: string | undefined,
  sheet: string | undefined,
  fn: () => Promise<T>,
  options?: { signal?: AbortSignal; contextLabel?: string },
): Promise<T> {
  if (!pinnedWorkbookName || !supportsRangeMutationWorkbookName()) {
    return fn();
  }
  return foregroundCoordinator.runInForeground(
    { workbookName: pinnedWorkbookName, sheet },
    fn,
    options,
  );
}

function dedupeEditedAddresses(addresses: readonly string[]): string[] {
  return Array.from(new Set(addresses));
}

function validateRangeWriteMatrix(
  matrix: unknown,
  label: "values" | "formulas",
): { rows: number; columns: number } | string {
  if (!Array.isArray(matrix) || matrix.length === 0) {
    return `${label} must be a non-empty rectangular matrix`;
  }
  const firstRow = matrix[0];
  if (!Array.isArray(firstRow) || firstRow.length === 0) {
    return `${label} must contain non-empty rows`;
  }
  const columns = firstRow.length;
  for (let rowIndex = 0; rowIndex < matrix.length; rowIndex += 1) {
    const row = matrix[rowIndex];
    if (!Array.isArray(row)) {
      return `${label} row ${rowIndex} is not an array`;
    }
    if (row.length !== columns) {
      return (
        `${label} row ${rowIndex} has ${row.length} columns; expected ` +
        `${columns} (rectangular matrix required)`
      );
    }
  }
  return { rows: matrix.length, columns };
}

function findRangeFillMatrixError(
  modifications: WorkbookModification,
): string | null {
  for (const worksheet of modifications.worksheets ?? []) {
    const sheetName = worksheet.sheetName?.trim() || "(active sheet)";
    for (const range of worksheet.ranges ?? []) {
      if (range.action !== "fill") continue;
      const hasValues = range.values !== undefined;
      const hasFormulas = range.formulas !== undefined;
      if (!hasValues && !hasFormulas) continue;
      if (hasValues && hasFormulas) {
        return (
          `Invalid range fill at ${sheetName}!${range.address}: provide ` +
          "either values or formulas, not both."
        );
      }
      const size = parseRangeSize(range.address);
      if (!size) {
        return `Invalid range address for fill: ${sheetName}!${range.address}`;
      }
      const label = hasFormulas ? "formulas" : "values";
      const dimensions = validateRangeWriteMatrix(
        hasFormulas ? range.formulas : range.values,
        label,
      );
      if (typeof dimensions === "string") {
        return `Invalid range fill at ${sheetName}!${range.address}: ${dimensions}`;
      }
      if (
        dimensions.rows !== size.rows ||
        dimensions.columns !== size.cols
      ) {
        return (
          `Range/matrix dimension mismatch at ${sheetName}!${range.address}: ` +
          `target is ${size.rows}x${size.cols}, but ${label} are ` +
          `${dimensions.rows}x${dimensions.columns}. Exact dimensions are ` +
          "required; broadcasting, padding, and truncation are not supported."
        );
      }
    }
  }
  return null;
}

function literalizeCodeOwnedValue(
  value: string | number | boolean | null,
): string | number | boolean | null {
  return typeof value === "string" && value.startsWith("=")
    ? `'${value}`
    : value;
}

/**
 * Repair runtime cell payloads that omit the type-required `action`.
 *
 * Value and formula writes unambiguously mean `set`; a format-only cell
 * unambiguously means `format`. Destructive actions are never inferred, and
 * range modifications are intentionally outside this normalizer's scope.
 * Returns the original object when no repair is needed.
 *
 * The function is copy-on-write: unchanged cells, worksheets, and the root
 * retain referential identity. When any cell is repaired, only affected
 * branches are cloned. This preserves the original model payload for tracing
 * and prevents normalization from leaking into callers that retain references.
 *
 * @param modifications - Parsed workbook modification payload at the common
 * host-apply boundary. Although `CellModification.action` is required by the
 * TypeScript type, model-produced JSON may omit it at runtime.
 * @returns The original modification when no action can be inferred; otherwise
 * a structurally shared copy with missing cell actions safely repaired.
 */
export function normalizeMissingCellActions(
  modifications: WorkbookModification,
): WorkbookModification {
  const worksheets = modifications.worksheets;
  if (!worksheets || worksheets.length === 0) return modifications;

  let normalizedCells = 0;
  const nextWorksheets = worksheets.map((worksheet) => {
    const cells = worksheet.cells;
    if (!cells || cells.length === 0) return worksheet;

    let worksheetChanged = false;
    const nextCells = cells.map((cell) => {
      const declaredAction = (cell as { action?: unknown }).action;
      if (declaredAction !== undefined) return cell;

      let inferredAction: CellModification["action"] | undefined;
      if (cell.value !== undefined || cell.formula !== undefined) {
        inferredAction = "set";
      } else if (cell.format !== undefined) {
        inferredAction = "format";
      }
      if (inferredAction === undefined) return cell;

      worksheetChanged = true;
      normalizedCells += 1;
      return { ...cell, action: inferredAction };
    });

    return worksheetChanged ? { ...worksheet, cells: nextCells } : worksheet;
  });

  if (normalizedCells === 0) return modifications;
  diag("write", "cell_action_normalized", {
    worksheetCount: nextWorksheets.length,
    normalizedCells,
  });
  return { ...modifications, worksheets: nextWorksheets };
}

/**
 * Apply workbook modifications from AI response
 *
 * Runtime payload repair occurs before FactSet-format normalization and before
 * either the intent-bridge or Office.js dispatcher sees the modification tree.
 *
 * @param modifications - Workbook changes to validate and apply.
 * @param onOperation - Optional callback for real-time operation logging.
 * @param options - Cancellation, workbook targeting, and normalization options
 * controlling the host apply.
 * @returns A promise resolving to the aggregate apply result, including
 * success, operation messages, edited cells, skips, and failures.
 */
export async function applyWorkbookModifications(
  modifications: WorkbookModification,
  onOperation?: (op: string) => void,
  options: ApplyWorkbookModificationsOptions = {}
): Promise<ApplyModificationsResult> {
  modifications = normalizeMissingCellActions(modifications);
  if (!options.skipFactSetFormatNormalization) {
    modifications = normalizeFactSetFormatsForModification(
      modifications,
      new FactSetFormulaFormatRegistry(),
      options.pinnedWorkbookName,
    );
  }
  const nonFinalFactSetCellWrites =
    collectNonFinalFactSetCellWrites(modifications);
  const operations: string[] = [];
  const editedCells: string[] = [];
  const skippedActions: string[] = [];
  const failedActions: string[] = [];

  // Helper to track operations
  const trackOperation = (op: string, _address?: string) => {
    operations.push(op);
    onOperation?.(op);
  };

  // Helper to track an action the bridge / Office.js path declined to
  // perform (warn-skips for unwired intents, no-payload no-ops, etc.). Both
  // logs the operation and records the skip label so the final result can
  // surface a "step did nothing" honest failure when `editedCells` ends
  // up empty.
  const trackSkip = (op: string, label: string) => {
    operations.push(op);
    onOperation?.(op);
    skippedActions.push(label);
  };

  // Helper to track an action that we attempted against the host but could not
  // apply. These are materially different from skipped actions: a failed write
  // means the planner's requested workbook state is incomplete even if sibling
  // cells in the same batch succeeded.
  const trackFailure = (op: string, label: string) => {
    operations.push(op);
    onOperation?.(op);
    failedActions.push(label);
  };

  const rangeFillMatrixError = findRangeFillMatrixError(modifications);
  if (rangeFillMatrixError) {
    trackOperation(`❌ ${rangeFillMatrixError}`);
    return {
      success: false,
      message: rangeFillMatrixError,
      operations,
      editedCells,
      failedActions: ["invalid range fill matrix"],
    };
  }

  // Empty-modification guard. The planner sometimes responds to a request
  // it can't express ("Refresh the FactSet codes" before the
  // `refreshFactSetData` verb landed; "Recalculate" without a `recalculate`
  // verb; etc.) by emitting `{ message: "...explanation..." }` with no
  // cells/ranges/sheets/operations. The apply path used to silently report
  // ✅ on these because there was nothing to fail on. Fail honestly here so
  // the user sees the gap and the verifier doesn't loop trying to "fix" a
  // step that did nothing. Note: a modification with only a `message`
  // field is treated as empty — narration is not a side-effect.
  const isStructurallyEmpty =
    (!modifications.sheets || modifications.sheets.length === 0) &&
    (!modifications.annotations || modifications.annotations.length === 0) &&
    (!modifications.workbookOperations || modifications.workbookOperations.length === 0) &&
    (!modifications.worksheets ||
      modifications.worksheets.every(
        (w) =>
          (!w.cells || w.cells.length === 0) &&
          (!w.ranges || w.ranges.length === 0) &&
          (!w.tables || w.tables.length === 0) &&
          (!w.charts || w.charts.length === 0) &&
          (!w.logos || w.logos.length === 0)
      ));
  if (isStructurallyEmpty) {
    const explainer = modifications.message
      ? ` Planner narration: "${modifications.message}".`
      : "";
    const errorMsg = `Empty modification — the planner produced no cells, ranges, annotations, sheets, or workbook operations. The request likely uses a verb the schema doesn't expose (e.g. recalc, refresh, format-only, or a feature that isn't wired).${explainer}`;
    trackOperation(`❌ ${errorMsg}`);
    return {
      success: false,
      message: errorMsg,
      operations,
      editedCells,
    };
  }

  const fqlDateViolation = findFqlDatePolicyViolation(modifications);
  if (fqlDateViolation) {
    const target =
      fqlDateViolation.address !== undefined
        ? ` at ${fqlDateViolation.sheetName ? `${fqlDateViolation.sheetName}!` : ""}${fqlDateViolation.address}`
        : "";
    const errorMsg =
      `Invalid FQL date serialization${target}: ${fqlDateViolation.message}`;
    trackOperation(`❌ ${errorMsg}`);
    return {
      success: false,
      message: errorMsg,
      operations,
      editedCells,
      failedActions: ["invalid FQL date serialization"],
    };
  }

  // When the FactSet intent bridge is in charge we must NOT wrap the work in
  // Excel.run — Office.js is either bypassed or Excel is not loaded in the
  // FactSet-hosted iframe, so the outer Excel.run() call itself would throw
  // "Excel is not defined". Fork here and dispatch supported operations
  // (cells:set and ranges:fill) directly through excelHostOps; warn on the
  // rest until dedicated intents land.
  if (shouldUseExcelIntentBridge()) {
    const result = await applyWorkbookModificationsViaIntents(
      modifications,
      trackOperation,
      trackSkip,
      trackFailure,
      operations,
      editedCells,
      skippedActions,
      failedActions,
      options,
      nonFinalFactSetCellWrites,
    );
    return {
      ...result,
      editedCells: dedupeEditedAddresses(result.editedCells),
    };
  }

  try {
    await Excel.run(async (context) => {
      // Handle sheet-level operations (create, delete, rename)
      if (modifications.sheets) {
        for (const sheetMod of modifications.sheets) {
          try {
            switch (sheetMod.action) {
              case "create":
                if (sheetMod.name) {
                  // Sanitise sheet name: Excel limits to 31 chars, forbids certain chars
                  const safeName = sheetMod.name
                    .replace(/[:\\/*?[\]]/g, "_")
                    .slice(0, 31);
                  const existingSheet = context.workbook.worksheets.getItemOrNullObject(safeName);
                  existingSheet.load("name");
                  await context.sync();
                  if (existingSheet.isNullObject) {
                    trackOperation(`📄 Creating sheet: ${safeName}`);
                    context.workbook.worksheets.add(safeName);
                    await context.sync();
                  } else {
                    trackOperation(`📄 Sheet "${safeName}" already exists, skipping create`);
                  }
                }
                break;
              case "delete":
                if (sheetMod.name) {
                  const delSheet = context.workbook.worksheets.getItemOrNullObject(sheetMod.name);
                  delSheet.load("name");
                  await context.sync();
                  if (!delSheet.isNullObject) {
                    trackOperation(`🗑️ Deleting sheet: ${sheetMod.name}`);
                    delSheet.delete();
                    await context.sync();
                  } else {
                    trackSkip(
                      `⚠️ Sheet "${sheetMod.name}" not found, skipping delete`,
                      `sheet "${sheetMod.name}" delete`,
                    );
                  }
                }
                break;
              case "rename":
                if (sheetMod.name && sheetMod.newName) {
                  const renameSheet = context.workbook.worksheets.getItemOrNullObject(sheetMod.name);
                  renameSheet.load("name");
                  await context.sync();
                  if (!renameSheet.isNullObject) {
                    const safeNewName = sheetMod.newName
                      .replace(/[:\\/*?[\]]/g, "_")
                      .slice(0, 31);
                    trackOperation(`✏️ Renaming sheet: ${sheetMod.name} → ${safeNewName}`);
                    renameSheet.name = safeNewName;
                    await context.sync();
                  } else {
                    trackSkip(
                      `⚠️ Sheet "${sheetMod.name}" not found, skipping rename`,
                      `sheet "${sheetMod.name}" rename`,
                    );
                  }
                }
                break;
              case "activate":
                if (sheetMod.name) {
                  const actSheet = context.workbook.worksheets.getItemOrNullObject(sheetMod.name);
                  actSheet.load("name");
                  await context.sync();
                  if (!actSheet.isNullObject) {
                    trackOperation(`👉 Activating sheet: ${sheetMod.name}`);
                    actSheet.activate();
                    await context.sync();
                  } else {
                    trackSkip(
                      `⚠️ Sheet "${sheetMod.name}" not found, skipping activate`,
                      `sheet "${sheetMod.name}" activate`,
                    );
                  }
                }
                break;
              case "move":
                // Reorder a sheet within the workbook via Office.js. Cross-
                // workbook moves (the bridge path supports them via
                // `move.targetWorkbookName`) are NOT implemented here —
                // Office.js's `worksheet.position` write is single-workbook
                // only, so we surface a warn and continue. Bridge mode
                // covers the cross-workbook case.
                if (sheetMod.move && typeof sheetMod.move.newIndex === "number") {
                  const moveSheetTarget = sheetMod.name
                    ? context.workbook.worksheets.getItemOrNullObject(sheetMod.name)
                    : context.workbook.worksheets.getActiveWorksheet();
                  moveSheetTarget.load("name");
                  await context.sync();
                  if (
                    "isNullObject" in moveSheetTarget &&
                    moveSheetTarget.isNullObject
                  ) {
                    trackSkip(
                      `⚠️ Sheet "${sheetMod.name ?? ""}" not found, skipping move`,
                      `sheet "${sheetMod.name ?? ""}" move`,
                    );
                  } else if (
                    sheetMod.move.targetWorkbookName !== undefined &&
                    sheetMod.move.sourceWorkbookName !== undefined &&
                    sheetMod.move.targetWorkbookName !==
                      sheetMod.move.sourceWorkbookName
                  ) {
                    trackSkip(
                      `⚠️ Cross-workbook MoveSheet not supported in Office.js mode; skipping (use intent-bridge mode)`,
                      `sheet "${sheetMod.name ?? ""}" cross-workbook move`,
                    );
                  } else {
                    trackOperation(
                      `↕️ Moving sheet "${moveSheetTarget.name}" to index ${sheetMod.move.newIndex}`
                    );
                    moveSheetTarget.position = sheetMod.move.newIndex;
                    if (sheetMod.move.selectSheet) {
                      moveSheetTarget.activate();
                    }
                    await context.sync();
                  }
                }
                break;
              case "setVisibility": {
                // Bridge-only — matching the freezePanes / shape CRUD /
                // chart-family precedent for the post-2026-05-06 wave.
                // Visibility is an ergonomic / UX concern (no data-
                // correctness implications) and is wired cleanly via
                // `factset.Spreadsheet.SetWorksheetVisibility` in the
                // bridge path; we don't maintain a duplicate Office.js
                // implementation. Logged through `trackSkip` (not
                // `trackOperation`) so the step-level no-op guard sees
                // an honest skip rather than silent inactivity, and so
                // the operator's skipped-actions tally is accurate.
                const visTarget = sheetMod.name
                  ? `"${sheetMod.name}"`
                  : "(active)";
                trackSkip(
                  `⚠️ setVisibility (${sheetMod.visibility ?? "?"}) on ${visTarget} not supported on Office.js path; skipping`,
                  `sheet ${visTarget} setVisibility (Office.js)`
                );
                break;
              }
            }
          } catch (sheetErr) {
            // Edit-mode errors must propagate to the outer Office.js catch
            // so the entire apply stops with the unified message instead
            // of cascading per-sheet warnings.
            if (isEditModeError(sheetErr)) throw sheetErr;
            const msg = sheetErr instanceof Error ? sheetErr.message : String(sheetErr);
            console.warn(`[Excel] Sheet operation "${sheetMod.action}" for "${sheetMod.name}" failed:`, sheetErr);
            trackFailure(
              `❌ Sheet "${sheetMod.name}" ${sheetMod.action} failed: ${msg}`,
              `sheet "${sheetMod.name}" ${sheetMod.action}`,
            );
          }
        }
      }

      // Handle worksheet modifications
      if (modifications.worksheets) {
        const worksheetWriteState: WorksheetContentWriteState = {
          confirmedBySheet: new Map(),
          failedBySheet: new Map(),
        };
        for (const worksheetMod of modifications.worksheets) {
          try {
            await applyWorksheetModification(
              context,
              worksheetMod,
              trackOperation,
              trackSkip,
              trackFailure,
              editedCells,
              worksheetWriteState,
              nonFinalFactSetCellWrites,
            );
          } catch (wsErr) {
            // Edit-mode errors must propagate to the outer Office.js catch
            // so the entire apply stops with the unified message — the
            // same cascade-protection rationale as the sheet/cell loops.
            if (isEditModeError(wsErr)) throw wsErr;
            const sheetLabel = worksheetMod.sheetName || "active";
            console.error(`[Excel] Worksheet "${sheetLabel}" modifications failed:`, wsErr);
            trackFailure(
              `❌ Sheet "${sheetLabel}" error: ${
                wsErr instanceof Error ? wsErr.message : String(wsErr)
              }`,
              `worksheet "${sheetLabel}" modifications`,
            );
          }
        }
      }

      // Threaded comments and notes are deliberately bridge-only. Do not
      // silently reinterpret them as the legacy Office.js comment path: the
      // two Excel annotation families have different semantics.
      for (const annotation of modifications.annotations ?? []) {
        const targets =
          annotation.action === "set"
            ? annotation.address
            : annotation.addresses.join(", ");
        if (
          annotation.kind === "note" &&
          annotation.action === "set" &&
          annotation.origin === "citation"
        ) {
          console.warn(
            `[Excel] Generated citation Note skipped on Office.js path for ${annotation.sheetName}!${targets}`,
          );
          continue;
        }
        trackSkip(
          `⚠️ ${annotation.kind} ${annotation.action} on ${annotation.sheetName}!${targets} is not supported on the Office.js path; skipping`,
          `${annotation.kind} ${annotation.action} on ${annotation.sheetName}!${targets} (Office.js path)`,
        );
      }

      // Workbook-level operations (`addWorkbook`, `copyWorksheet`,
      // `insertWorksheetsFromBase64`, `recalculate`, `refreshFactSetData`,
      // `refreshCalculate`, `getRefreshStatus`, `awaitRefreshCalculate`,
      // `addNamedObject`, `removeNamedObject`, `search`)
      // are wired only in the bridge path — they all map to
      // `factset.Spreadsheet.*` intents. The Office.js fallback can't
      // honour them today (no Office.js equivalent for FDS refresh; the
      // others would require a separate Office.js implementation we
      // haven't built). Record each as a skip so the no-op detector
      // surfaces it instead of misreporting success.
      if (modifications.workbookOperations) {
        for (const op of modifications.workbookOperations) {
          trackSkip(
            `⚠️ Workbook operation "${op.action}" not supported in Office.js mode (bridge only); skipping`,
            `workbook operation "${op.action}" (Office.js path)`
          );
        }
      }
    });

    // Handle logo insertions AFTER main Excel.run (they have their own Excel.run)
    if (modifications.worksheets) {
      for (const worksheetMod of modifications.worksheets) {
        if (worksheetMod.logos) {
          for (const logoMod of worksheetMod.logos) {
            try {
              trackOperation(
                `🏢 Inserting ${logoMod.companyName} logo at ${logoMod.address}`,
                logoMod.address
              );
              const result = await insertCompanyLogo(
                logoMod.companyName,
                logoMod.address,
                logoMod.width || 60,
                logoMod.height || 60,
                worksheetMod.sheetName,
              );
              if (!result.success) {
                console.warn(`[Excel] Logo insertion warning: ${result.message}`);
              }
            } catch (e) {
              // Edit-mode errors are surfaced to the outer try by re-throw
              // so the unified message replaces the per-logo warning. The
              // outer Office.js catch (below) detects `isEditModeError`
              // and returns `EDIT_MODE_USER_MESSAGE`.
              if (isEditModeError(e)) throw e;
              console.warn(`[Excel] Logo insertion failed for ${logoMod.companyName}:`, e);
            }
          }
        }
      }
    }

    if (
      editedCells.length === 0 &&
      skippedActions.length > 0 &&
      failedActions.length === 0
    ) {
      return {
        success: false,
        message: `No-op step: every action was skipped (${skippedActions.join("; ")}). The planner emitted actions the bridge can't honour today; rephrase the request or use a wired action.`,
        operations,
        editedCells: dedupeEditedAddresses(editedCells),
        skippedActions,
        ...(failedActions.length > 0 ? { failedActions } : {}),
      };
    }

    if (failedActions.length > 0) {
      return {
        success: false,
        message: `Some workbook edits failed (${failedActions.join("; ")}). The workbook may be partially updated; retry or adjust the failed actions before marking the step complete.`,
        operations,
        editedCells: dedupeEditedAddresses(editedCells),
        ...(skippedActions.length > 0 ? { skippedActions } : {}),
        failedActions,
      };
    }

    return {
      success: true,
      message: modifications.message || "Changes applied successfully",
      operations,
      editedCells: dedupeEditedAddresses(editedCells),
      ...(skippedActions.length > 0 ? { skippedActions } : {}),
      ...(failedActions.length > 0 ? { failedActions } : {}),
    };
  } catch (error) {
    if (isEditModeError(error)) {
      // Excel was (or entered) cell-edit mode mid-apply. The Office.js
      // path can't probe `isInEditMode` ahead of time the way the bridge
      // path does, so this catch is the safety net for both "user typing
      // when apply started" and "edit mode entered mid-flight". Surface a
      // clean, actionable message instead of leaking the host error code.
      const copy = hostReadyRetryCopy("stuck_edit", { retryAction: true });
      console.warn("[Excel] Office.js apply aborted due to cell-edit mode:", error);
      trackOperation(`⏳ ${copy.messageOneLiner}`);
      return {
        success: false,
        message: copy.messageOneLiner,
        operations,
        editedCells: dedupeEditedAddresses(editedCells),
        ...(skippedActions.length > 0 ? { skippedActions } : {}),
        ...(failedActions.length > 0 ? { failedActions } : {}),
        retryReason: "stuck_edit",
        hostDispatchAttempted: true,
      };
    }
    console.error("[Excel] Failed to apply modifications:", error);
    return {
      success: false,
      message: `Failed to apply changes: ${error instanceof Error ? error.message : String(error)}`,
      operations,
      editedCells: dedupeEditedAddresses(editedCells),
      ...(skippedActions.length > 0 ? { skippedActions } : {}),
      ...(failedActions.length > 0 ? { failedActions } : {}),
    };
  }
}

const TTF_OAUTH_GATE_CACHE_TTL_MS = 60_000;

type TtfOAuthGateSnapshot = Pick<TtfRunContext, "ttfAvailable" | "diagnostics">;

let cachedTtfOAuthGate:
  | { snapshot: TtfOAuthGateSnapshot; expiresAt: number; generation: number }
  | undefined;
let ttfOAuthGateProbe:
  | { promise: Promise<TtfOAuthGateSnapshot>; generation: number }
  | undefined;
let ttfOAuthGateGeneration = 0;

function buildTtfRunContext(snapshot: TtfOAuthGateSnapshot): TtfRunContext {
  return {
    ttfAvailable: snapshot.ttfAvailable,
    stickyDisabled: false,
    attemptsRemaining: EXCEL_TTF_MAX_ATTEMPTS_PER_APPLY,
    budgetRemainingMs: EXCEL_TTF_RUN_BUDGET_MS,
    deadlineAt: Date.now() + EXCEL_TTF_RUN_BUDGET_MS,
    diagnostics: snapshot.diagnostics
      ? {
          ...snapshot.diagnostics,
          scopes: snapshot.diagnostics.scopes?.slice(),
        }
      : undefined,
  };
}

async function probeTtfOAuthGate(): Promise<TtfOAuthGateSnapshot> {
  const session = await getFactSetOAuthSession();
  const scopes = Array.isArray(session.scopes) ? session.scopes : [];
  return {
    ttfAvailable:
      session.connected === true &&
      scopes.includes(FACTSET_SCOPES.TEXT_TO_FORMULA),
    diagnostics: {
      connected: session.connected === true,
      hasTextToFormulaAccess: session.hasTextToFormulaAccess === true,
      requiredScope: FACTSET_SCOPES.TEXT_TO_FORMULA,
      scopes,
    },
  };
}

export function clearTtfOAuthGateCache(): void {
  ttfOAuthGateGeneration += 1;
  cachedTtfOAuthGate = undefined;
}

registerFactSetClientSessionClearHandler(clearTtfOAuthGateCache);

/**
 * Compute a fresh per-apply FactSet TTF context from a short-lived cached
 * capability probe. Only the immutable connected/scope gate is shared;
 * mutable attempt/time budgets are recreated for every apply.
 *
 * Failure-tolerant: any probe error closes the gate for this apply and is
 * never cached. The gate deliberately ignores access-token expiry because
 * the server proxy owns refresh-token handling.
 */
export async function computeTtfRunContext(): Promise<TtfRunContext> {
  const now = Date.now();
  if (
    cachedTtfOAuthGate &&
    cachedTtfOAuthGate.generation === ttfOAuthGateGeneration &&
    cachedTtfOAuthGate.expiresAt > now
  ) {
    return buildTtfRunContext(cachedTtfOAuthGate.snapshot);
  }

  const probeGeneration = ttfOAuthGateGeneration;
  let activeProbe = ttfOAuthGateProbe;
  if (!activeProbe || activeProbe.generation !== probeGeneration) {
    const probe = {
      generation: probeGeneration,
      promise: probeTtfOAuthGate(),
    };
    ttfOAuthGateProbe = probe;
    activeProbe = probe;
    void probe.promise.finally(() => {
      if (ttfOAuthGateProbe === probe) {
        ttfOAuthGateProbe = undefined;
      }
    }).catch(() => {
      // The caller below owns probe error handling.
    });
  }

  try {
    // Keep a stable local reference: the shared slot is intentionally cleared
    // when the probe settles and may also be replaced by a newer generation.
    const snapshot = await activeProbe.promise;
    if (probeGeneration !== ttfOAuthGateGeneration) {
      return {
        ttfAvailable: false,
        stickyDisabled: false,
        diagnostics: {
          requiredScope: FACTSET_SCOPES.TEXT_TO_FORMULA,
          probeError: "FactSet session changed during the capability probe.",
        },
      };
    }
    cachedTtfOAuthGate = {
      snapshot,
      expiresAt: Date.now() + TTF_OAUTH_GATE_CACHE_TTL_MS,
      generation: probeGeneration,
    };
    return buildTtfRunContext(snapshot);
  } catch (e) {
    console.warn(
      "[Excel] FactSet OAuth session probe failed; TTF gate closed for this apply:",
      e
    );
    return {
      ttfAvailable: false,
      stickyDisabled: false,
      diagnostics: {
        requiredScope: FACTSET_SCOPES.TEXT_TO_FORMULA,
        probeError: e instanceof Error ? e.message : String(e),
      },
    };
  }
}

// `isFdsBoundFormula` lives in `./formulaWriteDetection` so the React
// component (`ExcelApp.tsx`) and this service module can both depend on
// a single source of truth without a circular import.

// ============================================================================
// computeUndoCaptureRange — AI-2617
// ============================================================================

/**
 * A1 column letters → 1-based index (A=1, Z=26, AA=27, ...).
 */
function a1ColumnToIndex(col: string): number {
  let n = 0;
  for (const ch of col.toUpperCase()) {
    const code = ch.charCodeAt(0);
    if (code < 65 || code > 90) return 0;
    n = n * 26 + (code - 64);
  }
  return n;
}

/**
 * 1-based column index → A1 letters (1=A, 26=Z, 27=AA, ...).
 */
function indexToA1Column(idx: number): string {
  if (idx < 1) return "A";
  let n = idx;
  let out = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

interface ParsedCell {
  col: number; // 1-based
  row: number; // 1-based
}

/**
 * Parse a single A1-style cell or range address (no sheet prefix), strip
 * `$` absolute markers, return the bounding `{ topLeft, bottomRight }`
 * cell-index pair. Returns `undefined` for shapes we can't reason about
 * (column-only `"A:A"`, row-only `"5:5"`, malformed, fully-qualified
 * `Sheet!A1` — strip the sheet prefix before calling).
 */
function parseA1RangeBounds(
  address: string
): { topLeft: ParsedCell; bottomRight: ParsedCell } | undefined {
  if (!address) return undefined;
  const cleaned = address.replace(/\$/g, "").trim();
  if (!cleaned) return undefined;
  const parts = cleaned.split(":");
  if (parts.length === 0 || parts.length > 2) return undefined;
  const cellRegex = /^([A-Za-z]+)(\d+)$/;
  const cells: ParsedCell[] = [];
  for (const part of parts) {
    const m = cellRegex.exec(part);
    if (!m) return undefined; // bare column ("A") or row ("5") → bail
    const col = a1ColumnToIndex(m[1]);
    const row = parseInt(m[2], 10);
    if (col < 1 || !Number.isFinite(row) || row < 1) return undefined;
    cells.push({ col, row });
  }
  const tl: ParsedCell = {
    col: Math.min(...cells.map((c) => c.col)),
    row: Math.min(...cells.map((c) => c.row)),
  };
  const br: ParsedCell = {
    col: Math.max(...cells.map((c) => c.col)),
    row: Math.max(...cells.map((c) => c.row)),
  };
  return { topLeft: tl, bottomRight: br };
}

/**
 * Split a possibly-qualified address (`Sheet1!A1:B5`) into
 * `{ sheet?: string, range: string }`. Single-quoted sheet names
 * (`'My Sheet'!A1`) are unwrapped.
 */
function splitSheetPrefix(
  address: string
): { sheet?: string; range: string } {
  const idx = address.lastIndexOf("!");
  if (idx <= 0) return { range: address };
  let sheet = address.slice(0, idx);
  if (sheet.startsWith("'") && sheet.endsWith("'")) {
    sheet = sheet.slice(1, -1);
  }
  return { sheet, range: address.slice(idx + 1) };
}

function excelRangeContainsAddress(
  containerAddress: string,
  targetAddress: string,
): boolean {
  const container = parseA1RangeBounds(
    splitSheetPrefix(containerAddress).range,
  );
  const target = parseA1RangeBounds(splitSheetPrefix(targetAddress).range);
  if (!container || !target) return false;
  return (
    target.topLeft.row >= container.topLeft.row &&
    target.bottomRight.row <= container.bottomRight.row &&
    target.topLeft.col >= container.topLeft.col &&
    target.bottomRight.col <= container.bottomRight.col
  );
}

function excelMoveDestinationRange(
  sourceAddress: string,
  destinationAddress: string | undefined,
): string | undefined {
  if (!destinationAddress) return undefined;
  const source = parseA1RangeBounds(splitSheetPrefix(sourceAddress).range);
  const destination = parseA1RangeBounds(
    splitSheetPrefix(destinationAddress).range,
  );
  if (!source || !destination) return undefined;
  const rowCount = source.bottomRight.row - source.topLeft.row + 1;
  const columnCount = source.bottomRight.col - source.topLeft.col + 1;
  const endRow = destination.topLeft.row + rowCount - 1;
  const endColumn = destination.topLeft.col + columnCount - 1;
  const start = `${columnIndexToLetter(destination.topLeft.col - 1)}${
    destination.topLeft.row
  }`;
  const end = `${columnIndexToLetter(endColumn - 1)}${endRow}`;
  return start === end ? start : `${start}:${end}`;
}

type ContentWriteLedger = Map<string, string[]>;

function contentWriteLedgerEntries(
  ledger: ContentWriteLedger,
  sheetName: string | undefined,
): string[] {
  const key = (sheetName ?? "").trim().toLowerCase();
  const existing = ledger.get(key);
  if (existing) return existing;
  const created: string[] = [];
  ledger.set(key, created);
  return created;
}

/**
 * Compute a single `RangeAddress` that bounds every cell-shaped write
 * in a `WorkbookModification` payload. Used by
 * `applyWorkbookModificationsViaIntents` to satisfy
 * `factset.Spreadsheet.BeginUndo`'s `captureRange` requirement
 * (AI-2617 — see the `BeginUndoOptions` JSDoc for the upstream
 * schema/catalog mismatch).
 *
 * Walks `worksheets[].cells[].address`, `worksheets[].ranges[].address`
 * (incl. `moveRange.destination` / cross-sheet `*SheetName`), and
 * `worksheets[].tables[].address`. Groups cells by sheet, picks the
 * **active sheet's** group when present, otherwise the first
 * non-empty group, and bounds it as a `Sheet!TL:BR` rectangle. Returns
 * `undefined` only when no cell-shaped writes are detectable (pure
 * structural / shape / chart edits) — the caller fills a sentinel
 * `${fallbackSheetName}!A1` `RangeAddress` in that case so the schema
 * accepts and the host's own capture heuristic kicks in.
 *
 * Pure / deterministic; no async, no side effects.
 */
function computeUndoCaptureRange(
  modifications: WorkbookModification,
  fallbackSheetName: string
): RangeAddress | undefined {
  // sheetName → list of parsed cell bounds discovered on that sheet
  const bySheet = new Map<string, ParsedCell[]>();

  const record = (sheetMaybe: string | undefined, range: string): void => {
    if (!range) return;
    const split = splitSheetPrefix(range);
    // Address-embedded sheet name wins over the outer worksheet's name.
    const sheet = (split.sheet ?? sheetMaybe ?? fallbackSheetName).trim();
    if (!sheet) return;
    const bounds = parseA1RangeBounds(split.range);
    if (!bounds) return;
    const arr = bySheet.get(sheet) ?? [];
    arr.push(bounds.topLeft, bounds.bottomRight);
    bySheet.set(sheet, arr);
  };

  for (const ws of modifications.worksheets ?? []) {
    const sheetForWs = ws.sheetName;
    for (const cell of ws.cells ?? []) {
      record(sheetForWs, cell.address);
    }
    for (const r of ws.ranges ?? []) {
      record(sheetForWs, r.address);
      // moveRange has a destination + optional cross-sheet override.
      const mv = r.moveRange;
      if (mv) {
        record(mv.sourceSheetName ?? sheetForWs, r.address);
        record(mv.destinationSheetName ?? sheetForWs, mv.destination);
      }
    }
    for (const t of ws.tables ?? []) {
      if (t.address) record(sheetForWs, t.address);
    }
  }
  for (const annotation of modifications.annotations ?? []) {
    if (annotation.action === "set") {
      record(annotation.sheetName, annotation.address);
    } else {
      for (const address of annotation.addresses) {
        record(annotation.sheetName, address);
      }
    }
  }

  if (bySheet.size === 0) return undefined;

  // Prefer the active sheet's group; otherwise pick the first group we
  // recorded (Map preserves insertion order, which matches the
  // worksheets[] order the planner emitted).
  let pickSheet = fallbackSheetName;
  let pickCells = bySheet.get(fallbackSheetName);
  if (!pickCells) {
    const firstEntry = bySheet.entries().next().value;
    if (firstEntry) {
      pickSheet = firstEntry[0];
      pickCells = firstEntry[1];
    }
  }
  if (!pickCells || pickCells.length === 0) return undefined;

  const tlCol = Math.min(...pickCells.map((c) => c.col));
  const tlRow = Math.min(...pickCells.map((c) => c.row));
  const brCol = Math.max(...pickCells.map((c) => c.col));
  const brRow = Math.max(...pickCells.map((c) => c.row));
  const tlA1 = `${indexToA1Column(tlCol)}${tlRow}`;
  const brA1 = `${indexToA1Column(brCol)}${brRow}`;
  const rangePart = tlA1 === brA1 ? tlA1 : `${tlA1}:${brA1}`;
  // Sheet names with spaces / punctuation need to be single-quoted on
  // the wire (`'My Sheet'!A1`). Keep the bare form for plain
  // identifiers so common cases stay readable in DevTools.
  const sheetNeedsQuote = /[^A-Za-z0-9_]/.test(pickSheet);
  const sheetPrefix = sheetNeedsQuote
    ? `'${pickSheet.replace(/'/g, "''")}'`
    : pickSheet;
  return {
    address: `${sheetPrefix}!${rangePart}`,
    referenceType: "absolute",
  };
}

/**
 * Dispatch modifications through the FactSet Spreadsheet intent bridge.
 *
 * Called when `shouldUseExcelIntentBridge()` is true. Iterates modifications
 * without touching `Excel.run` / `Office.js` at all — the outer call site has
 * already guaranteed Office.js is either bypassed or unavailable. Supports the
 * bridge-wired action set across sheets, cells/ranges, charts, ActiveGraph,
 * logos, and workbook-level operations; unwired actions emit warn-skips so the
 * caller can surface partial-application diagnostics honestly.
 */
async function applyWorkbookModificationsViaIntents(
  modifications: WorkbookModification,
  trackOperation: (op: string, address?: string) => void,
  trackSkip: (op: string, label: string) => void,
  trackFailure: (op: string, label: string) => void,
  operations: string[],
  editedCells: string[],
  skippedActions: string[],
  failedActions: string[],
  options: ApplyWorkbookModificationsOptions = {},
  nonFinalFactSetCellWrites: WeakSet<CellModification> = new WeakSet(),
): Promise<ApplyModificationsResult> {
  const pinnedWorkbookName = options.pinnedWorkbookName;
  const citationNoteFailures: string[] = [];
  const throwIfAborted = (): void => {
    if (options.signal?.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }
  };
  throwIfAborted();
  // Compute once up front so the per-burst recalc gate, the per-step
  // verifier (via the returned `ApplyModificationsResult`), and every
  // intermediate return site read the same value. Scans the planner's
  // `WorkbookModification` payload — see `formulaWriteDetection.ts` for
  // the slot coverage (cells, ranges, tables, and the TTF workbook-op
  // channel).
  const appliedFormulaWrites = anyFormulaWrite(modifications);
  // Sheet-CRUD / named-object intents accept `workbookName` on the
  // wire — thread the pin everywhere so the host transparently
  // routes those writes to the right workbook even when the user
  // has the focus elsewhere. Defaulting to `undefined` preserves
  // legacy (active-workbook) behaviour for one-shot apply calls
  // outside the plan loop (chat-chart insert, ad-hoc apply).
  const pinnedWorkbookOption = pinnedWorkbookName
    ? { workbookName: pinnedWorkbookName }
    : undefined;
  // Background-targeting master switch (default ON; f2_docs is the source of
  // truth). When ON, every write / range-mutation in this burst self-routes
  // to the pinned workbook via `workbookName`, so we skip the focus-stealing
  // pre-burst / per-sheet `activateWorksheet` calls entirely and the user's
  // Excel view stays where they left it. Flip OFF (the `excelTargetingConfig`
  // kill-switch) to fall back to the legacy activate-the-pin behaviour. Gated
  // on a defined pin so no-pin / Office.js one-shot applies keep their exact
  // prior behaviour. NB: the sheet-CRUD intents (create / delete / rename /
  // activate / move) always take `workbookName` via `pinnedWorkbookOption`
  // regardless of this switch.
  const useRangeMutationTargeting =
    pinnedWorkbookName !== undefined && supportsRangeMutationWorkbookName();
  // Pre-apply host-ready gate. If the host is mid-cell-edit (user typing
  // in a cell, prior write left the host in edit state) or busy (long
  // calc, modal dialog open), every subsequent write intent fails — most
  // notoriously with `InvalidOperationInCellEditMode` for the edit-mode
  // case — and the entire plan cascades silent failures across the rest
  // of the steps. Probe `GetActiveExcelContext` and wait briefly for the
  // flags to clear before opening the undo bracket / calc-mode bracket.
  // If still set after the wait, abort with a reason-specific actionable
  // message so the user knows whether to press a key (edit) or just wait
  // (busy) instead of seeing a wall of cryptic errors.
  //
  // A `probe_failed` reason means the bridge probe itself threw; we
  // can't tell the host's state, so let the apply proceed and rely on
  // the in-flight `isEditModeError` catch below as the safety net.
  //
  // When the short gate remains stuck, escalate into the patient gate:
  // it polls for up to 5 minutes while the caller surfaces explicit
  // "exit edit mode / wait for Excel" progress. If the host clears
  // during that window, this write burst resumes automatically.
  let hostGate = await waitForHostReady();
  throwIfAborted();
  if (hostGate.reason === "stuck_edit" || hostGate.reason === "stuck_busy") {
    console.warn(
      `[Excel] Pre-apply gate stuck (${hostGate.reason}) after ${hostGate.attempts} probes; entering patient wait.`
    );
    const patientGate = await waitForHostReadyPatient({
      initialResult: hostGate,
      signal: options.signal,
      onProgress: options.onHostReadyWaitProgress,
    });
    hostGate = patientGate;
    if (patientGate.ready) {
      options.onHostReadyWaitCleared?.({
        attempts: patientGate.attempts,
        initialReason: patientGate.initialReason,
      });
    }
    throwIfAborted();
  }
  if (hostGate.reason === "stuck_edit" || hostGate.reason === "stuck_busy") {
    const copy = hostReadyRetryCopy(hostGate.reason, { retried: true });
    const message = copy.messageOneLiner;
    trackOperation(`⏳ ${message}`);
    return {
      success: false,
      message,
      operations,
      editedCells,
      ...(failedActions.length > 0 ? { failedActions } : {}),
      // Surface the stuck reason so the plan runner can pause this run
      // and render an inline Retry button without continuing into
      // misleading downstream success indicators.
      retryReason: hostGate.reason,
      hostDispatchAttempted: false,
      appliedFormulaWrites,
    };
  }
  if (hostGate.reason === "cleared") {
    console.log(
      `[Excel] Pre-apply gate: host cleared after ${hostGate.attempts} attempts; proceeding.`
    );
  }

  // Counter for structural changes that succeeded but don't produce an
  // `editedCells` entry — sheet CRUD (create / delete / rename / activate)
  // and the visibility intents (hideColumns / hideRows) all qualify. Used
  // by the no-op guard below: a step that added or hid something but
  // didn't write a cell shouldn't be reported as "did nothing" — it did
  // real work even if no `editedCells` accumulated.
  let structuralChangesApplied = 0;

  // Resolve the active workbook and sheet names once, upfront — both
  // are needed for `BeginUndo`'s captureRange (AI-2617) and the
  // downstream `Charting.*` writes. Pulled out ahead of the BeginUndo
  // call so the undo bracket can target the actual modification range
  // rather than the host's default capture heuristic. Best-effort: a
  // probe failure leaves both `undefined` and the writes fall back to
  // the (often-buggy) host defaults.
  let activeWorkbookName: string | undefined;
  let activeSheetName = "";
  try {
    const ctx = await safeGetActiveExcelContext();
    const ctxRecord = asRecord(ctx);
    const activeSheetRecord = asRecord(ctxRecord?.activeSheet);
    activeWorkbookName = ctx.workbookName;
    activeSheetName =
      typeof activeSheetRecord?.name === "string"
        ? activeSheetRecord.name
        : typeof ctxRecord?.name === "string"
          ? ctxRecord.name
          : "";
  } catch (e) {
    console.warn(
      "[Excel] Active context probe failed; Charting.* writes will omit workbookName and BeginUndo will use a sentinel captureRange:",
      e
    );
  }

  // Route the pinned workbook's writes to the right workbook before the burst.
  //
  // Per `f2_docs/excel/excel-tools.md` (the source of truth) the entire write
  // surface this burst uses accepts a `workbookName` routing override, so it
  // can target a non-active workbook in the background:
  //   - `InsertContentsToRange` via an external-ref `destinationCell`
  //     (`[Book.xlsx]Sheet!A1`) — see `destinationCellOf` in `excelHostOps.ts`
  //     and the `pinnedWorkbookName` thread through
  //     `applyCellModificationViaIntents`.
  //   - `ApplyFormatting`, `DeleteRange`, `InsertRange`, `MoveRange`,
  //     `SortRange`, `ResizeExcelElements`, `ClearRange` via
  //     `RangeReferenceAddress.workbookName` — threaded through the
  //     `excelHostOps` dispatchers by `applyRangeModificationViaIntents`
  //     (gated on `useRangeMutationTargeting`).
  // The host services these against the named workbook and self-restores
  // focus, so a background-targeted burst never yanks the user's view.
  // (`HarmonizedRefreshWorksheets` is the one genuinely active-workbook-only
  // step and runs OUTSIDE this function via `finalizeRefreshAndRecalc` in
  // `ExcelApp.tsx`, serialized through the foreground coordinator.
  // `ApplicationCalculate` / `SetCalculationMode` are application-level per
  // f2_docs and never touch focus.)
  //
  // Two paths:
  //   - Background targeting ON (default): DON'T activate the pin. We still
  //     (a) confirm the pin is open via a focus-free `GetOpenWorkbooks`
  //     liveness probe, and (b) adopt the pin's `sheetHint` as
  //     `activeSheetName` so the downstream `pinnedSheetForRouting` fallback
  //     names a sheet on the PINNED workbook, not the user's currently-focused
  //     one — otherwise the external-ref builder could emit garbage like
  //     `'[PinnedBook.xlsx]SheetFromOtherBook'!A1`.
  //   - Kill-switch OFF: fall back to the legacy activate-the-pin behaviour so
  //     the (then un-pinned) mutation intents resolve against the active
  //     workbook. The activation doubles as the liveness check.
  if (
    pinnedWorkbookName &&
    pinnedWorkbookName !== activeWorkbookName
  ) {
    throwIfAborted();
    const sheetHint = pickReactivationSheetHint(modifications, activeSheetName);
    if (useRangeMutationTargeting) {
      await assertPinnedWorkbookOpenNoFocus(pinnedWorkbookName);
      activeWorkbookName = pinnedWorkbookName;
      activeSheetName = sheetHint;
      trackOperation(
        `🎯 Editing "${pinnedWorkbookName}" in the background (your current view stays put)`
      );
    } else {
      try {
        await activateWorksheet(sheetHint, pinnedWorkbookOption);
        activeWorkbookName = pinnedWorkbookName;
        activeSheetName = sheetHint;
        trackOperation(
          `🎯 Targeting workbook "${pinnedWorkbookName}" (user has switched focus; routing writes back to the plan's origin workbook)`
        );
      } catch (e) {
        // Most likely cause: the pinned workbook was closed mid-plan.
        // Surface a clean, user-facing failure with the structured reason so
        // the orchestrator can post the friendly "the workbook is no longer
        // open" message into the pinned chat and stop running the plan.
        const reason = e instanceof Error ? e.message : String(e);
        console.warn(
          `[Excel] Reactivation of pinned workbook "${pinnedWorkbookName}" failed; aborting apply:`,
          e,
        );
        throw new WorkbookUnavailableError(pinnedWorkbookName, reason);
      }
    }
  }

  // Outer bracket: collapse every host mutation in this apply into a single
  // user-facing undo entry (`Edit → Undo FactSet AI apply`). Best-effort and
  // sits OUTSIDE the manual-calc bracket so the undo also captures the
  // SetCalculationMode flip itself. Same failure contract as the calc
  // bracket: a transport failure here is logged and skipped, never thrown.
  // The `EndUndo` close runs in the `finally` below, after the calc-mode
  // restore, so the undo entry covers the entire burst (including restore).
  //
  // AI-2617: the production Zod schema rejects an omitted `captureRange`,
  // so we compute one from the incoming modifications. When no
  // cell-shaped writes are detectable (pure structural / shape / chart
  // edits), fall back to a degenerate `${activeSheetName}!A1`
  // sentinel — the schema accepts it and the host's own capture
  // heuristic kicks in for the actual undo bracket.
  let undoBegun = false;
  try {
    const computedCaptureRange = computeUndoCaptureRange(
      modifications,
      activeSheetName
    );
    const captureRange: RangeAddress =
      computedCaptureRange ?? {
        address: activeSheetName
          ? `${/[^A-Za-z0-9_]/.test(activeSheetName) ? `'${activeSheetName.replace(/'/g, "''")}'` : activeSheetName}!A1`
          : "A1",
        referenceType: "absolute",
      };
    if (useRangeMutationTargeting && pinnedWorkbookName) {
      captureRange.workbookName = pinnedWorkbookName;
    }
    await beginUndo({
      displayName: "FactSet AI apply",
      captureRange,
    });
    undoBegun = true;
  } catch (e) {
    console.warn(
      "[Excel] BeginUndo failed; continuing without cross-call undo bracket:",
      e
    );
  }
  // Bracket the whole burst of mutating intents in manual calc mode so each
  // write doesn't trigger its own recalc pass. Best-effort only — if the
  // host doesn't support SetCalculationMode, or the call fails for any
  // reason, we keep going. The `"automatic"` restore runs in `finally` so
  // a write crashing mid-flight never leaves the workbook stuck in manual.
  let calcModeFlipped = false;
  try {
    await setCalculationMode("manual");
    calcModeFlipped = true;
    // Diagnostic: log the actual mode the host reports rather than the
    // requested mode, so a silent SetCalculationMode no-op shows up here.
    try {
      const observed = await getCalculationMode();
      trackOperation(`🧮 Calculation mode set to manual (host reports: ${observed})`);
    } catch (modeErr) {
      console.warn("[Excel] GetCalculationMode read-back failed:", modeErr);
    }
  } catch (e) {
    console.warn(
      "[Excel] SetCalculationMode(manual) failed; continuing without recalc bracket:",
      e
    );
  }

  // Compute the FactSet TTF gate once for the whole apply. The cell
  // loop threads this object into every `tryDispatchCellViaTtf` call,
  // and `stickyDisabled` flips to `true` mid-loop if any cell hits a
  // reauth-required error (so subsequent cells stop wasting round
  // trips on a token the upstream just rejected). When the gate is
  // closed (no FactSet OAuth, missing TTF scope, or a session probe
  // failure) every cell silently falls through to the existing
  // LLM-formula write path. Expired access tokens are intentionally
  // still allowed through this gate so the server-side proxy can
  // refresh them before calling FactSet.
  const ttfRunCtx = await computeTtfRunContext();
  const ttfDiagnostics = ttfRunCtx.diagnostics;
  const ttfScopeSummary = ttfDiagnostics?.scopes?.length
    ? ttfDiagnostics.scopes.join(" ")
    : "<none>";
  const ttfGateStatus = ttfRunCtx.ttfAvailable ? "open" : "closed";
  trackOperation(
    `TTF gate ${ttfGateStatus}: connected=${ttfDiagnostics?.connected === true ? "yes" : "no"}, ` +
      `hasTextToFormulaAccess=${ttfDiagnostics?.hasTextToFormulaAccess === true ? "yes" : "no"}, ` +
      `requiredScope=${ttfDiagnostics?.requiredScope ?? FACTSET_SCOPES.TEXT_TO_FORMULA}, scopes=${ttfScopeSummary}` +
      (ttfDiagnostics?.probeError ? `, probeError=${ttfDiagnostics.probeError}` : ""),
  );

  const ensureActiveSheetForWrite = async (sheetName?: string): Promise<void> => {
    const target = sheetName?.trim();
    if (!target || target === activeSheetName) return;

    if (useRangeMutationTargeting) {
      // Background targeting: every write / range-mutation carries its own
      // `worksheetName` + `workbookName`, so there is no need to activate
      // (and steal focus to) the target sheet. Just record it as the routing
      // fallback for later modifications that omit their sheet.
      activeSheetName = target;
      return;
    }

    // Legacy path (kill-switch OFF): activate the target sheet so the
    // (un-pinned) mutation intents resolve against it. Always pass
    // `workbookName` when we know the active (or pinned) one — the host uses
    // it to disambiguate a sheet name that exists in more than one open
    // workbook, and a pin-aware apply requires it for the planner's
    // `targetSheet` to land on the right workbook even when the user has
    // clicked into a different one.
    await activateWorksheet(
      target,
      activeWorkbookName !== undefined ? { workbookName: activeWorkbookName } : undefined
    );
    activeSheetName = target;
    trackOperation(`🎯 Targeting worksheet "${target}" for bridge write`);
  };

  const resolveChartSheetRef = (
    explicit: Parameters<typeof addExcelChart>[0]["sheetRef"] | undefined,
    worksheetName?: string,
  ): Parameters<typeof addExcelChart>[0]["sheetRef"] | undefined => {
    if (explicit) return explicit;
    return worksheetName ? { name: worksheetName } : undefined;
  };

  // Hoisted above the outer try so the catch-block returns below can
  // still forward `deferredRefreshAll` / `deferredRecalcMode` if the
  // workbook-op loop ran (and stamped the context) before a later
  // mutation threw. Defaults to an empty struct — only the workbook-op
  // switch arms in `applyWorkbookOperationViaIntents` mutate this.
  const refreshOperationContext: RefreshOperationContext = {};
  const confirmedContentWritesBySheet: ContentWriteLedger = new Map();
  const failedContentWritesBySheet: ContentWriteLedger = new Map();

  try {
    if (modifications.sheets && modifications.sheets.length > 0) {
      for (const sheetMod of modifications.sheets) {
        try {
          switch (sheetMod.action) {
            case "create": {
              // The extended `AddWorksheet` payload (2026-04-28) accepts
              // `options.worksheetName` so a planner-requested name lands
              // through the host directly. The host still applies Excel
              // naming rules (sanitisation, 31-char cap) — read-back via
              // `getWorkbookSheets()` is left to verification flows.
              const opts: AddWorksheetOptions = {
                ...(sheetMod.name ? { worksheetName: sheetMod.name } : {}),
                ...(pinnedWorkbookName
                  ? { workbookName: pinnedWorkbookName }
                  : {}),
                // Background targeting: don't let the host activate the new
                // sheet (which would front the pinned workbook and yank the
                // user's view). Subsequent writes reach the sheet via an
                // external-ref `destinationCell`, so it needn't be active. When
                // targeting is off (legacy / no-pin) we omit the flag and keep
                // the host's default select-on-create behaviour.
                ...(useRangeMutationTargeting
                  ? { selectWorksheet: false }
                  : {}),
              };
              await addWorksheet(
                Object.keys(opts).length > 0 ? opts : undefined,
              );
              structuralChangesApplied += 1;
              trackOperation(
                sheetMod.name
                  ? `📄 Added new worksheet "${sheetMod.name}"`
                  : `📄 Added new worksheet`
              );
              break;
            }
            case "delete": {
              if (!sheetMod.name) {
                trackSkip(
                  `⚠️ Sheet delete: missing "name" in modification; skipping`,
                  `sheet delete (no name)`
                );
                break;
              }
              await deleteWorksheet(sheetMod.name, {
                displayAlerts: false,
                ...(pinnedWorkbookName
                  ? { workbookName: pinnedWorkbookName }
                  : {}),
              });
              structuralChangesApplied += 1;
              trackOperation(`🗑️ Deleted worksheet "${sheetMod.name}"`);
              break;
            }
            case "rename": {
              if (!sheetMod.name || !sheetMod.newName) {
                trackSkip(
                  `⚠️ Sheet rename: requires both "name" (current) and "newName"; skipping`,
                  `sheet rename (incomplete)`
                );
                break;
              }
              await renameWorksheet(
                sheetMod.name,
                sheetMod.newName,
                pinnedWorkbookOption,
              );
              structuralChangesApplied += 1;
              trackOperation(
                `📝 Renamed worksheet "${sheetMod.name}" → "${sheetMod.newName}"`
              );
              break;
            }
            case "activate": {
              if (!sheetMod.name) {
                trackSkip(
                  `⚠️ Sheet activate: missing "name" in modification; skipping`,
                  `sheet activate (no name)`
                );
                break;
              }
              await activateWorksheet(sheetMod.name, pinnedWorkbookOption);
              trackOperation(`👁️ Activated worksheet "${sheetMod.name}"`);
              break;
            }
            case "move": {
              const move = sheetMod.move;
              if (!move || typeof move.newIndex !== "number") {
                trackSkip(
                  `⚠️ Sheet move: missing "move.newIndex" in modification; skipping`,
                  `sheet move (no newIndex)`
                );
                break;
              }
              const result = await moveSheet(move.newIndex, {
                ...(sheetMod.name ? { worksheetName: sheetMod.name } : {}),
                ...(move.sourceWorkbookName !== undefined
                  ? { sourceWorkbookName: move.sourceWorkbookName }
                  : {}),
                ...(move.targetWorkbookName !== undefined
                  ? { targetWorkbookName: move.targetWorkbookName }
                  : {}),
                ...(move.selectSheet !== undefined
                  ? { selectSheet: move.selectSheet }
                  : {}),
              });
              structuralChangesApplied += 1;
              const crossWorkbook =
                result.originalWorkbookName &&
                result.newWorkbookName &&
                result.originalWorkbookName !== result.newWorkbookName;
              const sheetLabel = result.name || sheetMod.name || "active sheet";
              const indexFragment = `${result.originalIndex} → ${result.newIndex}`;
              trackOperation(
                crossWorkbook
                  ? `↕️ Moved worksheet "${sheetLabel}" from "${result.originalWorkbookName}" to "${result.newWorkbookName}" (index ${indexFragment})`
                  : `↕️ Moved worksheet "${sheetLabel}" (index ${indexFragment})`
              );
              break;
            }
            case "setVisibility": {
              if (
                sheetMod.visibility !== "Visible" &&
                sheetMod.visibility !== "Hidden" &&
                sheetMod.visibility !== "VeryHidden"
              ) {
                const targetLabel = sheetMod.name
                  ? `Sheet "${sheetMod.name}"`
                  : `Active sheet`;
                trackSkip(
                  `⚠️ ${targetLabel} setVisibility: invalid "visibility" (${JSON.stringify(sheetMod.visibility)}); expected "Visible" | "Hidden" | "VeryHidden". Skipping.`,
                  `sheet "${sheetMod.name ?? ""}" setVisibility (invalid visibility)`
                );
                break;
              }
              // `name` is optional (host change 2026-05-12); when omitted
              // the host defaults to the active worksheet.
              await setWorksheetVisibility(
                sheetMod.name,
                sheetMod.visibility,
                pinnedWorkbookOption,
              );
              structuralChangesApplied += 1;
              const visibilityIcon =
                sheetMod.visibility === "Visible"
                  ? "👁️"
                  : sheetMod.visibility === "Hidden"
                  ? "🙈"
                  : "🫥";
              const sheetLabel = sheetMod.name
                ? `"${sheetMod.name}"`
                : `(active)`;
              trackOperation(
                `${visibilityIcon} Sheet ${sheetLabel} visibility → ${sheetMod.visibility}`
              );
              break;
            }
            default: {
              trackSkip(
                `⚠️ Sheet "${sheetMod.name ?? ""}" ${String(
                  (sheetMod as { action?: unknown }).action
                )}: unknown action; skipping`,
                `sheet "${sheetMod.name ?? ""}" action "${String(
                  (sheetMod as { action?: unknown }).action
                )}"`
              );
            }
          }
        } catch (e) {
          // Edit-mode errors must propagate to the outer catch so the entire
          // apply stops cleanly with the actionable user message instead of
          // continuing to fail every subsequent sheet/cell write.
          if (isEditModeError(e)) throw e;
          const msg = e instanceof Error ? e.message : String(e);
          console.warn(
            `[Excel] Intent-bridge sheet ${sheetMod.action} for "${sheetMod.name ?? ""}" failed:`,
            e
          );
          trackFailure(
            `❌ Sheet "${sheetMod.name ?? ""}" ${sheetMod.action} failed: ${msg.slice(0, 100)}`,
            `sheet "${sheetMod.name ?? ""}" ${sheetMod.action}`,
          );
        }
      }
    }

    for (const worksheetMod of modifications.worksheets ?? []) {
      const dispatchedFormulaByAddress = new Map<string, string>();
      // Some bridge intents still interpret bare A1 addresses on the active
      // sheet. Re-activate the planner's target immediately before those
      // writes so a user clicking another tab between steps cannot redirect
      // the next mutation.

      // Freeze panes — applied once per sheet, BEFORE the cell / range
      // writes so subsequent dispatches see the final pane layout. The
      // intent's `options.{worksheetName,workbookName}` are honoured by
      // the host even when other intents (`hideColumns`, `autofit`,
      // etc.) silently no-op on a non-active target. Best-effort — a
      // freeze-pane failure should not abort the surrounding apply.
      if (worksheetMod.freezePanes) {
        const fp = worksheetMod.freezePanes;
        try {
          const optionsOnWire: Record<string, unknown> = {};
          if (fp.row !== undefined) optionsOnWire.row = fp.row;
          if (fp.column !== undefined) optionsOnWire.column = fp.column;
          // Resolve sheet target: explicit per-slot wins, otherwise
          // inherit from the enclosing `worksheetMod.sheetName` so the
          // planner doesn't have to repeat itself.
          const resolvedSheet =
            fp.worksheetName ?? worksheetMod.sheetName;
          if (resolvedSheet !== undefined) {
            optionsOnWire.worksheetName = resolvedSheet;
          }
          if (fp.workbookName !== undefined) {
            optionsOnWire.workbookName = fp.workbookName;
          } else if (useRangeMutationTargeting && pinnedWorkbookName) {
            optionsOnWire.workbookName = pinnedWorkbookName;
          }
          trackOperation(
            `${fp.mode === "Freeze" ? "🧊" : "💧"} ${fp.mode === "Freeze" ? "Freezing" : "Unfreezing"} panes${
              resolvedSheet ? ` on "${resolvedSheet}"` : ""
            }${
              fp.row !== undefined ? ` at row ${fp.row}` : ""
            }${
              fp.column !== undefined ? ` at column ${fp.column}` : ""
            } via FactSet intent`
          );
          await freezeUnfreezePanes(
            fp.mode,
            Object.keys(optionsOnWire).length > 0
              ? (optionsOnWire as Parameters<typeof freezeUnfreezePanes>[1])
              : undefined
          );
          structuralChangesApplied += 1;
        } catch (e) {
          // Edit-mode errors must propagate to the outer catch — same
          // cascade-protection rationale as the cell/range/sheet loops.
          if (isEditModeError(e)) throw e;
          const msg = e instanceof Error ? e.message : String(e);
          console.warn(
            `[Excel] Intent-bridge freezeUnfreezePanes(${fp.mode}) failed:`,
            e
          );
          trackOperation(
            `⚠️ freezePanes(${fp.mode}) failed: ${msg.slice(0, 200)}`
          );
        }
      }

      // Resolve the sheet that this `worksheetMod`'s writes are
      // intended to land on, so it can be threaded into the
      // external-ref `destinationCell` builder when a pinned workbook
      // is in play.
      //
      // Resolution policy:
      //  - If the planner gave us `worksheetMod.sheetName`, use that
      //    verbatim — the planner's explicit choice always wins.
      //  - Otherwise the write is intended for the burst's *current*
      //    sheet. We use the post-reactivation `activeSheetName`
      //    (which the burst-start `activateWorksheet` block sets to
      //    the pinned `sheetHint` after a successful flip), so the
      //    external-ref builder gets a valid sheet on the PINNED
      //    workbook instead of a sheet that might only exist on the
      //    user's currently-focused workbook.
      //  - Empty string falls back to `undefined` so the helper
      //    keeps the legacy "no sheet → bare A1" path, rather than
      //    embedding `''!A1` into the destination.
      const pinnedSheetForRouting =
        worksheetMod.sheetName ?? (activeSheetName || undefined);
      const confirmedContentWrites = contentWriteLedgerEntries(
        confirmedContentWritesBySheet,
        pinnedSheetForRouting,
      );
      const failedContentWrites = contentWriteLedgerEntries(
        failedContentWritesBySheet,
        pinnedSheetForRouting,
      );

      for (const cellMod of worksheetMod.cells ?? []) {
        const editedCellsBeforeCell = editedCells.length;
        try {
          const isNonFinalFactSetWrite =
            nonFinalFactSetCellWrites.has(cellMod);
          const hostCellMod = isNonFinalFactSetWrite
            ? withoutCellNumberFormat(cellMod)
            : cellMod;
          await ensureActiveSheetForWrite(worksheetMod.sheetName);
          // Legacy FactSet TTF interception — single-cell `set` writes that
          // are FDS-bound and include a `ttfQuery` get a TTF round
          // trip first when the gate is open. Tool-mode formulas produced by
          // `generate_formula` intentionally do not carry `ttfQuery`, so they
          // should flow directly through the normal write path. Range-shaped
          // addresses (`A1:B5`) and
          // non-`set` actions (`format` / `clear`) are NOT eligible —
          // TTF is one query per cell. The formula-prefix guard is
          // intentionally local rather than prompt-only: if the model
          // accidentally emits `ttfQuery` on a plain Excel formula or
          // label cell, runtime should ignore it. Anything that doesn't
          // dispatch falls through to the existing write path with the
          // supplied `formula` field intact.
          //
          // `workbookName: pinnedWorkbookName` is threaded through so the
          // TTF write routes to the pinned workbook. `placeApiResponseAtCell`
          // in `ttfCellDispatch.ts` places the resolved formula via
          // `setCellContents`, which builds an external-ref `destinationCell`
          // (`[Book1]Sheet1!A1`) from `workbookName` — a pure background write
          // (no `selectRange`, `selectRange: false` / `displayAlerts: false`
          // defaults). Without the pin, an FDS-bound formula would land on
          // whichever workbook is currently focused, even though every other
          // cell in the burst is pinned correctly.
          const ttfEligible =
            cellMod.action === "set" &&
            typeof cellMod.address === "string" &&
            !cellMod.address.includes(":") &&
            isFdsBoundFormula(cellMod.formula);
          const hasTtfQuery =
            typeof cellMod.ttfQuery === "string" && cellMod.ttfQuery.trim().length > 0;
          if (ttfEligible && hasTtfQuery) {
            const fallbackFormatHint = inferFactSetFormatHint({
              formula: cellMod.formula,
            });
            const requestedNumberFormat = extractNumberFormatString(
              hostCellMod.format,
            );
            trackOperation(
              `TTF candidate ${cellMod.address}: query=yes, gate=${ttfGateStatus}, ` +
                `attemptsRemaining=${ttfRunCtx.attemptsRemaining ?? "unbounded"}, ` +
                `budgetRemainingMs=${ttfRunCtx.budgetRemainingMs ?? "unbounded"}`,
              cellMod.address,
            );
            const outcome = await tryDispatchCellViaTtf(
              {
                ttfQuery: cellMod.ttfQuery,
                address: cellMod.address,
                sheetName: pinnedSheetForRouting,
                ...(pinnedWorkbookName ? { workbookName: pinnedWorkbookName } : {}),
                ...(requestedNumberFormat
                  ? {
                      numberFormat: requestedNumberFormat,
                    }
                  : {}),
                ...(isNonFinalFactSetWrite
                  ? { suppressInferredNumberFormat: true }
                  : {}),
                runCtx: ttfRunCtx,
                signal: options.signal,
              },
              {
                logEvent: (event, fields = {}) => {
                  if (
                    event === "cell_request_started" ||
                    event === "cell_response_received" ||
                    event === "cell_response_adapted" ||
                    event === "cell_succeeded" ||
                    event === "cell_fell_back"
                  ) {
                    trackOperation(
                      `TTF ${event} ${cellMod.address}: ${JSON.stringify(fields)}`,
                      cellMod.address,
                    );
                  }
                },
                // Surface real TTF failures in the user-visible
                // activity log. The dispatcher already filters
                // expected outcomes (gate closed, no query,
                // cancelled) so this only fires when TTF actually
                // attempted and failed. The fallback formula still
                // lands through the regular write path below.
                onFallback: (entry) => {
                  trackOperation(
                    `🔄 TTF unavailable for cell ${entry.address} — using supplied formula. (${entry.category})`,
                  );
                },
              },
            );
            throwIfAborted();
            if (outcome.outcome === "dispatched") {
              // Preserve the formula that actually landed. The executor keeps
              // this modification object as its post-success provenance, so
              // recording the fallback formula here would make later format
              // enforcement reason about content that is not in the cell.
              if (outcome.dispatchedFormula) {
                cellMod.formula = outcome.dispatchedFormula;
                dispatchedFormulaByAddress.set(
                  cellMod.address.replace(/\$/g, "").toUpperCase(),
                  outcome.dispatchedFormula,
                );
              }
              confirmedContentWrites.push(cellMod.address);
              editedCells.push(cellMod.address);
              trackOperation(
                `📐 ${cellMod.address} = ${
                  outcome.dispatchedFormula ?? "(FactSet TTF formula)"
                } (via FactSet TTF)`,
                cellMod.address
              );
              const landedSemantics = inferFactSetFormatHint({
                formula: outcome.dispatchedFormula,
              }).semantics;
              const resetInheritedReturnFormat =
                !outcome.dispatchedNumberFormat &&
                !requestedNumberFormat &&
                fallbackFormatHint.semantics === "percentage_points" &&
                landedSemantics !== "percentage_points";
              if (hostCellMod.format || resetInheritedReturnFormat) {
                const correctedFormat = outcome.dispatchedNumberFormat
                  ? {
                      ...(hostCellMod.format ?? {}),
                      numberFormat: {
                        format: outcome.dispatchedNumberFormat,
                      },
                    }
                  : resetInheritedReturnFormat
                    ? {
                        ...(hostCellMod.format ?? {}),
                        numberFormat: { format: "General" },
                      }
                    : hostCellMod.format!;
                await applyFormatToRangeViaBridge(
                  cellMod.address,
                  correctedFormat,
                  trackOperation,
                  pinnedSheetForRouting,
                  pinnedWorkbookName,
                  resetInheritedReturnFormat,
                );
              }
              continue;
            }
            trackOperation(
              `TTF fell through for ${cellMod.address}: ${outcome.reason ?? "unknown"} — using supplied formula`,
              cellMod.address,
            );
          }
          await applyCellModificationViaIntents(
            isNonFinalFactSetWrite
              ? hostCellMod
              : normalizeDirectFactSetCellFormat(hostCellMod),
            trackOperation,
            trackSkip,
            editedCells,
            pinnedSheetForRouting,
            pinnedWorkbookName,
          );
          if (
            cellMod.action === "set" &&
            (cellMod.formula !== undefined || cellMod.value !== undefined)
          ) {
            confirmedContentWrites.push(cellMod.address);
          }
        } catch (e) {
          // Operation logging and Office/bridge helpers may queue optimistic
          // address entries. A rejected host write must not leak them.
          editedCells.splice(editedCellsBeforeCell);
          // Edit-mode errors must propagate so the outer catch can stop the
          // entire apply with a single clean message — otherwise every
          // remaining cell would log its own warn-skip and the user sees a
          // wall of cryptic per-cell failures (the original L5 cascade).
          if (isEditModeError(e) || isExcelAbortError(e)) throw e;
          const msg = e instanceof Error ? e.message : String(e);
          console.warn(`[Excel] Intent-bridge cell modification at ${cellMod.address} failed:`, e);
          trackFailure(
            `❌ Cell ${cellMod.address} failed: ${msg.slice(0, 200)}`,
            `cell ${cellMod.address} action "${cellMod.action}"`
          );
          failedContentWrites.push(cellMod.address);
        }
      }

      for (const rangeMod of worksheetMod.ranges ?? []) {
        const editedCellsBeforeRange = editedCells.length;
        try {
          const formatGuard = getFactSetFormatGuard(rangeMod);
          if (formatGuard?.requiresSuccessfulWrite) {
            const dispatchedFormula = dispatchedFormulaByAddress.get(
              rangeMod.address.replace(/\$/g, "").toUpperCase(),
            );
            const landedFormula =
              dispatchedFormula ?? formatGuard.expectedFormula;
            const landedSemantics =
              typeof landedFormula === "string"
                ? inferFactSetFormatHint({ formula: landedFormula }).semantics
                : "unknown";
            if (
              !confirmedContentWrites.some((address) =>
                excelRangeContainsAddress(address, rangeMod.address),
              ) ||
              failedContentWrites.some((address) =>
                excelRangeContainsAddress(address, rangeMod.address),
              ) ||
              (formatGuard.mode === "percentage_points" &&
                landedSemantics !== "percentage_points") ||
              (formatGuard.mode === "reset_stale_percentage_points" &&
                landedSemantics === "percentage_points")
            ) {
              trackOperation(
                `Skipped internal FactSet percent guard for ${rangeMod.address} because its source formula was not confirmed.`,
              );
              continue;
            }
          }
          if (rangeMod.action !== "select") {
            await ensureActiveSheetForWrite(worksheetMod.sheetName);
          }
          await applyRangeModificationViaIntents(
            rangeMod,
            trackOperation,
            trackSkip,
            editedCells,
            pinnedSheetForRouting,
            pinnedWorkbookName,
          );
          if (
            rangeMod.action === "fill" ||
            (rangeMod.action === "clear" &&
              (rangeMod.clear?.clearType ?? "All") === "Contents")
          ) {
            confirmedContentWrites.push(rangeMod.address);
          } else if (rangeMod.action === "moveRange") {
            const movedDestination = excelMoveDestinationRange(
              rangeMod.address,
              rangeMod.moveRange?.destination,
            );
            if (movedDestination) {
              const destinationWrites = contentWriteLedgerEntries(
                confirmedContentWritesBySheet,
                rangeMod.moveRange?.destinationSheetName?.trim() ||
                  pinnedSheetForRouting,
              );
              destinationWrites.push(movedDestination);
            }
          }
        } catch (e) {
          editedCells.splice(editedCellsBeforeRange);
          // Edit-mode errors must propagate so the outer catch can stop the
          // entire apply with a single clean message — the same cascade
          // protection as the cell loop.
          if (isEditModeError(e)) throw e;
          const msg = e instanceof Error ? e.message : String(e);
          console.warn(`[Excel] Intent-bridge range modification at ${rangeMod.address} failed:`, e);
          // Structural / shift-style failures (`insertRange`, `deleteRange`)
          // are non-recoverable: the host did not perform the operation, the
          // workbook is in the pre-state, and the verifier will (correctly)
          // flag the result as incomplete and try to re-fire — which the
          // fingerprint guard will refuse, so the user would see a step
          // marked ✅ that did nothing. Re-throw so the step fails honestly.
          // For `fill` / `autofit` / `clear` / `format`, keep the existing
          // warn-and-continue behaviour: a single bad cell shouldn't abort a
          // multi-range write.
          const isStructural =
            rangeMod.action === "insertRange" || rangeMod.action === "deleteRange";
          trackFailure(
            `❌ Range ${rangeMod.address} failed: ${msg.slice(0, 200)}`,
            `range ${rangeMod.address} action "${rangeMod.action}"`
          );
          failedContentWrites.push(rangeMod.address);
          if (isStructural) {
            throw e instanceof Error
              ? e
              : new Error(`Range ${rangeMod.address} (${rangeMod.action}) failed: ${msg}`);
          }
        }
      }

      if (worksheetMod.tables && worksheetMod.tables.length > 0) {
        trackSkip(
          `⚠️ Table operations (${worksheetMod.tables.length}) not supported by intent bridge yet; skipping`,
          `tables (${worksheetMod.tables.length}) on ${worksheetMod.sheetName ?? "active sheet"}`
        );
      }
      // Chart insertion via the intent bridge.
      //
      // Two graph paths are intentionally separate:
      //   1. Native Excel graphs in `charts[]` route through
      //      `factset.Spreadsheet.Charting.*`.
      //   2. FactSet ActiveGraphs route through `activeGraphs[]` below.
      //
      // Anything in `charts[]` without a source range has nothing to feed
      // Excel's chart engine, so skip honestly.
      //
      // Until 2026-05-04 path #2 didn't exist — the chart was either
      // routed via AddActiveGraph (FactSet styling only) or warn-skipped
      // with "no `factset.Spreadsheet.InsertChart` intent".
      // `Charting.AddExcelChart` closes that gap for raw-data charts.
      if (worksheetMod.charts && worksheetMod.charts.length > 0) {
        for (const chartMod of worksheetMod.charts) {
          // `update` routes to `Charting.UpdateExcelChart`. Today the
          // planner only emits chart-level property changes (chartType,
          // title); series writes belong to the not-yet-wired
          // `*SeriesInChart` family. `delete` is still skipped — pending
          // FactSet's pre-announced shape-ops intent family
          // (`#open-shape-ops` in `missing-intents-v0.md`).
          if (chartMod.action === "update") {
            const targetChartIdx = chartMod.chartIndex ?? 0;
            const plannerType = chartMod.type?.toLowerCase();
            const wireChartType =
              chartMod.chartType ||
              (plannerType ? PLANNER_CHART_TYPE_TO_WIRE[plannerType] : undefined);
            const newTitle = chartMod.title;
            const description = newTitle
              ? `chart #${targetChartIdx} "${newTitle}"`
              : `chart #${targetChartIdx}`;
            const chartInfo: Parameters<typeof updateExcelChart>[0]["chartInfo"] = {
              ...(chartMod.chartInfo ?? {}),
              ...(wireChartType ? { chartType: wireChartType } : {}),
              ...(newTitle !== undefined
                ? { title: { hasTitle: true, text: newTitle } }
                : {}),
            };
            if (Object.keys(chartInfo).length === 0) {
              trackSkip(
                `⚠️ ${description}: chart "update" had no fields to apply (set \`type\` or \`title\`); skipping`,
                `${description} empty update`
              );
              continue;
            }
            const resolvedWorkbookName = chartMod.workbookName ?? activeWorkbookName;
            const resolvedSheetRef = resolveChartSheetRef(
              chartMod.sheetRef,
              worksheetMod.sheetName,
            );
            try {
              const result = await updateExcelChart({
                chartInfo,
                ...(resolvedWorkbookName !== undefined
                  ? { workbookName: resolvedWorkbookName }
                  : {}),
                ...(resolvedSheetRef ? { sheetRef: resolvedSheetRef } : {}),
                chartIndex: targetChartIdx,
              });
              if (result.success !== true) {
                const hostDetails = result.warnings.length
                  ? `; warnings: ${result.warnings.join("; ")}`
                  : "";
                throw new Error(`host reported success=false${hostDetails}`);
              }
              const summary = result.appliedProperties.length
                ? ` (applied ${result.appliedProperties.join(", ")})`
                : "";
              const warnSuffix = result.warnings.length
                ? ` ⚠️ host warnings: ${result.warnings.join("; ")}`
                : "";
              trackOperation(
                `📈 Updated ${description} via factset.Spreadsheet.Charting.UpdateExcelChart${summary}${warnSuffix}`
              );
              structuralChangesApplied += 1;
            } catch (e) {
              if (isEditModeError(e)) throw e;
              const msg = e instanceof Error ? e.message : String(e);
              console.warn(
                `[Excel] Charting.UpdateExcelChart for ${description} failed:`,
                e
              );
              trackSkip(
                `⚠️ ${description}: UpdateExcelChart failed: ${msg.slice(0, 200)}`,
                `${description} via Charting.UpdateExcelChart`
              );
            }
            continue;
          }
          if (chartMod.action === "addSeries") {
            const targetChartIdx = chartMod.chartIndex ?? 0;
            const series = chartMod.series;
            if (!series || typeof series.values !== "string" || series.values.length === 0) {
              trackSkip(
                `⚠️ chart #${targetChartIdx} addSeries requires \`series.values\` (A1 range); skipping`,
                `chart #${targetChartIdx} addSeries missing values`
              );
              continue;
            }
            const wireSeriesBody = chartSeriesToWire(series);
            const resolvedWorkbookName = chartMod.workbookName ?? activeWorkbookName;
            const resolvedSheetRef = resolveChartSheetRef(
              chartMod.sheetRef,
              worksheetMod.sheetName,
            );
            const description = series.name
              ? `series "${series.name}" on chart #${targetChartIdx}`
              : `series on chart #${targetChartIdx}`;
            const addSeriesOptions: Parameters<typeof addSeriesToChart>[0] = {
              series: wireSeriesBody as Parameters<typeof addSeriesToChart>[0]["series"],
              ...(resolvedWorkbookName !== undefined
                ? { workbookName: resolvedWorkbookName }
                : {}),
              ...(resolvedSheetRef ? { sheetRef: resolvedSheetRef } : {}),
              chartIndex: targetChartIdx,
            };
            try {
              const result = await addSeriesToChart(addSeriesOptions);
              if (result.success !== true) {
                throw new Error("host reported success=false");
              }
              trackOperation(
                `📈 Added ${description} (host index ${result.seriesIndex}) via factset.Spreadsheet.Charting.AddSeriesToChart`
              );
              structuralChangesApplied += 1;
            } catch (e) {
              if (isEditModeError(e)) throw e;
              const msg = e instanceof Error ? e.message : String(e);
              console.warn(
                `[Excel] Charting.AddSeriesToChart for ${description} failed:`,
                { error: e, payload: addSeriesOptions }
              );
              trackSkip(
                `⚠️ ${description}: AddSeriesToChart failed: ${msg.slice(0, 200)}`,
                `${description} via Charting.AddSeriesToChart`
              );
            }
            continue;
          }
          if (chartMod.action === "updateSeries") {
            const targetChartIdx = chartMod.chartIndex ?? 0;
            const seriesIndex = chartMod.seriesIndex;
            const series = chartMod.series;
            if (typeof seriesIndex !== "number") {
              trackSkip(
                `⚠️ chart #${targetChartIdx} updateSeries requires \`seriesIndex\` (0-based number); skipping`,
                `chart #${targetChartIdx} updateSeries missing seriesIndex`
              );
              continue;
            }
            if (!series || typeof series !== "object") {
              trackSkip(
                `⚠️ chart #${targetChartIdx} series #${seriesIndex} updateSeries requires a \`series\` body with at least one field; skipping`,
                `chart #${targetChartIdx} series #${seriesIndex} updateSeries empty body`
              );
              continue;
            }
            // Reuse the same friendly/host-shaped translation as addSeries —
            // every wire-side field is optional on update, and the host applies
            // only the keys actually present.
            const wireSeriesBody = chartSeriesToWire(series);
            if (Object.keys(wireSeriesBody).length === 0) {
              trackSkip(
                `⚠️ chart #${targetChartIdx} series #${seriesIndex} updateSeries had no recognised fields (set values / categories / name / chartType / axisGroup / fill / line); skipping`,
                `chart #${targetChartIdx} series #${seriesIndex} updateSeries empty after translation`
              );
              continue;
            }
            const description = series.name
              ? `series #${seriesIndex} "${series.name}" on chart #${targetChartIdx}`
              : `series #${seriesIndex} on chart #${targetChartIdx}`;
            const resolvedWorkbookName = chartMod.workbookName ?? activeWorkbookName;
            const resolvedSheetRef = resolveChartSheetRef(
              chartMod.sheetRef,
              worksheetMod.sheetName,
            );
            const updateSeriesOptions: Parameters<typeof updateSeriesInChart>[0] = {
              seriesIndex,
              series: wireSeriesBody,
              ...(resolvedWorkbookName !== undefined
                ? { workbookName: resolvedWorkbookName }
                : {}),
              ...(resolvedSheetRef ? { sheetRef: resolvedSheetRef } : {}),
              chartIndex: targetChartIdx,
            };
            try {
              const result = await updateSeriesInChart(updateSeriesOptions);
              if (result.success !== true) {
                throw new Error("host reported success=false");
              }
              const summary = result.appliedProperties.length
                ? ` (applied ${result.appliedProperties.join(", ")})`
                : "";
              trackOperation(
                `📈 Updated ${description} via factset.Spreadsheet.Charting.UpdateSeriesInChart${summary}`
              );
              structuralChangesApplied += 1;
            } catch (e) {
              if (isEditModeError(e)) throw e;
              const msg = e instanceof Error ? e.message : String(e);
              console.warn(
                `[Excel] Charting.UpdateSeriesInChart for ${description} failed:`,
                { error: e, payload: updateSeriesOptions }
              );
              trackSkip(
                `⚠️ ${description}: UpdateSeriesInChart failed: ${msg.slice(0, 200)}`,
                `${description} via Charting.UpdateSeriesInChart`
              );
            }
            continue;
          }
          if (chartMod.action === "removeSeries") {
            const targetChartIdx = chartMod.chartIndex ?? 0;
            const seriesIndex = chartMod.seriesIndex;
            if (typeof seriesIndex !== "number") {
              trackSkip(
                `⚠️ chart #${targetChartIdx} removeSeries requires \`seriesIndex\` (0-based number); skipping`,
                `chart #${targetChartIdx} removeSeries missing seriesIndex`
              );
              continue;
            }
            const description = `series #${seriesIndex} on chart #${targetChartIdx}`;
            const resolvedWorkbookName = chartMod.workbookName ?? activeWorkbookName;
            const resolvedSheetRef = resolveChartSheetRef(
              chartMod.sheetRef,
              worksheetMod.sheetName,
            );
            try {
              await removeSeriesFromChart({
                seriesIndex,
                ...(resolvedWorkbookName !== undefined
                  ? { workbookName: resolvedWorkbookName }
                  : {}),
                ...(resolvedSheetRef ? { sheetRef: resolvedSheetRef } : {}),
                chartIndex: targetChartIdx,
              });
              trackOperation(
                `📈 Removed ${description} via factset.Spreadsheet.Charting.RemoveSeriesFromChart`
              );
              structuralChangesApplied += 1;
            } catch (e) {
              if (isEditModeError(e)) throw e;
              const msg = e instanceof Error ? e.message : String(e);
              console.warn(
                `[Excel] Charting.RemoveSeriesFromChart for ${description} failed:`,
                e
              );
              trackSkip(
                `⚠️ ${description}: RemoveSeriesFromChart failed: ${msg.slice(0, 200)}`,
                `${description} via Charting.RemoveSeriesFromChart`
              );
            }
            continue;
          }
          if (chartMod.action !== "create") {
            trackSkip(
              `⚠️ Chart action "${chartMod.action}" not supported by intent bridge yet; skipping`,
              `chart ${chartMod.action} on ${worksheetMod.sheetName ?? "active sheet"}`
            );
            continue;
          }
          // Native Excel chart (column/line/bar/...). Requires a source range —
          // without one we have nothing to bind the chart to and the host
          // would reject the call anyway, so skip honestly. We pin
          // `plannerType` (defaulting to "column", matching the create
          // path's default) at the top so the skip and the success
          // descriptions stay in sync.
          const plannerType = (chartMod.type || "column").toLowerCase();
          const sourceRange = chartMod.sourceRange ?? chartMod.dataRange;
          if (!sourceRange) {
            const skipTitle = chartMod.title ?? chartMod.chartInfo?.title?.text;
            const skipDesc = skipTitle
              ? `${plannerType} chart "${skipTitle}"`
              : `${plannerType} chart`;
            trackSkip(
              `⚠️ ${skipDesc}: chart "create" requires \`sourceRange\` / \`dataRange\`; skipping`,
              `${skipDesc} missing sourceRange`
            );
            continue;
          }
          const wireChartType =
            chartMod.chartType ||
            chartMod.chartInfo?.chartType ||
            PLANNER_CHART_TYPE_TO_WIRE[plannerType] ||
            "xlColumnClustered";
          const pointPosition = chartPositionToPoints(chartMod.position);
          const title = chartMod.title ?? chartMod.chartInfo?.title?.text;
          const description = title
            ? `${plannerType} chart "${title}"`
            : `${plannerType} chart`;
          const chartInfo = sanitizeAddExcelChartInfo({
            ...(chartMod.chartInfo ?? {}),
            ...(title && !chartMod.chartInfo?.title
              ? { title: { hasTitle: true, text: title } }
              : {}),
          });
          const resolvedWorkbookName = chartMod.workbookName ?? activeWorkbookName;
          const resolvedSheetRef = resolveChartSheetRef(
            chartMod.sheetRef,
            worksheetMod.sheetName,
          );
          const addChartOptions: Parameters<typeof addExcelChart>[0] = {
            sourceRange,
            chartType: wireChartType,
            ...(resolvedWorkbookName !== undefined
              ? { workbookName: resolvedWorkbookName }
              : {}),
            ...(resolvedSheetRef ? { sheetRef: resolvedSheetRef } : {}),
            chartIndex: chartMod.chartIndex ?? 0,
            ...(pointPosition ? { position: pointPosition } : {}),
            ...(title
              ? {
                  chartTitle: title,
                }
              : {}),
            ...(chartInfo && Object.keys(chartInfo).length > 0 ? { chartInfo } : {}),
          };
          try {
            const result = await addExcelChart(addChartOptions);
            const placement = chartMod.position?.startCell
              ? ` at ${chartMod.position.startCell}`
              : "";
            const hostName = result.name ? ` as "${result.name}"` : "";
            trackOperation(
              `📈 Inserted ${description}${placement}${hostName} via factset.Spreadsheet.Charting.AddExcelChart`,
              sourceRange
            );
            editedCells.push(sourceRange);
            structuralChangesApplied += 1;
          } catch (e) {
            if (isEditModeError(e)) throw e;
            const msg = e instanceof Error ? e.message : String(e);
            console.warn(
              `[Excel] Charting.AddExcelChart for ${description} failed:`,
              { error: e, payload: addChartOptions }
            );
            throw new Error(
              `${description}: AddExcelChart failed: ${msg}; payload: ${JSON.stringify(addChartOptions)}`
            );
          }
        }
      }
      // FactSet ActiveGraph (FDSChart OLE) operations. Two action
      // variants, dispatched independently:
      //   - "create"          → factset.Spreadsheet.AddActiveGraph
      //   - "update-selected" → factset.Charting.SetActiveGraphXml
      //
      // Distinct from the `charts[]` collection above (native Excel
      // charts). Run AFTER the chart loop so a worksheet that emits
      // both gets the native chart placed first, then the OLE drop on
      // top.
      //
      // Sheet targeting: AddActiveGraph operates on the **active sheet**
      // of the active workbook — the host doesn't accept a sheet-name
      // parameter on this intent — so when `worksheetMod.sheetName` names
      // a non-active sheet we activate it first so the drop lands where
      // the planner asked for.
      //
      // **Planner-emittable actions are limited to `"create"` today.**
      // The previous `"update-selected"` literal (which dispatched to
      // `factset.Charting.SetActiveGraphXml`) was removed from the
      // planner-emittable union — the surface is too immature for
      // round-trip mutations. The branch is retained structurally as a
      // defensive `trackSkip` so any caller that bypasses the type
      // narrowing produces a clean, honest skip rather than a silent
      // execution. The dispatcher / wrapper remain wired in
      // `excelHostOps.ts` / `spreadsheetIntents.ts` for dev-tools and
      // explicit non-agent flows.
      if (worksheetMod.activeGraphs && worksheetMod.activeGraphs.length > 0) {
        // `action` is narrowed to "create" at the type level but we
        // accept any string at runtime so a stale planner / smuggled
        // payload still hits the defensive guards below.
        const widenedAction = (ag: ActiveGraphModification): string =>
          ag.action as unknown as string;
        for (const ag of worksheetMod.activeGraphs) {
          const action = widenedAction(ag);
          if (action !== "create" && action !== "update-selected") {
            trackSkip(
              `⚠️ activeGraphs action "${action}" not supported by intent bridge yet; skipping`,
              `activeGraph ${action} on ${worksheetMod.sheetName ?? "active sheet"}`
            );
            continue;
          }
          if (typeof ag.activeGraphContent !== "string" || ag.activeGraphContent.length === 0) {
            trackSkip(
              `⚠️ activeGraph "${action}" requires a non-empty \`activeGraphContent\`; skipping`,
              `activeGraph ${action} on ${worksheetMod.sheetName ?? "active sheet"}`
            );
            continue;
          }
          const title = ag.title || "ActiveGraph";
          if (action === "update-selected") {
            // Belt-and-suspenders. The planner-emittable
            // `ActiveGraphModification.action` union no longer contains
            // `"update-selected"` (the underlying
            // `factset.Charting.SetActiveGraphXml` surface is too
            // immature for round-trip planner use); this branch is
            // only reachable if a non-agent caller smuggles the
            // literal past the type narrowing. Skip cleanly and let
            // the operator know — the dispatcher is still available
            // for direct invocation via `excelHostOps.setActiveGraphXml`
            // if needed.
            trackSkip(
              `⚠️ activeGraph "update-selected" not exposed to the planner; skipping "${title}" (use the dev-tools dialog or call the dispatcher directly)`,
              `activeGraph "${title}" via SetActiveGraphXml (planner-hidden)`
            );
            continue;
          }
          const coords = ag.position
            ? {
                ...(ag.position.top !== undefined ? { top: ag.position.top } : {}),
                ...(ag.position.left !== undefined ? { left: ag.position.left } : {}),
                ...(ag.position.width !== undefined ? { width: ag.position.width } : {}),
                ...(ag.position.height !== undefined ? { height: ag.position.height } : {}),
              }
            : undefined;
          try {
            await ensureActiveSheetForWrite(worksheetMod.sheetName);
            // Active-sheet-only intent (see the chart-mod path above): under
            // background targeting the pin isn't active, so route through the
            // foreground coordinator to briefly bring it forward and restore
            // the user's focus afterwards.
            await runForegroundOnlyOnPin(
              pinnedWorkbookName,
              worksheetMod.sheetName ?? activeSheetName,
              () => addActiveGraph(ag.activeGraphContent, coords),
              { signal: options.signal, contextLabel: `AddActiveGraph "${title}"` },
            );
            trackOperation(
              `📊 Inserted "${title}" via factset.Spreadsheet.AddActiveGraph`
            );
          } catch (e) {
            if (isEditModeError(e)) throw e;
            const msg = e instanceof Error ? e.message : String(e);
            console.warn(
              `[Excel] AddActiveGraph for "${title}" failed:`,
              e
            );
            trackSkip(
              `⚠️ AddActiveGraph for "${title}" failed: ${msg.slice(0, 200)}`,
              `activeGraph "${title}" via AddActiveGraph`
            );
          }
        }
      }
      // Generic shape operations (delete / modify / select). Closes
      // the long-standing chart-action gap from §4.3 of
      // `missing-intents-v0.md`. Batch `modify` entries into a single
      // `ModifyShapes` call when possible; `delete` and `select` each
      // dispatch through their own host intents.
      //
      // The returned count is rolled into `structuralChangesApplied`
      // so a shapes-only step survives the no-op guard below — without
      // this, a "delete shape #42" step that succeeded but had any
      // sibling warn-skip in `skippedActions` would get misreported
      // as a no-op.
      const shapeMods = worksheetMod.shapes;
      if (shapeMods && shapeMods.length > 0) {
        // Shape ops resolve targets by numeric shape ID. Per f2_docs the host
        // supports `workbookName` on DeleteShape / ModifyShapes / SelectShapes,
        // but our wrappers don't expose it for delete/modify yet (SelectShapes
        // additionally *activates* the workbook + sheet). So under background
        // targeting — where the pin isn't active — an un-pinned shape op would
        // look up IDs on the user's active workbook and either mis-hit or skip.
        // Run the whole shape burst with the pin briefly fronted via the
        // foreground coordinator (serialised + focus-restored): the ops then
        // resolve against the pin, preserving output parity, and the user's
        // view is put back afterwards. When targeting is off the pin is already
        // active and the helper runs inline. (Future: thread `workbookName`
        // into the delete/modify wrappers for a true background path — see
        // `missing-intents-v0.md`.)
        structuralChangesApplied += await runForegroundOnlyOnPin(
          pinnedWorkbookName,
          worksheetMod.sheetName ?? activeSheetName,
          () =>
            applyShapeModificationsViaIntents(
              shapeMods,
              worksheetMod.sheetName,
              trackOperation,
              trackSkip
            ),
          { signal: options.signal, contextLabel: "shape modifications" },
        );
      }
      if (worksheetMod.logos && worksheetMod.logos.length > 0) {
        for (const logoMod of worksheetMod.logos) {
          try {
            await ensureActiveSheetForWrite(worksheetMod.sheetName);
            await applyLogoModificationViaIntents(
              logoMod,
              trackOperation,
              worksheetMod.sheetName,
              pinnedWorkbookName
            );
          } catch (e) {
            if (isEditModeError(e)) throw e;
            const msg = e instanceof Error ? e.message : String(e);
            console.warn(
              `[Excel] Intent-bridge logo modification for "${logoMod.companyName}" failed:`,
              e
            );
            trackOperation(
              `⚠️ Logo "${logoMod.companyName}" failed: ${msg.slice(0, 100)}`
            );
          }
        }
      }
    }

    // Explicit note / threaded-comment mutations. These live outside
    // worksheets[] to keep them distinct from the disabled legacy
    // `CellModification.comment` field, but still participate in the same
    // host-ready, workbook-pinning, undo, failure, and no-op accounting.
    for (const annotation of modifications.annotations ?? []) {
      throwIfAborted();
      const isGeneratedCitationNote =
        annotation.kind === "note" &&
        annotation.action === "set" &&
        annotation.origin === "citation";
      const sheetName = annotation.sheetName.trim();
      const addresses =
        annotation.action === "set"
          ? [annotation.address.trim()]
          : annotation.addresses.map((address) => address.trim());
      const referenceAddresses: RangeAddress[] = addresses.map((address) => ({
        address,
        referenceType: "absolute",
        worksheetName: sheetName,
        ...(useRangeMutationTargeting && pinnedWorkbookName
          ? { workbookName: pinnedWorkbookName }
          : {}),
      }));
      const targetLabel = `${sheetName}!${addresses.join(", ")}`;
      if (isGeneratedCitationNote && citationNoteFailures.length > 0) {
        console.warn(
          `[Excel] Generated citation Note skipped after an earlier attachment failure for ${targetLabel}`,
        );
        if (annotation.replaceExisting) {
          try {
            await ensureActiveSheetForWrite(sheetName);
            await deleteCellNote(referenceAddresses);
          } catch (cleanupError) {
            console.warn(
              `[Excel] Stale generated citation Note cleanup failed for ${targetLabel}:`,
              cleanupError,
            );
          }
        }
        continue;
      }
      try {
        await ensureActiveSheetForWrite(sheetName);
        if (annotation.action === "set") {
          const request = {
            referenceAddress: referenceAddresses[0],
            text: annotation.text,
            ...(annotation.replaceExisting !== undefined
              ? { replaceExisting: annotation.replaceExisting }
              : {}),
            ...(annotation.kind === "note" &&
                annotation.richTextRuns !== undefined
              ? { richTextRuns: annotation.richTextRuns }
              : {}),
          };
          if (annotation.kind === "comment") {
            await setCellComment(request);
          } else {
            await setCellNote(request);
          }
          trackOperation(
            `${annotation.kind === "comment" ? "💬" : "📝"} Set ${annotation.kind} on ${targetLabel}`,
            addresses[0],
          );
          editedCells.push(...addresses);
        } else {
          if (annotation.kind === "comment") {
            await deleteCellComment(referenceAddresses);
          } else {
            await deleteCellNote(referenceAddresses);
          }
          trackOperation(
            `🗑️ Deleted ${annotation.kind}${addresses.length === 1 ? "" : "s"} from ${targetLabel}`,
          );
          // Delete is idempotent: a successful host acknowledgement counts as
          // an applied mutation even when a target had no annotation.
          editedCells.push(...addresses);
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : String(error);
        if (isGeneratedCitationNote) {
          citationNoteFailures.push(
            `note set on ${targetLabel}: ${message.slice(0, 200)}`,
          );
          console.warn(
            `[Excel] Generated citation Note attachment failed for ${targetLabel}; value writes remain successful:`,
            error,
          );
          if (annotation.replaceExisting) {
            try {
              await deleteCellNote(referenceAddresses);
            } catch (cleanupError) {
              console.warn(
                `[Excel] Stale generated citation Note cleanup failed for ${targetLabel}:`,
                cleanupError,
              );
            }
          }
          continue;
        }
        if (isEditModeError(error)) throw error;
        console.warn(
          `[Excel] Intent-bridge ${annotation.kind} ${annotation.action} failed for ${targetLabel}:`,
          error,
        );
        trackFailure(
          `❌ ${annotation.kind} ${annotation.action} failed for ${targetLabel}: ${message.slice(0, 200)}`,
          `${annotation.kind} ${annotation.action} on ${targetLabel}`,
        );
      }
    }

    // Workbook-level operations (add/copy/insert workbooks). Run after the
    // per-sheet writes so they don't race against in-flight cell mutations
    // on the active sheet, and outside the per-worksheet loop because they
    // aren't tied to a single `WorksheetModification`. `refreshOperationContext`
    // is hoisted above the try block so the catch returns can still
    // forward any planner-deferred refresh / recalc flags stamped here.
    for (const op of modifications.workbookOperations ?? []) {
      try {
        await applyWorkbookOperationViaIntents(
          op,
          trackOperation,
          trackSkip,
          refreshOperationContext,
          pinnedWorkbookName,
          activeSheetName,
        );
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        console.warn(
          `[Excel] Intent-bridge workbook operation "${op.action}" failed:`,
          e
        );
        // Workbook operations the planner emitted are user-requested
        // primary actions (refresh, recalc, add/copy/insert workbook) —
        // not best-effort side nudges. A host-side failure means the
        // user did NOT get what they asked for, so fail the step
        // honestly instead of warn-and-continue. The internal post-
        // write recalc nudge below stays best-effort because it's
        // ours, not the user's. Symmetric with the range-mod fix for
        // `insertRange` / `deleteRange`.
        trackOperation(
          `❌ Workbook op "${op.action}" failed: ${msg.slice(0, 200)}`
        );
        throw e instanceof Error
          ? e
          : new Error(`Workbook operation "${op.action}" failed: ${msg}`);
      }
    }

    // Per-burst recalc: force a full local recalc so the burst is
    // observable to the user (and to the verifier read-back at the top
    // of the next step) before we exit the manual-calc bracket and
    // return.
    //
    // Gated on `appliedFormulaWrites` alone, NOT `calcModeFlipped`.
    // `calcModeFlipped` is false when `SetCalculationMode("manual")`
    // failed (e.g. transient host error or a host that doesn't support
    // the intent). If the workbook happens to be in manual mode in
    // either of those cases, writes did NOT auto-recalc, and skipping
    // the per-burst recalc would leak stale formula values into the
    // next step's verifier read. `ApplicationCalculate` is idempotent —
    // calling it after live auto-recalc in the automatic-mode happy
    // path is essentially free, so we err on the side of correctness.
    // Non-formula bursts (formatting, sheet CRUD, plain values) skip
    // the recalc entirely.
    //
    // Best-effort — never fail the apply on a recalc trigger error.
    // The trailing `finalizeRefreshAndRecalc` in `ExcelApp.tsx` is the
    // safety net for any FDS / explicit-recalc cases this misses.
    if (appliedFormulaWrites) {
      try {
        await applicationCalculate("full");
      } catch (e) {
        console.warn(
          "[Excel] Per-burst ApplicationCalculate(full) failed; the trailing recalc (if any) is the safety net:",
          e
        );
      }
    }

    // Detect "step did nothing" no-ops: the planner asked us to do
    // something, but every action the bridge could see was either warn-
    // skipped (unwired intent) or a no-payload no-op, and zero cells
    // ended up edited and zero structural changes were applied. This used
    // to silently report success, which meant the user saw a green ✅ on
    // a step that didn't run. Now we surface it honestly so the executor
    // can fail the step and the user can rephrase or pick a different
    // verb. We only fail when *all* three conditions hold — a build with
    // 50 successful fills + one ignorable `format` warn-skip on a cell
    // still succeeds, and a step that performed a structural change
    // (sheet CRUD, hide cols/rows, image insert) still succeeds even
    // when no cell values changed — `structuralChangesApplied` covers
    // those. We just rewrite the message below to reflect what really
    // happened.
    if (
      editedCells.length === 0 &&
      structuralChangesApplied === 0 &&
      skippedActions.length > 0 &&
      failedActions.length === 0
    ) {
      // Engineering-side diagnostic: keep the full `skippedActions` list
      // (e.g. `workbook operation "refreshFactSetData" (Office.js path)`,
      // `range action "format" not supported`, …) reachable in DevTools
      // so we can still debug planner / bridge gaps. The user-facing
      // `message` below stays generic on purpose — no action names, no
      // "intent bridge" / "Office.js" / "wired action" / "planner"
      // language. This is a client-facing surface (enterprise users) and
      // the previous diagnostic-style copy was leaking internals.
      // `skippedActions` is also still returned on the result so the
      // orchestrator's verifier loop can reason about which actions
      // didn't run.
      console.warn(
        "[Excel] No-op step — every action was skipped:",
        skippedActions
      );
      return {
        success: false,
        message:
          "This step couldn't be completed in your current Excel session. Try rephrasing the request, or perform the action manually in Excel.",
        operations,
        editedCells,
        skippedActions,
        ...(failedActions.length > 0 ? { failedActions } : {}),
        appliedFormulaWrites,
        ...(refreshOperationContext.deferredRefreshAll
          ? { deferredRefreshAll: true }
          : {}),
        ...(refreshOperationContext.deferredRecalcMode
          ? { deferredRecalcMode: refreshOperationContext.deferredRecalcMode }
          : {}),
      };
    }

    if (failedActions.length > 0) {
      return {
        success: false,
        message: `Some workbook edits failed (${failedActions.join("; ")}). The workbook may be partially updated; retry or adjust the failed actions before marking the step complete.`,
        operations,
        editedCells,
        ...(skippedActions.length > 0 ? { skippedActions } : {}),
        failedActions,
        ...(citationNoteFailures.length > 0
          ? { citationNoteFailures }
          : {}),
        appliedFormulaWrites,
        ...(refreshOperationContext.deferredRefreshAll
          ? { deferredRefreshAll: true }
          : {}),
        ...(refreshOperationContext.deferredRecalcMode
          ? { deferredRecalcMode: refreshOperationContext.deferredRecalcMode }
          : {}),
      };
    }

    // When the bridge had to skip part of the planner's request (e.g.
    // a `format` action on a `RangeModification`, a `tables.create` write,
    // or a hide-cols call against a row-shaped address), append a
    // "skipped" tail to the success message so the verifier (which
    // reads `modifications.message` as the canonical "what happened"
    // blurb) doesn't get fooled by an optimistic planner narration.
    // Without this the verifier sees that message in `COMPLETED STEPS`
    // and rubber-stamps the run — the original observation 2026-04-27
    // was `Add a sheet called Assumptions` returning ✅ even though the
    // new sheet was named `Sheet5`. That specific case is now resolved
    // by the extended `AddWorksheet` payload; the broader skipped-tail
    // mechanism still matters for the warn-skips that remain (see §4.3
    // of `missing-intents-v0.md`).
    const baseMessage = modifications.message || "Changes applied successfully";
    const finalMessage =
      skippedActions.length > 0
        ? `${baseMessage} (skipped: ${skippedActions.join("; ")})`
        : baseMessage;

    return {
      success: true,
      message: finalMessage,
      operations,
      editedCells,
      ...(skippedActions.length > 0 ? { skippedActions } : {}),
      ...(failedActions.length > 0 ? { failedActions } : {}),
      ...(citationNoteFailures.length > 0 ? { citationNoteFailures } : {}),
      appliedFormulaWrites,
      ...(refreshOperationContext.deferredRefreshAll
        ? { deferredRefreshAll: true }
        : {}),
      ...(refreshOperationContext.deferredRecalcMode
        ? { deferredRecalcMode: refreshOperationContext.deferredRecalcMode }
        : {}),
    };
  } catch (error) {
    if (isExcelAbortError(error)) throw error;
    if (isEditModeError(error)) {
      // The pre-apply gate above only catches cases where the host was
      // already in edit mode at the start of the apply. This catch
      // handles the harder case: edit mode was entered MID-FLIGHT
      // (e.g. one of our writes triggered Excel into a repair / edit
      // state, or the user clicked into a cell during a long apply).
      // Same user message for consistency — they don't care which leg
      // of the apply tripped it.
      console.warn(
        "[Excel] Bridge apply aborted due to mid-flight cell-edit mode:",
        error
      );
      const copy = hostReadyRetryCopy("stuck_edit", { retryAction: true });
      trackOperation(`⏳ ${copy.messageOneLiner}`);
      return {
        success: false,
        message: copy.messageOneLiner,
        operations,
        editedCells,
        ...(skippedActions.length > 0 ? { skippedActions } : {}),
        ...(failedActions.length > 0 ? { failedActions } : {}),
        retryReason: "stuck_edit",
        hostDispatchAttempted: true,
        appliedFormulaWrites,
        ...(refreshOperationContext.deferredRefreshAll
          ? { deferredRefreshAll: true }
          : {}),
        ...(refreshOperationContext.deferredRecalcMode
          ? { deferredRecalcMode: refreshOperationContext.deferredRecalcMode }
          : {}),
      };
    }
    console.error("[Excel] Failed to apply modifications via intent bridge:", error);
    return {
      success: false,
      message: `Failed to apply changes: ${error instanceof Error ? error.message : String(error)}`,
      operations,
      editedCells,
      ...(skippedActions.length > 0 ? { skippedActions } : {}),
      ...(failedActions.length > 0 ? { failedActions } : {}),
      appliedFormulaWrites,
      ...(refreshOperationContext.deferredRefreshAll
        ? { deferredRefreshAll: true }
        : {}),
      ...(refreshOperationContext.deferredRecalcMode
        ? { deferredRecalcMode: refreshOperationContext.deferredRecalcMode }
        : {}),
    };
  } finally {
    // Restore automatic recalc iff we successfully flipped into manual. If
    // the manual flip failed earlier, the workbook was never nudged away
    // from whatever the user had configured, so leave it alone.
    if (calcModeFlipped) {
      try {
        await setCalculationMode("automatic");
      } catch (e) {
        console.warn(
          "[Excel] SetCalculationMode(automatic) failed; workbook may be left in manual mode:",
          e
        );
      }
    }
    // Close the outer undo bracket iff we successfully opened it. Mirrors
    // the `calcModeFlipped` pattern — we only attempt `EndUndo` when the
    // matching `BeginUndo` succeeded, so a host that doesn't support the
    // bracket pair is left untouched. Failure here is logged and
    // swallowed; an undo bracket failure is never user-facing.
    if (undoBegun) {
      try {
        await endUndo();
      } catch (e) {
        console.warn(
          "[Excel] EndUndo failed; the host may not have closed the undo entry cleanly:",
          e
        );
      }
    }
    // Drain any stuck cell-edit flag the apply burst may have left
    // behind. Some bridge writes commit the value but leave the
    // host's `isInEditMode` set, which then blocks the NEXT step
    // (or template open) at its pre-apply gate with the misleading
    // "Excel is currently editing a cell" message — for an edit
    // the AI itself left behind, not anything the user did. See
    // `hostReady.ts::drainEditModeAfterWrite` for the probe + force-
    // commit semantics; the drain is best-effort and never throws,
    // so it's safe to fire inside the existing finally block.
    try {
      const drainResult = await drainEditModeAfterWrite();
      if (drainResult === "forced_commit") {
        console.log(
          "[Excel] Post-apply drain: forced commit of stuck edit-mode flag."
        );
      }
    } catch (e) {
      console.warn(
        "[Excel] Post-apply edit-mode drain threw unexpectedly; ignoring:",
        e
      );
    }
  }
}

/**
 * Apply a single `CellModification` through the FactSet intent bridge.
 *
 * Handles every action in the `CellModification.action` type union:
 * `"set"` writes the value / formula (with optional post-set format pass),
 * `"clear"` routes to `factset.Spreadsheet.ClearRange` with the host's
 * default `clearType: "All"`, and `"format"` runs a pure formatting pass
 * via `applyFormatToRangeViaBridge`. Any other action falls through to a
 * catch-all `trackSkip` — dead code today given the type union, kept
 * defensively in case the schema widens.
 *
 * `sheetName` is the enclosing `WorksheetModification.sheetName` (or
 * the burst's `pinnedSheetForRouting` fallback when the worksheet mod
 * left it blank); the outer apply loop pre-activates it via
 * `ensureActiveSheetForWrite`, so intents whose `worksheetName` field
 * is silently dropped by the host still land on the correct sheet.
 *
 * `pinnedWorkbookName`, when set, is forwarded to `setCellContents` /
 * `fillRangeContents` as `workbookName`. The host wrappers embed it
 * in `destinationCell` as an Excel external reference
 * (`[Book.xlsx]Sheet1!A1`) so the write routes to the plan's origin
 * workbook even if the user switched focus mid-burst. Excel external
 * refs require both `[book]` and `sheet!`, so this only takes effect
 * when a resolvable sheet is also provided; the helper falls back to
 * the legacy form with a warning otherwise.
 */
async function applyCellModificationViaIntents(
  modification: CellModification,
  trackOperation: (op: string, address?: string) => void,
  trackSkip: (op: string, label: string) => void,
  editedCells: string[],
  sheetName?: string,
  pinnedWorkbookName?: string
): Promise<void> {
  if (!modification.address || !isValidExcelAddress(modification.address)) {
    throw new Error(
      `Invalid cell address: "${modification.address}". Expected format like A1, B2, or A1:G1`
    );
  }

  // Range-mutation targeting pin (kill-switch gated). The cell WRITE path
  // (setCellContents / fillRangeContents) always pins via the fully-supported
  // InsertContentsToRange external-ref, so it uses `pinnedWorkbookName`
  // directly; only the mutation intents (ClearRange, ApplyFormatting) route
  // through `rangeMutationPin`, which is `undefined` when the switch is off
  // (legacy active-workbook wire shape).
  const rangeMutationPin =
    pinnedWorkbookName !== undefined && supportsRangeMutationWorkbookName()
      ? pinnedWorkbookName
      : undefined;

  if (modification.action === "format") {
    // Pure-format cell action — no value/formula write, only style changes.
    // The post-`set` format pass below handles the "set + format" combo.
    if (!modification.format) {
      trackSkip(
        `⚠️ ${modification.address}: "format" requires a "format" payload; skipping`,
        `cell ${modification.address} action "format" missing format`
      );
      return;
    }
    trackOperation(
      `🎨 Formatting ${modification.address} via FactSet intent`,
      modification.address
    );
    await applyFormatToRangeViaBridge(
      modification.address,
      modification.format,
      trackOperation,
      sheetName,
      rangeMutationPin,
    );
    editedCells.push(modification.address);
    if (modification.comment) {
      trackOperation(
        `⚠️ ${modification.address}: skipping comment — not supported by intent bridge`,
        modification.address
      );
    }
    return;
  }

  if (modification.action === "clear") {
    // Pure-clear cell action — routes to `factset.Spreadsheet.ClearRange`
    // (same wire intent as `RangeModification.action = "clear"`). Default
    // `clearType` is the host's `"All"` (wipes contents + formats +
    // comments), which matches the user-facing semantics of "clear this
    // cell" and "undo the last change" (the planner maps an undo prompt
    // to a clear on the recently-edited cell). `value` / `formula` /
    // `format` fields on the same modification are silently ignored —
    // clear wins.
    trackOperation(
      `🧹 Clearing ${modification.address} via FactSet intent`,
      modification.address
    );
    await clearRange(modification.address, undefined, sheetName, rangeMutationPin);
    editedCells.push(modification.address);
    if (modification.comment) {
      trackOperation(
        `⚠️ ${modification.address}: skipping comment — not supported by intent bridge`,
        modification.address
      );
    }
    return;
  }

  if (modification.action !== "set") {
    trackSkip(
      `⚠️ ${modification.address}: action "${modification.action}" not supported by intent bridge yet; skipping`,
      `cell ${modification.address} action "${modification.action}"`
    );
    return;
  }

  const numberFormat = extractNumberFormatString(modification.format);
  let payload: string | number | boolean | null | undefined;
  let description: string;

  if (modification.formula !== undefined) {
    payload = modification.formula;
    description = `Set formula: ${modification.formula}`;
    diag("write", "cell_write", { address: modification.address, formula: modification.formula, numberFormat, source: "model-direct", isFdsBound: typeof modification.formula === "string" && modification.formula.trim().toUpperCase().startsWith("=FDS") });
    trackOperation(
      `📐 ${modification.address} = ${modification.formula} (via FactSet intent)`
    );
  } else if (modification.value !== undefined) {
    // InsertContentsToRange exposes one wire field named `formula` for both
    // values and formulas. Prefix formula-looking values with an apostrophe
    // so a value slot can never become executable Excel content.
    payload = literalizeCodeOwnedValue(modification.value);
    const preview =
      typeof modification.value === "string"
        ? `"${modification.value.slice(0, 30)}${
            modification.value.length > 30 ? "..." : ""
          }"`
        : modification.value;
    description = `Set value: ${preview}`;
    trackOperation(
      `✏️ ${modification.address} = ${preview} (via FactSet intent)`
    );
  } else {
    return;
  }

  const isRange = modification.address.includes(":");
  if (isRange) {
    const size = parseRangeSize(modification.address);
    if (size) {
      const values = Array.from({ length: size.rows }, () =>
        Array(size.cols).fill(payload ?? "")
      );
      await fillRangeContents(modification.address, values, {
        numberFormat,
        undoEntryText: "FactSet AI: fill range",
        sheetName,
        workbookName: pinnedWorkbookName,
      });
    } else {
      await setCellContents(modification.address, payload, {
        numberFormat,
        undoEntryText: "FactSet AI: set cell",
        sheetName,
        workbookName: pinnedWorkbookName,
      });
    }
  } else {
    await setCellContents(modification.address, payload, {
      numberFormat,
      undoEntryText: "FactSet AI: set cell",
      sheetName,
      workbookName: pinnedWorkbookName,
    });
  }
  editedCells.push(modification.address);

  // Ensure `description` is referenced so tooling/lint won't flag it; the
  // trackOperation messages above already surface the action to the user.
  void description;

  if (modification.format) {
    trackOperation(
      `🎨 Formatting ${modification.address} via FactSet intent`,
      modification.address
    );
    await applyFormatToRangeViaBridge(
      modification.address,
      modification.format,
      trackOperation,
      sheetName,
      rangeMutationPin,
    );
  }
  if (modification.comment) {
    trackOperation(
      `⚠️ ${modification.address}: skipping comment — not supported by intent bridge`,
      modification.address
    );
  }
}

/**
 * Apply a single `RangeModification` through the FactSet intent bridge.
 *
 * Supported actions:
 *  - `"fill"`        — via `factset.Spreadsheet.InsertContentsToRange`
 *                     (post-fill `format` pass via `factset.Spreadsheet.ApplyFormatting`)
 *  - `"autofit"`     — via `factset.Spreadsheet.ResizeExcelElements` (FIT)
 *  - `"resize"`      — via `factset.Spreadsheet.ResizeExcelElements` (ABSOLUTE)
 *  - `"deleteRange"` — via `factset.Spreadsheet.DeleteRange`
 *  - `"insertRange"` — via `factset.Spreadsheet.InsertRange`
 *  - `"moveRange"`   — via `factset.Spreadsheet.MoveRange`
 *  - `"clear"`       — via `factset.Spreadsheet.ClearRange`
 *  - `"sort"`        — via `factset.Spreadsheet.SortRange`
 *  - `"format"`      — via `factset.Spreadsheet.ApplyFormatting`
 *  - `"hideColumns"` / `"showColumns"` / `"hideRows"` / `"showRows"` —
 *                     via the matching host intents
 *
 * Other actions (`filter`) emit a warning and return until their
 * dedicated intents land.
 */
async function applyRangeModificationViaIntents(
  modification: RangeModification,
  trackOperation: (op: string, address?: string) => void,
  trackSkip: (op: string, label: string) => void,
  editedCells: string[],
  sheetName?: string,
  pinnedWorkbookName?: string
): Promise<void> {
  if (!modification.address || !isValidExcelAddress(modification.address)) {
    throw new Error(
      `Invalid range address: "${modification.address}". Expected format like A1:B10 or A1`
    );
  }

  // Range-mutation targeting pin (kill-switch gated; see
  // `applyCellModificationViaIntents`). Threaded into every mutation
  // dispatcher below so the host routes the mutation to the pinned workbook
  // in the background; `undefined` when the switch is off restores the legacy
  // active-workbook wire shape.
  const rangeMutationPin =
    pinnedWorkbookName !== undefined && supportsRangeMutationWorkbookName()
      ? pinnedWorkbookName
      : undefined;

  if (modification.action === "deleteRange") {
    const dir = modification.deleteRange?.shiftDirection;
    const shift = dir === "left" ? "ShiftLeft" : dir === "up" ? "ShiftUp" : undefined;
    trackOperation(
      `✂️ Deleting ${modification.address}${
        shift ? ` (shift ${dir})` : ""
      } via FactSet intent`,
      modification.address
    );
    await deleteRange(modification.address, shift, sheetName, rangeMutationPin);
    editedCells.push(modification.address);
    if (modification.comment) {
      trackOperation(
        `⚠️ ${modification.address}: skipping comment — not supported by intent bridge`,
        modification.address
      );
    }
    return;
  }

  if (modification.action === "insertRange") {
    const dir = modification.insertRange?.shiftDirection;
    const shift =
      dir === "right" ? "ShiftRight" : dir === "down" ? "ShiftDown" : undefined;
    const co = modification.insertRange?.copyOrigin;
    const copyOrigin =
      co === "leftOrAbove"
        ? "LeftOrAbove"
        : co === "rightOrBelow"
          ? "RightOrBelow"
          : undefined;
    trackOperation(
      `➕ Inserting cells at ${modification.address}${
        shift ? ` (shift ${dir})` : ""
      } via FactSet intent`,
      modification.address
    );
    await insertRange(
      modification.address,
      {
        ...(shift !== undefined ? { shift } : {}),
        ...(copyOrigin !== undefined ? { copyOrigin } : {}),
      },
      sheetName,
      rangeMutationPin,
    );
    editedCells.push(modification.address);
    if (modification.comment) {
      trackOperation(
        `⚠️ ${modification.address}: skipping comment — not supported by intent bridge`,
        modification.address
      );
    }
    return;
  }

  if (modification.action === "moveRange") {
    // Per-side names override the enclosing worksheet independently. When
    // omitted, both sides belong to that enclosing worksheet rather than
    // whichever sheet happens to be active in the pinned workbook.
    const destination = modification.moveRange?.destination;
    if (!destination) {
      trackSkip(
        `⚠️ ${modification.address}: "moveRange" requires moveRange.destination; skipping`,
        `range ${modification.address} action "moveRange" missing destination`
      );
      return;
    }
    const preserve = modification.moveRange?.preserveSource === true;
    const verb = preserve ? "Copying" : "Moving";
    trackOperation(
      `↔️ ${verb} ${modification.address} → ${destination} via FactSet intent`,
      modification.address
    );
    await moveRange(
      modification.address,
      destination,
      preserve ? { preserveSource: true } : undefined,
      modification.moveRange?.sourceSheetName?.trim() || sheetName,
      modification.moveRange?.destinationSheetName?.trim() || sheetName,
      rangeMutationPin,
    );
    editedCells.push(modification.address);
    editedCells.push(destination);
    if (modification.comment) {
      trackOperation(
        `⚠️ ${modification.address}: skipping comment — not supported by intent bridge`,
        modification.address
      );
    }
    return;
  }

  if (modification.action === "clear") {
    const clearType = normalizeClearRangeClearType(modification.clear?.clearType);
    if (clearType === null) {
      trackSkip(
        `⚠️ ${modification.address}: unsupported clear type "${modification.clear?.clearType}"; skipping`,
        `range ${modification.address} action "clear" has an unsupported clear type`,
      );
      return;
    }
    trackOperation(
      `🧹 Clearing ${modification.address}${
        clearType ? ` (${clearType})` : ""
      } via FactSet intent`,
      modification.address
    );
    await clearRange(
      modification.address,
      clearType !== undefined ? { clearType } : undefined,
      sheetName,
      rangeMutationPin,
    );
    editedCells.push(modification.address);
    if (modification.comment) {
      trackOperation(
        `⚠️ ${modification.address}: skipping comment — not supported by intent bridge`,
        modification.address
      );
    }
    return;
  }

  if (modification.action === "sort") {
    const sortKeys = modification.sort?.sortKeys;
    if (!Array.isArray(sortKeys) || sortKeys.length === 0) {
      trackSkip(
        `⚠️ ${modification.address}: "sort" requires sort.sortKeys[] with at least one entry; skipping`,
        `range ${modification.address} action "sort" missing sortKeys`
      );
      return;
    }
    if (sortKeys.length > 3) {
      trackSkip(
        `⚠️ ${modification.address}: "sort" accepts at most 3 sortKeys (got ${sortKeys.length}); skipping`,
        `range ${modification.address} action "sort" too many sortKeys`
      );
      return;
    }
    const summary = sortKeys
      .map((k) => `${k.key}${k.order === "Descending" ? "↓" : "↑"}`)
      .join(", ");
    trackOperation(
      `🔀 Sorting ${modification.address} by ${summary} via FactSet intent`,
      modification.address
    );
    await sortRange(
      modification.address,
      sortKeys,
      modification.sort?.options,
      sheetName,
      rangeMutationPin,
    );
    editedCells.push(modification.address);
    if (modification.comment) {
      trackOperation(
        `⚠️ ${modification.address}: skipping comment — not supported by intent bridge`,
        modification.address
      );
    }
    return;
  }

  if (modification.action === "format") {
    // Pure-format action: no value/formula write, only style changes.
    // The post-fill format pass below handles the "fill + format" combo.
    if (!modification.format) {
      trackSkip(
        `⚠️ ${modification.address}: "format" requires a "format" payload; skipping`,
        `range ${modification.address} action "format" missing format`
      );
      return;
    }
    trackOperation(
      `🎨 Formatting ${modification.address} via FactSet intent`,
      modification.address
    );
    await applyFormatToRangeViaBridge(
      modification.address,
      modification.format,
      trackOperation,
      sheetName,
      rangeMutationPin,
      getFactSetFormatGuard(modification) !== undefined,
    );
    editedCells.push(modification.address);
    if (modification.comment) {
      trackOperation(
        `⚠️ ${modification.address}: skipping comment — not supported by intent bridge`,
        modification.address
      );
    }
    return;
  }

  if (modification.action === "hideColumns") {
    if (!isColumnShapedAddress(modification.address)) {
      trackSkip(
        `⚠️ ${modification.address}: "hideColumns" requires a column-shaped range (e.g. "A:C"); skipping`,
        `range ${modification.address} action "hideColumns" wrong shape`
      );
      return;
    }
    trackOperation(
      `🙈 Hiding columns ${modification.address} via FactSet intent`,
      modification.address
    );
    await hideColumns({
      address: modification.address,
      referenceType: "absolute",
      ...(sheetName ? { worksheetName: sheetName } : {}),
      ...(rangeMutationPin ? { workbookName: rangeMutationPin } : {}),
    });
    editedCells.push(modification.address);
    return;
  }

  if (modification.action === "hideRows") {
    if (!isRowShapedAddress(modification.address)) {
      trackSkip(
        `⚠️ ${modification.address}: "hideRows" requires a row-shaped range (e.g. "5:10"); skipping`,
        `range ${modification.address} action "hideRows" wrong shape`
      );
      return;
    }
    trackOperation(
      `🙈 Hiding rows ${modification.address} via FactSet intent`,
      modification.address
    );
    await hideRows({
      address: modification.address,
      referenceType: "absolute",
      ...(sheetName ? { worksheetName: sheetName } : {}),
      ...(rangeMutationPin ? { workbookName: rangeMutationPin } : {}),
    });
    editedCells.push(modification.address);
    return;
  }

  if (modification.action === "showColumns") {
    if (!isColumnShapedAddress(modification.address)) {
      trackSkip(
        `⚠️ ${modification.address}: "showColumns" requires a column-shaped range (e.g. "A:C"); skipping`,
        `range ${modification.address} action "showColumns" wrong shape`
      );
      return;
    }
    trackOperation(
      `👁️ Showing columns ${modification.address} via FactSet intent`,
      modification.address
    );
    await showColumns({
      address: modification.address,
      referenceType: "absolute",
      ...(sheetName ? { worksheetName: sheetName } : {}),
      ...(rangeMutationPin ? { workbookName: rangeMutationPin } : {}),
    });
    editedCells.push(modification.address);
    return;
  }

  if (modification.action === "showRows") {
    if (!isRowShapedAddress(modification.address)) {
      trackSkip(
        `⚠️ ${modification.address}: "showRows" requires a row-shaped range (e.g. "5:10"); skipping`,
        `range ${modification.address} action "showRows" wrong shape`
      );
      return;
    }
    trackOperation(
      `👁️ Showing rows ${modification.address} via FactSet intent`,
      modification.address
    );
    await showRows({
      address: modification.address,
      referenceType: "absolute",
      ...(sheetName ? { worksheetName: sheetName } : {}),
      ...(rangeMutationPin ? { workbookName: rangeMutationPin } : {}),
    });
    editedCells.push(modification.address);
    return;
  }

  if (modification.action === "groupColumns") {
    if (!isColumnShapedAddress(modification.address)) {
      trackSkip(
        `⚠️ ${modification.address}: "groupColumns" requires a column-shaped range (e.g. "A:C"); skipping`,
        `range ${modification.address} action "groupColumns" wrong shape`
      );
      return;
    }
    trackOperation(
      `📂 Grouping columns ${modification.address} via FactSet intent`,
      modification.address
    );
    await groupColumns({
      address: modification.address,
      referenceType: "absolute",
      ...(sheetName ? { worksheetName: sheetName } : {}),
      ...(rangeMutationPin ? { workbookName: rangeMutationPin } : {}),
    });
    editedCells.push(modification.address);
    return;
  }

  if (modification.action === "groupRows") {
    if (!isRowShapedAddress(modification.address)) {
      trackSkip(
        `⚠️ ${modification.address}: "groupRows" requires a row-shaped range (e.g. "5:10"); skipping`,
        `range ${modification.address} action "groupRows" wrong shape`
      );
      return;
    }
    trackOperation(
      `📂 Grouping rows ${modification.address} via FactSet intent`,
      modification.address
    );
    await groupRows({
      address: modification.address,
      referenceType: "absolute",
      ...(sheetName ? { worksheetName: sheetName } : {}),
      ...(rangeMutationPin ? { workbookName: rangeMutationPin } : {}),
    });
    editedCells.push(modification.address);
    return;
  }

  if (modification.action === "ungroupColumns") {
    if (!isColumnShapedAddress(modification.address)) {
      trackSkip(
        `⚠️ ${modification.address}: "ungroupColumns" requires a column-shaped range (e.g. "A:C"); skipping`,
        `range ${modification.address} action "ungroupColumns" wrong shape`
      );
      return;
    }
    trackOperation(
      `📂 Ungrouping columns ${modification.address} via FactSet intent`,
      modification.address
    );
    await ungroupColumns({
      address: modification.address,
      referenceType: "absolute",
      ...(sheetName ? { worksheetName: sheetName } : {}),
      ...(rangeMutationPin ? { workbookName: rangeMutationPin } : {}),
    });
    editedCells.push(modification.address);
    return;
  }

  if (modification.action === "ungroupRows") {
    if (!isRowShapedAddress(modification.address)) {
      trackSkip(
        `⚠️ ${modification.address}: "ungroupRows" requires a row-shaped range (e.g. "5:10"); skipping`,
        `range ${modification.address} action "ungroupRows" wrong shape`
      );
      return;
    }
    trackOperation(
      `📂 Ungrouping rows ${modification.address} via FactSet intent`,
      modification.address
    );
    await ungroupRows({
      address: modification.address,
      referenceType: "absolute",
      ...(sheetName ? { worksheetName: sheetName } : {}),
      ...(rangeMutationPin ? { workbookName: rangeMutationPin } : {}),
    });
    editedCells.push(modification.address);
    return;
  }

  if (modification.action === "select") {
    // `factset.Spreadsheet.SelectRange` — pure cursor/UX action, no data
    // write. Per f2_docs it "will activate the sheet and workbook associated
    // with the range", so it is inherently focus-changing. We still thread
    // `workbookName` on the `RangeAddress` so the selection lands on the
    // pinned workbook (not the user's currently-focused one).
    //
    // Under background targeting the pin is NOT the active workbook, so a raw
    // SelectRange would yank the user onto the pin. Route it through the
    // foreground coordinator: it snapshots the user's real focus, lets
    // SelectRange front + select on the pin, then restores the user's focus —
    // the selection persists on the pin (full parity with the planner's
    // intent) while the user stays where they were. Serialised so concurrent
    // multi-workbook runs don't ping-pong. When targeting is off the pin is
    // already active (legacy pre-burst activation) and the helper runs inline.
    trackOperation(
      `🎯 Selecting ${modification.address} via FactSet intent`,
      modification.address
    );
    await runForegroundOnlyOnPin(
      pinnedWorkbookName,
      sheetName,
      () =>
        selectRange({
          address: modification.address,
          referenceType: "absolute",
          ...(sheetName ? { worksheetName: sheetName } : {}),
          ...(pinnedWorkbookName ? { workbookName: pinnedWorkbookName } : {}),
        }),
      { contextLabel: `SelectRange ${modification.address}` },
    );
    // Don't push to `editedCells` — selection isn't an edit, and a
    // refresh-after-edit triggered by selection would just re-read the
    // sheet for no reason. The sheet's snapshot is unchanged.
    return;
  }

  if (modification.action === "autofit") {
    if (modification.fitColumns) {
      trackOperation(
        `📏 Auto-fitting columns in ${modification.address} (via FactSet intent)`,
        modification.address
      );
      await autofitRange(modification.address, "COL", sheetName, rangeMutationPin);
      editedCells.push(modification.address);
    }
    if (modification.fitRows) {
      trackOperation(
        `📏 Auto-fitting rows in ${modification.address} (via FactSet intent)`,
        modification.address
      );
      await autofitRange(modification.address, "ROW", sheetName, rangeMutationPin);
      editedCells.push(modification.address);
    }
    return;
  }

  if (modification.action === "resize") {
    const { width, height } = modification;
    if (width === undefined && height === undefined) {
      trackSkip(
        `⚠️ ${modification.address}: "resize" requires "width" (for a column range) or "height" (for a row range); skipping`,
        `range ${modification.address} action "resize" missing width/height`
      );
      return;
    }
    if (width !== undefined) {
      trackOperation(
        `📐 Setting column width of ${modification.address} to ${width} (via FactSet intent)`,
        modification.address
      );
      await resizeAbsolute(modification.address, "COL", width, sheetName, rangeMutationPin);
      editedCells.push(modification.address);
    }
    if (height !== undefined) {
      trackOperation(
        `📐 Setting row height of ${modification.address} to ${height} (via FactSet intent)`,
        modification.address
      );
      await resizeAbsolute(modification.address, "ROW", height, sheetName, rangeMutationPin);
      editedCells.push(modification.address);
    }
    return;
  }

  if (modification.action !== "fill") {
    trackSkip(
      `⚠️ ${modification.address}: action "${modification.action}" not supported by intent bridge yet; skipping`,
      `range ${modification.address} action "${modification.action}"`
    );
    return;
  }

  assertUnambiguousRangeFill(
    modification.address,
    modification.values,
    modification.formulas,
  );
  const numberFormat = extractNumberFormatString(modification.format);
  const values2d: (string | number | boolean | null | undefined)[][] | undefined =
    modification.formulas ??
    modification.values?.map((row) => row.map(literalizeCodeOwnedValue));
  if (!values2d) return;

  const rowCount = values2d.length;
  const colCount = values2d[0]?.length || 0;
  const hasFormulas = modification.formulas !== undefined;
  const icon = hasFormulas ? "📐" : "📊";

  trackOperation(
    `${icon} Filling ${modification.address} (${rowCount}×${colCount}) via FactSet intent`
  );

  await fillRangeContents(modification.address, values2d, {
    numberFormat,
    undoEntryText: "FactSet AI: fill range",
    sheetName,
    workbookName: pinnedWorkbookName,
  });
  editedCells.push(modification.address);

  if (modification.format) {
    trackOperation(
      `🎨 Formatting ${modification.address} via FactSet intent`,
      modification.address
    );
    await applyFormatToRangeViaBridge(
      modification.address,
      modification.format,
      trackOperation,
      sheetName,
      rangeMutationPin,
    );
  }
  if (modification.comment) {
    trackOperation(
      `⚠️ ${modification.address}: skipping comment — not supported by intent bridge`,
      modification.address
    );
  }
}

/**
 * Apply a single `WorkbookOperation` through the FactSet intent bridge.
 *
 * Each `op.action` maps 1:1 to a host intent:
 *  - `"addWorkbook"`                   → `factset.Spreadsheet.AddWorkbook`
 *  - `"copyWorksheet"`                 → `factset.Spreadsheet.CopyWorksheet`
 *  - `"insertWorksheetsFromBase64"`    → `factset.Spreadsheet.InsertWorksheetsFromBase64`
 *  - `"recalculate"`                   → `factset.Spreadsheet.ApplicationCalculate`
 *  - `"refreshFactSetData"`            → `factset.Spreadsheet.HarmonizedRefreshWorksheets`
 *  - `"refreshCalculate"`              → `factset.RefreshCalculate`
 *  - `"getRefreshStatus"`              → `factset.GetRefreshStatus`
 *  - `"getRefresh"`                    → `factset.GetRefresh`
 *  - `"awaitRefreshCalculate"`         → `factset.GetRefreshStatus` (poll loop)
 *  - `"addNamedObject"`                → `factset.Spreadsheet.AddNamedObject`
 *  - `"removeNamedObject"`             → `factset.Spreadsheet.RemoveNamedObject`
 *  - `"search"`                        → `factset.Spreadsheet.Search`
 *  - `"insertTtfApiResponse"`          → `factset.Spreadsheet.InsertTtfApiResponse`
 *
 * Errors are propagated to the caller, which logs and fails the current
 * apply step honestly.
 */
/**
 * Fetch an image URL on the client and convert it to a `data:image/...;
 * base64,...` URI suitable for `factset.Spreadsheet.AddImageFromBase64`.
 *
 * The intent requires a **full** data URI (per the captured example in
 * `excel-docs/intent-details.md` §"Add Image From Base64") — bare
 * base64 has been observed to silently no-op. Caller is responsible
 * for the network failure / CORS surface; we surface the original
 * error verbatim so the trackOperation log is actionable.
 */
async function imageUrlToDataUri(url: string): Promise<string> {
  const response = await fetch(url, { method: "GET" });
  if (!response.ok) {
    throw new Error(`Logo fetch failed: HTTP ${response.status}`);
  }
  const contentType = response.headers.get("content-type") || "image/png";
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  // `btoa` doesn't accept binary strings >2 MiB cleanly; chunk to be
  // safe on logo payloads (typically <100 KB but be defensive).
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + CHUNK))
    );
  }
  const base64 = btoa(binary);
  return `data:${contentType};base64,${base64}`;
}

/**
 * Bridge-mode replacement for the Office.js `insertCompanyLogo` path.
 * Resolves the logo URL via the existing Finster API, converts it to
 * a base64 data URI, and dispatches `factset.Spreadsheet.
 * AddImageFromBase64` with the modification's address as the anchor.
 *
 * Errors at any step warn-and-track but never throw past the per-
 * modification boundary — except `InvalidOperationInCellEditMode` from
 * the host write, which is re-thrown so the surrounding apply's outer
 * catch can surface the unified L5 abort message. Matches the shape of
 * the existing cell/range loop in `applyWorkbookModificationsViaIntents`.
 */
async function applyLogoModificationViaIntents(
  logoMod: LogoModification,
  trackOperation: (op: string, address?: string) => void,
  sheetName?: string,
  pinnedWorkbookName?: string
): Promise<void> {
  trackOperation(
    `🏢 Inserting ${logoMod.companyName} logo at ${logoMod.address} (via FactSet intent)`,
    logoMod.address
  );
  let logoUrl: string;
  try {
    const result = await getCompanyLogo(logoMod.companyName);
    if (!result.success || !result.logoUrl) {
      trackOperation(
        `⚠️ Logo for "${logoMod.companyName}" not resolved: ${result.error || "unknown error"}`
      );
      return;
    }
    logoUrl = result.logoUrl;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn(`[Excel] Logo lookup for "${logoMod.companyName}" failed:`, e);
    trackOperation(
      `⚠️ Logo lookup for "${logoMod.companyName}" failed: ${msg.slice(0, 100)}`
    );
    return;
  }

  let dataUri: string;
  try {
    dataUri = await imageUrlToDataUri(logoUrl);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn(
      `[Excel] Logo download/encode for "${logoMod.companyName}" failed:`,
      e
    );
    trackOperation(
      `⚠️ Logo download for "${logoMod.companyName}" failed: ${msg.slice(0, 100)}`
    );
    return;
  }

  // The captured intent payload doesn't expose a `sheetName`/`worksheetName`
  // field — `anchorCell` is interpreted on the active sheet
  // (`AddImageFromBase64` "supports only the active sheet" per f2_docs). Strip
  // any sheet prefix the planner emitted; the active sheet is prepared just
  // below (under background targeting via the foreground coordinator, else by
  // the caller's legacy pre-burst activation).
  const bang = logoMod.address.lastIndexOf("!");
  const bareAddress =
    bang >= 0 ? logoMod.address.slice(bang + 1) : logoMod.address;
  if (bang >= 0) {
    const embeddedSheet = logoMod.address.slice(0, bang).replace(/^'|'$/g, "");
    if (sheetName && embeddedSheet && embeddedSheet !== sheetName) {
      trackOperation(
        `⚠️ Logo address sheet "${embeddedSheet}" differs from worksheet target "${sheetName}"; using worksheet target`
      );
    }
  }

  try {
    // Active-sheet-only intent: front the pin briefly (serialised +
    // focus-restored) so the image lands on the run's workbook, not the user's
    // currently-focused one. The network fetch above ran OUTSIDE this window so
    // we don't hold the global foreground lock during I/O. Runs inline when
    // targeting is off (pin already active) or unpinned (single-workbook).
    await runForegroundOnlyOnPin(
      pinnedWorkbookName,
      sheetName,
      () =>
        addImageFromBase64({
          dataUri,
          anchorCell: bareAddress.replace(/\$/g, ""),
          name: `Logo_${logoMod.companyName.replace(/\s+/g, "_")}`,
          width: logoMod.width ?? 60,
          height: logoMod.height ?? 60,
        }),
      { contextLabel: `logo "${logoMod.companyName}"` },
    );
  } catch (e) {
    // AddImageFromBase64 is a host mutation — propagate edit-mode errors
    // so the outer apply catch can surface the unified abort message.
    if (isEditModeError(e)) throw e;
    const msg = e instanceof Error ? e.message : String(e);
    console.warn(
      `[Excel] AddImageFromBase64 for "${logoMod.companyName}" failed:`,
      e
    );
    trackOperation(
      `⚠️ Logo insert for "${logoMod.companyName}" failed: ${msg.slice(0, 100)}`
    );
  }
}

/**
 * Apply a batch of `ShapeModification` entries via the intent bridge.
 *
 * Closes the chart-action gap previously surfaced as
 * `Chart action "delete" / "update" not supported by intent bridge
 * yet; skipping` in §4.3 of `missing-intents-v0.md`. Dispatches each
 * action group through the wrapper most natural for it:
 *
 *  - `"delete"`  → one `factset.Spreadsheet.DeleteShape` call per
 *                  entry (the host doesn't expose a batch-delete
 *                  variant today; submit them serially).
 *  - `"modify"`  → one `factset.Spreadsheet.ModifyShapes` call,
 *                  batching all entries on the worksheet into a
 *                  single `shapeModifications[]` payload. Sheet
 *                  inheritance: explicit `sheetName` per entry wins,
 *                  otherwise the enclosing `WorksheetModification.sheetName`.
 *  - `"select"`  → one `factset.Spreadsheet.SelectShapes` call,
 *                  batching all entries' ids. Per-entry `sheetName`
 *                  is gathered into the single host call when all
 *                  entries agree on a sheet; otherwise we split into
 *                  one call per distinct sheet.
 *
 * Best-effort by contract: a single failing entry warn-skips and
 * lets the remaining entries proceed. Structural failures (i.e. the
 * host rejecting the whole batch) surface through `trackOperation`
 * but don't abort the surrounding apply — chart writes commonly
 * have `chartIndex` skew between planner and host, and we'd rather
 * have the user re-run than nuke the rest of the apply. Exception:
 * `InvalidOperationInCellEditMode` is re-thrown so the surrounding
 * apply's outer catch can surface the unified L5 abort message
 * instead of letting the cascade silently continue.
 *
 * Returns the count of shape entries that were successfully
 * dispatched to the host, so the caller can roll this into
 * `structuralChangesApplied` and dodge the no-op guard. Without
 * this, a step whose only useful work was a shape op would get
 * misreported as a no-op whenever any other slot warn-skipped (see
 * the no-op detection block in `applyWorkbookModificationsViaIntents`,
 * `editedCells.length === 0 && structuralChangesApplied === 0 &&
 * skippedActions.length > 0`). The count is per-entry: each
 * succeeded delete / modify / select adds one, matching the
 * `addActiveGraph` / `freezePanes` / `copyWorksheet` accounting
 * pattern elsewhere in this file. Selects are pure UX (no
 * workbook mutation), but they're still planner-requested actions
 * and we'd rather not punish a "select these three shapes" step
 * just because nothing else changed.
 */
async function applyShapeModificationsViaIntents(
  shapeMods: ShapeModification[],
  enclosingSheetName: string | undefined,
  trackOperation: (op: string, address?: string) => void,
  trackSkip: (op: string, label: string) => void
): Promise<number> {
  const deletes = shapeMods.filter((m) => m.action === "delete");
  const modifies = shapeMods.filter((m) => m.action === "modify");
  const selects = shapeMods.filter((m) => m.action === "select");
  let succeeded = 0;

  for (const d of deletes) {
    if (typeof d.id !== "number" || !Number.isFinite(d.id)) {
      trackSkip(
        `⚠️ shapes "delete": entry missing numeric \`id\`; skipping`,
        `shape delete missing id`
      );
      continue;
    }
    try {
      trackOperation(`✂️ Deleting shape id=${d.id} via FactSet intent`);
      await deleteShape({
        id: d.id,
        ...(d.sheetName !== undefined ? { sheetName: d.sheetName } : {}),
      });
      succeeded += 1;
    } catch (e) {
      if (isEditModeError(e)) throw e;
      const msg = e instanceof Error ? e.message : String(e);
      console.warn(`[Excel] DeleteShape(id=${d.id}) failed:`, e);
      trackOperation(
        `⚠️ DeleteShape id=${d.id} failed: ${msg.slice(0, 200)}`
      );
    }
  }

  if (modifies.length > 0) {
    const entries: ShapeModificationEntry[] = modifies.map((m) => ({
      shape: {
        id: m.id,
        // Per-entry `sheetName` wins; fall back to the enclosing
        // `WorksheetModification.sheetName` so the planner doesn't
        // have to repeat itself.
        ...(m.sheetName !== undefined
          ? { sheetName: m.sheetName }
          : enclosingSheetName !== undefined
            ? { sheetName: enclosingSheetName }
            : {}),
      },
      ...(m.top !== undefined ? { top: m.top } : {}),
      ...(m.left !== undefined ? { left: m.left } : {}),
      ...(m.height !== undefined ? { height: m.height } : {}),
      ...(m.width !== undefined ? { width: m.width } : {}),
    }));
    try {
      const summary = entries
        .map((e) => `#${e.shape.id}`)
        .join(", ");
      trackOperation(`✏️ Modifying ${entries.length} shape(s) [${summary}] via FactSet intent`);
      await modifyShapes(entries);
      // `modifyShapes` is a single batched host call — if it
      // resolves, all `entries` landed atomically; if it rejects
      // they all failed. Count the whole batch on success.
      succeeded += entries.length;
    } catch (e) {
      if (isEditModeError(e)) throw e;
      const msg = e instanceof Error ? e.message : String(e);
      console.warn(`[Excel] ModifyShapes batch failed:`, e);
      trackOperation(
        `⚠️ ModifyShapes (${entries.length} entries) failed: ${msg.slice(0, 200)}`
      );
    }
  }

  if (selects.length > 0) {
    // Group selects by resolved sheet target. When everything agrees
    // on a sheet we issue one host call; otherwise we dispatch once
    // per distinct sheet (the host's intent only carries a single
    // `sheetName` per call). Same fall-back-to-enclosing rule as the
    // modify path.
    const buckets = new Map<string, number[]>();
    const noSheet: number[] = [];
    for (const s of selects) {
      const resolvedSheet = s.sheetName ?? enclosingSheetName;
      if (resolvedSheet !== undefined) {
        const arr = buckets.get(resolvedSheet) ?? [];
        arr.push(s.id);
        buckets.set(resolvedSheet, arr);
      } else {
        noSheet.push(s.id);
      }
    }
    const dispatch = async (ids: number[], sheetName?: string) => {
      if (ids.length === 0) return 0;
      try {
        trackOperation(
          `🎯 Selecting shapes [${ids.join(", ")}]${
            sheetName ? ` on "${sheetName}"` : ""
          } via FactSet intent`
        );
        await selectShapes({
          shapeIds: ids,
          ...(sheetName !== undefined ? { sheetName } : {}),
        });
        return ids.length;
      } catch (e) {
        // Edit-mode propagates: it'll abort the dispatch loop and bubble
        // up through `applyShapeModificationsViaIntents`'s caller into
        // the outer apply catch, which surfaces the unified message.
        if (isEditModeError(e)) throw e;
        const msg = e instanceof Error ? e.message : String(e);
        console.warn(`[Excel] SelectShapes(${ids.join(",")}) failed:`, e);
        trackOperation(
          `⚠️ SelectShapes [${ids.join(", ")}] failed: ${msg.slice(0, 200)}`
        );
        return 0;
      }
    };
    // `Array.from(map)` materialises the entries as a plain
    // `[K, V][]` so iteration works under `target: "es5"` without
    // `--downlevelIteration` (a `for...of` straight off a `Map`
    // trips TS2802 — see CI failure noted in this file's git
    // history). The sequencing matters: we need `await` per bucket,
    // so `forEach` would silently drop awaits.
    for (const [sheetName, ids] of Array.from(buckets)) {
      succeeded += await dispatch(ids, sheetName);
    }
    succeeded += await dispatch(noSheet, undefined);
  }

  return succeeded;
}

interface RefreshOperationContext {
  lastRefreshId?: string;
  /**
   * Stamped by `case "refreshFactSetData"` when the planner deferred a
   * refresh. The workbook-op schema has no sheets payload, so a
   * planner-emitted refresh is always implicitly "refresh all".
   * Forwarded onto the returned `ApplyModificationsResult` after the
   * workbook-op loop completes, then consumed by the trailing
   * `finalizeRefreshAndRecalc` in `ExcelApp.tsx`.
   */
  deferredRefreshAll?: boolean;
  /**
   * Stamped by `case "recalculate"` with the planner-requested mode.
   * Forwarded onto the returned `ApplyModificationsResult`.
   */
  deferredRecalcMode?: "recalculate" | "full" | "fullRebuild";
}

async function applyWorkbookOperationViaIntents(
  op: WorkbookOperation,
  trackOperation: (op: string, address?: string) => void,
  trackSkip: (op: string, label: string) => void,
  refreshContext?: RefreshOperationContext,
  pinnedWorkbookName?: string,
  // The burst's currently-tracked active sheet on the pin. Used to bring the
  // pin forward for the active-workbook-only `CopyWorksheet` intent when the
  // planner omitted a `sourceSheetName` (i.e. "copy the active sheet").
  activeSheetHint?: string,
): Promise<void> {
  const resolveRefreshId = (explicit?: string): string | undefined => {
    const fromPayload = explicit?.trim();
    if (fromPayload) return fromPayload;
    const fromContext = refreshContext?.lastRefreshId?.trim();
    return fromContext && fromContext.length > 0 ? fromContext : undefined;
  };

  switch (op.action) {
    case "addWorkbook": {
      trackOperation(`📚 Adding new workbook (via FactSet intent)`);
      const message = await addWorkbook();
      if (message) {
        trackOperation(`📚 ${message}`);
      }
      return;
    }

    case "copyWorksheet": {
      const sourceSheetName = op.copyWorksheet?.sourceSheetName;
      const location = op.copyWorksheet?.location;
      const sourceLabel = sourceSheetName ? `"${sourceSheetName}"` : "active sheet";
      trackOperation(
        `📑 Copying ${sourceLabel}${location ? ` (${location})` : ""} via FactSet intent`
      );
      // CopyWorksheet supports only an active workbook (f2_docs). Under
      // background targeting the pin isn't active, so bring it forward via the
      // foreground coordinator. Activating the source sheet (or the burst's
      // tracked active sheet when the planner omitted one) both fronts the pin
      // and makes the intent's "active sheet" default resolve to the pin.
      await runForegroundOnlyOnPin(
        pinnedWorkbookName,
        sourceSheetName ?? activeSheetHint,
        () =>
          copyWorksheet({
            ...(sourceSheetName !== undefined ? { sheetName: sourceSheetName } : {}),
            ...(location !== undefined ? { location } : {}),
          }),
        { contextLabel: `CopyWorksheet ${sourceLabel}` },
      );
      return;
    }

    case "insertWorksheetsFromBase64": {
      const payload = op.insertWorksheetsFromBase64;
      if (!payload?.base64) {
        trackOperation(
          `⚠️ insertWorksheetsFromBase64 skipped — missing required \`base64\` payload`
        );
        return;
      }
      trackOperation(
        `📥 Inserting worksheets from base64 file (via FactSet intent)`
      );
      const sheetNames = await insertWorksheetsFromBase64(payload.base64, {
        addWorkbook: payload.addWorkbook,
      });
      if (sheetNames.length > 0) {
        trackOperation(`📥 Inserted: ${sheetNames.join(", ")}`);
      }
      return;
    }

    case "recalculate": {
      // Local recalc only — does NOT re-fetch FDS data over the wire.
      // Default mode is "recalculate" (changed cells); the wrapper sends
      // `fdc3.nothing` for undefined, which the host treats as default.
      //
      // Deferred to the trailing recalc in `finalizeRefreshAndRecalc` so
      // each `handleQuery` invocation fires at most one
      // `ApplicationCalculate` for planner-explicit requests, instead of
      // one per step. The per-burst recalc that runs inside the
      // manual-calc bracket (gated on `appliedFormulaWrites`) still
      // keeps formula-write verifier reads fresh between steps.
      const mode = op.recalculate?.mode ?? "recalculate";
      if (refreshContext) {
        refreshContext.deferredRecalcMode = mode;
        trackOperation(
          `🧮 Recalculation deferred to end of run (mode: ${mode})`,
        );
      } else {
        // No collector — fall back to inline so the planner's request
        // isn't silently dropped for callers that don't thread a
        // context. Production callers always pass one.
        trackOperation(`🧮 Recalculating workbook (mode: ${mode})`);
        const message = await applicationCalculate(op.recalculate?.mode);
        if (message) {
          trackOperation(`🧮 ${message}`);
        }
      }
      return;
    }

    case "refreshFactSetData": {
      // Network re-fetch of FactSet data (FDS / FDSC / FDSZ formulas) via
      // `factset.Spreadsheet.HarmonizedRefreshWorksheets`. Empty options
      // refresh all worksheets with host defaults.
      //
      // Deferred to the trailing refresh in `finalizeRefreshAndRecalc`
      // so each `handleQuery` invocation fires at most one
      // HarmonizedRefresh per run, rather than one per step. The
      // workbook-op schema has no sheet payload, so a deferred refresh
      // is always implicitly "refresh all".
      if (refreshContext) {
        refreshContext.deferredRefreshAll = true;
        trackOperation(`🔄 FactSet refresh deferred to end of run`);
      } else {
        trackOperation(`🔄 Refreshing FactSet data (via FactSet intent)`);
        const message = await refreshWorksheets({});
        if (message) {
          trackOperation(`🔄 ${message}`);
        }
      }
      return;
    }

    case "refreshCalculate": {
      const payload = op.refreshCalculate;
      const key = payload?.key?.trim();
      const inputsRaw = payload?.inputs;
      if (
        !key ||
        !inputsRaw ||
        typeof inputsRaw !== "object" ||
        Array.isArray(inputsRaw)
      ) {
        trackSkip(
          `⚠️ refreshCalculate skipped — requires \`key\` and \`inputs\` map`,
          `workbook operation "refreshCalculate" incomplete payload`
        );
        return;
      }
      const inputs: Record<string, string> = {};
      for (const [inputKey, inputValue] of Object.entries(inputsRaw)) {
        if (
          typeof inputValue === "string" ||
          typeof inputValue === "number" ||
          typeof inputValue === "boolean"
        ) {
          inputs[inputKey] = String(inputValue);
          continue;
        }
        trackSkip(
          `⚠️ refreshCalculate skipped — input "${inputKey}" must be string/number/boolean`,
          `workbook operation "refreshCalculate" invalid input "${inputKey}"`
        );
        return;
      }
      trackOperation(`⏳ Starting refreshCalculate for template "${key}"`);
      const result = await refreshCalculate(
        { key, inputs },
        payload?.timeoutMs !== undefined ? { timeoutMs: payload.timeoutMs } : undefined
      );
      if (refreshContext) {
        refreshContext.lastRefreshId = result.id;
      }
      const httpSuffix =
        result.httpStatus !== undefined ? `, http ${result.httpStatus}` : "";
      trackOperation(
        `⏳ refreshCalculate queued (id: ${result.id}, status: ${
          result.status || "unknown"
        }${httpSuffix})`
      );
      return;
    }

    case "getRefreshStatus": {
      const payload = op.getRefreshStatus;
      const refreshId = resolveRefreshId(payload?.refreshId);
      if (!refreshId) {
        trackSkip(
          `⚠️ getRefreshStatus skipped — missing \`refreshId\` (and no prior refreshCalculate id in this batch)`,
          `workbook operation "getRefreshStatus" missing refreshId`
        );
        return;
      }
      trackOperation(`⏱️ Checking refresh status for job ${refreshId}`);
      const result = await getRefreshStatus(
        refreshId,
        payload?.timeoutMs !== undefined ? { timeoutMs: payload.timeoutMs } : undefined
      );
      if (refreshContext) {
        refreshContext.lastRefreshId =
          typeof result.body.id === "string" && result.body.id.length > 0
            ? result.body.id
            : refreshId;
      }
      const status =
        typeof result.body.status === "string" && result.body.status.length > 0
          ? result.body.status
          : "unknown";
      const httpSuffix =
        result.httpStatus !== undefined ? ` (http ${result.httpStatus})` : "";
      trackOperation(`⏱️ refresh status: ${status}${httpSuffix}`);
      return;
    }

    case "getRefresh": {
      const payload = op.getRefresh;
      const refreshId = resolveRefreshId(payload?.refreshId);
      const orUrl = payload?.orUrl?.trim();
      if (!refreshId && !orUrl) {
        trackSkip(
          `⚠️ getRefresh skipped — missing \`refreshId\`/\`orUrl\` (and no prior refreshCalculate id in this batch)`,
          `workbook operation "getRefresh" missing refresh target`
        );
        return;
      }
      trackOperation(
        `📦 Fetching refresh result for ${
          refreshId ? `job ${refreshId}` : "explicit orUrl"
        }`
      );
      const result = await getRefresh(
        {
          ...(refreshId ? { refreshId } : {}),
          ...(orUrl ? { orUrl } : {}),
        },
        payload?.timeoutMs !== undefined ? { timeoutMs: payload.timeoutMs } : undefined
      );
      const resolvedRefreshId =
        result.refreshStatus &&
        typeof result.refreshStatus.id === "string" &&
        result.refreshStatus.id.length > 0
          ? result.refreshStatus.id
          : refreshId;
      if (refreshContext && resolvedRefreshId) {
        refreshContext.lastRefreshId = resolvedRefreshId;
      }
      if (result.error) {
        throw new Error(`getRefresh failed: ${result.error}`);
      }
      if (result.workbookBase64) {
        const approxKb = Math.max(
          1,
          Math.round((result.workbookBase64.length * 0.75) / 1024)
        );
        const httpSuffix =
          result.status !== undefined ? ` (http ${result.status})` : "";
        trackOperation(
          `📦 getRefresh returned workbook bytes (~${approxKb} KB)${httpSuffix}`
        );
        return;
      }
      const status =
        result.refreshStatus &&
        typeof result.refreshStatus.status === "string" &&
        result.refreshStatus.status.length > 0
          ? result.refreshStatus.status
          : "unknown";
      const httpSuffix =
        result.status !== undefined ? ` (http ${result.status})` : "";
      trackOperation(`📦 getRefresh status: ${status}${httpSuffix}`);
      return;
    }

    case "awaitRefreshCalculate": {
      const payload = op.awaitRefreshCalculate;
      const refreshId = resolveRefreshId(payload?.refreshId);
      if (!refreshId) {
        trackOperation(
          `⚠️ awaitRefreshCalculate requires a refresh id from refreshCalculate; skipping`
        );
        return;
      }
      trackOperation(`⌛ Waiting for refresh job ${refreshId} to finish`);
      let lastStatus: string | undefined;
      const result = await awaitRefreshCalculate(refreshId, {
        ...(payload?.intervalMs !== undefined ? { intervalMs: payload.intervalMs } : {}),
        ...(payload?.timeoutMs !== undefined ? { timeoutMs: payload.timeoutMs } : {}),
        ...(payload?.pollTimeoutMs !== undefined
          ? { pollTimeoutMs: payload.pollTimeoutMs }
          : {}),
        onUpdate: (update) => {
          const status =
            typeof update.body.status === "string" && update.body.status.length > 0
              ? update.body.status
              : "unknown";
          if (status === lastStatus) return;
          lastStatus = status;
          const httpSuffix =
            update.httpStatus !== undefined ? ` (http ${update.httpStatus})` : "";
          trackOperation(`⌛ refresh status update: ${status}${httpSuffix}`);
        },
      });
      if (refreshContext) {
        refreshContext.lastRefreshId =
          typeof result.body.id === "string" && result.body.id.length > 0
            ? result.body.id
            : refreshId;
      }
      const finalStatus =
        typeof result.body.status === "string" && result.body.status.length > 0
          ? result.body.status
          : "unknown";
      const httpSuffix =
        result.httpStatus !== undefined ? ` (http ${result.httpStatus})` : "";
      trackOperation(`✅ Refresh job ${refreshId} completed with status ${finalStatus}${httpSuffix}`);
      return;
    }

    case "addNamedObject": {
      const payload = op.addNamedObject;
      const name = payload?.name?.trim();
      const addressText = payload?.address?.trim();
      const refersToText = payload?.refersTo?.trim();
      if (!name || (!refersToText && !addressText)) {
        trackSkip(
          `⚠️ addNamedObject skipped — requires \`name\` and either \`refersTo\` or \`address\``,
          `workbook operation "addNamedObject" incomplete payload`
        );
        return;
      }
      const rangeAddress = addressText
        ? (() => {
            const bang = addressText.lastIndexOf("!");
            if (bang < 0) {
              return {
                address: addressText,
                referenceType: "absolute" as const,
              };
            }
            const worksheetName = addressText
              .slice(0, bang)
              .replace(/^'|'$/g, "");
            return {
              address: addressText.slice(bang + 1),
              referenceType: "absolute" as const,
              ...(worksheetName ? { worksheetName } : {}),
            };
          })()
        : undefined;
      const targetRef = refersToText ?? addressText;
      const isVisible = payload?.isVisible ?? payload?.visible;
      trackOperation(
        `🏷️ Adding named object "${name}"${
          targetRef ? ` → ${targetRef}` : ""
        } via FactSet intent`
      );
      // Prefer the planner-supplied workbook name, fall back to the
      // pinned workbook (so named-object writes never leak into the
      // user's currently-focused workbook when the plan started in
      // another one). Address-form named objects don't carry a
      // workbookName on the `RangeAddress`, so the `options.workbookName`
      // is the only routing hint the host gets.
      const resolvedNoWorkbookName =
        payload?.workbookName ?? pinnedWorkbookName;
      await addNamedObject({
        name,
        ...(refersToText ? { refersTo: refersToText } : {}),
        ...(rangeAddress ? { address: rangeAddress } : {}),
        ...(payload?.nameType ? { nameType: payload.nameType } : {}),
        ...(resolvedNoWorkbookName
          ? { workbookName: resolvedNoWorkbookName }
          : {}),
        ...(payload?.scope !== undefined ? { scope: payload.scope } : {}),
        ...(isVisible !== undefined ? { isVisible } : {}),
        ...(payload?.comment ? { comment: payload.comment } : {}),
      });
      // AI-2616: drop the cached `namedObjects` slice so the next
      // `extractWorkbookContent*` re-fetches and the planner sees the
      // newly-defined name on the very next prompt. `connections` /
      // `openWorkbooks` slots stay populated — they don't depend on
      // named-object mutation.
      invalidateNamedObjectsCache();
      return;
    }

    case "removeNamedObject": {
      const payload = op.removeNamedObject;
      const name = payload?.name?.trim();
      if (!name) {
        trackSkip(
          `⚠️ removeNamedObject skipped — missing required \`name\``,
          `workbook operation "removeNamedObject" missing name`
        );
        return;
      }
      trackOperation(`🏷️ Removing named object "${name}" via FactSet intent`);
      const resolvedRemoveWorkbookName =
        payload?.workbookName ?? pinnedWorkbookName;
      await removeNamedObject({
        name,
        ...(resolvedRemoveWorkbookName
          ? { workbookName: resolvedRemoveWorkbookName }
          : {}),
        ...(payload?.scope !== undefined ? { scope: payload.scope } : {}),
      });
      // AI-2616: drop the cached `namedObjects` slice (see addNamedObject
      // branch for the rationale).
      invalidateNamedObjectsCache();
      return;
    }

    case "search": {
      const payload = op.search;
      const query = payload?.query?.trim();
      if (!query) {
        trackSkip(
          `⚠️ search skipped — missing required \`query\``,
          `workbook operation "search" missing query`
        );
        return;
      }
      trackOperation(`🔎 Searching workbook for "${query}" via FactSet intent`);
      const result = await searchWorkbook({
        query,
        ...(payload?.workbookName ? { workbookName: payload.workbookName } : {}),
        ...(payload?.worksheetName ? { worksheetName: payload.worksheetName } : {}),
        ...(payload?.matchCase !== undefined ? { matchCase: payload.matchCase } : {}),
        ...(payload?.matchWholeCell !== undefined
          ? { matchWholeCell: payload.matchWholeCell }
          : {}),
        ...(payload?.searchIn ? { searchIn: payload.searchIn } : {}),
        ...(payload?.maxResults !== undefined ? { maxResults: payload.maxResults } : {}),
      });
      const count = result.matches.length;
      if (count === 0) {
        trackOperation(`🔎 Search found no matches for "${query}"`);
      } else {
        const preview = result.matches
          .slice(0, 5)
          .map((m) => {
            const addr =
              (typeof m.address === "string" && m.address) ||
              (typeof m.rangeAddress === "string" && m.rangeAddress) ||
              (typeof m.cellAddress === "string" && m.cellAddress) ||
              "(unknown address)";
            const sheet =
              (typeof m.worksheetName === "string" && m.worksheetName) ||
              (typeof m.sheetName === "string" && m.sheetName);
            return sheet ? `${sheet}!${addr}` : addr;
          })
          .join(", ");
        trackOperation(
          `🔎 Search found ${count} match(es)${
            preview ? `: ${preview}${count > 5 ? ", ..." : ""}` : ""
          }`
        );
      }
      return;
    }

    case "insertTtfApiResponse": {
      // The Phase 4 cell-write path (see the cells loop in
      // `applyWorkbookModificationsViaIntents`) is the primary
      // producer of TTF host writes today — it builds an
      // `apiResponse` in-process via `runTtfQuery` +
      // `adaptTtfApiResponse` and dispatches there. This dispatcher
      // case stays wired for the (currently rare) path where a
      // producer hands a pre-built `apiResponse` envelope through the
      // workbook-operations channel directly. Both routes terminate
      // in the same `excelHostOps.insertTtfApiResponse` wrapper.
      const payload = op.insertTtfApiResponse;
      if (!payload?.apiResponse) {
        trackSkip(
          `⚠️ Workbook operation "insertTtfApiResponse" missing apiResponse; skipping`,
          `workbook operation "insertTtfApiResponse" (no payload)`
        );
        return;
      }
      trackOperation(
        `📐 Inserting TTF API response (${payload.apiResponse.results?.length ?? 0} formula(s))`
      );
      await insertTtfApiResponse(payload.apiResponse, payload.options);
      return;
    }

    default: {
      // Exhaustiveness guard — surface unrecognised actions instead of
      // silently dropping them, so a planner regression is loud.
      const exhaustive: never = op.action;
      void exhaustive;
      trackSkip(
        `⚠️ Workbook operation action "${String(op.action)}" not recognised; skipping`,
        `workbook operation "${String(op.action)}"`
      );
      return;
    }
  }
}

/**
 * Apply modifications to a single worksheet
 */
interface WorksheetContentWriteState {
  confirmedBySheet: ContentWriteLedger;
  failedBySheet: ContentWriteLedger;
}

async function applyWorksheetModification(
  context: Excel.RequestContext,
  modification: WorksheetModification,
  trackOperation: (op: string, address?: string) => void,
  trackSkip: (op: string, label: string) => void,
  trackFailure: (op: string, label: string) => void,
  editedCells: string[],
  writeState: WorksheetContentWriteState,
  nonFinalFactSetCellWrites: WeakSet<CellModification>,
): Promise<void> {
  // Get target sheet (active if not specified), using getItemOrNullObject to avoid throwing
  let sheet: Excel.Worksheet;
  if (modification.sheetName) {
    const candidate = context.workbook.worksheets.getItemOrNullObject(modification.sheetName);
    candidate.load("name");
    await context.sync();
    if (candidate.isNullObject) {
      const message = `Sheet "${modification.sheetName}" not found`;
      console.warn(`[Excel] ${message}; skipping worksheet modification`);
      trackFailure(`❌ ${message}; no cells were changed`, `worksheet "${modification.sheetName}"`);
      return;
    } else {
      sheet = candidate;
    }
  } else {
    sheet = context.workbook.worksheets.getActiveWorksheet();
  }
  const confirmedContentWrites = contentWriteLedgerEntries(
    writeState.confirmedBySheet,
    modification.sheetName,
  );
  const failedContentWrites = contentWriteLedgerEntries(
    writeState.failedBySheet,
    modification.sheetName,
  );

  // Apply cell modifications - sync after each to isolate errors
  if (modification.cells) {
    for (const cellMod of modification.cells) {
      const editedCellsBeforeCell = editedCells.length;
      try {
        const isNonFinalFactSetWrite =
          nonFinalFactSetCellWrites.has(cellMod);
        const hostCellMod = isNonFinalFactSetWrite
          ? withoutCellNumberFormat(cellMod)
          : normalizeDirectFactSetCellFormat(cellMod);
        const pendingEdits: string[] = [];
        await applyCellModification(
          context,
          sheet,
          hostCellMod,
          trackOperation,
          pendingEdits,
        );
        await context.sync();
        editedCells.push(...pendingEdits);
        if (
          cellMod.action === "set" &&
          (cellMod.formula !== undefined || cellMod.value !== undefined)
        ) {
          confirmedContentWrites.push(cellMod.address);
        }
      } catch (e) {
        // The write is only confirmed after context.sync resolves.
        editedCells.splice(editedCellsBeforeCell);
        // Edit-mode errors must propagate so the outer Office.js apply
        // catch can stop with the unified message instead of cascading
        // a per-cell warning for every remaining cell in the loop.
        if (isEditModeError(e)) throw e;
        const msg = e instanceof Error ? e.message : String(e);
        console.warn(`[Excel] Cell modification at ${cellMod.address} failed:`, e);
        trackFailure(
          `❌ Cell ${cellMod.address} failed: ${msg.slice(0, 100)}`,
          `cell ${cellMod.address} action "${cellMod.action}"`,
        );
        failedContentWrites.push(cellMod.address);
      }
    }
  }

  // Apply range modifications - sync after each to isolate errors
  if (modification.ranges) {
    for (const rangeMod of modification.ranges) {
      const editedCellsBeforeRange = editedCells.length;
      try {
        const formatGuard = getFactSetFormatGuard(rangeMod);
        if (
          formatGuard?.requiresSuccessfulWrite &&
          (!confirmedContentWrites.some((address) =>
            excelRangeContainsAddress(address, rangeMod.address),
          ) ||
            failedContentWrites.some((address) =>
              excelRangeContainsAddress(address, rangeMod.address),
            ) ||
            (formatGuard.mode === "percentage_points" &&
              (typeof formatGuard.expectedFormula !== "string" ||
                inferFactSetFormatHint({
                  formula: formatGuard.expectedFormula,
                }).semantics !== "percentage_points")) ||
            (formatGuard.mode === "reset_stale_percentage_points" &&
              typeof formatGuard.expectedFormula === "string" &&
              inferFactSetFormatHint({
                formula: formatGuard.expectedFormula,
              }).semantics === "percentage_points"))
        ) {
          trackOperation(
            `Skipped internal FactSet percent guard for ${rangeMod.address} because its source formula was not confirmed.`,
          );
          continue;
        }
        const pendingEdits: string[] = [];
        await applyRangeModification(
          context,
          sheet,
          rangeMod,
          trackOperation,
          trackSkip,
          pendingEdits
        );
        await context.sync();
        editedCells.push(...pendingEdits);
        if (
          rangeMod.action === "fill" ||
          (rangeMod.action === "clear" &&
            (rangeMod.clear?.clearType ?? "All") === "Contents")
        ) {
          confirmedContentWrites.push(rangeMod.address);
        } else if (rangeMod.action === "moveRange") {
          const movedDestination = excelMoveDestinationRange(
            rangeMod.address,
            rangeMod.moveRange?.destination,
          );
          if (movedDestination) {
            const destinationWrites = contentWriteLedgerEntries(
              writeState.confirmedBySheet,
              rangeMod.moveRange?.destinationSheetName?.trim() ||
                modification.sheetName,
            );
            destinationWrites.push(movedDestination);
          }
        }
      } catch (e) {
        // applyRangeModification queues work before context.sync. Roll back
        // every address attributed to this range when validation or sync
        // rejects.
        editedCells.splice(editedCellsBeforeRange);
        if (isEditModeError(e)) throw e;
        const msg = e instanceof Error ? e.message : String(e);
        console.warn(`[Excel] Range modification at ${rangeMod.address} failed:`, e);
        trackFailure(
          `❌ Range ${rangeMod.address} failed: ${msg.slice(0, 100)}`,
          `range ${rangeMod.address} action "${rangeMod.action}"`,
        );
        failedContentWrites.push(rangeMod.address);
      }
    }
  }

  // Apply table modifications
  if (modification.tables) {
    for (const tableMod of modification.tables) {
      const editedCellsBeforeTable = editedCells.length;
      try {
        await applyTableModification(context, sheet, tableMod, trackOperation, editedCells);
        await context.sync();
      } catch (e) {
        editedCells.splice(editedCellsBeforeTable);
        if (isEditModeError(e)) throw e;
        const msg = e instanceof Error ? e.message : String(e);
        console.warn(`[Excel] Table modification failed:`, e);
        trackFailure(
          `❌ Table "${tableMod.name || "unnamed"}" at ${
            tableMod.address || "?"
          } failed: ${msg.slice(0, 100)}`,
          `table "${tableMod.name || "unnamed"}" action "${tableMod.action}"`,
        );
      }
    }
  }

  // Apply chart modifications (Office.js path — sideloaded Excel only).
  //
  // The bridge path's equivalent loop in `applyWorkbookModificationsViaIntents`
  // routes `ChartModification.action = "create"` through
  // `factset.Spreadsheet.Charting.AddExcelChart` and
  // `ChartModification.action = "update"` through
  // `Charting.UpdateExcelChart`. Here we do the same outcome via
  // native Office.js (`sheet.charts.add()` for create; index into
  // `sheet.charts.items` for update). Same planner schema produces the
  // same visual result on either execution path.
  //
  // `delete` is still skipped on both paths — pending FactSet's
  // pre-announced shape-ops intent family (`#open-shape-ops`).
  // Office.js could implement `delete` natively (`chart.delete()`),
  // but bridge parity wins until both paths can ship it together.
  if (modification.charts) {
    for (const chartMod of modification.charts) {
      try {
        if (chartMod.action === "addSeries") {
          const targetIdx = chartMod.chartIndex ?? 0;
          const series = chartMod.series;
          if (!series || typeof series.values !== "string" || series.values.length === 0) {
            trackSkip(
              `⚠️ chart #${targetIdx} addSeries requires \`series.values\` (A1 range); skipping`,
              `chart #${targetIdx} addSeries missing values`
            );
            continue;
          }
          const charts = sheet.charts;
          charts.load("items/name");
          await context.sync();
          const target = charts.items[targetIdx];
          if (!target) {
            trackSkip(
              `⚠️ chart #${targetIdx} addSeries: target chart not found on ${
                modification.sheetName ?? "active sheet"
              } (sheet has ${charts.items.length} chart(s)); skipping`,
              `chart #${targetIdx} addSeries`
            );
            continue;
          }
          // `chart.series.add(name?)` returns a new ChartSeries appended
          // to the collection. We then bind values + categories via
          // `setValues` / `setXAxisValues` on resolved Range objects.
          // Office.js doesn't expose a single-call series-add API that
          // takes range strings, and `Excel.Workbook` lacks a
          // top-level `getRange()` in this project's typings — so we
          // route through `resolveRangeForOfficeJs` which strips any
          // `Sheet1!` prefix and resolves via
          // `worksheets.getItem(...).getRange(address)` (or the
          // chart's containing sheet for bare addresses).
          const newSeries = target.series.add(series.name ?? "");
          newSeries.setValues(
            resolveRangeForOfficeJs(context, sheet, series.values)
          );
          if (series.categories) {
            newSeries.setXAxisValues(
              resolveRangeForOfficeJs(context, sheet, series.categories)
            );
          }
          if (series.axisGroup === "secondary") {
            newSeries.axisGroup = Excel.ChartAxisGroup.secondary;
          } else if (series.axisGroup === "primary") {
            newSeries.axisGroup = Excel.ChartAxisGroup.primary;
          }
          const seriesOfficeType = plannerChartTypeToOffice(series.chartType);
          if (seriesOfficeType !== undefined) {
            newSeries.chartType = seriesOfficeType;
          }
          if (series.fillColor) {
            newSeries.format.fill.setSolidColor(series.fillColor);
          }
          if (series.lineColor) {
            newSeries.format.line.color = series.lineColor;
          }
          if (series.lineWeight !== undefined) {
            newSeries.format.line.weight = series.lineWeight;
          }
          await context.sync();
          const desc = series.name
            ? `series "${series.name}" on chart #${targetIdx}`
            : `series on chart #${targetIdx}`;
          trackOperation(`📈 Added ${desc} via Office.js Excel.run`);
          continue;
        }
        if (chartMod.action === "update") {
          const targetIdx = chartMod.chartIndex ?? 0;
          const charts = sheet.charts;
          charts.load("items/name");
          await context.sync();
          const target = charts.items[targetIdx];
          if (!target) {
            trackSkip(
              `⚠️ Chart update target index ${targetIdx} not found on ${
                modification.sheetName ?? "active sheet"
              } (sheet has ${charts.items.length} chart(s)); skipping`,
              `chart update #${targetIdx}`
            );
            continue;
          }
          const updateOfficeType = plannerChartTypeToOffice(chartMod.type);
          const applied: string[] = [];
          if (updateOfficeType !== undefined) {
            target.chartType = updateOfficeType;
            applied.push("chartType");
          }
          if (chartMod.title !== undefined) {
            target.title.text = chartMod.title;
            target.title.visible = true;
            applied.push("title.text");
          }
          if (applied.length === 0) {
            trackSkip(
              `⚠️ chart #${targetIdx} update had no fields to apply (set \`type\` or \`title\`); skipping`,
              `chart #${targetIdx} empty update`
            );
            continue;
          }
          await context.sync();
          const desc = chartMod.title
            ? `chart #${targetIdx} "${chartMod.title}"`
            : `chart #${targetIdx}`;
          trackOperation(
            `📈 Updated ${desc} via Office.js Excel.run (applied ${applied.join(", ")})`
          );
          continue;
        }
        if (chartMod.action === "updateSeries") {
          // Office.js parity for `Charting.UpdateSeriesInChart`. We
          // grab `chart.series.getItemAt(seriesIndex)` and mutate the
          // same fields the bridge wrapper accepts. Each set the
          // planner emits maps onto an `applied` entry so the success
          // message mirrors the host's `appliedProperties[]` (the
          // bridge response field is the source of truth on parity).
          // Same `resolveRangeForOfficeJs` used by `addSeries` handles
          // the `Sheet1!A1:A10`-style sheet-prefixed range strings
          // for `values` / `categories`.
          const targetIdx = chartMod.chartIndex ?? 0;
          const seriesIndex = chartMod.seriesIndex;
          const series = chartMod.series;
          if (typeof seriesIndex !== "number") {
            trackSkip(
              `⚠️ chart #${targetIdx} updateSeries requires \`seriesIndex\` (0-based number); skipping`,
              `chart #${targetIdx} updateSeries missing seriesIndex`
            );
            continue;
          }
          if (!series || typeof series !== "object") {
            trackSkip(
              `⚠️ chart #${targetIdx} series #${seriesIndex} updateSeries requires a \`series\` body with at least one field; skipping`,
              `chart #${targetIdx} series #${seriesIndex} updateSeries empty body`
            );
            continue;
          }
          const charts = sheet.charts;
          charts.load("items/name");
          await context.sync();
          const targetChart = charts.items[targetIdx];
          if (!targetChart) {
            trackSkip(
              `⚠️ chart #${targetIdx} updateSeries: target chart not found on ${
                modification.sheetName ?? "active sheet"
              } (sheet has ${charts.items.length} chart(s)); skipping`,
              `chart #${targetIdx} updateSeries`
            );
            continue;
          }
          const targetSeriesItems = targetChart.series;
          targetSeriesItems.load("count");
          await context.sync();
          if (seriesIndex < 0 || seriesIndex >= targetSeriesItems.count) {
            trackSkip(
              `⚠️ chart #${targetIdx} updateSeries: seriesIndex ${seriesIndex} out of range (chart has ${targetSeriesItems.count} series); skipping`,
              `chart #${targetIdx} series #${seriesIndex} updateSeries out of range`
            );
            continue;
          }
          const targetSeries = targetSeriesItems.getItemAt(seriesIndex);
          const applied: string[] = [];
          if (series.name !== undefined) {
            targetSeries.name = series.name;
            applied.push("name");
          }
          if (series.values !== undefined) {
            targetSeries.setValues(
              resolveRangeForOfficeJs(context, sheet, series.values)
            );
            applied.push("values");
          }
          if (series.categories !== undefined) {
            targetSeries.setXAxisValues(
              resolveRangeForOfficeJs(context, sheet, series.categories)
            );
            applied.push("categories");
          }
          if (series.axisGroup === "secondary") {
            targetSeries.axisGroup = Excel.ChartAxisGroup.secondary;
            applied.push("axisGroup");
          } else if (series.axisGroup === "primary") {
            targetSeries.axisGroup = Excel.ChartAxisGroup.primary;
            applied.push("axisGroup");
          }
          const seriesOfficeType = plannerChartTypeToOffice(series.chartType);
          if (seriesOfficeType !== undefined) {
            targetSeries.chartType = seriesOfficeType;
            applied.push("chartType");
          }
          if (series.fillColor) {
            targetSeries.format.fill.setSolidColor(series.fillColor);
            applied.push("fill");
          }
          if (series.lineColor) {
            targetSeries.format.line.color = series.lineColor;
            applied.push("line.color");
          }
          if (series.lineWeight !== undefined) {
            targetSeries.format.line.weight = series.lineWeight;
            applied.push("line.weight");
          }
          if (applied.length === 0) {
            trackSkip(
              `⚠️ chart #${targetIdx} series #${seriesIndex} updateSeries had no recognised fields (set values / categories / name / chartType / axisGroup / fillColor / lineColor / lineWeight); skipping`,
              `chart #${targetIdx} series #${seriesIndex} updateSeries empty after translation`
            );
            continue;
          }
          await context.sync();
          const desc = series.name
            ? `series #${seriesIndex} "${series.name}" on chart #${targetIdx}`
            : `series #${seriesIndex} on chart #${targetIdx}`;
          trackOperation(
            `📈 Updated ${desc} via Office.js Excel.run (applied ${applied.join(", ")})`
          );
          continue;
        }
        if (chartMod.action === "removeSeries") {
          // Office.js parity for `Charting.RemoveSeriesFromChart`. The
          // Excel JavaScript API exposes `series.delete()` on the
          // `ChartSeries` object (Excel API 1.7+); after removal,
          // every later `seriesIndex` shifts down by one, so the
          // planner is told to emit batched removes in DESCENDING
          // index order (see `ChartModification.seriesIndex` JSDoc).
          const targetIdx = chartMod.chartIndex ?? 0;
          const seriesIndex = chartMod.seriesIndex;
          if (typeof seriesIndex !== "number") {
            trackSkip(
              `⚠️ chart #${targetIdx} removeSeries requires \`seriesIndex\` (0-based number); skipping`,
              `chart #${targetIdx} removeSeries missing seriesIndex`
            );
            continue;
          }
          const charts = sheet.charts;
          charts.load("items/name");
          await context.sync();
          const targetChart = charts.items[targetIdx];
          if (!targetChart) {
            trackSkip(
              `⚠️ chart #${targetIdx} removeSeries: target chart not found on ${
                modification.sheetName ?? "active sheet"
              } (sheet has ${charts.items.length} chart(s)); skipping`,
              `chart #${targetIdx} removeSeries`
            );
            continue;
          }
          const removeSeriesItems = targetChart.series;
          removeSeriesItems.load("count");
          await context.sync();
          if (seriesIndex < 0 || seriesIndex >= removeSeriesItems.count) {
            trackSkip(
              `⚠️ chart #${targetIdx} removeSeries: seriesIndex ${seriesIndex} out of range (chart has ${removeSeriesItems.count} series); skipping`,
              `chart #${targetIdx} series #${seriesIndex} removeSeries out of range`
            );
            continue;
          }
          removeSeriesItems.getItemAt(seriesIndex).delete();
          await context.sync();
          trackOperation(
            `📈 Removed series #${seriesIndex} on chart #${targetIdx} via Office.js Excel.run`
          );
          continue;
        }
        if (chartMod.action !== "create") {
          trackSkip(
            `⚠️ Chart action "${chartMod.action}" not supported on Office.js path yet; skipping`,
            `chart ${chartMod.action} on ${modification.sheetName ?? "active sheet"}`
          );
          continue;
        }
        const plannerType = (chartMod.type || "column").toLowerCase();
        if (!chartMod.dataRange) {
          const desc = chartMod.title
            ? `${plannerType} chart "${chartMod.title}"`
            : `${plannerType} chart`;
          trackSkip(
            `⚠️ ${desc}: chart "create" requires \`dataRange\`; skipping`,
            `${desc} missing dataRange`
          );
          continue;
        }
        const officeChartType =
          plannerChartTypeToOffice(plannerType) ?? Excel.ChartType.columnClustered;
        const dataRange = sheet.getRange(chartMod.dataRange);
        const newChart = sheet.charts.add(
          officeChartType,
          dataRange,
          Excel.ChartSeriesBy.auto
        );
        // Cell-anchored placement uses Office.js's `setPosition(start, end)`
        // which takes A1-style cell references — much cleaner than the
        // pixel translation the bridge path has to do, and accurate at any
        // zoom level since Excel resolves the cell anchors itself.
        //
        // **Inclusive endpoints.** `setPosition(start, end)` treats
        // `end` as the inclusive bottom-right cell, so a chart that
        // should occupy `widthCells` columns × `heightRows` rows
        // starting at `(colIdx, rowNum)` ends at
        // `(colIdx + widthCells - 1, rowNum + heightRows - 1)` — NOT
        // `(colIdx + widthCells, rowNum + heightRows)`, which would
        // make the chart 1 column wider and 1 row taller than asked.
        // This matches the bridge path's `plannerChartPositionToPoints`,
        // where `width = wCells * colWidth` (no extra cell). The
        // `Math.max(...)` clamp guards against degenerate
        // `widthCells: 0` / `heightRows: 0` payloads — those land
        // the chart on a single anchor cell rather than producing a
        // negative column letter.
        const startCell = chartMod.position?.startCell;
        if (startCell) {
          const widthCells = chartMod.position?.width ?? 6;
          const heightRows = chartMod.position?.height ?? 15;
          const cellMatch = /^\$?([A-Z]+)\$?(\d+)$/i.exec(startCell);
          if (cellMatch) {
            const colIdx = columnLetterToIndex(cellMatch[1]);
            const rowNum = parseInt(cellMatch[2], 10);
            const endColIdx = Math.max(colIdx, colIdx + widthCells - 1);
            const endRowNum = Math.max(rowNum, rowNum + heightRows - 1);
            const endCell = `${columnIndexToLetter(endColIdx)}${endRowNum}`;
            newChart.setPosition(startCell, endCell);
          }
        }
        if (chartMod.title) {
          newChart.title.text = chartMod.title;
          newChart.title.visible = true;
        }
        // Legend default mirrors what the bridge dispatcher would emit
        // for a no-config chart: bottom-anchored, visible. Planner schema
        // doesn't expose legend overrides today; expose a sub-object on
        // `ChartModification` if/when it does.
        newChart.legend.visible = true;
        newChart.legend.position = Excel.ChartLegendPosition.bottom;
        await context.sync();
        const placement = startCell ? ` at ${startCell}` : "";
        const desc = chartMod.title
          ? `${plannerType} chart "${chartMod.title}"`
          : `${plannerType} chart`;
        trackOperation(
          `📈 Inserted ${desc}${placement} via Office.js Excel.run`,
          chartMod.dataRange
        );
      } catch (e) {
        if (isEditModeError(e)) throw e;
        const msg = e instanceof Error ? e.message : String(e);
        console.warn(`[Excel] Chart modification failed:`, e);
        trackFailure(
          `❌ Chart "${chartMod.title || "unnamed"}" (${chartMod.type ?? "native"}) at ${
            chartMod.position?.startCell ?? "?"
          } failed: ${msg.slice(0, 100)}`,
          `chart "${chartMod.title || "unnamed"}"`,
        );
      }
    }
  }

  // FactSet ActiveGraph (FDSChart OLE) embeds — bridge-only.
  //
  // The bridge path's equivalent in `applyWorkbookModificationsViaIntents`
  // dispatches each entry through `factset.Spreadsheet.AddActiveGraph`.
  // Office.js has no native equivalent: ActiveGraph is a FactSet-
  // specific OLE control, and the public Office.js API exposes neither
  // the OLE registration nor the FDSChart binder. Warn-skip wholesale
  // here so the planner's emitted JSON still validates and the operator
  // sees an honest "skipped on this path" rather than a silent drop.
  if (modification.activeGraphs && modification.activeGraphs.length > 0) {
    trackSkip(
      `⚠️ ActiveGraph operations (${modification.activeGraphs.length}) not supported on Office.js path; skipping`,
      `activeGraphs (${modification.activeGraphs.length}) on ${modification.sheetName ?? "active sheet"}`
    );
  }

  // Freeze / unfreeze panes — bridge-only.
  //
  // The bridge path in `applyWorkbookModificationsViaIntents` dispatches
  // through `factset.Spreadsheet.FreezeUnfreezePanes`, which accepts
  // `row` and `column` together in a single call. We previously
  // shipped an Office.js fallback that mapped onto
  // `Worksheet.freezePanes.{freezeRows,freezeColumns,freezeAt,unfreeze}`,
  // but Office.js's `WorksheetFreezePanes` holds a SINGLE freeze
  // state per worksheet — a `freezeRows(r); freezeColumns(c)`
  // sequence ends with only the columns frozen (the second call
  // replaces the first's state), and the `freezeAt(frozenRange)`
  // workaround carries its own footguns (the parameter is the frozen
  // rectangle itself, NOT the anchor cell — see [Microsoft Learn](https://learn.microsoft.com/javascript/api/excel/excel.worksheetfreezepanes)).
  // Since freeze-pane behaviour only matters for ergonomics (no data
  // correctness implications) and the FactSet host wires it cleanly,
  // we warn-skip on the Office.js path rather than maintain a
  // duplicate implementation that has to keep ducking these API
  // quirks. The planner's emitted JSON still validates and the
  // operator sees an honest "skipped on this path".
  if (modification.freezePanes) {
    trackSkip(
      `⚠️ freezePanes (${modification.freezePanes.mode}) not supported on Office.js path; skipping`,
      `freezePanes (${modification.freezePanes.mode}) on ${modification.sheetName ?? "active sheet"}`
    );
  }

  // Generic shape operations (delete / modify / select) — bridge-only.
  //
  // The bridge path dispatches each entry through
  // `applyShapeModificationsViaIntents`, which uses host-assigned
  // numeric ids for routing. Office.js mode does not have a stable
  // mapping from those ids to local Shape / Chart objects (the
  // host's `chartIndex` and `id` come from FactSet's chart-family
  // writes; Office.js exposes its own `sheet.shapes.items[i].id`
  // values that don't match). Until both surfaces converge, warn-skip
  // wholesale here so the planner's emitted JSON still validates
  // honestly and the operator sees a "skipped on this path" rather
  // than a silent drop / mismatched action.
  if (modification.shapes && modification.shapes.length > 0) {
    trackSkip(
      `⚠️ Shape operations (${modification.shapes.length}) not supported on Office.js path; skipping`,
      `shapes (${modification.shapes.length}) on ${modification.sheetName ?? "active sheet"}`
    );
  }

  // Note: Logo modifications are handled in applyWorkbookModifications
  // after the main Excel.run completes (they have their own Excel.run)
}

/**
 * Apply modification to a single cell or small range
 */
async function applyCellModification(
  context: Excel.RequestContext,
  sheet: Excel.Worksheet,
  modification: CellModification,
  trackOperation: (op: string, address?: string) => void,
  editedCells: string[]
): Promise<void> {
  if (!modification.address || !isValidExcelAddress(modification.address)) {
    console.error(`[Excel] Invalid cell address: "${modification.address}"`);
    throw new Error(
      `Invalid cell address: "${modification.address}". Expected format like A1, B2, or A1:G1`
    );
  }

  try {
    const range = sheet.getRange(modification.address);
    const isRange = modification.address.includes(":");
    let actionDescription = "";

    switch (modification.action) {
      case "set":
        if (modification.formula !== undefined) {
          trackOperation(
            `📐 ${modification.address} = ${modification.formula}`,
            modification.address
          );
          editedCells.push(modification.address);
          actionDescription = `Set formula: ${modification.formula}`;
          if (isRange) {
            // For ranges, set formula for each cell (Excel will auto-adjust references)
            const size = parseRangeSize(modification.address);
            if (size) {
              const formulas = Array(size.rows)
                .fill(null)
                .map(() => Array(size.cols).fill(modification.formula));
              range.formulas = formulas;
            } else {
              range.formulas = [[modification.formula]];
            }
          } else {
            range.formulas = [[modification.formula]];
          }
        } else if (modification.value !== undefined) {
          const literalValue = literalizeCodeOwnedValue(modification.value);
          const displayValue =
            typeof modification.value === "string"
              ? `"${modification.value.slice(0, 30)}${modification.value.length > 30 ? "..." : ""}"`
              : modification.value;
          trackOperation(`✏️ ${modification.address} = ${displayValue}`, modification.address);
          editedCells.push(modification.address);
          actionDescription = `Set value: ${displayValue}`;
          // Value and formula slots are deliberately distinct. In
          // particular, a value beginning "=" remains literal text.
          if (isRange) {
            const size = parseRangeSize(modification.address);
            if (size) {
              const values = Array(size.rows)
                .fill(null)
                .map(() => Array(size.cols).fill(literalValue));
              range.values = values;
            } else {
              range.values = [[literalValue]];
            }
          } else {
            range.values = [[literalValue]];
          }
        }
        if (modification.format) {
          trackOperation(`🎨 Formatting ${modification.address}`, modification.address);
          await applyFormatToRange(range, modification.format);
          actionDescription += actionDescription ? " + formatted" : "Formatted";
        }
        break;

      case "clear":
        trackOperation(`🗑️ Clearing ${modification.address}`, modification.address);
        editedCells.push(modification.address);
        actionDescription = "Cleared";
        range.clear();
        break;

      case "format":
        if (modification.format) {
          trackOperation(`🎨 Formatting ${modification.address}`, modification.address);
          editedCells.push(modification.address);
          actionDescription = "Formatted";
          await applyFormatToRange(range, modification.format);
        }
        break;
    }

    // Cell-comment writes are deferred from the MVP (see the module-level
    // note on `addCommentsOnEdit` above and `f2_docs/excel/finster-docs/
    // missing-intents-v0.md` v0 §6.21 / §6.22). The branch below is kept
    // wired so a future revival can re-enable comments by either flipping
    // `addCommentsOnEdit` back to `true` or removing the `false &&` guard
    // here — `modification.comment` payloads currently arrive on the wire
    // but are intentionally ignored.
    if (false && (modification.comment || addCommentsOnEdit)) {
      await addFinsterComment(
        context,
        sheet,
        modification.address,
        modification.comment,
        actionDescription
      );
    }
  } catch (error) {
    // Preserve the original `OfficeExtension.Error` for edit-mode cases —
    // wrapping in `new Error(...)` would drop the `code` property
    // (`InvalidOperationInCellEditMode`) and `isEditModeError` upstream
    // would no longer match, breaking the L5 cascade-protection guard.
    if (isEditModeError(error)) throw error;
    console.error(`[Excel] Cell modification failed at ${modification.address}:`, error);
    throw new Error(
      `Cell modification failed at ${modification.address}: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Apply modification to a range of cells
 */
async function applyRangeModification(
  context: Excel.RequestContext,
  sheet: Excel.Worksheet,
  modification: RangeModification,
  trackOperation: (op: string, address?: string) => void,
  trackSkip: (op: string, label: string) => void,
  editedCells: string[]
): Promise<void> {
  if (!modification.address || !isValidExcelAddress(modification.address)) {
    console.error(`[Excel] Invalid range address: "${modification.address}"`);
    throw new Error(
      `Invalid range address: "${modification.address}". Expected format like A1:B10 or A1`
    );
  }
  if (modification.action === "fill") {
    assertUnambiguousRangeFill(
      modification.address,
      modification.values,
      modification.formulas,
    );
    if (modification.values !== undefined) {
      assertExactRangeMatrixShape(
        modification.address,
        modification.values,
        "values",
      );
    }
    if (modification.formulas !== undefined) {
      assertExactRangeMatrixShape(
        modification.address,
        modification.formulas,
        "formulas",
      );
    }
    if (
      modification.values === undefined &&
      modification.formulas === undefined
    ) {
      throw new Error(
        `Range ${modification.address} action "fill" requires a non-empty values or formulas matrix.`,
      );
    }
  }

  let actionDescription = "";

  try {
    const range = sheet.getRange(modification.address);

    switch (modification.action) {
      case "fill":
        if (modification.formulas) {
          trackOperation(
            `📐 Filling ${modification.address} with ${modification.formulas.length} formula rows`,
            modification.address
          );
          editedCells.push(modification.address);
          actionDescription = `Filled with ${modification.formulas.length} formula rows`;
          range.formulas = modification.formulas;
        } else if (modification.values) {
          const rowCount = modification.values.length;
          const colCount = modification.values[0]?.length || 0;

          trackOperation(
            `📊 Filling ${modification.address} (${rowCount}×${colCount})`,
            modification.address
          );
          editedCells.push(modification.address);
          actionDescription = `Filled range (${rowCount}×${colCount})`;
          range.values = modification.values.map((row) =>
            row.map(literalizeCodeOwnedValue),
          );
        }
        if (modification.format) {
          trackOperation(`🎨 Formatting ${modification.address}`, modification.address);
          await applyFormatToRange(range, modification.format);
          actionDescription += actionDescription ? " + formatted" : "Formatted";
        }
        break;

      case "clear":
        trackOperation(`🗑️ Clearing ${modification.address}`, modification.address);
        editedCells.push(modification.address);
        actionDescription = "Cleared";
        range.clear();
        break;

      case "format":
        if (modification.format) {
          trackOperation(`🎨 Formatting ${modification.address}`, modification.address);
          editedCells.push(modification.address);
          actionDescription = "Formatted";
          await applyFormatToRange(range, modification.format);
        }
        break;

      case "sort": {
        const sortKeys = modification.sort?.sortKeys;
        if (!Array.isArray(sortKeys) || sortKeys.length === 0) {
          trackSkip(
            `⚠️ ${modification.address}: "sort" requires sort.sortKeys[] with at least one entry; skipping`,
            `range ${modification.address} action "sort" missing sortKeys`
          );
          break;
        }
        // Office.js `range.sort.apply` wants a 0-based column index relative
        // to the range. Translate from the catalog's cell-reference shape.
        const colLetter = (cellRef: string) =>
          (cellRef.match(/^[A-Za-z]+/)?.[0] ?? "").toUpperCase();
        const letterToIdx = (letter: string) =>
          letter.split("").reduce((acc, ch) => acc * 26 + (ch.charCodeAt(0) - 64), 0) - 1;
        const rangeStartLetter = colLetter(modification.address);
        if (!rangeStartLetter) {
          trackSkip(
            `⚠️ ${modification.address}: cannot derive starting column from range; skipping sort`,
            `range ${modification.address} action "sort" unparseable address`
          );
          break;
        }
        const rangeStartIdx = letterToIdx(rangeStartLetter);
        const officeSortFields = sortKeys.map((k) => {
          const letter = colLetter(k.key);
          const absIdx = letter ? letterToIdx(letter) : rangeStartIdx;
          return {
            key: Math.max(0, absIdx - rangeStartIdx),
            ascending: (k.order ?? "Ascending") === "Ascending",
          };
        });
        const summary = sortKeys
          .map((k) => `${k.key}${k.order === "Descending" ? "↓" : "↑"}`)
          .join(", ");
        trackOperation(
          `🔀 Sorting ${modification.address} by ${summary}`,
          modification.address
        );
        editedCells.push(modification.address);
        actionDescription = `Sorted by ${summary}`;
        range.sort.apply(
          officeSortFields,
          modification.sort?.options?.matchCase ?? false,
          modification.sort?.options?.header === "Yes"
        );
        break;
      }

      case "autofit":
        if (modification.fitColumns) {
          trackOperation(
            `📏 Auto-fitting columns in ${modification.address}`,
            modification.address
          );
          actionDescription = "Auto-fitted columns";
          range.format.autofitColumns();
        }
        if (modification.fitRows) {
          trackOperation(`📏 Auto-fitting rows in ${modification.address}`, modification.address);
          actionDescription += actionDescription ? " + rows" : "Auto-fitted rows";
          range.format.autofitRows();
        }
        break;

      case "resize": {
        const { width, height } = modification;
        if (width === undefined && height === undefined) {
          trackSkip(
            `⚠️ ${modification.address}: "resize" requires "width" or "height"; skipping`,
            `range ${modification.address} action "resize" missing width/height`
          );
          break;
        }
        if (width !== undefined) {
          trackOperation(
            `📐 Setting column width of ${modification.address} to ${width}`,
            modification.address
          );
          editedCells.push(modification.address);
          actionDescription = `Set column width to ${width}`;
          range.format.columnWidth = width;
        }
        if (height !== undefined) {
          trackOperation(
            `📐 Setting row height of ${modification.address} to ${height}`,
            modification.address
          );
          editedCells.push(modification.address);
          actionDescription += actionDescription
            ? ` + row height to ${height}`
            : `Set row height to ${height}`;
          range.format.rowHeight = height;
        }
        break;
      }

      case "hideColumns": {
        // Office.js mirror of `factset.Spreadsheet.HideColumns`. Same
        // address-shape contract as the bridge path: address must be
        // a whole-column reference (`"A:A"`, `"B:D"`, `"$A:$C"`).
        // Cell-shaped or row-shaped addresses are rejected up front so
        // we don't silently set `columnHidden = true` on a stray cell
        // (which would still hide the whole column in Office.js, but
        // for an address the user didn't mean).
        if (!isColumnShapedAddress(modification.address)) {
          trackSkip(
            `⚠️ ${modification.address}: "hideColumns" requires a column-shaped range (e.g. "A:C"); skipping`,
            `range ${modification.address} action "hideColumns" wrong shape`
          );
          break;
        }
        trackOperation(
          `🙈 Hiding columns ${modification.address}`,
          modification.address
        );
        editedCells.push(modification.address);
        actionDescription = `Hid columns ${modification.address}`;
        range.columnHidden = true;
        break;
      }

      case "hideRows": {
        // Office.js mirror of `factset.Spreadsheet.HideRows`. Address
        // must be a whole-row reference (`"5:5"`, `"5:10"`,
        // `"$5:$10"`). See `hideColumns` above for the rationale.
        if (!isRowShapedAddress(modification.address)) {
          trackSkip(
            `⚠️ ${modification.address}: "hideRows" requires a row-shaped range (e.g. "5:10"); skipping`,
            `range ${modification.address} action "hideRows" wrong shape`
          );
          break;
        }
        trackOperation(
          `🙈 Hiding rows ${modification.address}`,
          modification.address
        );
        editedCells.push(modification.address);
        actionDescription = `Hid rows ${modification.address}`;
        range.rowHidden = true;
        break;
      }

      case "showColumns": {
        // Office.js mirror of `factset.Spreadsheet.ShowColumns`.
        if (!isColumnShapedAddress(modification.address)) {
          trackSkip(
            `⚠️ ${modification.address}: "showColumns" requires a column-shaped range (e.g. "A:C"); skipping`,
            `range ${modification.address} action "showColumns" wrong shape`
          );
          break;
        }
        trackOperation(
          `👁️ Showing columns ${modification.address}`,
          modification.address
        );
        editedCells.push(modification.address);
        actionDescription = `Showed columns ${modification.address}`;
        range.columnHidden = false;
        break;
      }

      case "showRows": {
        // Office.js mirror of `factset.Spreadsheet.ShowRows`.
        if (!isRowShapedAddress(modification.address)) {
          trackSkip(
            `⚠️ ${modification.address}: "showRows" requires a row-shaped range (e.g. "5:10"); skipping`,
            `range ${modification.address} action "showRows" wrong shape`
          );
          break;
        }
        trackOperation(
          `👁️ Showing rows ${modification.address}`,
          modification.address
        );
        editedCells.push(modification.address);
        actionDescription = `Showed rows ${modification.address}`;
        range.rowHidden = false;
        break;
      }

      case "groupColumns": {
        // Office.js mirror of `factset.Spreadsheet.GroupColumns`. Uses
        // `Range.group(GroupOption.byColumns)` for the outline-grouping
        // semantics. Same address-shape contract as the bridge path:
        // column-shaped reference (`"A:C"`, `"$A:$C"`).
        if (!isColumnShapedAddress(modification.address)) {
          trackSkip(
            `⚠️ ${modification.address}: "groupColumns" requires a column-shaped range (e.g. "A:C"); skipping`,
            `range ${modification.address} action "groupColumns" wrong shape`
          );
          break;
        }
        trackOperation(
          `📂 Grouping columns ${modification.address}`,
          modification.address
        );
        editedCells.push(modification.address);
        actionDescription = `Grouped columns ${modification.address}`;
        range.group(Excel.GroupOption.byColumns);
        break;
      }

      case "groupRows": {
        // Office.js mirror of `factset.Spreadsheet.GroupRows`.
        if (!isRowShapedAddress(modification.address)) {
          trackSkip(
            `⚠️ ${modification.address}: "groupRows" requires a row-shaped range (e.g. "5:10"); skipping`,
            `range ${modification.address} action "groupRows" wrong shape`
          );
          break;
        }
        trackOperation(
          `📂 Grouping rows ${modification.address}`,
          modification.address
        );
        editedCells.push(modification.address);
        actionDescription = `Grouped rows ${modification.address}`;
        range.group(Excel.GroupOption.byRows);
        break;
      }

      case "ungroupColumns": {
        // Office.js mirror of `factset.Spreadsheet.UngroupColumns`.
        if (!isColumnShapedAddress(modification.address)) {
          trackSkip(
            `⚠️ ${modification.address}: "ungroupColumns" requires a column-shaped range (e.g. "A:C"); skipping`,
            `range ${modification.address} action "ungroupColumns" wrong shape`
          );
          break;
        }
        trackOperation(
          `📂 Ungrouping columns ${modification.address}`,
          modification.address
        );
        editedCells.push(modification.address);
        actionDescription = `Ungrouped columns ${modification.address}`;
        range.ungroup(Excel.GroupOption.byColumns);
        break;
      }

      case "ungroupRows": {
        // Office.js mirror of `factset.Spreadsheet.UngroupRows`.
        if (!isRowShapedAddress(modification.address)) {
          trackSkip(
            `⚠️ ${modification.address}: "ungroupRows" requires a row-shaped range (e.g. "5:10"); skipping`,
            `range ${modification.address} action "ungroupRows" wrong shape`
          );
          break;
        }
        trackOperation(
          `📂 Ungrouping rows ${modification.address}`,
          modification.address
        );
        editedCells.push(modification.address);
        actionDescription = `Ungrouped rows ${modification.address}`;
        range.ungroup(Excel.GroupOption.byRows);
        break;
      }

      case "select": {
        // Office.js mirror of `factset.Spreadsheet.SelectRange`. Pure
        // cursor / UX action — call `Range.select()` to update what's
        // highlighted in the host. The enclosing loop already resolved
        // `range` against the right `sheet` (see `worksheetMod.sheetName`
        // handling above), so cross-sheet selects work without an
        // explicit `sheet.activate()` here — `Range.select()` activates
        // the parent worksheet implicitly to match the bridge's
        // behaviour.
        trackOperation(
          `🎯 Selecting ${modification.address}`,
          modification.address
        );
        actionDescription = `Selected ${modification.address}`;
        range.select();
        // Don't push to `editedCells` — selection isn't an edit, and a
        // refresh-after-edit triggered by selection would re-read the
        // sheet for no reason. The sheet's snapshot is unchanged.
        break;
      }

      case "moveRange": {
        // Office.js mirror of `factset.Spreadsheet.MoveRange`. Two
        // semantics: `preserveSource: true` is a copy (source kept),
        // `preserveSource: false` (default) is a cut (source cleared
        // after the copy lands). Cross-sheet moves are supported by
        // overriding either / both of `sourceSheetName` /
        // `destinationSheetName`. We use `Range.copyFrom("All")` (Excel
        // API 1.9+) so values, formulas, AND formatting all travel
        // together — that matches the bridge's cut-and-paste fidelity.
        const moveSpec = modification.moveRange;
        if (!moveSpec || !moveSpec.destination) {
          trackSkip(
            `⚠️ ${modification.address}: "moveRange" requires moveRange.destination; skipping`,
            `range ${modification.address} action "moveRange" missing destination`
          );
          break;
        }
        const sourceSheet = moveSpec.sourceSheetName
          ? context.workbook.worksheets.getItemOrNullObject(moveSpec.sourceSheetName)
          : sheet;
        const destSheet = moveSpec.destinationSheetName
          ? context.workbook.worksheets.getItemOrNullObject(moveSpec.destinationSheetName)
          : sheet;
        // Resolve any null sheets up front; bare `getItemOrNullObject`
        // proxies don't throw until property access, so eager `load + sync`
        // catches the typo'd-sheet case with a clean warn rather than a
        // generic Office.js "GeneralException" inside `copyFrom`.
        sourceSheet.load("name");
        destSheet.load("name");
        await context.sync();
        if ("isNullObject" in sourceSheet && sourceSheet.isNullObject) {
          trackSkip(
            `⚠️ ${modification.address}: source sheet "${moveSpec.sourceSheetName}" not found; skipping moveRange`,
            `range ${modification.address} action "moveRange" missing source sheet`
          );
          break;
        }
        if ("isNullObject" in destSheet && destSheet.isNullObject) {
          trackSkip(
            `⚠️ ${modification.address}: destination sheet "${moveSpec.destinationSheetName}" not found; skipping moveRange`,
            `range ${modification.address} action "moveRange" missing destination sheet`
          );
          break;
        }
        // `Range.copyFrom` automatically resizes the destination to
        // match the source dimensions, so we anchor the destination at
        // its top-left cell (`moveSpec.destination`) and let Office.js
        // expand. Wrong shape (multi-cell destination address) is
        // tolerated — Office.js anchors at the top-left either way.
        const sourceRange = sourceSheet.getRange(modification.address);
        const destRange = destSheet.getRange(moveSpec.destination);
        const preserveSource = moveSpec.preserveSource === true;
        const verb = preserveSource ? "Copy" : "Move";
        const acrossSheets =
          moveSpec.sourceSheetName !== undefined &&
          moveSpec.destinationSheetName !== undefined &&
          moveSpec.sourceSheetName !== moveSpec.destinationSheetName;
        trackOperation(
          `↔️ ${verb} ${modification.address}${
            acrossSheets ? ` (${moveSpec.sourceSheetName})` : ""
          } → ${moveSpec.destination}${
            acrossSheets ? ` (${moveSpec.destinationSheetName})` : ""
          }`,
          modification.address
        );
        editedCells.push(modification.address);
        editedCells.push(moveSpec.destination);
        actionDescription = `${verb}d ${modification.address} → ${moveSpec.destination}`;
        // `copyFrom` semantics: dest.copyFrom(source) copies source's
        // contents into dest. Excel API 1.9+. Pass `"All"` so values,
        // formulas, and formats all travel together.
        destRange.copyFrom(sourceRange, "All");
        if (!preserveSource) {
          // Cut semantics — clear the source AFTER the copy lands.
          // Order matters: clearing first would leave nothing to copy.
          sourceRange.clear();
        }
        break;
      }
    }

    // Cell-comment writes are deferred from the MVP — see the matching
    // note in the cell-modification path above and the module-level
    // `addCommentsOnEdit` doc comment for the rationale and revival path.
    if (false && (modification.comment || addCommentsOnEdit)) {
      await addFinsterComment(
        context,
        sheet,
        modification.address,
        modification.comment,
        actionDescription
      );
    }
  } catch (error) {
    // Preserve the original `OfficeExtension.Error` for edit-mode cases
    // (see `applyCellModification` for the rationale).
    if (isEditModeError(error)) throw error;
    console.error(`[Excel] Range modification failed at ${modification.address}:`, error);
    throw new Error(
      `Range modification failed at ${modification.address}: ${error instanceof Error ? error.message : String(error)}`
    );
  }
}

/**
 * Parse range address to get dimensions (e.g., "A1:C5" -> {rows: 5, cols: 3})
 */
function parseRangeSize(address: string): { rows: number; cols: number } | null {
  const parsed = parseCellRangeAddress(address);
  return parsed
    ? { rows: parsed.rowCount, cols: parsed.columnCount }
    : null;
}

export function assertUnambiguousRangeFill(
  address: string,
  values: unknown,
  formulas: unknown,
): void {
  if (values !== undefined && formulas !== undefined) {
    throw new Error(
      `Range ${address} action "fill" accepts exactly one of values or formulas; supplying both is ambiguous.`,
    );
  }
}

export function assertExactRangeMatrixShape(
  address: string,
  matrix: unknown,
  field: "values" | "formulas",
): void {
  const target = parseCellRangeAddress(address);
  if (!target) {
    throw new Error(
      `Invalid range address "${address}". Expected a non-reversed A1 cell or rectangular range.`,
    );
  }
  if (!Array.isArray(matrix) || matrix.length === 0) {
    throw new Error(
      `Range ${target.normalizedAddress} requires a non-empty ${field} matrix.`,
    );
  }
  const first = matrix[0];
  if (!Array.isArray(first) || first.length === 0) {
    throw new Error(
      `Range ${target.normalizedAddress} requires non-empty ${field} rows.`,
    );
  }
  const columns = first.length;
  for (let row = 0; row < matrix.length; row += 1) {
    if (!Array.isArray(matrix[row]) || matrix[row].length !== columns) {
      throw new Error(
        `Range ${target.normalizedAddress} ${field} matrix is ragged at row ${row}.`,
      );
    }
  }
  if (
    matrix.length !== target.rowCount ||
    columns !== target.columnCount
  ) {
    throw new Error(
      `Range ${target.normalizedAddress} is ` +
      `${target.rowCount}×${target.columnCount}, but ${field} is ` +
      `${matrix.length}×${columns}. Matrices are not padded or truncated.`,
    );
  }
}

/**
 * Apply table modification
 */
async function applyTableModification(
  context: Excel.RequestContext,
  sheet: Excel.Worksheet,
  modification: TableModification,
  trackOperation: (op: string, address?: string) => void,
  editedCells: string[]
): Promise<void> {
  switch (modification.action) {
    case "create":
      if (modification.address && modification.headers && modification.rows) {
        // Build the full data array (headers + rows)
        const allData = [modification.headers, ...modification.rows];
        const tableName = modification.name || "Table";

        trackOperation(
          `📋 Creating table "${tableName}" at ${modification.address} (${modification.rows.length} rows)`,
          modification.address
        );
        editedCells.push(modification.address);

        // First, fill in the data
        const dataRange = sheet
          .getRange(modification.address)
          .getResizedRange(allData.length - 1, modification.headers.length - 1);
        dataRange.values = allData;

        await context.sync();
        
        // Create a table from that range (with unique name to avoid conflicts)
        try {
          const table = sheet.tables.add(dataRange, true /* hasHeaders */);
          if (modification.style) {
            table.style = modification.style;
          }
        } catch (tableErr) {
          // Edit-mode errors must propagate so the outer Office.js apply
          // catch can stop with the unified message — see the cell loop
          // for the cascade-protection rationale.
          if (isEditModeError(tableErr)) throw tableErr;
          // If table creation fails (e.g. overlapping table), data is already filled — continue
          const msg = tableErr instanceof Error ? tableErr.message : String(tableErr);
          trackOperation(`⚠️ Table object creation skipped: ${msg}`);
        }
      }
      break;

    case "delete":
      if (modification.name) {
        trackOperation(`🗑️ Deleting table "${modification.name}"`);
        const table = sheet.tables.getItem(modification.name);
        table.delete();
      }
      break;

    case "addRow":
      if (modification.name && modification.data) {
        trackOperation(`➕ Adding row to table "${modification.name}"`);
        const table = sheet.tables.getItem(modification.name);
        table.rows.add(modification.index ?? null, [modification.data]);
      }
      break;

    case "addColumn":
      if (modification.name && modification.data) {
        trackOperation(`➕ Adding column to table "${modification.name}"`);
        const table = sheet.tables.getItem(modification.name);
        // Convert 1D array to 2D array for column values
        const columnValues = modification.data.map((val) => [val]);
        const column = table.columns.add(modification.index ?? null, columnValues);
        column.load("name");
      }
      break;
  }
}

/**
 * Sanitize color value to valid Excel format
 */
function sanitizeColor(color: string | undefined): string | null {
  if (!color) return null;

  // Already a valid hex color
  if (/^#[0-9A-Fa-f]{6}$/.test(color)) return color;

  // Hex without #
  if (/^[0-9A-Fa-f]{6}$/.test(color)) return `#${color}`;

  // Named colors mapping
  const namedColors: Record<string, string> = {
    black: "#000000",
    white: "#FFFFFF",
    red: "#FF0000",
    green: "#00FF00",
    blue: "#0000FF",
    yellow: "#FFFF00",
    gray: "#808080",
    grey: "#808080",
    lightgray: "#D3D3D3",
    lightgrey: "#D3D3D3",
    darkgray: "#A9A9A9",
    darkgrey: "#A9A9A9",
    navy: "#000080",
    darkblue: "#00008B",
    orange: "#FFA500",
    purple: "#800080",
  };

  const normalized = color.toLowerCase().replace(/\s/g, "");
  if (namedColors[normalized]) return namedColors[normalized];

  // If still invalid, return null (skip this color)
  console.warn(`[Excel] Invalid color value: "${color}", skipping`);
  return null;
}

/**
 * Apply formatting to a range with error handling for each property
 */
async function applyFormatToRange(range: Excel.Range, format: CellFormat): Promise<void> {
  // Merge cells first (before other formatting)
  if (format.merge === true) {
    try {
      range.merge();
    } catch (e) {
      console.warn("[Excel] Failed to merge cells:", e);
    }
  }

  // Font styling
  if (format.font) {
    try {
      if (format.font.name) range.format.font.name = format.font.name;
      if (format.font.size && typeof format.font.size === "number" && format.font.size > 0) {
        range.format.font.size = format.font.size;
      }
      const fontColor = sanitizeColor(format.font.color);
      if (fontColor) range.format.font.color = fontColor;
      if (format.font.bold !== undefined) range.format.font.bold = format.font.bold;
      if (format.font.italic !== undefined) range.format.font.italic = format.font.italic;
      if (format.font.strikethrough !== undefined) {
        range.format.font.strikethrough = format.font.strikethrough;
      }
      if (format.font.underline) {
        range.format.font.underline =
          format.font.underline === "double"
            ? Excel.RangeUnderlineStyle.double
            : format.font.underline === "single"
              ? Excel.RangeUnderlineStyle.single
              : Excel.RangeUnderlineStyle.none;
      }
    } catch (e) {
      console.warn("[Excel] Failed to apply font formatting:", e);
    }
  }
  
  // Fill styling — clear before color to avoid conflict
  if (format.fill) {
    try {
      if (format.fill.pattern === "none") {
        range.format.fill.clear();
      } else {
        const fillColor = sanitizeColor(format.fill.color);
        if (fillColor) {
          range.format.fill.color = fillColor;
        }
      }
    } catch (e) {
      console.warn("[Excel] Failed to apply fill formatting:", e);
    }
  }

  // Border styling
  if (format.borders) {
    try {
      const borderPositions = [
        { pos: Excel.BorderIndex.edgeTop, style: format.borders.top },
        { pos: Excel.BorderIndex.edgeBottom, style: format.borders.bottom },
        { pos: Excel.BorderIndex.edgeLeft, style: format.borders.left },
        { pos: Excel.BorderIndex.edgeRight, style: format.borders.right },
      ];

      for (const { pos, style } of borderPositions) {
        if (style) {
          const border = range.format.borders.getItem(pos);
          const borderColor = sanitizeColor(style.color);
          if (borderColor) border.color = borderColor;
          if (style.style) {
            border.style = mapBorderStyle(style.style);
          }
        }
      }
    } catch (e) {
      console.warn("[Excel] Failed to apply border formatting:", e);
    }
  }

  // Alignment
  if (format.alignment) {
    try {
      if (format.alignment.horizontal) {
        range.format.horizontalAlignment = mapHorizontalAlignment(format.alignment.horizontal);
      }
      if (format.alignment.vertical) {
        range.format.verticalAlignment = mapVerticalAlignment(format.alignment.vertical);
      }
      if (format.alignment.wrapText !== undefined) {
        range.format.wrapText = format.alignment.wrapText;
      }
      if (format.alignment.indent !== undefined && typeof format.alignment.indent === "number") {
        range.format.indentLevel = Math.max(0, Math.min(250, format.alignment.indent));
      }
      if (
        format.alignment.textRotation !== undefined &&
        typeof format.alignment.textRotation === "number"
      ) {
        range.format.textOrientation = Math.max(-90, Math.min(90, format.alignment.textRotation));
      }
    } catch (e) {
      console.warn("[Excel] Failed to apply alignment formatting:", e);
    }
  }
  
  // Number format — build a 2D array matching the range dimensions
  if (format.numberFormat) {
    try {
      const formatStr = typeof format.numberFormat === 'string' 
        ? format.numberFormat 
        : format.numberFormat.format;
      if (formatStr && typeof formatStr === 'string') {
        range.load("rowCount,columnCount");
        await range.context.sync();
        const rows = range.rowCount;
        const cols = range.columnCount;
        const fmt: string[][] = Array.from({ length: rows }, () => Array(cols).fill(formatStr));
        range.numberFormat = fmt;
      }
    } catch (e) {
      // This is the only sync in this helper, so it also flushes any content
      // write queued before formatting. Never suppress the rejection: doing
      // so could report a locked/invalid formula write as confirmed and commit
      // provenance for content that did not land.
      throw e;
    }
  }
}

// ============================================================================
// COMMENT FUNCTIONS
// ============================================================================

/**
 * Add a comment to a cell indicating it was modified by Finster
 * @param context The Excel request context
 * @param sheet The worksheet
 * @param address The cell address (e.g., "A1" or "A1:C5")
 * @param commentText Optional explicit comment text
 * @param action The action that was performed (for auto-generated comments)
 */
async function addFinsterComment(
  context: Excel.RequestContext,
  sheet: Excel.Worksheet,
  address: string,
  commentText?: string,
  action?: string
): Promise<void> {
  if (!addCommentsOnEdit && !commentText) {
    return;
  }

  try {
    const range = sheet.getRange(address);

    // For ranges, get just the first cell for the comment
    const firstCell = range.getCell(0, 0);
    firstCell.load("address");
    await context.sync();

    // Build comment text
    const timestamp = new Date().toLocaleString();
    let text = commentText;

    if (!text && addCommentsOnEdit) {
      // Auto-generate comment based on action
      const actionDesc = action || "modified";
      text = `Modified by Finster\nAction: ${actionDesc}\nCell: ${address}\nTime: ${timestamp}`;
    }

    if (text) {
      // Try to get existing comment at this cell and delete it
      try {
        const existingComment = sheet.comments.getItemByCell(firstCell);
        existingComment.delete();
        await context.sync();
      } catch {
        // No existing comment at this cell, that's fine
      }

      // Add new comment
      sheet.comments.add(firstCell, text);
      await context.sync();
    }
  } catch (e) {
    // Comments API may not be available in all Excel versions (requires Excel 2019+)
    console.warn(`[Excel] Could not add comment to ${address}:`, e);
  }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function mapBorderStyle(style: string): Excel.BorderLineStyle {
  // Map string style names to Excel BorderLineStyle values
  switch (style.toLowerCase()) {
    case "none":
      return "None" as Excel.BorderLineStyle;
    case "thin":
      return "Thin" as Excel.BorderLineStyle;
    case "medium":
      return "Medium" as Excel.BorderLineStyle;
    case "thick":
      return "Thick" as Excel.BorderLineStyle;
    case "dashed":
      return "Dash" as Excel.BorderLineStyle;
    case "dotted":
      return "Dot" as Excel.BorderLineStyle;
    case "double":
      return "Double" as Excel.BorderLineStyle;
    default:
      return "Thin" as Excel.BorderLineStyle;
  }
}

function mapHorizontalAlignment(align: string): Excel.HorizontalAlignment {
  const alignMap: Record<string, Excel.HorizontalAlignment> = {
    left: Excel.HorizontalAlignment.left,
    center: Excel.HorizontalAlignment.center,
    right: Excel.HorizontalAlignment.right,
    fill: Excel.HorizontalAlignment.fill,
    justify: Excel.HorizontalAlignment.justify,
  };
  return alignMap[align] || Excel.HorizontalAlignment.general;
}

function mapVerticalAlignment(align: string): Excel.VerticalAlignment {
  const alignMap: Record<string, Excel.VerticalAlignment> = {
    top: Excel.VerticalAlignment.top,
    center: Excel.VerticalAlignment.center,
    bottom: Excel.VerticalAlignment.bottom,
  };
  return alignMap[align] || Excel.VerticalAlignment.center;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get the selected range address.
 *
 * In FactSet intent-bridge mode, dispatches through
 * `factset.Spreadsheet.GetSelectedAddressRange`. The intent today returns
 * a bare A1-style range with no sheet prefix.
 */
export async function getSelectedRangeAddress(): Promise<string> {
  if (shouldUseExcelIntentBridge()) {
    return getSelectedAddressRange();
  }
  return Excel.run(async (context) => {
    const range = context.workbook.getSelectedRange();
    range.load("address");
    await context.sync();
    return range.address;
  });
}

/**
 * Resolve the address of the currently active cell (e.g. `"B3"`).
 *
 * In FactSet intent-bridge mode, dispatches through
 * `factset.Spreadsheet.GetActiveCellAddress`. Note the intent today returns
 * a bare cell with no sheet prefix — see
 * `spreadsheetIntents.ts::getActiveCellAddressViaIntents` and
 * `missing-intents-v0.md` §8.1.
 */
export async function getActiveCellAddress(): Promise<string> {
  if (shouldUseExcelIntentBridge()) {
    return getActiveCellAddressViaBridge();
  }
  return Excel.run(async (context) => {
    const cell = context.workbook.getActiveCell();
    cell.load("address");
    await context.sync();
    return cell.address;
  });
}

/**
 * Get the active sheet name.
 *
 * In FactSet intent-bridge mode, resolves the name via the
 * `factset.Spreadsheet.GetActiveExcelContext` intent (see
 * `excelHostOps.ts::getActiveSheetName`). Failures there are swallowed to
 * an empty string so the sidebar's 2s polling loop never throws.
 *
 * The intent's wire payload is fully typed; `excel-tools.md` is still
 * catching up (see `missing-intents-v0.md` §2.2), but Finster talks
 * to the host directly and trusts the typed shape.
 */
export async function getActiveSheetName(): Promise<string> {
  if (shouldUseExcelIntentBridge()) {
    try {
      return await getActiveSheetNameViaBridge();
    } catch (e) {
      console.warn("[Excel] Intent-bridge active-sheet resolution failed:", e);
      return "";
    }
  }
  return Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet();
    sheet.load("name");
    await context.sync();
    return sheet.name;
  });
}

/**
 * Get selected range data (for context).
 *
 * In FactSet intent-bridge mode, fans out to `GetSelectedAddressRange` to
 * resolve the address and then `GetRangeInfo` (with a `GetRangeAsCsv`
 * fallback) to read its values/formulas. Returns `null` if either leg
 * fails — matches the Office.js path's "no-selection" semantics.
 */
export async function getSelectedRangeData(): Promise<CellRange | null> {
  if (shouldUseExcelIntentBridge()) {
    let address: string;
    try {
      address = await getSelectedAddressRange();
    } catch (e) {
      console.warn("[Excel] GetSelectedAddressRange failed:", e);
      return null;
    }
    const range = await readKnownRangeViaBridge(address);
    return range ?? null;
  }
  return Excel.run(async (context) => {
    const range = context.workbook.getSelectedRange();
    range.load("address,values,formulas,rowCount,columnCount");

    await context.sync();

    return {
      address: range.address,
      startRow: 0,
      startColumn: 0,
      rowCount: range.rowCount,
      columnCount: range.columnCount,
      values: sanitizeValueMatrix(
        range.values as (string | number | boolean | null)[][]
      ) as (string | number | boolean | null)[][],
      formulas: range.formulas as (string | null)[][],
    };
  });
}

// ============================================================================
// FINSTER API INTEGRATIONS
// ============================================================================

/**
 * Insert a company logo as an image in Excel.
 *
 * Returns `{ success, message }` for every recoverable failure
 * (logo not found, image insert failed, etc.) so the AI apply Office.js
 * logo loop can warn-and-continue without aborting the surrounding apply.
 *
 * **Exception**: `InvalidOperationInCellEditMode` is re-thrown so the
 * surrounding apply's outer catch can surface the unified L5 abort
 * message. Without this, the caller (which only checks `result.success`)
 * would loop into every subsequent logo and re-hit the same edit-mode
 * error per entry instead of stopping cleanly on the first hit.
 *
 * @param companyName The company name to fetch logo for
 * @param cellAddress The cell address where the logo should be placed (e.g., "A1")
 * @param width Optional width in pixels (default 60)
 * @param height Optional height in pixels (default 60)
 */
export async function insertCompanyLogo(
  companyName: string,
  cellAddress: string,
  width: number = 60,
  height: number = 60,
  sheetName?: string,
): Promise<{ success: boolean; message: string }> {
  try {
    // Fetch logo URL from Finster API
    const logoResult = await getCompanyLogo(companyName);

    if (!logoResult.success || !logoResult.logoUrl) {
      return {
        success: false,
        message: `Could not find logo for ${companyName}: ${logoResult.error || "Not found"}`,
      };
    }

    // Insert the image into Excel
    return Excel.run(async (context) => {
      const sheet = sheetName
        ? context.workbook.worksheets.getItem(sheetName)
        : context.workbook.worksheets.getActiveWorksheet();
      const range = sheet.getRange(cellAddress);
      range.load("left,top");
      await context.sync();

      // Add the image at the cell's position
      const image = sheet.shapes.addImage(logoResult.logoUrl!);
      image.left = range.left;
      image.top = range.top;
      image.width = width;
      image.height = height;
      image.name = `Logo_${companyName.replace(/\s+/g, "_")}`;

      await context.sync();

      return {
        success: true,
        message: `Inserted ${companyName} logo at ${cellAddress}`,
      };
    });
  } catch (error) {
    // Edit-mode errors must throw rather than be folded into the result
    // object: callers of this function (the AI apply Office.js logo loop
    // and `insertCompanyLogos`) only check `result.success` and would
    // otherwise continue calling the next logo, re-hitting the same
    // edit-mode error per entry. Throwing routes the error to the
    // surrounding apply's outer catch (or `insertCompanyLogos`' caller)
    // which surfaces the unified `EDIT_MODE_USER_MESSAGE`.
    if (isEditModeError(error)) throw error;
    console.error("[Excel] Failed to insert logo:", error);
    return {
      success: false,
      message: `Failed to insert logo: ${error instanceof Error ? error.message : String(error)}`,
    };
  }
}

/**
 * Insert multiple company logos in a column.
 *
 * Preserves the batch return contract even when an individual insert
 * throws. Edit-mode errors stop the loop after recording the current
 * failure so callers receive partial results instead of a rejected
 * promise, while avoiding a cascade of identical host failures.
 *
 * @param companies Array of company names
 * @param startCell Starting cell (e.g., "A1")
 * @param logoSize Size in pixels for logos
 */
export async function insertCompanyLogos(
  companies: string[],
  startCell: string = "A1",
  logoSize: number = 40
): Promise<{
  success: boolean;
  results: Array<{ company: string; success: boolean; message: string }>;
}> {
  const results: Array<{ company: string; success: boolean; message: string }> = [];

  // Parse start cell
  const match = startCell.match(/^([A-Z]+)(\d+)$/i);
  if (!match) {
    return {
      success: false,
      results: [{ company: "", success: false, message: `Invalid start cell: ${startCell}` }],
    };
  }

  const col = match[1];
  let row = parseInt(match[2]);

  for (const company of companies) {
    const cellAddress = `${col}${row}`;
    try {
      const result = await insertCompanyLogo(company, cellAddress, logoSize, logoSize);
      results.push({ company, ...result });
    } catch (error) {
      if (isEditModeError(error)) {
        const copy = hostReadyRetryCopy("stuck_edit");
        results.push({
          company,
          success: false,
          message: copy.messageOneLiner,
        });
        break;
      }
      results.push({
        company,
        success: false,
        message: `Failed to insert logo: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
    row++;
  }

  return {
    success: results.every((r) => r.success),
    results,
  };
}

// ============================================================================
// EXECUTOR READ HELPERS (exported for executorReadTools.ts)
// ============================================================================

/**
 * Metadata shape returned by `listWorksheetsMetadata`. A projection of
 * `AdaptedWorkbookSheet` into a stable public contract for the executor
 * read tools — no internal `UsedRangeBox` exposure.
 */
export interface WorksheetMetadata {
  /** Worksheet display name as it appears in the tab bar. This is the sheet identity. */
  name: string;
  /** Tab-order position in the workbook (host-dependent 0- or 1-based). Not an identity. */
  index: number;
  /**
   * Excel-native visibility (`Visible` / `Hidden` / `VeryHidden`).
   * Host COM constants (`xlSheetVisible` / `xlSheetHidden` /
   * `xlSheetVeryHidden`) are mapped here. Omitted when unknown, not Visible.
   */
  visibility?: "Visible" | "Hidden" | "VeryHidden";
  /**
   * Used-range bounds.
   * - object: host returned a parseable used range
   * - `null`: host confirmed the sheet is blank
   * - omitted: host did not send `usedRange` (unknown, not blank)
   */
  usedRange?: {
    /** Bare A1 rectangle for the used range (e.g. `"D5:F10"`). */
    address: string;
    /** Number of rows in that rectangle. */
    rowCount: number;
    /** Number of columns in that rectangle. */
    columnCount: number;
  } | null;
  /** Host sheet kind (`xlWorksheet`, `xlChart`, …). Absent when the host omitted it. */
  sheetType?: string;
}

/**
 * Lists worksheet metadata for the active/pinned workbook via the
 * Bridge `GetWorkbookSheets` intent. Wraps the private
 * `adaptWorkbookSheets` adapter so external callers (executor read
 * tools) get the same parsing of casing variants, `{sheets}`/`{items}`
 * envelopes, string/object/quad used-range shapes, sheetType, and
 * visibility normalization.
 *
 * Throws when the host payload is not a recognisable sheet list
 * (`null`, `{}`, `{ sheets: null }`, `{ sheets: "x" }`). A genuine
 * empty workbook returns `[]` — those two must not be conflated.
 * Each entry's `usedRange` is the tight rectangle (may not start at
 * A1); `null` means confirmed blank; omitted means unknown.
 *
 * Does NOT read cell values — use `readRangeContent` for that.
 *
 * @param workbookName - Target workbook name. Omit or pass `undefined`
 *   to use the active/pinned workbook.
 * @returns Array of worksheet metadata entries, one per sheet in
 *   workbook tab order.
 */
export async function listWorksheetsMetadata(
  workbookName?: string,
): Promise<WorksheetMetadata[]> {
  const raw = await getWorkbookSheets(workbookName, {
    properties: ["usedRange", "visibility"],
  });
  if (!isWorkbookSheetsPayload(raw)) {
    throw new Error("GetWorkbookSheets: response did not include `sheets`");
  }
  const adapted = adaptWorkbookSheets(raw);
  return adapted.map((s) => {
    const meta: WorksheetMetadata = {
      name: s.name,
      index: s.index,
    };
    if (s.visibility) {
      meta.visibility = s.visibility;
    }
    if (s.sheetType) {
      meta.sheetType = s.sheetType;
    }
    if (s.usedRange) {
      meta.usedRange = projectUsedRangeBox(s.usedRange);
    } else if (s.usedRangePresent && !s.usedRangeUnparsed) {
      meta.usedRange = null;
    }
    return meta;
  });
}

/**
 * True when the host payload is a recognisable sheet list (bare array,
 * `{ sheets: [] }`, or `{ items: [] }`).
 *
 * `adaptWorkbookSheets` cannot make this call itself: it returns `[]`
 * for both a genuinely empty workbook and an unrecognised payload. That
 * made a transport/shape failure indistinguishable from "this workbook
 * has no sheets", so the agent would conclude there was nothing to read
 * instead of retrying. Callers gate on this first and throw.
 */
function isWorkbookSheetsPayload(raw: unknown): boolean {
  if (Array.isArray(raw)) return true;
  const rec = asRecord(raw);
  if (!rec) return false;
  return Array.isArray(rec.sheets) || Array.isArray(rec.items);
}

/**
 * Projects an internal {@link UsedRangeBox} into the public
 * `{ address, rowCount, columnCount }` triple.
 *
 * Counts are rectangle extents measured from the box's own origin, not
 * bottom-right indices: `D5:F10` is 6 rows × 3 columns. Reporting
 * `lastRow` / `lastCol` instead (the previous behaviour) overstated
 * both whenever the used range did not start at `A1`.
 */
function projectUsedRangeBox(box: UsedRangeBox): {
  address: string;
  rowCount: number;
  columnCount: number;
} {
  const startRow = box.startRow > 0 ? box.startRow : 1;
  const startCol = box.startCol > 0 ? box.startCol : 1;
  return {
    address:
      box.address ||
      a1RangeFromBounds(startRow, startCol, box.lastRow, box.lastCol),
    rowCount: box.lastRow - startRow + 1,
    columnCount: box.lastCol - startCol + 1,
  };
}

/** Options for `readRangeContent`. */
export interface ReadRangeContentOptions {
  /** When true, request formula TEXT alongside values. */
  includeFormulas?: boolean;
  /** When true, request number-format metadata. */
  includeFormats?: boolean;
  /** When true, ask the host to include empty cells. The executor still pads. */
  includeEmptyCells?: boolean;
}

/** Result of `readRangeContent`. */
export interface ReadRangeContentResult {
  /** Adapted rectangle, or `undefined` when `emptyRange` (confirmed blank). */
  range?: CellRange;
  /**
   * True when clipped by `MAX_EXECUTOR_READ_CELLS`. Can be true
   * together with `emptyRange` — clip runs before the host call, so a
   * confirmed-blank read of a huge request is still truncated.
   */
  truncated: boolean;
  /**
   * Confirmed blank. Distinct from a thrown or unusable GetRangeInfo
   * plus empty CSV (dual-host failure).
   */
  emptyRange?: boolean;
  /** Evidence path used to construct the matrix. */
  evidenceSource?: "rich" | "csv_fallback" | "rich_blank";
  /**
   * Observation caveats (`csv_fallback_used`, `formulas_unavailable`).
   * A recognised empty ParseFormulas list is not `formulas_unavailable`.
   */
  warnings?: string[];
}

/**
 * Safety ceiling: maximum cells per single `readRangeContent` call.
 * Prevents the agent from accidentally requesting a million-cell range
 * and blowing context/cost. When a request exceeds this, rows are
 * clipped and `truncated: true` is returned so the agent can page.
 */
const MAX_EXECUTOR_READ_CELLS = 20_000;

/**
 * Shrink a requested rectangle so it contains at most
 * `MAX_EXECUTOR_READ_CELLS` before we call the host or allocate a
 * padded matrix. Rows are dropped from the bottom; column count stays.
 */
function clipReadAddressToCeiling(
  address: string,
  bounds: { topLeft: ParsedCell; bottomRight: ParsedCell },
): {
  address: string;
  truncated: boolean;
  bounds: { topLeft: ParsedCell; bottomRight: ParsedCell };
} {
  const rowCount = bounds.bottomRight.row - bounds.topLeft.row + 1;
  const columnCount = bounds.bottomRight.col - bounds.topLeft.col + 1;
  if (columnCount <= 0 || rowCount * columnCount <= MAX_EXECUTOR_READ_CELLS) {
    return { address, truncated: false, bounds };
  }
  const maxRows = Math.max(1, Math.floor(MAX_EXECUTOR_READ_CELLS / columnCount));
  const lastRow = bounds.topLeft.row + maxRows - 1;
  const clippedBounds = {
    topLeft: bounds.topLeft,
    bottomRight: { row: lastRow, col: bounds.bottomRight.col },
  };
  const bare = a1RangeFromBounds(
    clippedBounds.topLeft.row,
    clippedBounds.topLeft.col,
    clippedBounds.bottomRight.row,
    clippedBounds.bottomRight.col,
  );
  const bang = address.lastIndexOf("!");
  const clippedAddress = bang >= 0 ? `${address.slice(0, bang)}!${bare}` : bare;
  return { address: clippedAddress, truncated: true, bounds: clippedBounds };
}

/**
 * Expand a tight/sparse `CellRange` onto the requested rectangle so
 * empty cells stay as `null` at their real coordinates. Cells that
 * fall outside the destination (should not happen after a clip) are
 * dropped.
 */
function padCellRangeToBounds(
  range: CellRange,
  bounds: { topLeft: ParsedCell; bottomRight: ParsedCell },
  requestAddress: string,
): CellRange {
  const destStartRow = bounds.topLeft.row - 1;
  const destStartCol = bounds.topLeft.col - 1;
  const rowCount = bounds.bottomRight.row - bounds.topLeft.row + 1;
  const columnCount = bounds.bottomRight.col - bounds.topLeft.col + 1;
  const values = makeValueMatrix(rowCount, columnCount);
  const formulas = range.formulas
    ? makeNullableStringMatrix(rowCount, columnCount)
    : undefined;
  const numberFormats = range.numberFormats
    ? makeNullableStringMatrix(rowCount, columnCount)
    : undefined;
  const cellStyles: CellRange["cellStyles"] = range.cellStyles ? {} : undefined;

  for (let r = 0; r < range.rowCount; r++) {
    for (let c = 0; c < range.columnCount; c++) {
      const destR = range.startRow - destStartRow + r;
      const destC = range.startColumn - destStartCol + c;
      if (destR < 0 || destC < 0 || destR >= rowCount || destC >= columnCount) {
        continue;
      }
      values[destR][destC] = range.values[r]?.[c] ?? null;
      if (formulas) {
        formulas[destR][destC] = range.formulas?.[r]?.[c] ?? null;
      }
      if (numberFormats) {
        numberFormats[destR][destC] = range.numberFormats?.[r]?.[c] ?? null;
      }
    }
  }
  if (range.cellStyles && cellStyles) {
    for (const [key, style] of Object.entries(range.cellStyles)) {
      const [sr, sc] = key.split(",").map(Number);
      const destR = range.startRow - destStartRow + sr;
      const destC = range.startColumn - destStartCol + sc;
      if (destR < 0 || destC < 0 || destR >= rowCount || destC >= columnCount) {
        continue;
      }
      cellStyles[`${destR},${destC}`] = style;
    }
  }

  const bang = requestAddress.lastIndexOf("!");
  const address = bang >= 0 ? requestAddress.slice(bang + 1) : requestAddress;

  return {
    address,
    startRow: destStartRow,
    startColumn: destStartCol,
    rowCount,
    columnCount,
    values,
    formulas,
    numberFormats,
    cellStyles,
    totalRowCount: range.totalRowCount,
  };
}

function makeValueMatrix(
  rowCount: number,
  columnCount: number,
): (string | number | boolean | null)[][] {
  return Array.from({ length: rowCount }, () =>
    Array.from({ length: columnCount }, () => null),
  );
}

function makeNullableStringMatrix(
  rowCount: number,
  columnCount: number,
): (string | null)[][] {
  return Array.from({ length: rowCount }, () =>
    Array.from({ length: columnCount }, (): string | null => null),
  );
}

function cellRangeHasFormulaText(range: CellRange): boolean {
  if (!range.formulas) return false;
  for (const row of range.formulas) {
    for (const cell of row) {
      if (typeof cell === "string" && cell.length > 0) return true;
    }
  }
  return false;
}

/**
 * Recognised ParseFormulas shape is `{ address, formula }[]`.
 *
 * - `[]`: host confirmed no formula TEXT. Return `range` unchanged so
 *   the caller does not warn `formulas_unavailable`.
 * - Non-empty recognised records: merge formula strings onto the
 *   requested rectangle.
 * - Anything else (non-array, malformed items): unknown. Return
 *   `undefined`; keep values, do not invent formula strings.
 *
 * Well-formed records that none land in the destination still return
 * `undefined` — we received formulas we could not apply.
 */
function mergeFormulaRecordsOntoRange(
  range: CellRange,
  payload: unknown,
): CellRange | undefined {
  if (!Array.isArray(payload)) return undefined;
  const records: Array<{ address: string; formula: string }> = [];
  for (const item of payload) {
    if (item === null || typeof item !== "object") return undefined;
    const rec = item as Record<string, unknown>;
    if (typeof rec.address !== "string" || typeof rec.formula !== "string") {
      return undefined;
    }
    records.push({ address: rec.address, formula: rec.formula });
  }
  if (records.length === 0) return range;

  const formulas = range.formulas
    ? range.formulas.map((row) => row.slice())
    : makeNullableStringMatrix(range.rowCount, range.columnCount);
  let placed = 0;
  for (const rec of records) {
    const bounds = parseA1RangeBounds(splitSheetPrefix(rec.address).range);
    if (!bounds) continue;
    const destR = bounds.topLeft.row - 1 - range.startRow;
    const destC = bounds.topLeft.col - 1 - range.startColumn;
    if (
      destR < 0 ||
      destC < 0 ||
      destR >= range.rowCount ||
      destC >= range.columnCount
    ) {
      continue;
    }
    formulas[destR][destC] = rec.formula;
    placed += 1;
  }
  if (placed === 0) return undefined;
  return { ...range, formulas };
}

/**
 * When formula TEXT was requested and GetRangeInfo did not provide any,
 * merge ParseFormulas StringOnly records `{ address, formula }[]`.
 * Skip the extra host call if the rich read already has formula strings.
 * A recognised empty list is confirmed non-formulas (no warning).
 * Unknown payloads do not invent formulas — they surface
 * `formulas_unavailable`.
 */
async function maybeFillFormulasFromParseFormulas(
  range: CellRange,
  options: ReadRangeContentOptions | undefined,
  sheetName: string,
  workbookName: string | undefined,
  resolvedAddress: string,
): Promise<{ range: CellRange; warnings: string[] }> {
  if (!options?.includeFormulas) return { range, warnings: [] };
  if (cellRangeHasFormulaText(range)) return { range, warnings: [] };

  try {
    const payload = await parseFormulas(
      resolvedAddress,
      "StringOnly",
      sheetName,
      workbookName,
    );
    const merged = mergeFormulaRecordsOntoRange(range, payload);
    if (merged) return { range: merged, warnings: [] };
  } catch (e) {
    if (isExcelAbortError(e)) throw e;
    console.warn(
      `[Excel] readRangeContent: ParseFormulas failed for "${sheetName}" range "${resolvedAddress}":`,
      e,
    );
  }
  return { range, warnings: ["formulas_unavailable"] };
}

/**
 * Reads one A1 rectangle from a worksheet via GetRangeInfo, with
 * GetRangeAsCsv as fallback.
 *
 * The returned matrix is the requested rectangle: empty cells stay
 * `null` at their real coordinates rather than being dropped. Unparseable
 * addresses (`A:A`, `""`) throw before the host is called. `address: null`
 * uses the sheet's used range from `listWorksheetsMetadata`; an omitted
 * usedRange throws (unknown, not blank); `usedRange: null` returns
 * `emptyRange: true`.
 *
 * Clip to `MAX_EXECUTOR_READ_CELLS` happens before pad so a huge
 * request cannot allocate a 20k+ null grid. A confirmed-blank read of
 * a clipped address still sets `truncated` so the agent pages rather
 * than treating rows past the ceiling as non-existent. CSV fallback is
 * RFC 4180 (quoted commas stay one cell) and is also padded to the
 * request. A thrown or unusable GetRangeInfo plus empty CSV is a
 * failed read, not a blank range; only a well-formed empty envelope
 * (`[]` or `{ rangeInfo: [] }`) is `emptyRange`. When `includeFormulas`
 * is set and GetRangeInfo has no formula TEXT, ParseFormulas StringOnly
 * is merged. A recognised empty list is confirmed non-formulas (no
 * warning); unknown payloads surface `formulas_unavailable`.
 *
 * @param sheetName - Worksheet name to read from.
 * @param address - A1-notation range address, or `null` to read the
 *   sheet's used range (may not start at A1).
 * @param options - Optional flags to include formulas or number formats.
 * @param workbookName - Target workbook name. Omit or pass `undefined`
 *   to use the active/pinned workbook.
 * @returns Adapted `CellRange` (or `undefined` when blank), plus
 *   `truncated` / `emptyRange` / `evidenceSource` / `warnings`.
 */
export async function readRangeContent(
  sheetName: string,
  address: string | null,
  options?: ReadRangeContentOptions,
  workbookName?: string,
): Promise<ReadRangeContentResult> {
  let resolvedAddress: string;
  let richReadReturnedEmpty = false;
  let requestClipped = false;

  if (address === null) {
    const sheets = await listWorksheetsMetadata(workbookName);
    const sheet = sheets.find(
      (s) => s.name.toLowerCase() === sheetName.toLowerCase(),
    );
    if (!sheet) {
      throw new Error(`read_range: sheet "${sheetName}" not found`);
    }
    // Omitted usedRange is unknown, not blank — the agent must pass
    // an explicit address rather than conclude the sheet is empty.
    if (sheet.usedRange === undefined) {
      throw new Error(
        "usedRange is unknown; pass an explicit address",
      );
    }
    if (sheet.usedRange === null) {
      return { range: undefined, truncated: false, emptyRange: true };
    }
    resolvedAddress = sheet.usedRange.address;
  } else {
    resolvedAddress = address;
  }

  const { range: bareResolved } = splitSheetPrefix(resolvedAddress);
  const requestBounds = parseA1RangeBounds(bareResolved);
  if (!requestBounds) {
    throw new Error(`unparseable address: ${resolvedAddress || "(empty)"}`);
  }

  const clipped = clipReadAddressToCeiling(resolvedAddress, requestBounds);
  resolvedAddress = clipped.address;
  requestClipped = clipped.truncated;
  const destBounds = clipped.bounds;

  // Always request the "values" property group explicitly — relying on the
  // host default is brittle and inconsistent with the other rich-read paths
  // (probe ladder / used-range), which all pass ["values", "formulas"].
  // Formulas are requested via the `properties: ["formula"]` field rather
  // than the "formulas" property group: the formulas group was found to be
  // unsupported on this host (it returned no formula strings), while the
  // explicit "formula" property reliably populates them.
  const properties: string[] = [];
  const propertyGroups: string[] = ["values"];
  if (options?.includeFormulas) {
    properties.push("formula");
  }
  if (options?.includeFormats) {
    properties.push(...RANGE_INFO_FORMAT_PROPERTIES);
  }

  try {
    const rangeInfo = await getRangeInfo(
      resolvedAddress,
      {
        propertyGroups,
        properties: properties.length > 0 ? properties : undefined,
        includeEmptyCells: options?.includeEmptyCells === true,
      },
      sheetName,
      workbookName,
    );
    if (isDiagEnabled() && options?.includeFormulas) {
      logGetRangeInfoFormulaDiagnostics({
        sheetName,
        requestedAddress: resolvedAddress,
        workbookName,
        propertyGroups,
        properties,
        raw: rangeInfo,
      });
    }
    if (isDiagEnabled() && options?.includeFormats) {
      const records = Array.isArray(rangeInfo) ? rangeInfo : [];
      const fontSamples = records.slice(0, 10).map((rec: unknown, i: number) => {
        const r = rec as Record<string, unknown>;
        return { index: i, font: r.font, fontSize: (r.font as Record<string, unknown>)?.size, fontName: (r.font as Record<string, unknown>)?.name };
      });
      console.log("[Finster][read_range font diagnostics]", {
        sheetName,
        requestedAddress: resolvedAddress,
        recordCount: records.length,
        fontSamples,
      });
    }
    const classified = classifyGetRangeInfoPayload(rangeInfo, resolvedAddress);
    if (isDiagEnabled() && options?.includeFormulas) {
      console.log("[Finster][read_range adapted formulas]", {
        sheetName,
        requestedAddress: resolvedAddress,
        adaptedAddress:
          classified.kind === "range" ? classified.range.address : undefined,
        formulasAvailable:
          classified.kind === "range"
            ? Boolean(classified.range.formulas)
            : false,
        formulaSample:
          classified.kind === "range"
            ? classified.range.formulas?.slice(0, 5)
            : undefined,
      });
    }
    if (classified.kind === "range") {
      const padded = padCellRangeToBounds(
        classified.range,
        destBounds,
        resolvedAddress,
      );
      const filled = await maybeFillFormulasFromParseFormulas(
        padded,
        options,
        sheetName,
        workbookName,
        resolvedAddress,
      );
      const capped = applyReadCeiling(filled.range);
      return {
        ...capped,
        truncated: capped.truncated || requestClipped,
        evidenceSource: "rich",
        warnings: filled.warnings.length > 0 ? filled.warnings : undefined,
      };
    }
    // Only a well-formed empty envelope is a confirmed blank. An
    // unusable success payload must not set this flag — empty CSV then
    // errors, matching a thrown GetRangeInfo.
    if (classified.kind === "empty") {
      richReadReturnedEmpty = true;
    }
  } catch (e) {
    if (isExcelAbortError(e)) throw e;
    console.warn(
      `[Excel] readRangeContent: GetRangeInfo failed for "${sheetName}" range "${resolvedAddress}", falling back to CSV:`,
      e,
    );
  }

  // CSV fallback
  try {
    const csv = await getRangeAsCsv(
      resolvedAddress,
      undefined,
      sheetName,
      workbookName,
    );
    const rows = parseCsvRows(csv);
    if (!rows) {
      return resolveEmptyCsvFallback({
        resolvedAddress,
        options,
        richReadReturnedEmpty,
        sheetName,
        requestClipped,
      });
    }
    const box = boundingBoxFromCsvRows(rows);
    if (!box) {
      return resolveEmptyCsvFallback({
        resolvedAddress,
        options,
        richReadReturnedEmpty,
        sheetName,
        requestClipped,
      });
    }
    const fallback = buildCellRangeFromCsvRows(
      rows,
      box.lastRow,
      box.lastCol,
      tightBoundingBoxAddress(box.lastRow, box.lastCol),
    );
    // The CSV box is 1-indexed relative to the requested range's top-left,
    // so re-anchor the fallback range to the requested address origin.
    // Without this the fallback would report the data as A1-anchored, and
    // the executor would verify / base follow-up writes on the wrong cells.
    const anchored = padCellRangeToBounds(
      anchorCsvRangeToRequested(fallback, resolvedAddress),
      destBounds,
      resolvedAddress,
    );
    const filled = await maybeFillFormulasFromParseFormulas(
      anchored,
      options,
      sheetName,
      workbookName,
      resolvedAddress,
    );
    const capped = applyReadCeiling(filled.range);
    return {
      ...capped,
      truncated: capped.truncated || requestClipped,
      evidenceSource: "csv_fallback",
      warnings: ["csv_fallback_used", ...filled.warnings],
    };
  } catch (e) {
    if (isExcelAbortError(e)) throw e;
    console.warn(
      `[Excel] readRangeContent: CSV fallback also failed for "${sheetName}" range "${resolvedAddress}":`,
      e,
    );
    throwDualHostReadFailure(sheetName, resolvedAddress);
  }
}

function throwDualHostReadFailure(
  sheetName: string,
  resolvedAddress: string,
): never {
  throw new Error(
    `GetRangeInfo and GetRangeAsCsv both failed for "${sheetName}" range "${resolvedAddress}"`,
  );
}

/**
 * CSV produced no grid. A well-formed empty GetRangeInfo envelope is a
 * confirmed blank rectangle; a thrown or unusable GetRangeInfo plus
 * empty CSV is a failed read, not "nothing here".
 *
 * `requestClipped` is OR'd onto the blank result because clip runs
 * before the host call — the synthesised rectangle is already the
 * shrunk address, so `truncated` would otherwise be lost.
 */
function resolveEmptyCsvFallback(args: {
  resolvedAddress: string;
  options?: ReadRangeContentOptions;
  richReadReturnedEmpty: boolean;
  sheetName: string;
  requestClipped: boolean;
}): ReadRangeContentResult {
  if (args.richReadReturnedEmpty) {
    const blank = synthesizeBlankRangeIfExplicit({
      resolvedAddress: args.resolvedAddress,
      options: args.options,
      richReadReturnedEmpty: true,
    });
    if (blank) {
      return {
        ...blank,
        truncated: blank.truncated || args.requestClipped,
      };
    }
  }
  throwDualHostReadFailure(args.sheetName, args.resolvedAddress);
}

function synthesizeBlankRangeIfExplicit(args: {
  resolvedAddress: string;
  options?: ReadRangeContentOptions;
  richReadReturnedEmpty: boolean;
}): ReadRangeContentResult | undefined {
  if (!args.richReadReturnedEmpty) return undefined;
  const { range } = splitSheetPrefix(args.resolvedAddress);
  const bounds = parseA1RangeBounds(range);
  if (!bounds) return undefined;

  const rowCount = bounds.bottomRight.row - bounds.topLeft.row + 1;
  const columnCount = bounds.bottomRight.col - bounds.topLeft.col + 1;
  if (rowCount <= 0 || columnCount <= 0) return undefined;

  const totalCells = rowCount * columnCount;
  const truncated = totalCells > MAX_EXECUTOR_READ_CELLS;
  const includedRows = truncated
    ? Math.max(1, Math.floor(MAX_EXECUTOR_READ_CELLS / columnCount))
    : rowCount;
  const nullRows = makeNullMatrix(includedRows, columnCount);
  const rangeResult: CellRange = {
    address: args.resolvedAddress,
    startRow: bounds.topLeft.row - 1,
    startColumn: bounds.topLeft.col - 1,
    rowCount: includedRows,
    columnCount,
    values: nullRows,
    totalRowCount: truncated ? rowCount : undefined,
  };

  if (args.options?.includeFormulas) {
    rangeResult.formulas = makeNullMatrix(includedRows, columnCount);
  }
  if (args.options?.includeFormats) {
    rangeResult.numberFormats = makeNullMatrix(includedRows, columnCount);
    rangeResult.cellStyles = {};
  }

  return {
    range: rangeResult,
    truncated,
    emptyRange: true,
    evidenceSource: "rich_blank",
  };
}

function makeNullMatrix(rowCount: number, columnCount: number): null[][] {
  return Array.from({ length: rowCount }, () =>
    Array.from({ length: columnCount }, () => null),
  );
}

/**
 * Re-anchor an A1-rooted CSV-fallback `CellRange` onto the requested
 * address origin. `buildCellRangeFromCsvRows` always returns a range
 * rooted at A1 (`startRow`/`startColumn` 0, `A1:...` address), but the
 * CSV the host returned for a non-A1 request (e.g. `B10:K28`) is
 * relative to that request's top-left. Without re-anchoring, the tool
 * would report the data at the wrong address, and the executor could
 * verify or base follow-up writes on the wrong cells.
 *
 * Falls back to the original range unchanged when the requested address
 * can't be parsed into cell bounds (e.g. whole-column `A:A`).
 */
function anchorCsvRangeToRequested(
  range: CellRange,
  resolvedAddress: string,
): CellRange {
  const { range: bareRange } = splitSheetPrefix(resolvedAddress);
  const bounds = parseA1RangeBounds(bareRange);
  if (!bounds) return range;

  const startRow = bounds.topLeft.row - 1;
  const startColumn = bounds.topLeft.col - 1;
  const topLeft = `${columnIndexToLetter(startColumn)}${startRow + 1}`;
  const bottomRight = `${columnIndexToLetter(startColumn + range.columnCount - 1)}${
    startRow + range.rowCount
  }`;
  const address =
    range.rowCount === 1 && range.columnCount === 1
      ? topLeft
      : `${topLeft}:${bottomRight}`;

  return { ...range, address, startRow, startColumn };
}

function logGetRangeInfoFormulaDiagnostics(args: {
  sheetName: string;
  requestedAddress: string;
  workbookName?: string;
  propertyGroups: string[];
  properties: string[];
  raw: unknown;
}): void {
  const root = asRecord(args.raw);
  const rangeInfo = root?.rangeInfo ?? args.raw;
  const records = Array.isArray(rangeInfo)
    ? rangeInfo.filter((item): item is Record<string, unknown> => !!item && typeof item === "object")
    : [];
  const sampleRecords = records.slice(0, 8);
  const formulaLikeKeys = Array.from(
    new Set(
      records.flatMap((record) =>
        Object.keys(record).filter((key) => key.toLowerCase().includes("formula")),
      ),
    ),
  );
  const sampleFormulaLikeValues = sampleRecords.map((record) =>
    Object.fromEntries(
      Object.entries(record).filter(([key]) => key.toLowerCase().includes("formula")),
    ),
  );

  console.log("[Finster][read_range raw GetRangeInfo]", {
    sheetName: args.sheetName,
    requestedAddress: args.requestedAddress,
    workbookName: args.workbookName,
    propertyGroups: args.propertyGroups,
    properties: args.properties,
    rawKeys: root ? Object.keys(root) : undefined,
    rangeInfoShape: Array.isArray(rangeInfo)
      ? "cellRecords"
      : rangeInfo && typeof rangeInfo === "object"
        ? "object"
        : typeof rangeInfo,
    recordCount: records.length,
    sampleRecords,
    formulaLikeKeys,
    sampleFormulaLikeValues,
    raw: args.raw,
  });
}

/**
 * Detects cancellation errors that must propagate out of `readRangeContent`
 * instead of being converted into a blank read result.
 */
function isExcelAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === "AbortError") ||
    (err instanceof Error && err.name === "AbortError")
  );
}

/**
 * Belt-and-suspenders clip after pad. The host request is already
 * bounded by `clipReadAddressToCeiling`; this still trims a range that
 * somehow arrived oversized.
 *
 * @param range - Cell range from the adapter or CSV fallback.
 * @returns The (possibly clipped) range and `truncated` when rows were
 *   removed.
 */
function applyReadCeiling(range: CellRange): ReadRangeContentResult {
  const totalCells = range.rowCount * range.columnCount;
  if (totalCells <= MAX_EXECUTOR_READ_CELLS) {
    return { range, truncated: false };
  }
  const maxRows = Math.max(1, Math.floor(MAX_EXECUTOR_READ_CELLS / range.columnCount));
  const clipped: CellRange = {
    ...range,
    values: range.values.slice(0, maxRows),
    formulas: range.formulas?.slice(0, maxRows),
    numberFormats: range.numberFormats?.slice(0, maxRows),
    cellStyles: range.cellStyles
      ? Object.fromEntries(
          Object.entries(range.cellStyles).filter(([key]) => {
            const rowIndex = Number(key.split(",")[0]);
            return Number.isInteger(rowIndex) && rowIndex < maxRows;
          }),
        )
      : undefined,
    rowCount: maxRows,
    totalRowCount: range.rowCount,
  };
  return { range: clipped, truncated: true };
}

